/**
 * Canvas V3 — full chrome (legacy /canvas left palette + top action bar +
 * right properties panel) with the V2 hierarchical-ELK + ReactFlow DAG
 * renderer (drill-in / ⌘K palette / version diff / exec streaming) swapped
 * into the centre. Internal task #182.
 *
 * Architecture
 * ────────────
 * - Inherits the legacy CanvasPage.jsx body wholesale via file copy so
 *   sidebar palette, top action bar, right Workflow/Task/Code tabs, all
 *   modals, all keyboard shortcuts, all hooks (useCanvas, etc.) keep
 *   working without porting.
 * - The legacy SVG DAG region is replaced by `<V2CanvasView/>` which
 *   takes `{nodes, connections}` from useCanvas and renders the V2
 *   hierarchical pipeline. SubWorkflow drill-in / ⌘K palette / exec
 *   overlay all ride on top.
 * - Selection, dirty tracking, save, execute and right-panel form sync
 *   stay routed through useCanvas — V2CanvasView reports clicks via
 *   onSelectNode and the host calls canvas.selectNode() in response.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCanvas, NODE_W, NODE_H, SHAPE_GEOM, getNodeShapeType } from '../hooks/useCanvas';
import { layoutDAG } from '../utils/dagLayout';
// IR pipeline (flatToIR → layoutIR → irToFlat) gated by the `irPipeline`
// feature flag. Off by default: `runLayout` resolves to `layoutDAG`. See
// utils/ir/* and utils/irAdapter.js for the new pipeline. Step 8 of IR build.
import { layoutDAGviaIR } from '../utils/irAdapter';
import { isIRPipelineEnabled, isCodeTabEnabled, isWorkerHostingEnabled, isCanvasPickerEnabled } from '../utils/featureFlags';
import TaskPicker from '../components/canvas/TaskPicker';
import { stageAutoCreateRunbook } from '../services/api';
import { getCuratedRecipes, getRecipeById } from '../data/canvasRecipes';
import { generateWorkerStub, fileExtensionFor, WORKER_STUB_LANGUAGES } from '../utils/workerStubGenerator';
import { jsToJq, buildJqConversionPatch } from '../utils/jsToJq';
/* G273 — shared mirror of the backend's Skill.requiresHumanApproval() so the HUMAN GATE
 * badge is computed from exactly what the runtime pauses on (displayed == enforced). */
import { requiresHumanApproval } from '../constants/autonomy';
// Right-panel "Code" tab — Conductor OSS-parity full-workflow JSON editor. Bidirectional:
// edit JSON → debounced parse → canvas re-renders via loadCanvasData. See #136.
import WorkflowCodeTab from '../components/canvas/WorkflowCodeTab';
// #182 — V2 hierarchical-ELK DAG renderer embedded as the centre panel.
// Owns its own layout, drill-in, ⌘K palette, diff/exec overlays.
import V2CanvasView from './v2/V2CanvasView';
// RC-18a / RC-19 — ⌘K Universal Command Palette. Modal-centric,
// RC-20e — V3CommandPalette removed entirely. Decision rationale:
// the palette duplicated functionality already covered by the TaskPicker
// (insert) and the platform top-bar (search), while introducing an
// extra hotkey (⌘⇧P) the operator found out of sync with the rest of
// the canvas UX. The Jump-to-Node palette (kept, rebound to ⌘⇧F) covers
// the "navigate to a node by name" case; the TaskPicker covers "add a
// new node" via the per-node "+" button and the ⌘E global trigger. One
// insertion mechanism, one navigation mechanism, no duplicate chord.
// RC-18b / RC-19 — Pre-save Governance Diff Modal. Fires when
// WorkflowPhaseAllowlistGuard rejects a save with 409/423 + structured
// diff body. Surfaces attemptedAdds/Removes/Mutates with a Regress
// button. V3-namespaced copy.
import V3GovernanceDiffModal from './v3/V3GovernanceDiffModal';
// RC-18c / RC-19 — Pure workflow validator. Returns
// {issues:[{severity, code, message, nodeId?}], stats}. Runs alongside
// V3's canvas.dagValidation as a richer soft check — flags missing
// required config per type, orphan/unreachable/dangling nodes, dead-end
// branches. Non-blocking; surfaces as a chip near the Save button.
import { validateWorkflow as v3ValidateWorkflow } from './v3/v3Validate';
// RC-18d / RC-19 — Per-type Inspector router. Dispatches to
// type-specific inspectors (LlmTask, ConnectorTask, DataTransform, etc.)
// for supported node types and falls through to V3's generic InputTab
// for everything else. Zero regression: unsupported types render
// exactly as today.
import V3Inspector from './v3/V3Inspector';
// #182-followup — Tier-S #1: AI Explainer panel sits at the top of the
// Task tab when a node is selected. Auto-fetches a Business + Technical
// breakdown via aiCanvasDebug with a focused subgraph prompt.
import AIExplainerPanel from './v2/AIExplainerPanel';
/* Bug #161 — Manual in-place node-type swap control. Renders in the
 * right-panel Task tab between the AI Explainer and the properties
 * panel, lets an operator swap a node's type deterministically via
 * the backend CanvasNodeSwapService. */
import NodeTypeSwapControl from '../components/canvas/NodeTypeSwapControl';
import { isAIFeatureEnabled } from '../utils/featureFlags';
import { useIRHeightFeedback } from '../hooks/useIRHeightFeedback';
// Phase R1: per-type metadata line rendered under the sublabel — HTTP shows
// method+URL, KAFKA shows topic, INLINE/LAMBDA show code preview, etc.
import { getTaskMetadata } from '../utils/nodeAnatomy';

/**
 * Pick between the IR pipeline and legacy Sugiyama layout based on the
 * runtime feature flag. Falls back to `layoutDAG` when the IR pipeline
 * returns null (malformed / unsupported canvas).
 */
function runLayout(nodes, connections, opts) {
  if (isIRPipelineEnabled()) {
    const positioned = layoutDAGviaIR(nodes, connections, opts);
    if (positioned != null) return positioned;
  }
  return layoutDAG(nodes, connections, opts);
}
import {
  NODE_CATEGORIES,
  NODE_TYPE_MAP,
  getNodeTypeDef,
  searchNodes,
  getCategoryNodes,
  getNodePorts,
} from '../data/soarNodes';
import { SOAR_PLAYBOOKS, PLAYBOOK_CATEGORIES, getPlaybookById } from '../data/soarPlaybooks';
import { useMarketplaceCatalogue, getMarketplaceConnectors } from '../data/marketplaceRegistry';
import { autoInsertEnforcementNodes, stripEnforcementNodes, isAutoInsertedNode, isPlatformEnforcementNode, getEnforcementNodeIds } from '../services/enforcementAutoInsertion';
import { resolveWorkflowConnectorRefs, findInstalledBySlug } from '../utils/connectorRefs';
import PolicyCoverageTab from '../components/canvas/PolicyCoverageTab';
import { connectWorkflowWebSocket, disconnectWorkflowWebSocket } from '../services/websocket';
import { DemoSequencePanel } from '../components/enforcement';
import { generateSoc2Certificate, simulateDemoFullSequence, getScheduleStatus,
  getWorkflowRequiredInputs, getAgent, getWorkflows,
  /* G238 — policy + framework binding fetches for canvas materialisation. */
  getInheritedPolicyBindings, listPolicyDocuments, listPolicyActivations,
  /* G243 — per-skill execution stats derived from the policy audit trail. */
  getPolicyAuditTrail,
  /* G258 — phase-advance from the canvas banner (mirror of agent
   * detail page gate-advance). Aliased to avoid colliding with the
   * local `advancingPhase` flag in the component. */
  advancePhase as apiAdvancePhase,
  /* G274 — the single reader of the advance-phase 200-on-refusal contract. */
  readAdvanceResult,
  /* G264 — server-side aggregator + reverse-sync endpoints. */
  getCanvasMaterialisation,
  reconcileCanvas,
  /* G268 — Agent-level phase regress (canvas pre-save diff modal action). */
  regressPhase as apiRegressPhase,
  /* updateAgent — flip executionMode + cognitiveBrief etc */
  updateAgent,
} from '../services/api';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../contexts/NavigationContext';
import { usePersistedState } from '../hooks/usePersistedState';
import {
  getNodeExecutions, getRunDagSnapshot, getWorkflowRun, retryFromFailedStep, replayWorkflowRun, resumeWorkflowRun, terminateWorkflowRun,
  getWorkflowAssignments, createWorkflowAssignment, removeWorkflowAssignment,
  getWorkflowAuditLog, getRunAuditLog, getWorkflowLinkedSkills,
  getUsers, getGroups, getAgents, getConnectors, aiCanvasBuild, aiCanvasBuildFromProcessFiles, aiCanvasDebug,
  getTemplates, getWorkflow,
  intelligenceToggleSystemWorkflow,
  disableSchedule, enableSchedule,
  archiveWorkflow, unarchiveWorkflow,
} from '../services/api';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Slideout } from '../components/v2/Slideout';
// RC-97e2 — V2 Slideout component for the Canvas Issues panel.
// Per feedback_v2_inline_over_modal memory: operator rejects modal
// overlays as breaking the V2 redesign; surfaces should use Slideout
// or anchored dropdowns. The other modals in this file (Save As,
// New Workflow, Execute Inputs, Template) are pre-V2 and live in V1
// chrome — leave them alone to scope-creep elsewhere. THIS panel is
// new (RC-97a) so it belongs on the V2 primitive from the start.
//
// RC-111 — `New Workflow` is the exception: operator flagged the
// modal as breaking the V2 redesign (see memory
// `feedback_v2_inline_over_modal` — "operator rejects modal overlays
// as breaking the V2 redesign; prefer anchored dropdowns/slideouts").
// Converted to V2Slideout below. Other modals (Save As, Execute
// Input, Template) stay on V1 chrome for now; they're separate RCs
// if/when Sony flags them.
import {
  Slideout as V2Slideout,
  Input as V2Input,
  Textarea as V2Textarea,
  Select as V2Select,
  FieldGroup as V2FieldGroup,
  Button as V2Button,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  ZoomIn, ZoomOut, Maximize2, Save, Play, Plus, Trash2, Settings, X,
  ArrowRight, Activity, Server, Cpu, Clock, CheckCircle, XCircle,
  AlertTriangle, RotateCw, ChevronDown, ChevronRight, Circle, Loader,
  Layers, GitBranch, Eye, Download, Zap, Shield, PlayCircle, Lock,
  Database, Bot, Plug, Filter, Search, Copy, Maximize, Minimize,
  Mail, Globe, Brain, Square, RefreshCw, StopCircle, SkipForward,
  Users, FileText, FileSpreadsheet, Upload, Clipboard, PenTool, List, UserPlus, Hash,
  MessageSquare, Sparkles, Send, Loader2, ChevronUp, Bug, Wrench, User, Archive, ArchiveRestore,
  Minus, Home, ChevronLeft, PanelLeftClose, PanelRightClose,
  History, Undo2, Redo2,
} from 'lucide-react';
// RC-117 Phase 1 — Versions slideout. Self-contained component; opens
// from the canvas toolbar, scans listWorkflowVersions on each open,
// shows per-version metadata + read-only definition viewer.
import WorkflowVersionsSlideout from './v2/WorkflowVersionsSlideout';

// ============================================================================
// ICON MAP - For dynamic icon rendering from node definitions
// ============================================================================

const ICON_MAP = {
  Zap, Shield, PlayCircle, Lock, Database, Bot, Plug, Play, Plus,
  Trash2, Settings, X, ArrowRight, Activity, Server, Cpu, Clock,
  CheckCircle, XCircle, AlertTriangle, RotateCw, ChevronDown,
  ChevronRight, Circle, Loader, Layers, GitBranch, Eye, Download,
  Filter, Search, Copy, Maximize, Minimize, ZoomIn, ZoomOut, Maximize2,
  Save, Mail, Globe, Brain,
};

const getIcon = (iconName) => {
  const Icon = ICON_MAP[iconName];
  return Icon || Zap;
};

// ============================================================================
// SINGLE-INSTANCE ENTRY NODE TYPES
//
// A workflow can have at most ONE entry/trigger node. Adding a second
// "manual_start", "webhook", "schedule", "siem_alert", "email_trigger" or
// "system_event_trigger" produces a graph with multiple roots — ELK's
// flatToIR throws hard when it sees more than one Start-class node, blanking
// the canvas. Every insertion path (palette drop, TaskPicker palette/edge/
// after-node/branch) consults this set and refuses the insertion when an
// entry node already exists. The check looks at the existing node `type`
// values, not labels, so it survives renames.
// ============================================================================
/* Phase B-fix.3 — narrowed to the canonical entry sentinel only.
 *
 * Pre-fix, every trigger-capable type (webhook / schedule / siem_alert /
 * email_trigger / system_event) was in this set, which meant the auto-seeded
 * `manual_start` (added to every fresh canvas by makeFreshSentinelPayload at
 * line 487) blocked operators from dropping ANY trigger node onto a new
 * canvas — they'd hit "This workflow already has an entry node ('Start')".
 *
 * Design (operator request): manual_start is the canonical entry sentinel
 * (one per workflow, auto-seeded, hidden from palette in a follow-up). All
 * other trigger nodes (Schedule, Webhook, SIEM Alert, Email Trigger,
 * System Event Trigger) become regular addable nodes — droppable anywhere
 * in the DAG, multiple allowed. The guard now ONLY refuses duplicate
 * manual_start.
 *
 * Runtime safety: CanvasDAGWorkflowImpl line 1065 already treats all these
 * trigger types as no-op trigger emitters, so mid-DAG triggers don't break
 * execution. flatToIR.findStart prefers manual_start over trigger-capable
 * tiebreakers, so the rendered START sentinel stays stable. */
const SINGLE_INSTANCE_ENTRY_TYPES = new Set([
  'manual_start', 'start',
]);

// ============================================================================
// AGENT-LINK BANNER — surfaces ?agentId= context above the canvas
//
// Mounted by the host CanvasPage when /canvas?agentId=<uuid> is opened.
// Tells the analyst this canvas is a sandbox materialisation of a specific
// agent's AgentDefinition, and offers a one-click jump back to the agent
// record. The banner is dismissible; the dismissal is in-memory only
// (a hard reload restores it) — that's intentional, since the URL is
// the source of truth for the linkage.
//
// Phase chip colour-codes Build / Decide / Act so the analyst can see
// whether the source agent is still being designed (Build), in review
// (Decide), or live (Act). The publish version, when present, is shown
// as a faint mono tag.
//
// Reference: AGENT_DEFINITION_JSON_PREVIEW_SPEC.md §4.
// ============================================================================

const PHASE_CHIP_COLORS = {
  build:    { bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)', fg: 'var(--v2-action-strong)' },
  decide:   { bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)', fg: 'var(--v2-action-strong)' },
  act:      { bg: 'var(--v2-action-soft)', border: 'var(--v2-danger-soft)', fg: 'var(--v2-danger)' },
  research: { bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)', fg: 'var(--v2-action-strong)' },
};

function AgentCanvasLinkBanner({ agent, workflowKind, onClose, onSync, syncing, phaseLockMessage, phaseLockKind, onAdvancePhase, onRegressPhase, advancing, regressing, onCycleExecutionMode, cyclingExecutionMode }) {
  /* Two-click inline confirm for the (destructive-ish) phase regress — platform-native
   * danger affordance, not a browser dialog. */
  const [regressArmed, setRegressArmed] = React.useState(false);
  const phase = (agent.phase || '').toLowerCase();
  /* Disarm whenever the phase actually changes. Without this the button stayed ARMED after a
   * successful regress — and since the target recomputes off the new phase (act→decide, then
   * decide→build), a stray second click would have regressed the agent AGAIN, one phase
   * further, with no confirmation. An armed destructive control must never survive the action
   * that armed it. */
  React.useEffect(() => { setRegressArmed(false); }, [phase]);
  const phaseLabel = phase === 'research' ? 'Build'
                    : phase ? (phase.charAt(0).toUpperCase() + phase.slice(1))
                    : '—';
  const chip = PHASE_CHIP_COLORS[phase] || PHASE_CHIP_COLORS.build;

  return (
    <div
      role="status"
      style={{
        margin: '10px 16px 0 16px',
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid var(--v2-action-soft)',
        background: 'linear-gradient(180deg, var(--v2-danger-soft) 0%, var(--v2-bg-surface) 80%)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
      <div style={{
        width: 30, height: 30, borderRadius: 6,
        background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-action-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Bot size={16} style={{ color: 'var(--v2-action)' }} />
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--v2-text-muted)', fontWeight: 500 }}>
          Linked to agent
        </span>
        <span style={{
          fontSize: 13, fontWeight: 650, color: 'var(--v2-text-primary)',
          maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={agent.name}>
          {agent.name || agent.id}
        </span>

        {/* Phase chip */}
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
          background: chip.bg, color: chip.fg, border: `1px solid ${chip.border}`,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {phaseLabel}
        </span>

        {/* Publish version — only shown when present */}
        {agent.publishVersion && (
          <span style={{
            fontSize: 10, fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
            color: 'var(--v2-text-muted)', background: 'var(--v2-bg-elevated)',
            padding: '2px 6px', borderRadius: 4, border: '1px solid var(--v2-border-default)',
          }}>
            v{agent.publishVersion}
          </span>
        )}

        {/* G242 / G264 — Execution mode chip is now a clickable toggle.
          * Click cycles deterministic → cognitive → hybrid → deterministic.
          * Backed by V172 schema (agent.execution_mode). PUT /agents/{id}
          * persists; canvas re-materialises on the next reload / Sync.
          *
          * Disabled when:
          *   - onCycleExecutionMode prop is unwired (read-only opens)
          *   - the agent is in a published phase (act) where mode is
          *     immutable per §7 invariant 1
          *   - the agent is an AutoLet (mode is runbook-derived)
          */}
        {agent.executionMode && (() => {
          const mode = String(agent.executionMode || 'deterministic').toLowerCase();
          const phase = String(agent.phase || '').toLowerCase();
          const isAutoLet = agent.isRunbookManaged === true
            || (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'));
          const lockedByPhase = phase === 'act';
          const clickable = typeof onCycleExecutionMode === 'function' && !isAutoLet && !lockedByPhase;
          const tooltipBase = mode === 'cognitive'
            ? 'Cognitive mode — LLM picks tools at runtime. Skills are a catalog, not a sequence.'
            : mode === 'hybrid'
              ? 'Hybrid mode — deterministic head/tail wrap an LLM-driven body.'
              : 'Deterministic mode — pre-authored DAG with explicit transitions.';
          const tooltipSuffix = !clickable
            ? (isAutoLet
                ? ' (read-only — AutoLets derive mode from the runbook)'
                : lockedByPhase
                  ? ' (locked — agent is in Act phase; execution mode is immutable across a published version)'
                  : '')
            : ' · click to cycle modes';
          const bg = mode === 'cognitive' ? 'var(--v2-accent-trust-soft)'
                   : mode === 'hybrid'    ? 'var(--v2-accent-cyan-soft)'
                   : 'var(--v2-bg-muted)';
          const fg = mode === 'cognitive' ? 'var(--v2-accent-trust)'
                   : mode === 'hybrid'    ? 'var(--v2-accent-cyan)'
                   : 'var(--v2-text-secondary)';
          const border = mode === 'cognitive' ? 'var(--v2-accent-trust-soft)'
                       : mode === 'hybrid'    ? 'var(--v2-accent-cyan-soft)'
                       : 'var(--v2-border-default)';
          return (
            <button
              type="button"
              onClick={clickable ? () => onCycleExecutionMode(mode) : undefined}
              disabled={!clickable || cyclingExecutionMode}
              title={tooltipBase + tooltipSuffix}
              style={{
                fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                background: bg, color: fg, border: `1px solid ${border}`,
                textTransform: 'uppercase', letterSpacing: 0.5,
                cursor: clickable ? (cyclingExecutionMode ? 'wait' : 'pointer') : 'not-allowed',
                opacity: cyclingExecutionMode ? 0.6 : 1,
                lineHeight: '14px',
              }}>
              {cyclingExecutionMode ? 'switching…' : mode}
            </button>
          );
        })()}

        <span style={{ fontSize: 11, color: 'var(--v2-text-disabled)' }}>·</span>
        <span style={{ fontSize: 11, color: 'var(--v2-text-muted)', lineHeight: 1.4 }}>
          {/* G279 — accurate reverse-sync messaging. The Let CEG (kind='let_canvas')
            * is a real, executable workflow whose Save reverse-syncs to the agent's
            * skills, phase-gated. The old copy ("edits don't touch the agent record,
            * reverse sync lands in 1.1") was stale/false. Non-let_canvas canvases
            * (standalone / autolet sandbox) genuinely don't write back. */}
          {workflowKind === 'let_canvas'
            ? "This is the agent's canonical execution graph. Saving syncs your changes to its skills — add/remove in Build, tune-only in Decide, locked in Act."
            : "This canvas is materialised from the agent's definition; edits here don't change the agent record."}
        </span>

        {/* G240 — Phase lock notice. Surfaces the current canvas governance
          * state inline with the agent context so the operator sees WHY
          * controls are disabled before clicking them. */}
        {phaseLockMessage && (
          <>
            <span style={{ fontSize: 11, color: 'var(--v2-text-disabled)' }}>·</span>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: phaseLockKind === 'full' ? 'var(--v2-danger)' : 'var(--v2-warning)',
              background: phaseLockKind === 'full' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)',
              border: `1px solid ${phaseLockKind === 'full' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)'}`,
              padding: '2px 8px', borderRadius: 4,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {phaseLockKind === 'full' ? '🔒 LOCKED' : '⚠️ Structural lock'}
              <span style={{ fontWeight: 400 }}>· {phaseLockMessage}</span>
            </span>
          </>
        )}
      </div>

      {/* G258 / LET_CANVAS_GOLDEN_RULES §1.4 — Phase-transition advance
        * directly on canvas. Operator should not need to bounce back to
        * the agent detail page just to advance Build → Decide → Act.
        *
        *   build  →  [Advance to Decide]
        *   decide →  [Advance to Act]
        *   act    →  (no advance — terminal)
        *
        * Regression is intentionally NOT on canvas — the canonical path
        * is per-skill regress on the agent detail page (G247), where the
        * confirm modal explains regressionCount + previousPhase audit
        * fields. The "↻ Regress" link below points operators there.
        *
        * Hidden for AutoLets (advance via runbook upload) and for any
        * agent without onAdvancePhase wired (read-only opens). */}
      {(() => {
        if (typeof onAdvancePhase !== 'function') return null;
        const phase = (agent.phase || '').toLowerCase();
        const isAutoLet = agent.isRunbookManaged === true
          || (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'));
        if (isAutoLet) return null;
        const nextLabel = phase === 'build' || phase === 'research' ? 'Decide'
          : phase === 'decide' ? 'Act' : null;
        if (!nextLabel) return null;
        return (
          <button
            type="button"
            onClick={() => onAdvancePhase(nextLabel.toLowerCase())}
            disabled={advancing}
            style={{
              fontSize: 11, fontWeight: 600,
              padding: '5px 10px', borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
              cursor: advancing ? 'wait' : 'pointer',
              opacity: advancing ? 0.6 : 1,
              color: 'var(--v2-bg-surface)', background: 'var(--v2-action)', border: '1px solid var(--v2-action-strong)',
            }}
            title={`Promote ${agent.name || 'this agent'} from ${phase || 'current phase'} to ${nextLabel.toLowerCase()} phase. Backend gate must pass.`}>
            {advancing ? 'Advancing…' : `Advance to ${nextLabel}`}
          </button>
        );
      })()}

      {/* Agent-level phase REGRESS.
        *
        * The lock banner tells the operator to "regress to Decide to edit" — but until now
        * nothing on ANY surface offered that action. `onRegressPhase` was a prop the banner
        * accepted and no call site ever passed, and the only apiRegressPhase() call sat
        * behind the pre-save governance-diff modal — which can't even fire on a fully-locked
        * Act canvas (the save is blocked before it reaches the backend). Net: the UI
        * prescribed a remedy it did not provide. This wires it where the instruction lives.
        *
        *   act    →  [Regress to Decide]   (unlocks the canvas; skills stay reviewed)
        *   decide →  [Regress to Build]    (unlocks structural edits)
        *   build  →  (nothing to regress to)
        *
        * Destructive-ish (regressSkill semantics are owned by the backend), so it asks first.
        * Hidden for AutoLets — their CEG is runbook-derived. */}
      {(() => {
        if (typeof onRegressPhase !== 'function') return null;
        const rPhase = (agent.phase || '').toLowerCase();
        const isAutoLet = agent.isRunbookManaged === true
          || (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'));
        if (isAutoLet) return null;
        const target = rPhase === 'act' ? 'decide' : rPhase === 'decide' ? 'build' : null;
        if (!target) return null;
        const targetLabel = target.charAt(0).toUpperCase() + target.slice(1);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => (regressArmed ? onRegressPhase(target) : setRegressArmed(true))}
              disabled={regressing}
              style={{
                fontSize: 11, fontWeight: 600,
                padding: '5px 10px', borderRadius: 6,
                display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                cursor: regressing ? 'wait' : 'pointer',
                opacity: regressing ? 0.6 : 1,
                color: regressArmed ? 'var(--v2-bg-surface)' : 'var(--v2-danger)',
                background: regressArmed ? 'var(--v2-danger)' : 'var(--v2-bg-surface)',
                border: '1px solid var(--v2-danger-soft)',
              }}
              title={`Regress ${agent.name || 'this agent'} from ${rPhase} back to ${target}. Unlocks the canvas; skills at or above ${target} are demoted and must be re-advanced through the B-D-A gate.`}>
              {regressing
                ? 'Regressing…'
                : regressArmed
                  ? `Confirm regress to ${targetLabel}`
                  : `Regress to ${targetLabel}`}
            </button>
            {regressArmed && !regressing && (
              <button
                type="button"
                onClick={() => setRegressArmed(false)}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '5px 8px', borderRadius: 6,
                  color: 'var(--v2-text-secondary)', background: 'transparent',
                  border: '1px solid var(--v2-border-subtle)', cursor: 'pointer', flexShrink: 0,
                }}>
                Cancel
              </button>
            )}
          </span>
        );
      })()}

      {/* G236 — Sync-from-agent button. Re-fetches the agent's skills and
        * rebuilds the canvas DAG so the Let-pipeline reflects what the agent
        * can actually do after the operator adds/removes skills on the
        * Build tab. AutoLets never get this button (their CEG is runbook-
        * derived; banner is hidden for them anyway by the agent-link
        * useEffect). Reverse sync (canvas → agent.skills) lands in a
        * future ticket and requires a backend reconciler. */}
      {typeof onSync === 'function' && (
        <button
          type="button"
          onClick={onSync}
          disabled={syncing}
          title="Re-pull this agent's skills and rebuild the canvas DAG. Edits beyond the skill pipeline will be replaced."
          style={{
            fontSize: 11, fontWeight: 600, color: syncing ? 'var(--v2-text-disabled)' : 'var(--v2-action)',
            background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-action-soft)', borderRadius: 6,
            padding: '5px 10px', cursor: syncing ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
          }}
        >
          {syncing ? 'Syncing…' : 'Sync from agent skills'}
        </button>
      )}
      <a
        /* Path-based route — /agents/{id} lands on the agent detail page.
         * The previous `/agents?agentId=…` query-param form landed on the
         * generic Agentlets list page (the list doesn't route by query
         * param to detail). Mirrors the same shape used at line 7647 of
         * this file for the Test Run / Act-phase deep link. */
        href={`/agents/${encodeURIComponent(agent.id)}`}
        style={{
          fontSize: 11, fontWeight: 600, color: 'var(--v2-action)',
          textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '5px 10px', borderRadius: 6, border: '1px solid var(--v2-action-soft)',
          background: 'var(--v2-bg-surface)', flexShrink: 0,
        }}
        title="Back to agent record"
      >
        Back to Agent <ArrowRight size={11} />
      </a>

      <button
        type="button"
        onClick={onClose}
        title="Dismiss banner"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 4, color: 'var(--v2-text-disabled)', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', borderRadius: 4,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ============================================================================
// ERROR BOUNDARY — catches runtime errors so the whole page doesn't go white
// ============================================================================

class CanvasErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[CanvasErrorBoundary]', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, background: 'var(--v2-danger-soft)', border: '2px solid var(--v2-danger-soft)', borderRadius: 8, margin: 8 }}>
          <h3 style={{ color: 'var(--v2-danger)', fontSize: 14, marginBottom: 8 }}>⚠️ Component Error</h3>
          <pre style={{ fontSize: 11, color: 'var(--v2-danger)', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', maxHeight: 200, overflow: 'auto', maxWidth: '100%' }}>
            {this.state.error?.toString()}
            {this.state.errorInfo?.componentStack}
          </pre>
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            style={{ marginTop: 8, padding: '6px 12px', background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-danger-soft)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// CANVAS HELPERS
// ============================================================================

/**
 * Build a fresh "blank workflow" payload — a Start sentinel connected
 * straight to an End sentinel. Used both when the user explicitly
 * creates a new workflow AND when the canvas needs to reset itself
 * (e.g. after the currently-selected workflow is deleted) so the user
 * never sees the empty `{nodes:[], connections:[]}` orphan in the Code
 * tab + a "Select a workflow…" dropdown — they immediately get a
 * usable virgin canvas instead.
 *
 * Ids are stamped with the current timestamp so successive resets get
 * fresh ids and don't collide with each other.
 */
function makeFreshSentinelPayload() {
  const t = Date.now();
  const startId = `n_${t}_start`;
  const endId = `n_${t}_end`;
  return {
    nodes: [
      { id: startId, type: 'manual_start', x: 400, y: 60,  config: {}, label: 'Start' },
      { id: endId,   type: 'end',          x: 400, y: 300, config: {}, label: 'End' },
    ],
    connections: [
      { id: `c_${t}`, from: startId, to: endId, fromPort: 'out', toPort: 'in' },
    ],
  };
}

/**
 * G236 — Build a canvas DAG that reflects an agent's actual skills.
 *
 * For non-AutoLet (Let) agents, the just-in-time Canvas workflow should
 * mirror what the agent can actually DO, not be a blank Start→End sentinel.
 * This converter walks the agent's skills array and produces a pipeline
 * Start → [policy layers] → skill_1 → ... → skill_n → [framework controls]
 * → End.
 *
 * G238 — Policy enforcement injection (read-only auto-generated layers
 * per LET_CANVAS_GOLDEN_RULES §2):
 *   1. ORG-LEVEL policies     (binding scope: org)
 *   2. TENANT-LEVEL policies  (binding scope: tenant)
 *   3. AGENT-LEVEL policies   (binding scope: agent)
 *   4. SKILL-LEVEL policies   (per-skill, wraps the skill node)
 *   + FRAMEWORK CONTROLS      (orthogonal cross-cutting layer)
 *
 * Each policy/framework node is keyed by `bindingId` / `controlId` in its
 * `config` so re-materialisation preserves identity. Empty layers collapse
 * (no placeholder).
 *
 * Each skill node is keyed by `skillId` in its `config`, which is the
 * bridge for two-way sync (G237):
 *   - Agent → Canvas: rebuild this DAG whenever agent.skills changes.
 *   - Canvas → Agent: on save, the backend reconciler matches skill nodes
 *     by `config.skillId` to detect adds/removes/edits.
 *
 * Node-type mapping mirrors v1 skill kinds:
 *   workflow_skill   → 'workflow_skill'
 *   policy_skill     → 'policy_skill'
 *   connector_skill  → 'connector_skill'
 *   anything else    → 'standard_skill'
 *
 * If the agent has zero skills AND no policy bindings, fall back to the
 * sentinel payload so the canvas is still usable for hand-building.
 *
 * @param {object} agent — the agent record
 * @param {Array}  skillsArg — optional explicit skills override
 * @param {object} [bindings] — G238 policy/framework data:
 *   {
 *     orgPolicies?:        [{id, name, ...}],
 *     tenantPolicies?:     [{id, name, ...}],
 *     agentPolicies?:      [{id, name, ...}],
 *     skillPolicyDocs?:    { [skillId]: [{id, enforcement, ...}] },
 *     frameworkControls?:  [{id, controlId, frameworkName, ...}]
 *   }
 */
/* G256 / LET_CANVAS_GOLDEN_RULES §3.4 — Node types that the
 * materialiser owns. Any node whose `type` is NOT in this set is
 * considered operator-authored and gets preserved across re-sync via
 * mergePreservingOperatorNodes() below. Update this set whenever the
 * materialiser starts emitting a new node type. */
const MATERIALISED_NODE_TYPES = new Set([
  'manual_start',
  'end',
  'standard',           // skill placeholder
  'cognitive_loop',
  'policy_gate_org',
  'policy_gate_tenant',
  'policy_gate_agent',
  'policy_gate_skill_pre',
  'policy_gate_skill_post',
  'framework_control',
]);

/* G267c — Gate-boundary chrome is owned by MARKER, not by type.
 *
 * The cognitive loop-entry / loop-exit gate groups are emitted as generic
 * `parallel_fork` / `parallel_join` nodes (emitGateBoundary). Those same two types are ALSO
 * in the operator's palette under Logic & Control Flow — so ownership cannot be decided by
 * type alone:
 *
 *   - Leave them OUT of MATERIALISED_NODE_TYPES  → every re-sync treats the previous gate
 *     fork/join as operator-authored and PRESERVES them, so each Sync accretes a duplicate
 *     orphan pair (observed: 15 nodes → 17, "preserved 2 operator-authored nodes",
 *     auto-repair firing on the orphans).
 *   - Put them IN MATERIALISED_NODE_TYPES → the materialiser now claims ownership of every
 *     fork/join on the canvas, and a deterministic CEG where the operator hand-authored a
 *     parallel branch would have that branch SILENTLY DESTROYED on the next Sync. Strictly
 *     worse: it trades a cosmetic duplicate for data loss.
 *
 * So we own them by the marker the gate emitter stamps (`config.gateBoundary`). An
 * operator's own fork/join carries no such marker and is still preserved, unchanged. */
const GATE_BOUNDARY_NODE_TYPES = new Set(['parallel_fork', 'parallel_join']);

function isMaterialiserOwned(n) {
  if (!n || !n.type) return false;
  if (MATERIALISED_NODE_TYPES.has(n.type)) return true;
  if (GATE_BOUNDARY_NODE_TYPES.has(n.type) && n.config && n.config.gateBoundary) return true;
  return false;
}

/* G256 / §3.4 — Overlay merge that preserves operator-authored
 * non-skill nodes across re-materialisation.
 *
 * Algorithm:
 *   1. Start with the freshly-materialised DAG (skills + policy gates +
 *      framework controls + cognitive_loop + sentinels).
 *   2. From the existing canvas, find every node whose `type` is NOT in
 *      MATERIALISED_NODE_TYPES — these are decision / branch / loop /
 *      wait / fork / join / etc that the operator added manually.
 *   3. Skip if the operator-node's ID would collide with a fresh one
 *      (defensive — IDs are timestamped so this should never happen).
 *   4. Append the operator nodes to the merged DAG.
 *   5. From the existing canvas, preserve any edge whose endpoints both
 *      survive the merge (operator-to-operator, or operator-to-
 *      materialised when that materialised node still exists by ID, or
 *      materialised-to-operator).
 *
 * Today the materialiser regenerates ID-stamped skill nodes every call
 * (`n_${t}_skill_...`), so any pre-existing operator-to-skill edge will
 * NOT survive (the skill node ID changed). That's a known limitation
 * documented in §3.4 — the operator gets a toast nudging them to
 * re-wire those edges. The decision / branch / loop nodes themselves
 * are preserved so their internal logic isn't lost.
 *
 * Pass `existingDefinition === null` (or omit) to disable the merge —
 * e.g. on first canvas creation when there's no prior state.
 */
function mergePreservingOperatorNodes(materialisedDag, existingDefinition) {
  if (!existingDefinition || !Array.isArray(existingDefinition.nodes)) {
    return materialisedDag;
  }
  const newNodeIds = new Set(materialisedDag.nodes.map((n) => n.id));
  const preservedNodes = existingDefinition.nodes.filter((n) => {
    if (!n || !n.id || !n.type) return false;
    if (isMaterialiserOwned(n)) return false;
    if (newNodeIds.has(n.id)) return false; // ID collision — skip
    return true;
  });
  if (preservedNodes.length === 0) return materialisedDag;

  /* Build the merged node set + a lookup of every surviving node ID for
   * edge filtering below. */
  const mergedNodes = [...materialisedDag.nodes, ...preservedNodes];
  const mergedNodeIds = new Set(mergedNodes.map((n) => n.id));

  /* Preserve operator-authored edges whose endpoints both survive. */
  const existingEdges = Array.isArray(existingDefinition.connections)
    ? existingDefinition.connections : [];
  const newEdgeIds = new Set(materialisedDag.connections.map((c) => c.id));
  const preservedEdges = existingEdges.filter((e) => {
    if (!e || !e.from || !e.to) return false;
    if (e.id && newEdgeIds.has(e.id)) return false; // ID collision — skip
    return mergedNodeIds.has(e.from) && mergedNodeIds.has(e.to);
  });
  return {
    nodes: mergedNodes,
    connections: [...materialisedDag.connections, ...preservedEdges],
  };
}

function buildAgentSkillsDag(agent, skillsArg, bindings) {
  const skills = Array.isArray(skillsArg) ? skillsArg
               : Array.isArray(agent?.skills) ? agent.skills
               : [];
  const b = bindings || {};
  const orgPolicies        = Array.isArray(b.orgPolicies)        ? b.orgPolicies        : [];
  const tenantPolicies     = Array.isArray(b.tenantPolicies)     ? b.tenantPolicies     : [];
  const agentPolicies      = Array.isArray(b.agentPolicies)      ? b.agentPolicies      : [];
  const skillPolicyDocs    = b.skillPolicyDocs && typeof b.skillPolicyDocs === 'object' ? b.skillPolicyDocs : {};
  const frameworkControls  = Array.isArray(b.frameworkControls)  ? b.frameworkControls  : [];
  /* G243 — per-skill execution stats keyed by skillId. */
  const executionStats     = b.executionStats && typeof b.executionStats === 'object' ? b.executionStats : {};

  /* Empty-canvas fallback: no skills AND no bindings → standard sentinel. */
  if (skills.length === 0 &&
      orgPolicies.length === 0 && tenantPolicies.length === 0 &&
      agentPolicies.length === 0 && frameworkControls.length === 0) {
    return makeFreshSentinelPayload();
  }

  const t = Date.now();
  const startId = `n_${t}_start`;
  const endId = `n_${t}_end`;
  const x = 400;
  const yStep = 130;
  let y = 60;
  const nodes = [
    { id: startId, type: 'manual_start', x, y, config: {}, label: 'Start' },
  ];
  const connections = [];
  let prevId = startId;

  /* Helper: append a node sequenced after prevId. */
  const appendSeq = (id, type, label, config) => {
    y += yStep;
    nodes.push({ id, type, x, y, label, config: config || {} });
    connections.push({
      id: `c_${id}`,
      from: prevId, to: id,
      fromPort: 'out', toPort: 'in',
    });
    prevId = id;
  };

  /* G238 — Policy layer injection. Each layer collapses if empty (no
   * placeholder node), keeping the canvas readable for tenants without
   * policies. Binding objects come from /policy/inherited-bindings.
   *
   * G290 — Bindings now arrive enriched with the joined PolicyRule
   * fields (ruleName / ruleDescription / ruleCategory / ruleSeverity /
   * ruleEnforcementPoint), so each policy node renders a
   * distinguishable label ("ORG · Block PII Egress" instead of seven
   * identical "ORG · platform default") and the right-pane inspector
   * can show the full rule shape. The config blob carries every field
   * the operator might need to understand what the gate enforces —
   * the inspector's "Config" + "Attributes" tabs read directly from
   * here without needing extra fetches. */
  /* Shared spec for ONE policy-gate node. Used by BOTH the deterministic
   * (interleaved, sequential) layer and the cognitive (boundary-grouped) layer so the
   * node/config shape — and therefore the inspector — is identical in both. */
  const policySpec = (scope, p, nodeType) => {
    const id = p?.bindingId || p?.id;
    if (!id) return null;
    // Prefer the actual rule name when the backend resolved it.
    // Fall back to sourceTag (PLATFORM_DEFAULT etc) and finally to
    // a stub "<scope> policy" so missing data is still readable.
    const ruleName = p?.ruleName || p?.displayName || p?.policyName || p?.name;
    const labelBase = ruleName || p?.sourceTag || 'unnamed rule';
    const cleanLabel = String(labelBase).replace(/_/g, ' ').toLowerCase();
    const label = ruleName
      ? `${scope.toUpperCase()} · ${cleanLabel}`
      : `${scope.toUpperCase()} · ${cleanLabel} · ${String(id).slice(0, 8)}`;
    return {
      id: `n_${t}_pol_${scope}_${id}`,
      type: nodeType,
      label,
      config: {
        bindingId: id,
        ruleId: p?.ruleId || null,
        bindingScope: scope,
        inheritedFrom: p?.inheritedFrom || null,
        sourceTag: p?.sourceTag || null,
        // G290 — full rule context for the inspector. Each field is
        // optional (older bindings without enrichment still render).
        ruleName: p?.ruleName || null,
        ruleDescription: p?.ruleDescription || null,
        ruleCategory: p?.ruleCategory || null,
        ruleSeverity: p?.ruleSeverity || null,
        ruleEnforcementPoint: p?.ruleEnforcementPoint || null,
        enforcement: p?.enforcement || p?.enforcementPoint || 'pre',
        activationStatus: p?.activationStatus || p?.status || null,
        frameworkBindingId: p?.frameworkBindingId || null,
        readOnly: true,
      },
    };
  };

  const policyLayer = (scope, list, nodeType) => {
    list.forEach((p) => {
      const s = policySpec(scope, p, nodeType);
      if (!s) return;
      appendSeq(s.id, s.type, s.label, s.config);
    });
  };

  /* G242 — Execution mode decides HOW governance is placed. Detected here (before the
   * policy layer) because a cognitive agent's gates are NOT a sequence.
   *
   * Detection per AGENT_EXECUTION_MODEL.md §8 backwards-compat: honor explicit
   * agent.executionMode; otherwise 'deterministic' (preserves existing canvas semantics). */
  const executionMode = (agent?.executionMode || 'deterministic').toLowerCase();
  const isCognitive = executionMode === 'cognitive';
  const isHybrid    = executionMode === 'hybrid';
  const isLoopShaped = isCognitive || isHybrid;

  /* ── Governance placement ───────────────────────────────────────────────────────────
   * DETERMINISTIC (and every other CEG type — AutoLet CEG / ExteLet / SysLet reach this
   * path): gates are INTERLEAVED into the linear spine. That is correct: execution really
   * is a static sequence. UNCHANGED.
   *
   * COGNITIVE / HYBRID: execution is a reasoning LOOP — there is no static step order to
   * interleave into, and a CONNECTOR-point rule fires on an edge the LLM picks at RUNTIME,
   * so it can never be a static node. Therefore a cognitive CEG has exactly TWO structural
   * gate boundaries — loop entry and loop exit — and the rest of the rules ride the thing
   * they actually gate:
   *
   *   AGENT_ENTRY / WORKFLOW_ENTRY / ALL  → loop-entry gate group   (nodes)
   *   TRUST_BOUNDARY                      → loop-exit gate group    (nodes)
   *   CONNECTOR / MCP_GATEWAY             → per tool call           (stamped on each tool)
   *   INTER_STEP                          → per reasoning iteration (stamped on the loop)
   *
   * Nothing is hidden — every rule stays inspectable, at the boundary where it truly fires.
   * Enforcement points mirror PolicyRule.EnforcementPoint (backend). */
  const ENTRY_EP  = new Set(['AGENT_ENTRY', 'WORKFLOW_ENTRY', 'ALL']);
  const EXIT_EP   = new Set(['TRUST_BOUNDARY']);
  const PERTOOL_EP = new Set(['CONNECTOR', 'MCP_GATEWAY']);
  const LOOP_EP   = new Set(['INTER_STEP']);
  const epOf = (p) => String(p?.ruleEnforcementPoint || p?.enforcementPoint || 'ALL').toUpperCase();

  const scopedPolicies = [
    ...orgPolicies.map((p)    => ({ p, scope: 'org',    nodeType: 'policy_gate_org' })),
    ...tenantPolicies.map((p) => ({ p, scope: 'tenant', nodeType: 'policy_gate_tenant' })),
    ...agentPolicies.map((p)  => ({ p, scope: 'agent',  nodeType: 'policy_gate_agent' })),
  ];
  // Rules that fire inside the loop — surfaced on the loop / tools, never as spine nodes.
  const perToolPolicies = scopedPolicies.filter(({ p }) => PERTOOL_EP.has(epOf(p))).map(({ p }) => p);
  const loopIterPolicies = scopedPolicies.filter(({ p }) => LOOP_EP.has(epOf(p))).map(({ p }) => p);
  const exitPolicies  = scopedPolicies.filter(({ p }) => EXIT_EP.has(epOf(p)));
  // Anything not explicitly loop-internal or exit-bound gates the ENTRY (fail-safe default:
  // an unrecognised enforcement point still gets enforced, at the door).
  const entryPolicies = scopedPolicies.filter(({ p }) => {
    const ep = epOf(p);
    return !EXIT_EP.has(ep) && !PERTOOL_EP.has(ep) && !LOOP_EP.has(ep);
  });

  /* Emit a set of gates as a PARALLEL group at a boundary (fork → legs → join), reusing the
   * existing parallel_fork/parallel_join machinery. They all gate the same boundary, so a
   * chain would falsely imply an order. A single gate is emitted inline — a 1-leg fork is
   * degenerate (v2Lint fork/degenerate-leg-count). */
  const emitGateBoundary = (gates, phase, title) => {
    /* Resolve the specs FIRST. policySpec() can return null (unmapped scope/type), and the
     * old code sized the fork off `gates.length` while emitting off the filtered set — a
     * null spec would declare N legs and wire N-1. Size everything off `specs`. */
    const specs = gates
      .map((g) => policySpec(g.scope, g.p, g.nodeType))
      .filter(Boolean);
    if (specs.length === 0) return;
    if (specs.length === 1) {
      const s = specs[0];
      appendSeq(s.id, s.type, s.label, { ...s.config, gateBoundary: phase });
      return;
    }
    const forkId = `n_${t}_gate_${phase}_fork`;
    const joinId = `n_${t}_gate_${phase}_join`;
    /* `branches` is REQUIRED config on parallel_fork (soarNodes configSchema; enforced by
     * v3Validate/v4Validate check 7 → REQUIRED_CONFIG_MISSING). Omitting it made the CEG
     * render a hard error with an "Ask AI to fix these" affordance pointed straight at a
     * graph the AI fixer would have flattened. The branch ids MUST match the `fromPort`
     * handles we wire below (`branch_N`) — flatToIR reads branch names off the edge
     * sourceHandle, so a mismatch would silently orphan legs. */
    const branchDefs = specs.map((s, i) => ({ id: `branch_${i + 1}`, label: s.label }));
    appendSeq(forkId, 'parallel_fork', title, {
      branches: branchDefs,
      gateBoundary: phase,
      ruleCount: specs.length,
      readOnly: true,
    });
    y += yStep;
    const laneY = y;
    const laneW = 250;
    const startX = x - (specs.length * laneW) / 2 + laneW / 2;
    specs.forEach((s, i) => {
      nodes.push({
        id: s.id, type: s.type, x: startX + i * laneW, y: laneY,
        label: s.label, config: { ...s.config, gateBoundary: phase },
      });
      connections.push({
        id: `c_${s.id}_in`, from: forkId, to: s.id, fromPort: `branch_${i + 1}`, toPort: 'in',
      });
      connections.push({
        id: `c_${s.id}_out`, from: s.id, to: joinId, fromPort: 'out', toPort: 'in',
      });
    });
    y += yStep;
    nodes.push({
      id: joinId, type: 'parallel_join', x, y,
      label: phase === 'entry' ? 'Entry gates cleared' : 'Exit gates cleared',
      config: {
        gateBoundary: phase,
        /* Mirror of the fork's legs — the join waits on all of them. */
        joinOn: branchDefs.map((b) => b.id),
        joinMode: 'ALL',
        readOnly: true,
      },
    });
    prevId = joinId;
  };

  if (isLoopShaped) {
    emitGateBoundary(entryPolicies, 'entry', 'Loop entry gates');
  } else {
    policyLayer('org',    orgPolicies,    'policy_gate_org');
    policyLayer('tenant', tenantPolicies, 'policy_gate_tenant');
    policyLayer('agent',  agentPolicies,  'policy_gate_agent');
  }

  /* G242 — Execution mode determines the skill layer's shape.
   *   - 'cognitive':     LLM brain node + fan-out tool catalog (skills as
   *                      available tools, NOT a sequence)
   *   - 'deterministic': skills in linear order (current behavior)
   *   - 'hybrid':        deterministic head → cognitive body → deterministic tail
   *                      (handled by inlining head/tail around the cognitive block)
   *
   * (executionMode / isCognitive / isHybrid are detected ABOVE — the governance layer
   * needs them before it can decide whether gates are interleaved or boundary-grouped.)
   */
  if (isLoopShaped) {
    /* Hybrid: layout the deterministic head skills first (sequential), then
     * the cognitive body. */
    const headIds = (agent?.hybridLayout?.deterministicHead || []).filter(Boolean);
    const tailIds = (agent?.hybridLayout?.deterministicTail || []).filter(Boolean);
    const headSkills = headIds.length > 0
      ? skills.filter((s) => headIds.includes(s.id))
      : [];
    const tailSkills = tailIds.length > 0
      ? skills.filter((s) => tailIds.includes(s.id))
      : [];
    const headSet = new Set(headSkills.map((s) => s.id));
    const tailSet = new Set(tailSkills.map((s) => s.id));
    const bodySkills = skills.filter((s) => !headSet.has(s.id) && !tailSet.has(s.id)
                                          && (s.callability || 'always') !== 'manual');

    /* Inline helper for head/tail sequential skill nodes — defined as a
     * const expression so it's hoisted before use under strict mode. */
    const appendSkillNode = (s, i, segment) => {
      const skillKind = (s.skillType || s.kind || 'standard').toLowerCase();
      const nid = `n_${t}_skill_${segment}_${s.id || i}`;
      appendSeq(nid, 'standard', s.name || `Skill ${i + 1}`, {
        agentSkillId: s.id, skillName: s.name, skillKind,
        riskLevel: s.riskLevel || null,
        autonomy: s.autonomy || null,
        inferenceMode: s.inferenceMode || null,
        category: s.category || null,
        description: s.description || null,
        callability: s.callability || 'always',
        preconditions: s.preconditions || [],
        parallelGroup: s.parallelGroup || null,
        retryPolicy: s.retryPolicy || null,
        timeoutMs: s.timeoutMs || null,
        hybridSegment: segment,
        /* G243 — Per-skill execution stats from /policy-audit. */
        executionStats: (s.id && executionStats[s.id]) || null,
      });
    };

    /* Deterministic head — linear chain of skill nodes. */
    headSkills.forEach((s, i) => appendSkillNode(s, i, 'head'));

    /* Cognitive Loop — the LLM brain. Sequenced after policies + head. */
    const loopId = `n_${t}_cognitive_loop`;
    const cb = agent?.cognitiveBrief || {};
    appendSeq(loopId, 'cognitive_loop', 'Cognitive Loop', {
      systemPrompt: cb.systemPrompt || null,
      maxIterations: cb.maxIterations || 10,
      toolSelectionStrategy: cb.toolSelectionStrategy || 'auto',
      iterationCostCapUsd: cb.iterationCostCapUsd || null,
      fallbackBehavior: cb.fallbackBehavior || 'stop',
      attachedToolCount: bodySkills.length,
      readOnly: false,  // editable in Build
      isCognitiveBrain: true,
      /* Governance that fires INSIDE the loop and therefore cannot be a spine node.
       * INTER_STEP rules re-evaluate on every reasoning iteration; CONNECTOR /
       * MCP_GATEWAY rules gate each tool call the LLM chooses at runtime. Surfaced here
       * (and on each tool card) so nothing is hidden just because it isn't a box. */
      iterationPolicies: loopIterPolicies.map((p) => ({
        bindingId: p?.bindingId || p?.id || null,
        ruleName: p?.ruleName || null,
        ruleSeverity: p?.ruleSeverity || null,
        ruleEnforcementPoint: p?.ruleEnforcementPoint || null,
      })),
      perToolPolicyCount: perToolPolicies.length,
    });

    /* Fan-out: lay skills out horizontally below the loop. */
    if (bodySkills.length > 0) {
      const fanoutY = y + yStep;
      const fanWidth = 220;
      const totalWidth = Math.max(1, bodySkills.length) * fanWidth;
      const startX = x - totalWidth / 2 + fanWidth / 2;
      bodySkills.forEach((s, i) => {
        const skillKind = (s.skillType || s.kind || 'standard').toLowerCase();
        const sx = startX + i * fanWidth;
        const nid = `n_${t}_skill_${s.id || i}`;
        nodes.push({
          id: nid,
          type: 'standard',
          x: sx, y: fanoutY,
          label: s.name || `Skill ${i + 1}`,
          config: {
            agentSkillId: s.id, skillName: s.name, skillKind,
            riskLevel: s.riskLevel || null,
            autonomy: s.autonomy || null,
            inferenceMode: s.inferenceMode || null,
            category: s.category || null,
            description: s.description || null,
            callability: s.callability || 'always',
            preconditions: s.preconditions || [],
            parallelGroup: s.parallelGroup || null,
            retryPolicy: s.retryPolicy || null,
            timeoutMs: s.timeoutMs || null,
            /* Per AGENT_EXECUTION_MODEL.md §4: edges from cognitive_loop to
             * skill nodes are "availability" edges, not "always invoked". */
            cognitiveAvailability: true,
            /* Governance that gates THIS tool call. A CONNECTOR / MCP_GATEWAY rule fires on
             * the connector-out of whichever tool the LLM picks at runtime — it cannot be a
             * static spine node, so it rides the tool it actually gates. The inspector lists
             * them; the card badges the posture. */
            connectorPolicies: perToolPolicies.map((p) => ({
              bindingId: p?.bindingId || p?.id || null,
              ruleName: p?.ruleName || null,
              ruleSeverity: p?.ruleSeverity || null,
              ruleEnforcementPoint: p?.ruleEnforcementPoint || null,
            })),
            guardrails: s.guardrails || null,
            /* G273 — displayed == enforced. This used to read the guardrails blob ONLY, so a
             * HUMAN_CONTROLLED skill (isolateHost) materialised with approvalRequired:false
             * even though the runtime pauses on it. Now derived from the same predicate the
             * backend gate uses (Skill.requiresHumanApproval) via the shared FE mirror. */
            approvalRequired: requiresHumanApproval(s),
            /* G243 — Per-skill execution stats from /policy-audit. */
            executionStats: (s.id && executionStats[s.id]) || null,
          },
        });
        /* G267 — Availability edge: loop → skill (LLM may invoke).
         *
         * `fromPort: 'cognitive_avail'` is a sentinel handle the IR/ELK
         * pipeline recognises as auxiliary (not part of the trunk walk), so
         * flatToIR.singleDefaultSuccessor still picks the cognitive_loop's
         * trunk-out (to the deterministic tail) and doesn't get confused by
         * the N fan-outs. flatToElkHierarchical layers these in as
         * cross-cutting edges on the root with `variant: 'availability'`. */
        connections.push({
          id: `c_${nid}_avail`,
          from: loopId, to: nid,
          fromPort: 'cognitive_avail', toPort: 'in',
          label: 'may invoke',
          style: 'dashed',
        });
        /* Return edge: skill → loop (back to cognitive reasoning).
         * `fromPort: 'cognitive_return'` is the corresponding sentinel; the
         * skill node otherwise has no outgoing edges, so without this tag the
         * IR walker would treat the return-to-loop as a structural edge and
         * collapse the trunk. */
        connections.push({
          id: `c_${nid}_return`,
          from: nid, to: loopId,
          fromPort: 'cognitive_return', toPort: 'in',
          label: 'return',
          style: 'solid',
        });
      });
      /* After the fan-out, downstream nodes (framework / tail / End) attach
       * to the cognitive_loop's exit port — prevId already === loopId. */
      y = fanoutY + yStep;
    }

    /* Deterministic tail */
    tailSkills.forEach((s, i) => appendSkillNode(s, i, 'tail'));

    /* Loop EXIT gate boundary — the second (and only other) structural enforcement point on
     * a cognitive CEG. Everything outbound (TRUST_BOUNDARY / egress / seal) fires here, on
     * the way out of the reasoning loop. */
    emitGateBoundary(exitPolicies, 'exit', 'Loop exit gates');
  } else {
    /* Deterministic mode (default) — skills in linear order.
     *
     * G239 — Use generic `standard` node type for skill placeholders. The
     * backend's canvas-definition validator requires `skill_*` typed nodes
     * to carry a registryId that resolves in the Skill Registry catalog —
     * but agent-skill instance IDs live in a different ID space, so
     * referencing them via skill_* types fails 422. Generic `standard` skips
     * that validator; we still preserve the skill identity via
     * `config.agentSkillId` and `config.skillKind` for our own materialisation /
     * reverse-sync logic. */
    deterministicSkillLayer();
  }

  function deterministicSkillLayer() {
  skills.forEach((s, i) => {
    const skillKind = (s.skillType || s.kind || 'standard').toLowerCase();
    const nodeType = 'standard';

    /* Per-skill pre-gates */
    const docs = (s.id && skillPolicyDocs[s.id]) ? skillPolicyDocs[s.id] : [];
    docs.forEach((d) => {
      const enf = (d.enforcement || d.enforcementPoint || 'pre').toLowerCase();
      if (enf === 'pre' || enf === 'wrap') {
        appendSeq(
          `n_${t}_pol_skill_pre_${s.id}_${d.id}`,
          'policy_gate_skill_pre',
          d.title || d.name || `Policy doc (pre)`,
          {
            bindingId: d.id,
            bindingScope: 'skill',
            /* G239b — see comment on agentSkillId above (skillId would
             * trip the WorkflowDraftValidator's Skill Registry lookup). */
            agentSkillId: s.id,
            enforcement: 'pre',
            readOnly: true,
          },
        );
      }
    });

    /* The skill node itself */
    const nid = `n_${t}_skill_${s.id || i}`;
    appendSeq(nid, nodeType, s.name || `Skill ${i + 1}`, {
      /* G239b — `agentSkillId` is the canonical join key for two-way sync.
       * IMPORTANT: cannot use `skillId` or `registryId` here — backend's
       * WorkflowDraftValidator (line 180-181) reads both names and tries
       * to resolve them against the Skill Registry catalog, which agent-
       * skill instance IDs don't appear in. The underscored-namespaced
       * `agentSkillId` is invisible to the validator. */
      agentSkillId: s.id,
      skillName: s.name,
      skillKind,
      riskLevel: s.riskLevel || null,
      autonomy: s.autonomy || null,
      inferenceMode: s.inferenceMode || null,
      category: s.category || null,
      description: s.description || null,
      /* G241 — execution-logic metadata (read by future runtime; default
       * safe values today). */
      callability: s.callability || 'always',
      preconditions: s.preconditions || [],
      parallelGroup: s.parallelGroup || null,
      retryPolicy: s.retryPolicy || null,
      timeoutMs: s.timeoutMs || null,
      /* G243 — Per-skill execution stats from /policy-audit (last 30d).
       * Renders as a side-badge per LET_CANVAS_GOLDEN_RULES §4.3. */
      executionStats: (s.id && executionStats[s.id]) || null,
    });

    /* Per-skill post-gates */
    docs.forEach((d) => {
      const enf = (d.enforcement || d.enforcementPoint || 'pre').toLowerCase();
      if (enf === 'post' || enf === 'wrap') {
        appendSeq(
          `n_${t}_pol_skill_post_${s.id}_${d.id}`,
          'policy_gate_skill_post',
          d.title || d.name || `Policy doc (post)`,
          {
            bindingId: d.id,
            bindingScope: 'skill',
            /* G239b — see comment on agentSkillId above. */
            agentSkillId: s.id,
            enforcement: 'post',
            readOnly: true,
          },
        );
      }
    });
  });
  } /* end deterministicSkillLayer */

  /* Framework controls (orthogonal cross-cutting layer — placed just before End). */
  frameworkControls.forEach((c) => {
    if (!c?.id) return;
    appendSeq(
      `n_${t}_framework_${c.id}`,
      'framework_control',
      c.controlId
        ? `${c.frameworkName || 'Framework'} · ${c.controlId}`
        : (c.frameworkName || c.title || 'Framework control'),
      {
        controlId: c.id,
        frameworkBindingId: c.frameworkBindingId || c.bindingId || null,
        frameworkName: c.frameworkName || null,
        externalControlId: c.controlId || null,
        readOnly: true,
      },
    );
  });

  /* End sentinel */
  y += yStep;
  nodes.push({ id: endId, type: 'end', x, y, config: {}, label: 'End' });
  connections.push({
    id: `c_${endId}`,
    from: prevId, to: endId,
    fromPort: 'out', toPort: 'in',
  });

  return { nodes, connections };
}

/**
 * G243 — Compute per-skill execution stats from the policy audit trail.
 *
 * Per LET_CANVAS_GOLDEN_RULES §4.3, the canvas must reflect what the agent
 * actually DOES, not just what was authored. Until the platform ships a
 * dedicated `/agents/{id}/execution-stats` endpoint, we derive the signal
 * client-side from /api/v1/policy-audit:
 *
 *   1. Pull POLICY_RULE_EVALUATION events for the agent in the last N hours.
 *   2. Group by skillId.
 *   3. Per skill: count, p50/p95 of evaluationTimeMs, errorRate, lastInvokedAt.
 *
 * The shape returned is `{ [skillId]: { count, p50, p95, errorRate,
 * lastInvokedAt } }` — buildAgentSkillsDag attaches each entry to the
 * matching skill node's `config.executionStats`. Right-pane inspect + audit
 * drill-ins read from there.
 *
 * Non-fatal: a failure returns an empty map; the canvas still materialises
 * cleanly without badges.
 */
async function fetchAgentSkillExecutionStats({ agentId, windowDays = 30 }) {
  const empty = {};
  if (!agentId) return empty;
  try {
    const res = await getPolicyAuditTrail({
      agentId, hours: windowDays * 24, page: 0, size: 500,
    });
    const items = Array.isArray(res?.data?.content) ? res.data.content
                : Array.isArray(res?.content) ? res.content
                : Array.isArray(res?.data?.items) ? res.data.items
                : Array.isArray(res?.items) ? res.items
                : Array.isArray(res?.data) ? res.data
                : Array.isArray(res) ? res : [];
    if (items.length === 0) return empty;

    const byId = {};
    items.forEach((e) => {
      const sid = e?.skillId;
      if (!sid) return;
      if (!byId[sid]) {
        byId[sid] = {
          count: 0, errors: 0, blocked: 0,
          latencies: [],
          lastInvokedAt: null,
          skillName: e.skillName || null,
        };
      }
      const b = byId[sid];
      b.count += 1;
      const dec = (e.decision || '').toUpperCase();
      if (dec === 'BLOCKED') b.blocked += 1;
      if (dec === 'FAILED')  b.errors += 1;
      const lat = e?.details?.evaluationTimeMs ?? e?.evaluationTimeMs;
      if (typeof lat === 'number' && Number.isFinite(lat)) b.latencies.push(lat);
      const ts = e.timestamp || e.createdAt;
      if (ts && (!b.lastInvokedAt || ts > b.lastInvokedAt)) b.lastInvokedAt = ts;
    });

    /* Percentile helper. */
    const pct = (arr, p) => {
      if (!arr.length) return null;
      const sorted = arr.slice().sort((a, b) => a - b);
      const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
      return sorted[idx];
    };

    const out = {};
    Object.entries(byId).forEach(([sid, b]) => {
      out[sid] = {
        count: b.count,
        p50: pct(b.latencies, 50),
        p95: pct(b.latencies, 95),
        errorRate: b.count > 0 ? (b.errors + b.blocked) / b.count : 0,
        blocked: b.blocked,
        errors: b.errors,
        lastInvokedAt: b.lastInvokedAt,
        skillName: b.skillName,
        windowDays,
      };
    });
    return out;
  } catch (_) { return empty; }
}

/**
 * G238 / G264 — Fetch the cascaded policy + framework bindings for an agent.
 *
 * Returns the shape consumed by buildAgentSkillsDag().
 *
 * Path A (preferred, post-G263): single round-trip to the canvas-materialisation
 *   aggregator endpoint. Returns agent + skills + bindings + execution stats
 *   in one shot. Cuts ~150ms p50 vs the 4-call fanout.
 *
 * Path B (fallback): the original four parallel fetches (inherited policy
 *   bindings + per-skill policy docs + policy activations). Only fires if the
 *   aggregator endpoint errors / 404s — keeps the canvas resilient when
 *   running against pre-G263 backends.
 */
async function fetchAgentCanvasBindings({ orgId, tenantId, agentId, skills }) {
  const empty = {
    orgPolicies: [], tenantPolicies: [], agentPolicies: [],
    skillPolicyDocs: {}, frameworkControls: [],
  };
  if (!agentId) return empty;

  /* G264 Path A — aggregator. Tolerant of older backends (returns ok:false
   * → falls through to Path B). */
  try {
    const aggr = await getCanvasMaterialisation(agentId, 30);
    if (aggr?.ok && aggr?.data) {
      const data = aggr.data;
      const out = { ...empty };
      const bindings = Array.isArray(data.inheritedBindings?.inheritedBindings)
        ? data.inheritedBindings.inheritedBindings
        : Array.isArray(data.inheritedBindings)
          ? data.inheritedBindings
          : [];
      bindings.forEach((b) => {
        if (!b || !b.bindingId) return;
        const scope = String(b.scope || '').toUpperCase();
        if (scope === 'PLATFORM' || scope === 'ORG') out.orgPolicies.push(b);
        else if (scope === 'TENANT') out.tenantPolicies.push(b);
        else if (scope === 'AGENT')  out.agentPolicies.push(b);
      });
      /* Aggregator response shape carries executionStats keyed by skillId. */
      if (data.executionStats && typeof data.executionStats === 'object') {
        out.executionStats = data.executionStats;
      }
      /* Per-skill policy docs are not yet included in the aggregator; fetch
       * them lazily for policy_skill kinds only. Cheap when there are none. */
      const policySkillIds = (Array.isArray(skills) ? skills : data.skills || [])
        .filter((s) => (s.skillType || s.kind || '').toLowerCase() === 'policy_skill' && s.id)
        .map((s) => s.id);
      if (policySkillIds.length > 0) {
        const docFetches = await Promise.all(policySkillIds.map(async (sid) => {
          try {
            const r = await listPolicyDocuments(sid);
            const items = Array.isArray(r?.data?.items) ? r.data.items
                       : Array.isArray(r?.items) ? r.items
                       : Array.isArray(r?.data)  ? r.data
                       : Array.isArray(r)        ? r : [];
            return [sid, items];
          } catch (_) { return [sid, []]; }
        }));
        docFetches.forEach(([sid, items]) => { out.skillPolicyDocs[sid] = items; });
      }
      /* Framework controls also still come from listPolicyActivations until
       * the aggregator absorbs them. Non-fatal. */
      try {
        const r = await listPolicyActivations({ agentId });
        const items = Array.isArray(r?.data?.items) ? r.data.items
                    : Array.isArray(r?.items) ? r.items
                    : Array.isArray(r?.data)  ? r.data
                    : Array.isArray(r)        ? r : [];
        out.frameworkControls = items;
      } catch (_) { /* non-fatal */ }
      return out;
    }
  } catch (_) { /* fall through to legacy path */ }

  /* G264 Path B — legacy 4-call fanout. */
  const out = { ...empty };

  /* Inherited policy bindings cascade (platform → org → tenant → agent).
   * Backend returns:
   *   { orgId, tenantId, agentId, inheritedBindings: [
   *       { bindingId, ruleId, scope: 'PLATFORM'|'ORG'|'TENANT'|'AGENT',
   *         scopeOrgId, scopeTenantId, scopeAgentId,
   *         sourceTag, inheritedFrom }
   *   ]}
   * We bucket by `scope` (uppercase) into the four canonical layers.
   * PLATFORM is rendered alongside ORG (highest precedence outer layer)
   * since both are tenant-wide defaults the operator can't override here.
   */
  try {
    const res = await getInheritedPolicyBindings({ orgId, tenantId, agentId });
    const data = res?.ok ? res.data : res;
    const bindings = Array.isArray(data?.inheritedBindings) ? data.inheritedBindings
                   : Array.isArray(data) ? data : [];
    bindings.forEach((b) => {
      if (!b || !b.bindingId) return;
      const scope = (b.scope || '').toUpperCase();
      if (scope === 'PLATFORM' || scope === 'ORG') out.orgPolicies.push(b);
      else if (scope === 'TENANT') out.tenantPolicies.push(b);
      else if (scope === 'AGENT')  out.agentPolicies.push(b);
    });
  } catch (_) { /* non-fatal */ }

  /* Per-skill policy documents — only fetched for policy_skill kind. */
  if (Array.isArray(skills)) {
    const policySkillIds = skills.filter((s) => {
      const k = (s.skillType || s.kind || '').toLowerCase();
      return k === 'policy_skill' && s.id;
    }).map((s) => s.id);
    if (policySkillIds.length > 0) {
      const docFetches = await Promise.all(policySkillIds.map(async (sid) => {
        try {
          const r = await listPolicyDocuments(sid);
          const items = Array.isArray(r?.data?.items) ? r.data.items
                     : Array.isArray(r?.items) ? r.items
                     : Array.isArray(r?.data)  ? r.data
                     : Array.isArray(r)        ? r : [];
          return [sid, items];
        } catch (_) { return [sid, []]; }
      }));
      docFetches.forEach(([sid, items]) => { out.skillPolicyDocs[sid] = items; });
    }
  }

  /* Framework control activations bound to this agent. */
  try {
    const r = await listPolicyActivations({ agentId });
    const items = Array.isArray(r?.data?.items) ? r.data.items
                : Array.isArray(r?.items) ? r.items
                : Array.isArray(r?.data)  ? r.data
                : Array.isArray(r)        ? r : [];
    out.frameworkControls = items;
  } catch (_) { /* non-fatal */ }

  return out;
}

// ============================================================================
// STYLE CONSTANTS
// ============================================================================

const btnBase = {
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--v2-border-default)',
  background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'Lexend, Inter, system-ui, sans-serif',
  transition: 'all 0.15s ease',
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
};

const btnPrimary = {
  ...btnBase,
  background: 'var(--v2-text-primary)',
  color: 'var(--v2-bg-surface)',
  border: 'none',
};

const btnSecondary = {
  ...btnBase,
  background: 'var(--v2-bg-muted)',
  border: '1px solid var(--v2-border-default)',
};

const miniBtn = {
  padding: '6px 8px',
  borderRadius: 4,
  border: '1px solid var(--v2-border-default)',
  background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-secondary)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--v2-border-default)',
  fontSize: 13,
  fontFamily: 'Lexend, Inter, system-ui, sans-serif',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  color: 'var(--v2-text-primary)',
};

const cardStyle = {
  background: 'var(--v2-bg-surface)',
  border: '1px solid var(--v2-border-default)',
  borderRadius: 8,
  padding: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

// NODE_W, NODE_H, SHAPE_GEOM, getNodeShapeType imported from useCanvas.js

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CanvasPage() {
  const {
    workflows, loadWorkflows, createWorkflow, updateWorkflow, deleteWorkflow,
    saveCanvas: saveCanvasApi, executeWorkflow: executeWorkflowApi,
    loadWorkflowRuns, workflowRuns, cancelWorkflowRun,
    loadOrchestrationStats, orchestrationStats,
    agents, loadAgents,
    // RC-96a — tenants list for the Save-As tenant picker (PA/SA path).
    // BE /api/v1/tenants already returns only the tenants the caller is
    // permitted to write into (TenantController:363 — PA=all, customer-
    // scoped SA=customer's tenants, others=accessibleTenantIds grant
    // union). We never need a "filter by caller permission" pass here.
    tenants, loadTenants,
  } = usePlatform();

  const { session, hasPermission, isSuperAdmin, isPlatformAdmin } = useAuth();
  const { addToast } = useToast();
  const nav = useNavigation();
  const navigate = useNavigate();

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  const userId = session?.userId;

  // Backend-registry-driven marketplace catalogue. Replaces the hardcoded
  // MARKETPLACE_CONNECTORS constant the page used to import. Consumed by
  // connector-name-matching effects and palette-derivation helpers below.
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();

  // RBAC permission flags for workflow operations
  const canCreateWorkflow = hasPermission('workflow.create');
  const canEditWorkflow = hasPermission('workflow.edit');
  const hasDeletePerm = hasPermission('workflow.delete');
  const canExecuteWorkflow = hasPermission('workflow.execute');
  const canCancelWorkflow = hasPermission('workflow.cancel');

  // =========================================================================
  // State Management
  // =========================================================================

  /* G232 — When /canvas?agentId=X is in the URL, the URL is the source of
   * truth, not whatever workflow was selected in a previous session. Earlier
   * code unconditionally hydrated from usePersistedState which caused agent
   * A's "Open in Canvas" click to flash agent B's CEG on first paint and
   * silently load that wrong workflow when agent A had no workflow yet.
   * Strategy: clear the persisted id synchronously BEFORE the persisted-
   * state hook reads it, then let the agent-driven effect below resolve
   * the correct workflow (auto-create blank for non-AutoLets, surface
   * Auto-Create-from-runbook CTA for AutoLets). */
  const hasAgentIdInUrl = (() => {
    if (typeof window === 'undefined') return false;
    try { return Boolean(new URLSearchParams(window.location.search).get('agentId')); }
    catch (_) { return false; }
  })();
  /* Pre-clear localStorage so usePersistedState's first read sees null. */
  if (hasAgentIdInUrl && typeof window !== 'undefined') {
    try {
      const ssKey = 'agentlets_ps_canvas.selectedWorkflowId';
      if (window.sessionStorage.getItem(ssKey)) {
        window.sessionStorage.removeItem(ssKey);
      }
    } catch (_) { /* ignore */ }
  }
  const [selectedWorkflowId, setSelectedWorkflowId] = usePersistedState('canvas.selectedWorkflowId', null);
  const [wfSearchTerm, setWfSearchTerm] = useState('');
  const [wfDropdownOpen, setWfDropdownOpen] = useState(false);
  const wfDropdownRef = useRef(null);

  // ── Agent-link banner state (forward Agent → Canvas materialisation) ──
  // When the user opens /canvas?agentId=…&workflowId=… from the
  // [Open in Canvas to Test & Verify] CTA, we surface a banner above the
  // canvas tying the test workflow back to the source agent. The agentId
  // is read from the URL once at mount; we re-fetch the agent payload so
  // the banner can show the up-to-date phase + publish version even if
  // the user had a stale agent record cached.
  //
  // Reference: AGENT_DEFINITION_JSON_PREVIEW_SPEC.md §4.
  /* RC-46 — linkedAgentId now derives from EITHER the URL param
   * (?agentId=…) OR the loaded workflow's own agentId field.
   *
   * Pre-RC-46 this was useMemo-from-URL-only. That worked when the
   * operator entered the canvas via /agents/{id} → "Open canvas"
   * (which threads ?agentId=… into the URL), but BROKE when they
   * came in via the workflow picker / inventory list (only
   * ?workflowId=… in URL). Agent-bound workflow kinds — autolet_ceg,
   * autolet_sandbox, syslet_canvas, let_canvas — always carry an
   * agentId on the row, so we can recover the parent-agent linkage
   * from the loaded selectedWorkflow without round-tripping back to
   * the agent detail page.
   *
   * Result: the "Back to Agent" banner now appears on a CEG canvas
   * regardless of how the operator landed there. */
  const [linkedAgentId, setLinkedAgentId] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('agentId') || null;
  });
  const [linkedAgent, setLinkedAgent] = useState(null);
  const [linkedAgentDismissed, setLinkedAgentDismissed] = useState(false);
  /* RC-54 — Tombstone state for CEGs whose parent agent is unreachable.
   *
   * Set true when the agent fetch for a non-null linkedAgentId returns
   * a 404 (or any non-OK response). Cleared whenever linkedAgentId
   * resets to null OR a subsequent fetch succeeds. Drives the
   * tombstone banner near the AgentCanvasLinkBanner site — operators
   * opening a CEG whose parent has been retired now see a clear
   * "Parent agent retired" notice with the agentId for forensic
   * reference, instead of an unexplained absence of the back-to-agent
   * chip. */
  const [linkedAgentRetired, setLinkedAgentRetired] = useState(false);
  /* G236 — Sync-from-agent flow: re-fetch agent.skills + rebuild DAG. */
  const [syncingFromAgent, setSyncingFromAgent] = useState(false);
  /* G258 — Phase-advance state for the canvas banner button. */
  const [advancingPhase, setAdvancingPhase] = useState(false);
  /* Agent-level phase regress from the banner (the action the Act lock message prescribes). */
  const [regressingPhase, setRegressingPhase] = useState(false);
  /* G264 — Execution-mode toggle state for the banner chip. */
  const [cyclingExecutionMode, setCyclingExecutionMode] = useState(false);
  /* G265 — Cognitive Brief editor state. Opens from double-click on
   * the cognitive_loop node. Form mirrors AGENT_EXECUTION_MODEL §2.1
   * cognitiveBrief shape. */
  const [cognitiveBriefOpen, setCognitiveBriefOpen] = useState(false);
  const [cognitiveBriefForm, setCognitiveBriefForm] = useState({
    systemPrompt: '',
    toolSelectionStrategy: 'auto',
    maxIterations: 10,
    iterationCostCapUsd: '',
    fallbackBehavior: 'stop',
  });
  const [savingCognitiveBrief, setSavingCognitiveBrief] = useState(false);
  const [cognitiveBriefError, setCognitiveBriefError] = useState(null);

  /* G268 / LET_CANVAS_GOLDEN_RULES §5.4 — Pre-save diff slideout state.
   *
   * Open whenever a canvas save is rejected by the backend
   * phase-allowlist (either WorkflowPhaseAllowlistGuard on the workflow
   * save path or CanvasReconcileService on the reverse-sync path).
   * Shape mirrors the unified 409/423 response body from both endpoints:
   *
   *   { open, code, phase, error, source, regressTarget,
   *     attemptedAdds: string[], attemptedRemoves: string[],
   *     attemptedMutates: string[], pendingData: canvasJson | null }
   *
   * pendingData carries the canvas payload the operator just tried to
   * save so we can re-fire the save after a successful regress without
   * the user having to click Save again. */
  const [diffModal, setDiffModal] = useState(null);
  const [regressingFromModal, setRegressingFromModal] = useState(false);
  /* Seed the form when the slideout opens from the current agent.cognitiveBrief. */
  useEffect(() => {
    if (!cognitiveBriefOpen) return;
    const cb = linkedAgent?.cognitiveBrief || {};
    setCognitiveBriefForm({
      systemPrompt: cb.systemPrompt || '',
      toolSelectionStrategy: cb.toolSelectionStrategy || 'auto',
      maxIterations: cb.maxIterations || 10,
      iterationCostCapUsd: cb.iterationCostCapUsd == null ? '' : String(cb.iterationCostCapUsd),
      fallbackBehavior: cb.fallbackBehavior || 'stop',
    });
    setCognitiveBriefError(null);
  }, [cognitiveBriefOpen, linkedAgent]);

  useEffect(() => {
    if (!linkedAgentId) {
      /* RC-50 — When linkedAgentId clears (e.g. operator switched from a
       * CEG canvas to a fresh blank standalone workflow), also clear the
       * cached linkedAgent payload so the "Back to Agent" banner stops
       * pointing at the previous CEG's parent. Without this, the
       * banner kept rendering with stale agent metadata even though
       * the active workflow had no agent linkage. */
      setLinkedAgent(null);
      setLinkedAgentDismissed(false);
      // RC-54 — also reset the tombstone state when leaving an agent-bound canvas.
      setLinkedAgentRetired(false);
      return;
    }
    // RC-54 — Optimistically clear the tombstone on each fetch attempt;
    // it'll be re-set below if the fetch returns non-OK. Without this
    // reset, switching from a retired CEG to a healthy one would keep
    // the tombstone visible against the new agent.
    setLinkedAgentRetired(false);
    let cancelled = false;
    /* G232 — Capture the agent payload locally so the auto-create branch
     * below has access to isRunbookManaged without waiting on setState. */
    let agentPayload = null;
    (async () => {
      // 1) Resolve agent for the back-link banner.
      try {
        const res = await getAgent(linkedAgentId);
        if (cancelled) return;
        // apiFetch envelope: { ok, data, ... }. Fall back to raw.
        // RC-54 — Detect a 404/non-OK envelope explicitly. apiFetch may
        // surface either { ok: false, status: 404 } OR throw, depending
        // on the fetch wrapper. Both paths land on the same tombstone.
        if (res && res.ok === false) {
          setLinkedAgentRetired(true);
          setLinkedAgent(null);
          return;
        }
        const payload = res?.ok ? res.data : res;
        if (payload && payload.id) {
          agentPayload = payload;
          setLinkedAgent(payload);
        } else {
          // Empty body / no id field — treat as unreachable.
          setLinkedAgentRetired(true);
          setLinkedAgent(null);
        }
      } catch (e) {
        // Non-fatal — banner just won't render. Keeping the canvas
        // usable is more important than the back-link.
        // RC-54 — Flip the tombstone on so the operator sees WHY the
        // back-to-agent chip is absent instead of guessing.
        setLinkedAgentRetired(true);
        setLinkedAgent(null);
        // eslint-disable-next-line no-console
        console.warn('[CanvasPage] couldn\'t resolve linked agent', linkedAgentId, e);
      }
      // 2) Auto-select the agent's linked workflow so the DAG renders
      //    without manual picker interaction. This closes a UX gap for
      //    Auto Create's success banner ("View … in Build → … Open in
      //    Canvas") and demo recordings that deep-link to a freshly
      //    created agent — without this, /canvas?agentId= shows an
      //    empty canvas because selectedWorkflowId is still whatever
      //    persisted state had (often null or a stale workflow).
      //
      //    We deliberately only auto-select if the user has no current
      //    selection OR the current selection isn't owned by this
      //    agent — never override an active user choice.
      try {
        const wfRes = await getWorkflows({ agentId: linkedAgentId });
        if (cancelled) return;
        const wfList = wfRes?.ok ? (wfRes.data || []) : (Array.isArray(wfRes) ? wfRes : []);

        // Detect whether the URL also carries an explicit ?workflowId=.
        // The `?workflowId=` deep-link useEffect (below) handles that path
        // on its own — it always wins regardless of agentId resolution.
        // We only run the agent-driven auto-select when the URL gives us
        // an agent but no specific workflow.
        const hasExplicitWorkflowId = (() => {
          try { return Boolean(new URLSearchParams(window.location.search).get('workflowId')); }
          catch (_) { return false; }
        })();
        if (hasExplicitWorkflowId) return;

        // ── Pick the canonical CEG ─────────────────────────────────────
        // When the user has clicked "Test & Verify in Canvas" repeatedly,
        // the agent has multiple linked workflows: one CEG (auto-derived
        // from the runbook) and N sandbox workflows ("Test · {agent} ·
        // {timestamp}"). Backend returns them sorted by updated_at DESC,
        // so a naive find() picks whichever sandbox was created last.
        // Priority: auto-create provenance → non-sandbox → oldest → any.
        const looksLikeSandbox = (w) =>
          typeof w?.name === 'string' && w.name.startsWith('Test · ');
        const hasAutoCreateProvenance = (w) =>
          w?.provenance && (w.provenance.autoCreatedFrom || w.provenance.source === 'runbook');
        const linkedWf =
          wfList.find((w) => w && w.id && hasAutoCreateProvenance(w))
          || wfList.filter((w) => w && w.id && !looksLikeSandbox(w))
            .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))[0]
          || wfList.find((w) => w && w.id);

        // ── URL is source of truth ─────────────────────────────────────
        // W131: previously this effect honoured the persisted localStorage
        // selection if it happened to be in wfList. That had two failure
        // modes:
        //   (a) Stale persisted selection from a different agent's session
        //       leaks across — user clicks "Open in Canvas" on agent A,
        //       canvas shows agent B's workflow because B was selected
        //       last and (with broken filtering) appeared in wfList.
        //   (b) Fresh AutoLet with zero linked workflows yet — wfList is
        //       empty, linkedWf is undefined, override never fires, and
        //       the canvas keeps showing whatever the last session loaded.
        // Both produced "wrong workflow renders" bug reports.
        //
        // Fix: when ?agentId= is present in the URL, the URL wins. Always
        // set selectedWorkflowId to the agent's canonical CEG (or null if
        // none exists yet — the canvas will render its empty-state CTA).
        // This matches user intent: deep-linking to /canvas?agentId=<X>
        // means "show me agent X's CEG", full stop.
        if (linkedWf) {
          setSelectedWorkflowId(linkedWf.id);
        } else {
          /* G232 + G235 — Per user requirement: auto-create a workflow on
           * first canvas-open for agents that don't have one yet. CEG-GOLDEN-
           * RULES §1 makes the CEG immutable + runbook-derived for AutoLets,
           * so we MUST NOT auto-create blank workflows for AutoLets, ExteLets
           * (which use the GEL bridge instead of canvas), or SysLets (platform-
           * owned). The backend's isRunbookManaged flag is unreliable on the
           * agent list endpoint — per GOLDEN_CEG_RULES §1.6 the canonical
           * AutoLet signal is the `AutoLets-` name prefix. Mirror the
           * deriveArchetype logic that AgentDetailPageV2 uses so the
           * archetype determination here matches what the user sees there. */
          const a = agentPayload || {};
          const archetype = (() => {
            if (a.archetype) return a.archetype;
            if (a.system === true || a.kind === 'system' || a.isSystemAgent === true) return 'SysLet';
            if (a.external === true || a.isExternal === true || a.gel === true ||
                a.type === 'external' || a.agentClassification === 'EXTERNAL') return 'ExteLet';
            if (a.autoCreated === true || a.source === 'auto-create' ||
                a.discoverySource === 'auto-create' || a.discoverySource === 'autolet' ||
                a.isRunbookManaged === true ||
                (typeof a.name === 'string' && a.name.startsWith('AutoLets-'))) return 'AutoLet';
            return 'Let';
          })();
          if (archetype === 'AutoLet') {
            setSelectedWorkflowId(null);
            addToast(
              `${a.name || 'This AutoLet'} has no CEG yet. The canonical execution graph must be materialised from the runbook — open the agent's Build tab and run Auto Create.`,
              'info',
              8000,
            );
            return;
          }
          if (archetype === 'ExteLet') {
            setSelectedWorkflowId(null);
            addToast(
              `${a.name || 'This ExteLet'} is an external agent — its execution graph lives at the external endpoint. Canvas authoring is not applicable. Manage the registration on the External Agents page.`,
              'info',
              8000,
            );
            return;
          }
          if (archetype === 'SysLet') {
            setSelectedWorkflowId(null);
            addToast(
              `${a.name || 'This SysLet'} is a platform-owned system agent — its workflow graph is managed by the platform and cannot be canvas-authored.`,
              'info',
              8000,
            );
            return;
          }
          /* G236 + G238 — Non-AutoLet path: auto-create a workflow whose
           * DAG reflects the agent's actual skills AND the cascaded policy
           * + framework bindings. Skill nodes keyed by `skillId`, policy
           * gates keyed by `bindingId`, framework controls keyed by
           * `controlId` for stable identity across re-materialisation.
           * When the agent has zero skills AND no bindings, builder falls
           * back to the Start→End sentinel so the canvas is still usable. */
          const skillCount = Array.isArray(agentPayload?.skills) ? agentPayload.skills.length : 0;
          try {
            const [canvasBindings, executionStats] = await Promise.all([
              fetchAgentCanvasBindings({
                orgId, tenantId,
                agentId: linkedAgentId,
                skills: agentPayload?.skills,
              }),
              /* G243 — pull per-skill execution stats so the freshly-
               * materialised DAG carries truth-in-canvas badges from day one. */
              fetchAgentSkillExecutionStats({ agentId: linkedAgentId, windowDays: 30 }),
            ]);
            canvasBindings.executionStats = executionStats;
            const dag = buildAgentSkillsDag(agentPayload, agentPayload?.skills, canvasBindings);
            const skillSummary = skillCount > 0
              ? ` Reflects ${skillCount} skill${skillCount === 1 ? '' : 's'} as a linear pipeline; re-arrange or branch from here.`
              : ' Empty starter canvas — add skills on the agent\'s Build tab and re-open the canvas to materialise a fresh DAG.';
            const created = await createWorkflow({
              /* G279 — CEG naming convention: every Canonical Execution Graph
               * workflow is named unambiguously with a `[CEG] ` prefix so it's
               * instantly distinguishable from user-authored standalone workflows
               * in lists/pickers/audit. (AutoLet CEGs carry the same prefix, set in
               * AutoCreateActivitiesImpl.) */
              name: `[CEG] ${agentPayload?.name || 'Untitled agent'}`,
              description: `Auto-created on first canvas open for ${agentPayload?.name || 'this agent'}.${skillSummary}`,
              agentId: linkedAgentId,
              orgId, tenantId,
              status: 'draft',
              /* G279 — stamp the native Let's Canonical Execution Graph CORRECTLY at
               * birth instead of letting it default to kind=standalone (the entity
               * default in Workflow.java). This branch is reached ONLY for
               * archetype==='Let' (AutoLet/ExteLet/SysLet all return early above), so
               * the stamp is unconditional and unambiguous:
               *   • kind: 'let_canvas'  → the ONLY reverse-sync-eligible kind
               *     (CanvasReconcileService); enables canvas Save → agent.skills sync,
               *     phase-gated (Build add/remove, Decide tune-only, Act locked). For a
               *     fresh Let, canvas skill-nodes == agent.skills (both empty), so the
               *     first reconcile is a guaranteed no-op — zero behavioral change.
               *   • isCanonical: true   → marks THE one CEG per agent (V168 partial
               *     unique index uq_workflows_canonical_per_agent); lets conformance +
               *     the WorkflowDraftValidator reliably identify the Let's CEG.
               *   • isStandalone: false → correctly excludes the CEG from the
               *     "From Existing Workflow" standalone picker (WorkflowService
               *     applyStandaloneFilter). */
              kind: 'let_canvas',
              isCanonical: true,
              isStandalone: false,
              definition: dag,
            });
            if (cancelled) return;
            const newWf = created?.ok ? created.data : created;
            if (newWf?.id) {
              setSelectedWorkflowId(newWf.id);
              const polCount =
                  (canvasBindings.orgPolicies?.length || 0)
                + (canvasBindings.tenantPolicies?.length || 0)
                + (canvasBindings.agentPolicies?.length || 0)
                + Object.values(canvasBindings.skillPolicyDocs || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
              const fwCount = canvasBindings.frameworkControls?.length || 0;
              const enfTail = (polCount + fwCount) > 0
                ? ` · ${polCount} policy gate${polCount === 1 ? '' : 's'}${fwCount > 0 ? ` + ${fwCount} framework check${fwCount === 1 ? '' : 's'}` : ''}`
                : '';
              addToast(
                skillCount > 0
                  ? `Created workflow for ${agentPayload?.name || 'agent'} — DAG materialised from ${skillCount} skill${skillCount === 1 ? '' : 's'}${enfTail}.`
                  : `Created workflow for ${agentPayload?.name || 'agent'} — Start → End canvas${enfTail ? ` with ${enfTail.replace(' · ','')}` : ' ready to build'}.`,
                'success',
              );
              /* Refresh the workspace list so the new workflow shows in the dropdown. */
              loadWorkflows({ orgId, tenantId, archived: 'all' });
            } else {
              setSelectedWorkflowId(null);
              addToast(
                `Couldn't auto-create a workflow for ${agentPayload?.name || 'this agent'}. ${created?.error || 'Backend rejected the request.'}`,
                'warn',
                8000,
              );
            }
          } catch (createErr) {
            setSelectedWorkflowId(null);
            // eslint-disable-next-line no-console
            console.warn('[CanvasPage] auto-create failed for agent', linkedAgentId, createErr);
            addToast(`Auto-create failed: ${createErr?.message || createErr}`, 'warn', 8000);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasPage] couldn\'t auto-select linked workflow', linkedAgentId, e);
      }
    })();
    return () => { cancelled = true; };
  }, [linkedAgentId]);
  const [viewMode, setViewMode] = usePersistedState('canvas.viewMode', 'canvas'); // canvas | list | orchestration
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [showSaveAs, setShowSaveAs] = useState(false);

  /* RC-117 Phase 1 — Versions slideout open-state. Driven from the
   * toolbar "Versions" button. Self-contained component manages its
   * own fetch lifecycle on open; the parent only owns the open/close
   * flag and the workflowId/name passthrough. */
  const [showVersionsOpen, setShowVersionsOpen] = useState(false);
  /* RC-118b — undo/redo persistence wiring lives BELOW the
   * `const canvas = useCanvas(...)` declaration (~line 2082).
   * Placing it here was a TDZ bug: the dep array
   * `[selectedWorkflowId, canvas.canUndo, canvas.canRedo]` is
   * evaluated at render-time, but `canvas` was declared further down.
   * Don't move the effects back without moving the canvas hook too. */
  const [saveAsName, setSaveAsName] = useState('');
  // P1-E ported: error / submitting state for the Save As modal so backend
  // rejections render inline and the modal only closes on success.
  const [saveAsError, setSaveAsError] = useState(null);
  const [saveAsSubmitting, setSaveAsSubmitting] = useState(false);
  /* RC-96a — Operator-picked target tenant for Platform Admin / Super Admin
   * sessions that have no JWT-bound tenant. The Save-As modal renders an
   * inline tenant Select when (session.tenantId == null), and the picked
   * tenant + its org_id are sent in the create-workflow payload instead
   * of the (null) session values. Never used when session.tenantId IS
   * set — the picker doesn't render and we fall back to the session
   * scope (preserving the Tenant Admin / scoped-SA path). */
  const [saveAsPickedTenantId, setSaveAsPickedTenantId] = useState('');
  /* RC-97a — Click-to-open issues panel.
   *
   * The err/warn/info chip in the canvas footer (line ~7224) had only
   * a hover tooltip listing the first 3 issues. Operators with 5+
   * issues had no way to see the rest, and no way to jump from an
   * issue to the offending node on the canvas. This state drives a
   * proper modal listing every issue with severity, message, suggest
   * hint, and a "Focus on canvas" button per issue that resolves
   * (issue.nodeId → canvas node) and calls canvas.selectNode().
   *
   * State stays in the host because the chip + the modal need to
   * share open/close, and the focus action needs canvas.selectNode
   * which is already a host responsibility. Tooltip on the chip
   * stays as a quick-preview fallback so the existing behavior is
   * unchanged for anyone who doesn't click. */
  const [showIssuesPanel, setShowIssuesPanel] = useState(false);
  /* RC-97e — "Ask AI to fix these" inside the issues panel.
   *
   * busy: true while a fix-request is in flight to /canvas/ai/build.
   *       Used to disable the button + show a spinner. Backed by a
   *       separate AbortController (issuesFixAbortRef) so closing the
   *       panel cancels the in-flight request rather than letting it
   *       race against a subsequent canvas action.
   * error: inline error message shown above the issues list when the
   *        last fix attempt failed. Cleared on next click. */
  const [issuesFixBusy, setIssuesFixBusy] = useState(false);
  const [issuesFixError, setIssuesFixError] = useState(null);
  const issuesFixAbortRef = React.useRef(null);
  /* RC-97e3 — AI fix audit trail.
   *
   * The pre-RC-97e3 success path showed only a toast then closed the
   * panel, leaving operators with NO trace of what AI actually
   * changed. This state captures the structured trace surface AI
   * always returns (summary + warnings) and persists it as a
   * dismissable banner above the canvas — the same visual pattern
   * the BE auto-repair already uses (canvas.orphanRepairReport).
   *
   * Shape: null when no AI fix has been applied; otherwise
   *   { summary: string, warnings: string[], appliedAt: ISO timestamp }
   * Banner is rendered separately (search "RC-97e3" near the
   * auto-repair banner). Dismissable via X; cleared on new fix
   * attempt so each AI fix shows its own trace. */
  const [aiFixTrace, setAiFixTrace] = useState(null);
  /* RC-130 — Focus-target state for the Canvas Issues panel's
   * "Focus on canvas" buttons. Shape: { nodeId, key } where `key` is
   * a monotonically-increasing counter that bumps on every focus
   * request so V2CanvasView's useEffect re-fires even when the same
   * nodeId is focused twice. The pre-RC-130 behavior was a silent
   * no-op for off-screen nodes (focusNode() only set the selection
   * state — no pan/zoom). */
  const [canvasFocusTarget, setCanvasFocusTarget] = useState(null);
  // P0-B ported: unresolved playbook / legacy slugs we couldn't map to an
  // installed connector. Surfaced as a yellow banner with an "Install" CTA.
  const [unresolvedConnectorSlugs, setUnresolvedConnectorSlugs] = useState([]);
  // P1-A ported: pending auto-protect confirmation modal target.
  const [pendingEnforcement, setPendingEnforcement] = useState(null);
  // V328 (2026-07-07) — Workflow Protect · operator-selected data-protection
  // default action. Threaded through pendingEnforcement.onConfirm into
  // handleProtectWorkflow → PUT /workflows/{id} { protectionPolicy: {...} }.
  // Default 'MASK' balances visibility (values still readable in shape) with
  // safety (raw PII redacted in display + reveal audit trail). BLOCK is
  // intentionally NOT the default because it will fail-closed on the first
  // detected PII field — that's a demoable footgun, not a demoable feature.
  // See docs/design/WORKFLOW_PROTECT_ENCRYPTION_AND_MASKING.md §5.3.
  const [protectAction, setProtectAction] = useState('MASK');
  // Phase 6 ported: first-time picker tooltip — once-per-browser hint
  // pointing the user at the new "+" affordance.
  const [showPickerTip, setShowPickerTip] = useState(() => {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    if (!isCanvasPickerEnabled()) return false;
    return window.localStorage.getItem('canvas.pickerTipDismissed') !== 'true';
  });
  const dismissPickerTip = useCallback(() => {
    setShowPickerTip(false);
    if (typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.setItem('canvas.pickerTipDismissed', 'true'); }
      catch (_) { /* swallow — quota / privacy mode */ }
    }
  }, []);

  // Picker takes the stage: while the inline TaskPicker is open, auto-
  // collapse the left palette and the right Inspector so the picker has
  // room without competing with the rest of the chrome. The prior state
  // is captured once per open and restored on close.
  //
  // Refs (not state) for the saved values — we don't need to re-render
  // when they change, only when we apply or restore them. Storing them in
  // state would cause a layout flicker on every picker toggle.
  const prePickerLeftCollapsedRef = useRef(null);
  const prePickerRightCollapsedRef = useRef(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  /* RC-111a — Two-view state for the New Workflow Slideout.
   * 'form'     = author blank workflow (name + description + agent link)
   * 'template' = browse Playbook templates IN-PLACE (no separate modal)
   *
   * The pre-RC-111a flow opened a separate Modal on "From Template" —
   * Sony flagged that as a lazy half-conversion ("popped up another modal").
   * Now From Template flips this view state and the slideout body swaps
   * to the template browser; Back arrow returns to 'form'.
   *
   * Reset to 'form' on every open / close so the slideout always starts
   * on the form view even when previously dismissed mid-template-browse. */
  const [newWorkflowView, setNewWorkflowView] = useState('form');
  const [workflowForm, setWorkflowForm] = useState({ name: '', description: '', agentId: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [paletteExpanded, setPaletteExpanded] = useState({});
  // RC-CanvasV3-PaletteCanvasSync — selection-driven palette sync state.
  //   • paletteHighlightType: the type id to outline + scroll-into-view in
  //     the palette. Cleared when selection changes to another node.
  //   • paletteUserCollapsedSince: set of category ids the operator has
  //     explicitly collapsed during the active selection. Reset whenever
  //     the selected node's CATEGORY changes — so a manual collapse of
  //     "Tasks & Actions" is respected while the operator clicks through
  //     several task nodes, but a click on an AI/RAG node clears that
  //     memory (different scope, fresh expectation).
  const [paletteHighlightType, setPaletteHighlightType] = useState(null);
  const [paletteUserCollapsedSince, setPaletteUserCollapsedSince] = useState(new Set());
  const [playbookFilter, setPlaybookFilter] = useState('');
  const [playbookCategoryFilter, setPlaybookCategoryFilter] = useState(null);
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLinkedSkills, setDeleteLinkedSkills] = useState([]);
  // Archive toggle for Workflow List view
  const [showArchived, setShowArchived] = useState(false);
  // Left panel mode: 'design' or 'execution'
  const [leftPanelMode, setLeftPanelMode] = usePersistedState('canvas.leftPanelMode', 'design');
  // ── Collapsible panels ───────────────────────────────────────────────
  // Allow users to claim back horizontal canvas real estate for large workflows
  // (e.g. the 30-node complex_claim_stress_test). Both panels persist their
  // collapsed state across page loads so the choice is sticky per user.
  const [leftPanelCollapsed, setLeftPanelCollapsed] = usePersistedState('canvas.leftPanelCollapsed', false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = usePersistedState('canvas.rightPanelCollapsed', false);
  // The auto-collapse useEffect that watches `insertionTarget` lives further
  // down the component body, AFTER `insertionTarget` is declared. Putting
  // it here would read `insertionTarget` from the closure before it's been
  // initialised (the deps array is evaluated synchronously during render),
  // tripping a Temporal Dead Zone error.
  // Right-panel tab state — Conductor OSS-parity tab strip (#136). 'task' when a node/connection
  // is selected (matches Conductor OSS auto-switch), 'code' surfaces the full-workflow JSON
  // editor, 'workflow' is a reserved slot for future workflow-level config parity.
  const [rightPanelTab, setRightPanelTab] = usePersistedState('canvas.rightPanelTab', 'task');
  // Lifted from WorkflowCodeTab so the toolbar Save/Execute can mirror Conductor OSS behavior:
  // invalid JSON in the Code tab halts DAG formation AND blocks save/execute until fixed.
  // null = valid; string = parse/schema error message.
  const [codeTabError, setCodeTabError] = useState(null);
  // Execution view state
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runNodeExecutions, setRunNodeExecutions] = useState([]);
  const [runAuditLog, setRunAuditLog] = useState([]);
  // Preserved design-mode nodes/connections so execution view doesn't mutate them
  const designSnapshotRef = useRef(null);
  const [workflowAuditLog, setWorkflowAuditLog] = useState([]);
  // Execution split-view detail tab
  const [execDetailTab, setExecDetailTab] = useState('input');
  const [selectedExecNodeId, setSelectedExecNodeId] = useState(null);
  // #182-followup — Workflow context fed into the AI Explainer prompt.
  // Persisted per workflow id so switching between workflows keeps each
  // one's context independent. Stored in localStorage via the
  // usePersistedState helper; first cut, would graduate to backend
  // persistence (workflow.workflowMeta.context) when we ship workflow
  // metadata writes alongside save.
  const [workflowContextMap, setWorkflowContextMap] = usePersistedState('canvas.workflowContextMap', {});
  const workflowContext = useMemo(() => {
    if (!selectedWorkflowId) return null;
    return workflowContextMap[selectedWorkflowId] || null;
  }, [workflowContextMap, selectedWorkflowId]);
  // #191 — explainer trigger state. The state itself can sit here (it
  // doesn't reference `canvas`), but the useMemo that resolves the
  // trigger ID into a full node lives BELOW the canvas hook init so
  // it doesn't TDZ on `canvas.nodes`.
  const [explainerTriggerNodeId, setExplainerTriggerNodeId] = useState(null);

  const updateWorkflowContext = useCallback((patch) => {
    if (!selectedWorkflowId) return;
    setWorkflowContextMap((prev) => ({
      ...prev,
      [selectedWorkflowId]: { ...(prev[selectedWorkflowId] || {}), ...patch },
    }));
  }, [selectedWorkflowId, setWorkflowContextMap]);
  // Installed connectors: UUID-keyed map (no duplicates) + marketplace-id index for palette/badge lookups
  const [installedConnectors, setInstalledConnectors] = useState({});
  const [installedConnectorsMkIndex, setInstalledConnectorsMkIndex] = useState({});

  // Auto-Create from process files — a single dialog with an inline multi-file
  // dropzone (no stacked slideouts). Files stage via the Auto-Create upload
  // endpoint (purpose=process_files); on draft we call the AI Workflow Builder
  // with the staging refs and load the result onto the canvas as an unsaved
  // draft. Plain const handlers (not hooks) so they close over
  // installedConnectors declared just above with no dep-array ordering risk.
  const [autoCreateFromFilesOpen, setAutoCreateFromFilesOpen] = useState(false);
  const [autoCreateFromFilesBusy, setAutoCreateFromFilesBusy] = useState(false);
  const [autoCreateUploads, setAutoCreateUploads] = useState([]); // [{ ref, filename, isSpreadsheet }]
  const [autoCreateUploading, setAutoCreateUploading] = useState(false);
  const openAutoCreateFromFiles = () => { setAutoCreateUploads([]); setAutoCreateFromFilesOpen(true); };
  const handleAutoCreateUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setAutoCreateUploading(true);
    try {
      const staged = [];
      for (const f of files) {
        const res = await stageAutoCreateRunbook(f, 'process_files');
        if (!res || !res.stagingId) throw new Error(`Upload failed for ${f.name}`);
        staged.push({ ref: res.stagingId, filename: res.filename || f.name, isSpreadsheet: /\.(xlsx|xls|csv)$/i.test(f.name) });
      }
      setAutoCreateUploads((prev) => [...prev, ...staged]);
    } catch (err) {
      addToast(`Upload failed: ${err?.message || err}`, 'error', 8000);
    } finally {
      setAutoCreateUploading(false);
    }
  };
  const removeAutoCreateUpload = (ref) => setAutoCreateUploads((prev) => prev.filter((u) => u.ref !== ref));
  const handleAutoCreateDraft = async () => {
    const refs = autoCreateUploads.map((u) => u.ref);
    if (refs.length === 0) return;
    setAutoCreateFromFilesBusy(true);
    addToast(`Drafting a workflow from ${refs.length} file${refs.length === 1 ? '' : 's'}…`, 'info');
    try {
      const connectorIds = Object.keys(installedConnectors || {});
      const res = await aiCanvasBuildFromProcessFiles(refs, connectorIds);
      const data = res?.ok ? res.data : res;
      if (data?.error) {
        addToast(`Could not draft a workflow: ${data.error}`, 'error', 8000);
        return;
      }
      if (data?.nodes && data.nodes.length > 0 && typeof canvas?.loadCanvasData === 'function') {
        canvas.loadCanvasData(data, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
        setAutoCreateFromFilesOpen(false);
        setAutoCreateUploads([]);
        addToast('Workflow drafted onto the canvas — review, then Save As to keep it.', 'success', 8000);
      } else {
        addToast('No workflow could be drafted from the uploaded files.', 'warn', 7000);
      }
    } catch (e) {
      addToast(`Could not draft a workflow: ${e?.message || e}`, 'error', 8000);
    } finally {
      setAutoCreateFromFilesBusy(false);
    }
  };
  // Learn section expanded state
  const [learnExpanded, setLearnExpanded] = useState(true);
  // Sequential insertion: connection ID where "+" was clicked
  const [insertionTarget, setInsertionTarget] = useState(null);

  // RC-20e — V3CommandPalette open state removed. See import-section
  // header for rationale (duplicate insertion + duplicate search chord).

  // RC-20a (task #1) — Unsaved-changes confirmation modal state.
  // Shape: { onSave, onDiscard, onCancel } or null when modal closed.
  // The handlers are populated by `confirmUnsavedChanges()` — they each
  // resolve the Promise returned to the caller of that function so the
  // caller's async flow continues correctly.
  const [unsavedChangesPrompt, setUnsavedChangesPrompt] = useState(null);

  // RC-18b (task #200) — Governance diff modal state. Populated when a
  // save returns status 409/423 with a structured rejection body;
  // operator clicks "Regress to {phase}" to demote the linked agent's
  // lifecycle phase and retry the save. Null = modal closed.
  const [governanceDiff, setGovernanceDiff] = useState(null);

  // Auto-collapse both side panels when the picker is open, restore on close.
  // The refs `prePickerLeftCollapsedRef` / `prePickerRightCollapsedRef` were
  // declared higher up; they cache the user's pane state at the moment the
  // picker opens so we restore exactly what they had before. This effect
  // MUST live after the `insertionTarget` declaration above — placing it
  // higher in the file would read `insertionTarget` from the deps array
  // during render, before useState had a chance to bind it (TDZ).
  useEffect(() => {
    const pickerOpen = !!insertionTarget && isCanvasPickerEnabled();
    if (pickerOpen) {
      if (prePickerLeftCollapsedRef.current === null) {
        prePickerLeftCollapsedRef.current = leftPanelCollapsed;
        prePickerRightCollapsedRef.current = rightPanelCollapsed;
        if (!leftPanelCollapsed) setLeftPanelCollapsed(true);
        if (!rightPanelCollapsed) setRightPanelCollapsed(true);
      }
    } else if (prePickerLeftCollapsedRef.current !== null) {
      setLeftPanelCollapsed(prePickerLeftCollapsedRef.current);
      setRightPanelCollapsed(prePickerRightCollapsedRef.current);
      prePickerLeftCollapsedRef.current = null;
      prePickerRightCollapsedRef.current = null;
    }
  }, [insertionTarget, leftPanelCollapsed, rightPanelCollapsed, setLeftPanelCollapsed, setRightPanelCollapsed]);

  // RBAC
  const [workflowAssignments, setWorkflowAssignments] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' });
  const [rbacUsers, setRbacUsers] = useState([]);
  const [rbacGroups, setRbacGroups] = useState([]);
  const [rbacAgents, setRbacAgents] = useState([]);
  const pgExecutionRuns = usePaginationState(10);

  /* RC-134 — Concurrent-edit optimistic-lock state.
   *
   * The ref holds the workflow version the operator most recently
   * loaded (or successfully saved). On every save we send this as the
   * If-Match header; the BE 412s if a concurrent save has bumped the
   * version since then. On 412 we open the conflict banner (state
   * below) which lets the operator Reload (discard local) or Save
   * anyway (overwrite, ack'd).
   *
   * A ref (not state) because: (a) the value never drives a render
   * directly — it's read inside async save callbacks; (b) refs don't
   * TDZ-trap a dep array (see reference_useeffect_dep_tdz). null
   * means "don't send If-Match" (back-compat / new-workflow case). */
  const canvasVersionRef = useRef(null);

  /* Banner state — non-null when a 412 has fired and we're waiting for
   * the operator to pick Reload vs Save-anyway. Shape:
   *   { currentVersion, attemptedVersion, pendingData, changeKind }
   * pendingData + changeKind are stashed so Save-anyway can retry with
   * the original payload + narrative without re-walking the save path. */
  const [versionConflict, setVersionConflict] = useState(null);

  /* RC-135 — Restore-on-load prompt state.
   *
   * On workflow load we check the autosave slot via
   * canvas.readUnsavedFor. If present, we DON'T silently overwrite
   * what we just loaded — we surface a banner with two buttons:
   *
   *   • Restore — calls loadCanvasData(autosaved), clearing the
   *     workflow we just fetched. Optimal when the operator's
   *     intention is "pick up where the crash left off."
   *
   *   • Discard — clears the autosave slot and continues with the
   *     server's loaded definition. Optimal when the autosaved edits
   *     are obsolete (e.g. the operator already redid them on a
   *     different machine).
   *
   * Shape: { savedAt, baseVersion, loadedVersion, snapshot } — savedAt
   * + the version delta drive the banner copy ("edits from 12m ago,
   * meanwhile someone saved twice"), snapshot is held so Restore can
   * apply it without re-reading from storage. Null when no prompt. */
  const [unsavedRestore, setUnsavedRestore] = useState(null);

  // Canvas hook
  const canvas = useCanvas({
    onSave: async (data) => {
      if (!selectedWorkflowId) {
        // No workflow exists yet — prompt user to save as new workflow
        addToast('No workflow selected — use "Save As" to save this workflow with a name', 'warn');
        return;
      }
      if (isAutoLetCegReadOnly) {
        addToast(
          `${linkedAgent?.name || 'This AutoLet'}'s CEG is immutable. Upload a revised runbook to change it.`,
          'warn',
          7000,
        );
        return;
      }
      /* G268 / LET_CANVAS_GOLDEN_RULES §5.4 — Pre-save phase enforcement.
       *
       * We used to short-circuit Act-phase and Decide-phase structural
       * saves on the frontend with a toast and bail. That was lossy:
       * the operator got a one-liner with no diff, no regress path, no
       * agency. The new pattern is server-authoritative — always attempt
       * the save and let the backend return 409 STRUCTURAL_LOCK / 423
       * FULL_LOCK with the actual {attemptedAdds, attemptedRemoves,
       * attemptedMutates, regressTarget} payload. The diff slideout
       * opens with that payload and offers Cancel + Regress.
       *
       * The originalSkillIdsRef frontend diff is gone with the bail
       * (the server computes the same diff and includes it in the
       * 409/423 body), removing a drift-prone source of truth. */
      try {
        /* RC-117c — Consume any pending changeKind hint the AI Build /
         * AI Fix / Import path set before triggering this save. The hook
         * clears the ref on consume so a stale hint never leaks to a
         * later unrelated save. Null → BE defaults to "manual_save". */
        const changeKind = canvas?.consumePendingChangeKind?.() || null;
        /* RC-134 — Pass the loaded-version as the If-Match guard. Null
         * means we never anchored (new workflow / first save) → no
         * guard, legacy last-write-wins (correct for the first-save
         * case). Subsequent saves carry the version, so a concurrent
         * remote save fires the 412 path below. */
        const ifMatchVersion = canvasVersionRef.current;
        const saveOpts = {};
        if (changeKind) saveOpts.changeKind = changeKind;
        if (ifMatchVersion != null) saveOpts.ifMatchVersion = ifMatchVersion;
        const result = await saveCanvasApi(selectedWorkflowId, data,
          Object.keys(saveOpts).length > 0 ? saveOpts : undefined);
        if (!result?.ok) {
          // G268 — Phase-allowlist rejection from WorkflowPhaseAllowlistGuard.
          if (result.status === 409 || result.status === 423) {
            setDiffModal({
              open: true,
              source: 'workflow_save',
              code: result.code || (result.status === 409 ? 'STRUCTURAL_LOCK' : 'FULL_LOCK'),
              phase: result.phase || (result.status === 409 ? 'decide' : 'act'),
              error: result.error || 'Phase-allowlist blocked the save.',
              attemptedAdds: Array.isArray(result.attemptedAdds) ? result.attemptedAdds : [],
              attemptedRemoves: Array.isArray(result.attemptedRemoves) ? result.attemptedRemoves : [],
              attemptedMutates: Array.isArray(result.attemptedMutates) ? result.attemptedMutates : [],
              regressTarget: result.regressTarget || (result.status === 409 ? 'build' : 'decide'),
              pendingData: data,
            });
            return;
          }
          /* RC-134 — Optimistic-lock 412. Surface the conflict banner
           * with both versions so the operator can choose Reload (drop
           * local edits, fetch remote head) or Save-anyway (overwrite,
           * sending If-Match: "*"). We deliberately DON'T auto-merge —
           * a structural canvas merge is too risky without operator
           * intent. The banner stashes the pending payload + changeKind
           * so Save-anyway can retry with the original work intact. */
          if (result.status === 412 || result.code === 'workflow_version_conflict') {
            setVersionConflict({
              currentVersion:   result.currentVersion ?? null,
              attemptedVersion: result.attemptedVersion ?? ifMatchVersion ?? null,
              pendingData:      data,
              changeKind,
            });
            return;
          }
          addToast('Save failed: ' + (result?.error || 'Unknown error'), 'error');
          return;
        }
        /* RC-134 — Save succeeded; bump the version ref to the BE's
         * reported new version so the NEXT save's If-Match check
         * against the latest baseline. Falls back to "increment by 1"
         * if the BE response shape doesn't include the version (which
         * shouldn't happen on the modern endpoints but is harmless
         * defensive code). */
        if (result?.data?.version != null) {
          canvasVersionRef.current = result.data.version;
        } else if (canvasVersionRef.current != null) {
          canvasVersionRef.current = canvasVersionRef.current + 1;
        }
        /* RC-135 — Save succeeded; the autosave blob is now obsolete
         * (its baseVersion is older than the version we just wrote).
         * Clear it so a future page reload doesn't prompt the operator
         * to restore work that's already been persisted upstream. */
        try { canvas.clearUnsavedFor && canvas.clearUnsavedFor(selectedWorkflowId); }
        catch { /* see useCanvas storage contract — soft-fail */ }
        /* G264 — Backend reverse-sync reconciler. Workflow definition is
         * already persisted; project canvas-side skill node changes back
         * onto agent.skills with authoritative phase-allowlist
         * enforcement. The reconciler returns
         *   { ok, phase, added, removed, mutated, summary }
         * on success, or
         *   { ok:false, status:409|423, code, phase, error,
         *     attemptedAdds, attemptedRemoves, attemptedMutates,
         *     regressTarget }
         * on a phase-allowlist rejection — opening the same diff modal.
         *
         * G385 — Reverse-sync now requires workflow.kind === 'let_canvas'.
         * Skip the call entirely when the source workflow isn't a Let
         * canvas — this is the structural fix for G383 (Native workflows
         * were silently nuking agent.skills via an empty-diff REMOVE-all).
         * Native, AutoLet CEG, AutoLet sandbox, SysLet, and standalone
         * workflows all fall outside the reverse-sync contract. */
        const reverseSyncEligible = selectedWorkflow?.kind === 'let_canvas';
        if (linkedAgentId && reverseSyncEligible) {
          try {
            const recon = await reconcileCanvas(linkedAgentId, data, selectedWorkflowId);
            const rd = recon?.data || recon || {};
            if (!recon?.ok) {
              const status = recon.status || rd.status;
              const code = rd.code || recon?.code;
              if (status === 409 || status === 423 || code === 'STRUCTURAL_LOCK' || code === 'FULL_LOCK') {
                setDiffModal({
                  open: true,
                  source: 'reconcile',
                  code: code || (status === 409 ? 'STRUCTURAL_LOCK' : 'FULL_LOCK'),
                  phase: rd.phase || recon.phase || (code === 'FULL_LOCK' ? 'act' : 'decide'),
                  error: rd.error || recon?.error || 'Phase-allowlist blocked the reverse-sync.',
                  attemptedAdds: Array.isArray(rd.attemptedAdds) ? rd.attemptedAdds : (Array.isArray(recon.attemptedAdds) ? recon.attemptedAdds : []),
                  attemptedRemoves: Array.isArray(rd.attemptedRemoves) ? rd.attemptedRemoves : (Array.isArray(recon.attemptedRemoves) ? recon.attemptedRemoves : []),
                  attemptedMutates: Array.isArray(rd.attemptedMutates) ? rd.attemptedMutates : (Array.isArray(recon.attemptedMutates) ? recon.attemptedMutates : []),
                  regressTarget: rd.regressTarget || recon.regressTarget || (code === 'FULL_LOCK' ? 'decide' : 'build'),
                  pendingData: data,
                });
              } else {
                addToast(
                  `Workflow saved. Reverse-sync warning: ${rd.error || recon?.error || 'reconcile failed'}.`,
                  'warn',
                );
              }
              return;
            }
            const adds    = (rd.added || []).length;
            const removes = (rd.removed || []).length;
            const mutates = (rd.mutated || []).length;
            const tail = (adds + removes + mutates) > 0
              ? ` · agent.skills synced (+${adds} -${removes} ~${mutates})`
              : '';
            addToast(`Workflow saved${tail}`, 'success');
          } catch (reconErr) {
            addToast(
              `Workflow saved. Reverse-sync threw: ${reconErr?.message || reconErr}`,
              'warn',
            );
          }
          return;
        }
        addToast('Workflow saved', 'success');
      } catch (err) {
        addToast(`Save error: ${err.message}`, 'error');
      }
    },
  });

  /* RC-134 — Anchor canvasVersionRef whenever the loaded workflow
   * changes (different workflow selected OR the same workflow
   * reloaded from a Reload action / external mutation). Reads from
   * the already-derived `selectedWorkflow` const declared later in
   * the file — but that's fine here because we depend on its
   * `.version`, not the const itself, via `selectedWorkflowId` and
   * a `workflows`-driven lookup at effect-fire time.
   *
   * Reset to null when no workflow is selected so the next first-save
   * doesn't carry a stale version from a previously-edited workflow.
   *
   * IMPORTANT (reference_useeffect_dep_tdz): this effect lives AFTER
   * the `const canvas = useCanvas(...)` declaration above to avoid
   * the same render-time TDZ that RC-118b's effects hit. The deps
   * `[selectedWorkflowId, workflows]` are both declared higher up in
   * the function body. */
  useEffect(() => {
    if (!selectedWorkflowId) {
      canvasVersionRef.current = null;
      return;
    }
    const wf = workflows.find((w) => w.id === selectedWorkflowId);
    if (wf && wf.version != null) {
      canvasVersionRef.current = wf.version;
    } else {
      // Workflow row not loaded yet — keep whatever we had (could be
      // null on first load, or the post-save bumped value). The
      // workflows list is async; clearing here would race the load
      // and could blank a valid version.
    }
    // Deps deliberately exclude any function refs — the lookup is
    // a plain Array.prototype.find on the value at effect-fire time.
  }, [selectedWorkflowId, workflows]);

  /* RC-118b — undo/redo persistence wiring (relocated from above the
   * canvas declaration to fix a TDZ that fired on every page load:
   * the dep array `[..., canvas.canUndo, canvas.canRedo]` evaluates
   * at render time, so it MUST come AFTER `const canvas = useCanvas(...)`).
   *
   * Two effects:
   *   1. After loadCanvasData lands (= selectedWorkflowId changes
   *      AND nodes are present), attempt to rehydrate persisted
   *      history for THIS workflowId. The rehydrate helper compares
   *      the persisted baseline signature against the freshly-loaded
   *      state and bails on mismatch — so undo never unloads someone
   *      else's edits made in another tab between sessions.
   *   2. After every commit (canvas.canUndo changes, which ticks on
   *      every history change), throttled-persist the current stacks
   *      back to localStorage. The throttle lives inside the hook
   *      so rapid mutations don't write every frame.
   *
   * Both effects are noop when localStorage is unavailable (private
   * window / quota exceeded) — pre-RC-118b in-memory behavior is
   * unchanged in that case. */
  useEffect(() => {
    if (!selectedWorkflowId) return;
    if (!canvas || typeof canvas.rehydrateHistoryFor !== 'function') return;
    // Defer one tick so the load-effect chain (loadCanvasData →
    // resetHistory → baseline-commit useEffect) settles into past[0]
    // before we attempt baseline-match against the persisted blob.
    const t = setTimeout(() => {
      try { canvas.rehydrateHistoryFor(selectedWorkflowId); } catch { /* soft */ }
    }, 50);
    return () => clearTimeout(t);
    // NOTE: deliberately deps-on selectedWorkflowId only — `canvas` is a
    // re-created object every render, including it would re-rehydrate
    // on every keystroke. The hook ref-stability inside useCanvas means
    // the function pointer is safe to read off the stale closure.
  }, [selectedWorkflowId]);

  useEffect(() => {
    if (!selectedWorkflowId) return;
    if (!canvas || typeof canvas.persistHistoryFor !== 'function') return;
    try { canvas.persistHistoryFor(selectedWorkflowId); } catch { /* soft */ }
    // Persist on every undo/redo state tick. The throttle inside the
    // hook (PERSIST_THROTTLE_MS) collapses bursts, so this effect
    // can fire freely without flooding localStorage. canUndo/canRedo
    // are stable primitives — they only flip on real history changes.
  }, [selectedWorkflowId, canvas.canUndo, canvas.canRedo]);

  /* RC-135 — Autosave cadence: every 30s while the canvas is dirty,
   * snapshot the current state to localStorage. The hook's
   * persistUnsavedFor is the storage primitive; the cadence policy
   * lives here so it's visible at the call site (where the operator-
   * facing dirty signal is also visible).
   *
   * Three protections:
   *   1. Gated on dirty — clean canvases don't burn writes.
   *   2. Gated on selectedWorkflowId — new (unsaved) workflows have no
   *      stable storage key; skip until Save As resolves to an id.
   *   3. Gated on the load-time anchor (canvasVersionRef) — we record
   *      the version this autosave was built on top of, so the
   *      restore prompt can warn about a concurrent edit during
   *      the disconnect.
   *
   * The cadence (30s) is conservative: tighter would burn write cycles
   * for no recovery win; looser would risk losing up to a minute of
   * edits on crash. If the dirty flag flips while the interval is
   * scheduled, the very next tick writes; we don't fire an immediate
   * write to keep typing-cadence churn off localStorage.
   *
   * Placement: AFTER `const canvas = useCanvas(...)` per the
   * reference_useeffect_dep_tdz memory — the dep array reads
   * `canvas.dirty` which would TDZ if this lived above the hook. */
  useEffect(() => {
    if (!selectedWorkflowId) return;
    if (!canvas || typeof canvas.persistUnsavedFor !== 'function') return;
    if (!canvas.dirty) return; // clean canvas → nothing to autosave
    const id = setInterval(() => {
      try {
        canvas.persistUnsavedFor(selectedWorkflowId, canvasVersionRef.current);
      } catch { /* soft per the hook contract */ }
    }, 30_000);
    return () => clearInterval(id);
  }, [selectedWorkflowId, canvas.dirty, canvas.persistUnsavedFor]);

  /* RC-135 — Restore-on-load check.
   *
   * Fires when (a) we have a selected workflow AND (b) we have the
   * loaded version anchor (canvasVersionRef.current — set by the
   * version-anchor effect just above). The combination tells us
   * "the page has fully loaded a workflow" — if there's an autosave
   * blob for this id with edits we never persisted upstream, surface
   * it.
   *
   * The two-effect cascade (anchor first, then check) avoids races
   * where the autosave check fires before we know the loaded version,
   * which would make the "concurrent edit during disconnect" warning
   * impossible to compute correctly.
   *
   * Suppression: don't re-prompt the operator on every keystroke.
   * loadedRef tracks "we already evaluated for this workflowId" so a
   * Discard / Restore decision sticks until the operator navigates
   * away and back. */
  const unsavedRestoreCheckedFor = useRef(null);
  useEffect(() => {
    if (!selectedWorkflowId) return;
    if (!canvas || typeof canvas.readUnsavedFor !== 'function') return;
    if (unsavedRestoreCheckedFor.current === selectedWorkflowId) return;
    // Wait until we have a version anchor before checking — otherwise
    // we can't tell whether the snapshot is fresh or stale-to-server.
    if (canvasVersionRef.current == null) return;
    unsavedRestoreCheckedFor.current = selectedWorkflowId;
    const snap = canvas.readUnsavedFor(selectedWorkflowId);
    if (!snap) return;
    // baseVersion < loaded.version means: someone saved over my edits
    // during my disconnect. snapshot is still recoverable but the
    // Restore action would overwrite the remote save. The banner shows
    // this explicitly so the operator picks deliberately.
    setUnsavedRestore({
      savedAt: snap.savedAt,
      baseVersion: snap.baseVersion,
      loadedVersion: canvasVersionRef.current,
      snapshot: { nodes: snap.nodes, connections: snap.connections },
    });
  }, [selectedWorkflowId, canvas]);

  /* Reset the suppression sentinel when the operator switches workflows
   * so the new workflow's autosave (if any) gets evaluated. */
  useEffect(() => {
    if (selectedWorkflowId !== unsavedRestoreCheckedFor.current) {
      unsavedRestoreCheckedFor.current = null;
      // Also drop any open prompt for the previous workflow.
      if (unsavedRestore) setUnsavedRestore(null);
    }
    // unsavedRestore deliberately excluded from deps — including it
    // would re-fire this effect on every banner state change and clear
    // the prompt the moment it opens (defeating the point). Reset
    // logic only depends on selectedWorkflowId switching.
  }, [selectedWorkflowId]);

  /* G236 — Sync-from-agent handler. Re-fetches the agent's CURRENT skills,
   * rebuilds the DAG via buildAgentSkillsDag, persists it to the workflow
   * via saveCanvas, and reloads the canvas surface so the user sees the new
   * pipeline immediately. Warning: this is a one-way operation today (agent
   * skills overwrite the canvas) — non-skill nodes the operator added (e.g.
   * approval gates, branches) will be lost. The button tooltip warns the
   * user. Reverse sync (canvas → agent.skills via backend reconciliation)
   * lands in G237. */
  const handleSyncFromAgent = useCallback(async () => {
    if (!linkedAgentId || !selectedWorkflowId) {
      addToast('Sync needs both an agent context and an active workflow.', 'warn');
      return;
    }
    setSyncingFromAgent(true);
    try {
      const res = await getAgent(linkedAgentId);
      const payload = res?.ok ? res.data : res;
      if (!payload || !payload.id) {
        addToast('Couldn\'t refresh the agent record — sync aborted.', 'error');
        return;
      }
      setLinkedAgent(payload);
      /* G238 + G243 — re-fetch the cascaded policy + framework bindings AND
       * per-skill execution stats so the Sync rebuilds the full
       * materialisation (skills + policy gates + framework + truth-in-canvas
       * badges) in one pass. */
      const [canvasBindings, executionStats] = await Promise.all([
        fetchAgentCanvasBindings({
          orgId, tenantId, agentId: linkedAgentId, skills: payload.skills,
        }),
        fetchAgentSkillExecutionStats({ agentId: linkedAgentId, windowDays: 30 }),
      ]);
      canvasBindings.executionStats = executionStats;
      const freshDag = buildAgentSkillsDag(payload, payload.skills, canvasBindings);
      /* G256 / §3.4 — Preserve operator-authored non-skill nodes (decision /
       * branch / loop / wait / fork / join / etc) across re-sync. Without
       * this merge, every Sync click silently destroys the operator's
       * conditional logic. */
      const existingDefinition = selectedWorkflow?.definition
        || canvas?.getCanvasData?.()
        || null;
      const preservedCount = (existingDefinition?.nodes || [])
        .filter((n) => !isMaterialiserOwned(n)).length;
      const dag = mergePreservingOperatorNodes(freshDag, existingDefinition);
      /* G236 — Use updateWorkflow (PUT /api/v1/workflows/{id} with full
       * payload) instead of saveCanvas (/canvas sub-resource), because the
       * /canvas endpoint returned 404 on workflows that were created via
       * POST /api/v1/workflows without an initial canvas materialisation.
       * updateWorkflow is the proven path used by the auto-create flow
       * earlier in this useEffect. */
      const wfPatch = {
        id: selectedWorkflowId,
        name: selectedWorkflow?.name || `${payload.name} workflow`,
        description: selectedWorkflow?.description || '',
        agentId: linkedAgentId,
        orgId, tenantId,
        status: selectedWorkflow?.status || 'draft',
        definition: dag,
      };
      const saveRes = await updateWorkflow(selectedWorkflowId, wfPatch);
      if (!saveRes || saveRes.ok === false) {
        addToast(`Sync failed: ${saveRes?.error || saveRes?.data?.error || 'Backend rejected the canvas update.'}`, 'error');
        return;
      }
      canvas.loadCanvasData(dag, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
      /* Reload the workflow list so the dropdown's name/version reflect the
       * patched workflow. */
      try { loadWorkflows({ orgId, tenantId, archived: 'all' }); } catch { /* ignore */ }
      const skillCount = Array.isArray(payload.skills) ? payload.skills.length : 0;
      const policyCount =
          (canvasBindings.orgPolicies?.length || 0)
        + (canvasBindings.tenantPolicies?.length || 0)
        + (canvasBindings.agentPolicies?.length || 0)
        + Object.values(canvasBindings.skillPolicyDocs || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      const frameworkCount = canvasBindings.frameworkControls?.length || 0;
      const enforcementSummary = (policyCount + frameworkCount) > 0
        ? ` · ${policyCount} policy gate${policyCount === 1 ? '' : 's'}${frameworkCount > 0 ? ` + ${frameworkCount} framework control${frameworkCount === 1 ? '' : 's'}` : ''}`
        : '';
      /* G243 — count skills that actually got invoked in the last 30d. */
      const statsCovered = Object.values(executionStats || {}).filter((v) => v && v.count > 0).length;
      const statsSummary = statsCovered > 0
        ? ` · ${statsCovered}/${skillCount} invoked in last 30d`
        : '';
      /* G256 — surface operator-authored node preservation in the toast
       * so the operator knows their decision/branch/loop nodes survived
       * the re-materialise (or that there were none to begin with). */
      const preservationSummary = preservedCount > 0
        ? ` · preserved ${preservedCount} operator-authored node${preservedCount === 1 ? '' : 's'}`
        : '';
      addToast(
        skillCount > 0
          ? `Canvas resynced from ${payload.name} — ${skillCount} skill${skillCount === 1 ? '' : 's'} materialised${enforcementSummary}${statsSummary}${preservationSummary}.`
          : `Canvas resynced from ${payload.name} — no skills yet${enforcementSummary ? `${enforcementSummary} only` : ', returned to Start → End sentinel'}${preservationSummary}.`,
        'success',
      );
    } catch (err) {
      addToast(`Sync error: ${err?.message || err}`, 'error');
    } finally {
      setSyncingFromAgent(false);
    }
    // eslint-disable-next-line
  }, [linkedAgentId, selectedWorkflowId, canvas, addToast, orgId, tenantId, updateWorkflow, loadWorkflows]);

  /* G265 / AGENT_EXECUTION_MODEL §2.1 — Save the Cognitive Brief.
   * Validates locally then PUTs the full brief onto agent.cognitiveBrief.
   * On success re-fetches the agent so the cognitive_loop node + banner
   * reflect the new brief. Iteration cost cap can be left blank to mean
   * "use org policy default". */
  const handleSaveCognitiveBrief = useCallback(async () => {
    if (!linkedAgentId) return;
    setCognitiveBriefError(null);
    const f = cognitiveBriefForm;
    if (!f.systemPrompt || !f.systemPrompt.trim()) {
      setCognitiveBriefError('System prompt is required.');
      return;
    }
    const maxIter = parseInt(f.maxIterations, 10);
    if (!Number.isFinite(maxIter) || maxIter < 1 || maxIter > 200) {
      setCognitiveBriefError('Max iterations must be an integer between 1 and 200.');
      return;
    }
    let costCap = null;
    if (f.iterationCostCapUsd !== '' && f.iterationCostCapUsd != null) {
      const n = parseFloat(f.iterationCostCapUsd);
      if (!Number.isFinite(n) || n < 0) {
        setCognitiveBriefError('Iteration cost cap must be a non-negative number (USD).');
        return;
      }
      costCap = n;
    }
    setSavingCognitiveBrief(true);
    try {
      const cognitiveBrief = {
        systemPrompt: f.systemPrompt.trim(),
        toolSelectionStrategy: f.toolSelectionStrategy,
        maxIterations: maxIter,
        iterationCostCapUsd: costCap,
        fallbackBehavior: f.fallbackBehavior,
      };
      const res = await updateAgent(linkedAgentId, { cognitiveBrief });
      if (!res || res.ok === false) {
        setCognitiveBriefError(res?.error || res?.data?.error || 'Backend rejected the save.');
        return;
      }
      addToast('Cognitive Brief saved.', 'success');
      setCognitiveBriefOpen(false);
      try {
        const refreshed = await getAgent(linkedAgentId);
        const payload = refreshed?.ok ? refreshed.data : refreshed;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      setCognitiveBriefError(err?.message || String(err));
    } finally {
      setSavingCognitiveBrief(false);
    }
  }, [linkedAgentId, cognitiveBriefForm, addToast]);

  /* G264 / AGENT_EXECUTION_MODEL §1 — Cycle the agent's execution mode
   * (deterministic → cognitive → hybrid → deterministic). Persists via
   * PUT /agents/{id} (updateAgent). On success re-fetches the agent so
   * the banner chip + downstream re-materialisation see the new mode.
   *
   * Blocked on AutoLets (mode is runbook-derived) and on Act-phase agents
   * (per §7 invariant 1: mode is immutable across a published version —
   * regress + republish to change). The banner's onCycleExecutionMode
   * prop is wired through only when those conditions allow. */
  const handleCycleExecutionMode = useCallback(async (currentMode) => {
    if (!linkedAgentId) return;
    const order = ['deterministic', 'cognitive', 'hybrid'];
    const idx = Math.max(0, order.indexOf(String(currentMode || 'deterministic').toLowerCase()));
    const next = order[(idx + 1) % order.length];
    setCyclingExecutionMode(true);
    try {
      const res = await updateAgent(linkedAgentId, { executionMode: next });
      if (!res || res.ok === false) {
        addToast(`Mode change failed: ${res?.error || res?.data?.error || 'unknown'}`, 'error');
        return;
      }
      addToast(`Execution mode → ${next}.`, 'success');
      /* Re-fetch the agent so the chip + canvas lock + execution-mode
       * fan-out vs sequential re-renders with the new mode. */
      try {
        const refreshed = await getAgent(linkedAgentId);
        const payload = refreshed?.ok ? refreshed.data : refreshed;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      addToast(`Mode change error: ${err?.message || err}`, 'error');
    } finally {
      setCyclingExecutionMode(false);
    }
  }, [linkedAgentId, addToast]);

  /* G258 / LET_CANVAS_GOLDEN_RULES §1.4 — Phase advance from the canvas
   * banner. Operator clicks "Advance to Decide" or "Advance to Act"
   * directly on the canvas top bar instead of bouncing back to the
   * agent detail page. Same backend call as the agent detail page's
   * gate-advance button (POST /agents/{id}/advance-phase). On success
   * we refresh the linkedAgent so the phase chip + canvas lock state
   * update immediately. */
  const handleAdvancePhase = useCallback(async (_targetPhaseHint) => {
    if (!linkedAgentId) return;
    setAdvancingPhase(true);
    try {
      const fn = apiAdvancePhase; // module-level api.advancePhase
      if (typeof fn !== 'function') {
        addToast('Advance unavailable — api.advancePhase is not wired in this build.', 'error');
        return;
      }
      const res = await fn(linkedAgentId, session?.userId);
      /* G274 — a REFUSED gate is HTTP 200 (see readAdvanceResult). Branching on `res.ok`
       * alone reported success while the agent never left Build. Read the body. */
      const { advanced, toPhase, failedChecks, error } = readAdvanceResult(res);
      if (!advanced) {
        const reason = error
          || failedChecks.map((c) => c.message || c.check).filter(Boolean).join('; ')
          || 'Gate not passing. Resolve the failed checks on the agent detail page.';
        addToast(`Advance refused — ${reason}`, 'error');
        /* Re-fetch anyway: the gate may have moved for other reasons and the banner must
         * never show a phase the server doesn't agree with. */
        try {
          const refreshed = await getAgent(linkedAgentId);
          const payload = refreshed?.ok ? refreshed.data : refreshed;
          if (payload?.id) setLinkedAgent(payload);
        } catch (_) { /* non-fatal */ }
        return;
      }
      const newPhase = toPhase || _targetPhaseHint;
      addToast(`Agent advanced${newPhase ? ` to ${newPhase}` : ''}.`, 'success');
      /* Re-fetch the agent so the banner phase chip + canvas lock state
       * + sync button visibility all reflect the new phase. */
      try {
        const refreshed = await getAgent(linkedAgentId);
        const payload = refreshed?.ok ? refreshed.data : refreshed;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      addToast(`Advance error: ${err?.message || err}`, 'error');
    } finally {
      setAdvancingPhase(false);
    }
  }, [linkedAgentId, addToast]);

  /* Agent-level phase REGRESS from the canvas banner.
   *
   * The Act lock banner instructs "regress to Decide to edit", but no surface offered the
   * action: onRegressPhase was an unwired banner prop, and the only apiRegressPhase() call
   * lived behind the pre-save governance-diff modal — which cannot fire on a fully-locked
   * canvas because the save is refused client-side before it reaches the backend. So the
   * operator was told to do something the product didn't let them do. This wires it.
   *
   * Server-authoritative: the endpoint validates the transition (forward / no-op rejected)
   * and cascades skill demotion through AgentService.regressSkill, so audit + gateway-resync
   * side effects fire from a single source of truth. We only refresh local state after. */
  const handleRegressPhaseFromBanner = useCallback(async (target) => {
    if (!linkedAgentId || !target) return;
    /* No window.confirm — the banner button is a two-click inline confirm (platform-native
     * danger affordance), per the V2 redesign rule against browser dialogs. */
    setRegressingPhase(true);
    try {
      const res = await apiRegressPhase(linkedAgentId, target, session?.userId || null);
      if (!res || res.ok === false) {
        addToast(`Regress refused: ${res?.error || res?.data?.error || 'Backend rejected the request.'}`, 'error');
        return;
      }
      addToast(`Agent regressed to ${target}. Canvas unlocked.`, 'success');
      try {
        const refreshed = await getAgent(linkedAgentId);
        const payload = refreshed?.ok ? refreshed.data : refreshed;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      addToast(`Regress error: ${err?.message || err}`, 'error');
    } finally {
      setRegressingPhase(false);
    }
  }, [linkedAgentId, session, addToast]);

  /* G268 / LET_CANVAS_GOLDEN_RULES §5.4 — Regress action from the
   * pre-save diff slideout. Demotes the agent's phase to the
   * server-suggested target (Decide → Build, or Act → Decide / Build
   * depending on whether the rejected change was structural or pure
   * mutate). On success, refreshes linkedAgent so the banner / lock
   * state update immediately, then re-fires the canvas save with the
   * payload the operator was trying to commit when the modal opened.
   *
   * Server-authoritative: the regress endpoint validates the transition
   * (forward / no-op rejected) and cascades skill regression through
   * AgentService.regressSkill so audit + websocket + gateway-resync
   * side effects fire per skill from a single source of truth. */
  const handleRegressFromDiffModal = useCallback(async () => {
    if (!diffModal || !linkedAgentId || !diffModal.regressTarget) return;
    const target = diffModal.regressTarget;
    const pendingData = diffModal.pendingData;
    setRegressingFromModal(true);
    try {
      const actingUserId = session?.userId || null;
      const res = await apiRegressPhase(linkedAgentId, target, actingUserId);
      const rd = res?.data || res;
      if (!res?.ok) {
        addToast(`Regress failed: ${rd?.error || res?.error || 'Unknown error'}`, 'error');
        setRegressingFromModal(false);
        return;
      }
      addToast(`Agent regressed to ${target}. Re-saving canvas…`, 'success');
      /* Refresh linkedAgent so canvas locks (isLetFullLock / isLetStructuralLock
       * / banner phase chip) reflect the new phase before the re-save fires. */
      try {
        const refreshed = await getAgent(linkedAgentId);
        const payload = refreshed?.ok ? refreshed.data : refreshed;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
      /* Close modal first so the second save attempt either succeeds
       * silently or opens a fresh modal (e.g. if the server rejects
       * again for an unrelated reason). */
      setDiffModal(null);
      setRegressingFromModal(false);
      /* Re-fire the save with the pending canvas payload. If this
       * succeeds, the toast inside onSave reports it. If this also
       * rejects, the modal re-opens with the fresh diff. */
      if (pendingData && canvas && typeof canvas.onSave === 'function') {
        await canvas.onSave(pendingData);
      } else if (pendingData) {
        // Direct fallback if the canvas wrapper is unavailable for some reason.
        try {
          await saveCanvasApi(selectedWorkflowId, pendingData);
        } catch (err) {
          addToast(`Re-save failed: ${err?.message || err}`, 'error');
        }
      }
    } catch (err) {
      addToast(`Regress error: ${err?.message || err}`, 'error');
      setRegressingFromModal(false);
    }
  }, [diffModal, linkedAgentId, session, addToast, canvas, selectedWorkflowId]);

  // #191 — resolve the explainer trigger id into a full node. Lives
  // here so we can safely reference `canvas.nodes` without TDZ. Stays
  // null until the user double-clicks; a single click never updates
  // explainerTriggerNodeId, so casual clicks don't reset the explainer.
  const explainerTriggerNode = useMemo(() => {
    if (!explainerTriggerNodeId) return null;
    return canvas.nodes.find((n) => n && n.id === explainerTriggerNodeId) || null;
  }, [explainerTriggerNodeId, canvas.nodes]);

  // RC-18c (task #201) — Soft validator running alongside V3's existing
  // canvas.dagValidation. v4Validate flags shape errors the legacy
  // validator misses (orphan nodes, missing required config per type,
  // dangling branches) at three severity levels. Non-blocking — surfaces
  // as a chip in the canvas footer. Hard-validate flow (`validateDefinitionOrThrow`
  // on the BE) is unchanged.
  const v4ValidationReport = useMemo(() => {
    try {
      return v3ValidateWorkflow({ nodes: canvas.nodes || [], connections: canvas.connections || [] });
    } catch (e) {
      return { issues: [], stats: { error: 0, warning: 0, info: 0, total: 0 } };
    }
  }, [canvas.nodes, canvas.connections]);

  // RC-18a / RC-19a — Command Palette items[] memo MOVED below handleAddNode.
  // See the moved memo declaration further down in this component, after
  // handleAddNode is declared. Pre-move, the useMemo lived here and listed
  // handleAddNode in its dependency array — TDZ violation because the
  // const handleAddNode (declared further down) was unreachable when the
  // useMemo evaluated. Surfaced as a render-time crash:
  //   "Cannot access 'handleAddNode' before initialization"
  // Moving the memo below handleAddNode resolves the TDZ without any
  // behavioral change.

  // =========================================================================
  // Step 8.4 — IR height-feedback second pass.
  // After the first-pass IR layout positions cards by default-leaf heights,
  // the DOM renders with the real CSS heights. This hook measures each node's
  // actual height (via its data-node-id attribute), bucket-rounds per Rule
  // S-G.a, and re-runs the IR pipeline. The second pass re-positions nodes
  // so containers align with their (now-correct) leaf heights.
  //
  // No-op when the IR pipeline flag is off (canvas.irLaidRef stays null).
  // Idempotent: repeated measurements that bucket to the same values skip
  // state churn entirely. Fallback-safe: pipeline errors keep first-pass
  // geometry instead of breaking the canvas.
  // =========================================================================
  useIRHeightFeedback({
    enabled: isIRPipelineEnabled(),
    laid: canvas.irLaidRef.current,
    nodes: canvas.nodes,
    getNodeElement: useCallback(
      (id) => (typeof document !== 'undefined'
        ? document.querySelector(`[data-node-id="${id}"]`)
        : null),
      [],
    ),
    setNodes: canvas.setNodes,
  });

  // =========================================================================
  // Deep-link: auto-select workflow when arriving with ?workflowId=… in the URL
  // Used by the Visual Workflow Mapper "Commit & Open in Canvas" flow so the
  // newly created native workflow opens directly instead of leaving the user
  // to hunt for it in the selector dropdown. Runs once on mount; strips the
  // query param from history so a refresh doesn't force-reselect every time.
  // =========================================================================
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const wfId = params.get('workflowId');
      if (wfId) {
        setSelectedWorkflowId(wfId);
        params.delete('workflowId');
        const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
        window.history.replaceState({}, '', clean);
      }
    } catch (_) { /* no-op */ }
    // Intentionally empty deps — run once on mount only.
  }, []);

  // =========================================================================
  // Template Center deep-link: /canvas?playbookId=<id>
  // Opens a curated workflow template directly on the canvas. If a workflow was
  // already created from this template (matched by name), open that saved
  // workflow at its latest version instead of creating a duplicate; otherwise
  // load the playbook DAG fresh (same path the Load Template modal uses).
  // Runs once on mount; strips the query param so a refresh doesn't re-fire.
  // =========================================================================
  useEffect(() => {
    let pid = null;
    try { pid = new URLSearchParams(window.location.search).get('playbookId'); } catch (_) { return; }
    if (!pid) return;
    // Strip the param up front so a refresh / re-render can't re-trigger the load.
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete('playbookId');
      const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    } catch (_) { /* no-op */ }
    const playbook = getPlaybookById(pid);
    if (!playbook) { addToast('Template not found in the playbook catalog', 'warn'); return; }
    let cancelled = false;
    (async () => {
      // Prefer an existing workflow created from this template (matched by name)
      // so we open its latest saved version rather than creating a duplicate.
      // Best-effort: any failure falls back to a fresh playbook load.
      try {
        const res = await getWorkflows({ orgId, tenantId });
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const existing = list.find((w) => w?.name === playbook.name);
        if (!cancelled && existing?.id) { setSelectedWorkflowId(existing.id); return; }
      } catch (_) { /* fall through to fresh load */ }
      if (!cancelled) handleLoadTemplate(playbook);
    })();
    return () => { cancelled = true; };
    // Intentionally empty deps — run once on mount only.
  }, []);

  // =========================================================================
  // Workflow search dropdown: click-outside close + filtered list
  // =========================================================================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wfDropdownRef.current && !wfDropdownRef.current.contains(e.target)) {
        setWfDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // V132 — Canvas top dropdown never shows archived workflows. Archived rows
  // are only reachable from the Archived view on the WorkflowListView. This
  // belt-and-braces filter is safe even if the backend default changes.
  const nonArchivedWorkflows = workflows.filter((w) => (w?.status || '').toLowerCase() !== 'archived');
  const filteredWorkflows = wfSearchTerm.trim()
    ? nonArchivedWorkflows.filter((w) =>
        (w.name || '').toLowerCase().includes(wfSearchTerm.toLowerCase()) ||
        (w.status || '').toLowerCase().includes(wfSearchTerm.toLowerCase())
      )
    : nonArchivedWorkflows;

  // =========================================================================
  // Data Loading
  // =========================================================================

  useEffect(() => {
    if (orgId && tenantId) {
      // V132 — Canvas page hosts both the active Workflows list AND the
      // Archived view (via the showArchived toggle). Fetch everything so
      // client-side toggling does not require a second round trip. The
      // dropdown filters archived out client-side (see filteredWorkflows).
      loadWorkflows({ orgId, tenantId, archived: 'all' });
      loadAgents({ orgId, tenantId });
      loadOrchestrationStats(orgId, tenantId);
      // Fetch installed connectors for status badges in canvas palette.
      //
      // RC-12 (task #192) — Unwrap the V226 / CONN-SCOPE ConnectorResponse
      // envelope. The backend wraps every entry as
      // `{ connector: <Connector>, isOwner, sharedWithTenantIds, sharedWithGrants }`
      // so the inner Connector entity (carrying id / name / type / status)
      // lives under `.connector`. Reading id/name/type from the wrapper —
      // as this code did pre-RC-12 — silently produced an installedConnectors
      // map with one all-undefined entry per fetch (everything keyed under
      // `undefined`), which dropped every status badge in the palette AND
      // emptied every connector picker that depended on this state
      // (connector_task, my new LLM picker, etc.). Tolerant of legacy flat
      // entries so a future un-wrap on the BE side doesn't re-break this.
      getConnectors({ tenantId, orgId }).then((res) => {
        if (res?.ok && Array.isArray(res.data)) {
          const byId = {};   // UUID → connector entry (no duplicates)
          const mkIndex = {};  // marketplace id → connector entry (for palette/badge lookups)
          res.data.forEach((row) => {
            // api.getConnectors unwraps the sharing envelope; `|| {}` keeps this
            // safe if the backend ever serves a malformed null entry.
            const c = row || {};
            if (!c.id) return;  // skip malformed rows rather than poison byId[undefined]
            const entry = {
              id: c.id, name: c.name, status: c.status, type: c.type, url: c.url,
              description: c.description, icon: c.icon,
            };
            byId[c.id] = entry;
            // Find matching marketplace entry — prioritize name matches over URL
            // to avoid cross-matching connectors that share the same API base
            // (e.g., Microsoft Entra ID, Teams, and Defender all use graph.microsoft.com)
            const mcMatch = marketplaceConnectors.find((mc) =>
              mc.id === c.type
              || mc.name === c.name
              || (c.name && mc.name && mc.name.toLowerCase() === c.name.toLowerCase())
            ) || marketplaceConnectors.find((mc) =>
              c.url && mc.baseUrl && mc.baseUrl.replace(/\{[^}]+\}/g, '').length > 5
                  && c.url.startsWith(mc.baseUrl.replace(/\{[^}]+\}/g, ''))
            );
            if (mcMatch) {
              mkIndex[mcMatch.id] = entry;
            }
          });
          setInstalledConnectors(byId);          // UUID-only: no duplicates in Object.values()
          setInstalledConnectorsMkIndex(mkIndex); // marketplace-id → entry for palette/badge lookups
        }
      }).catch(() => { /* silent — palette still works without status */ });
    }
    // Re-run when the marketplace catalogue loads so name-match lookups work
    // even if /connectors resolves before the registry fetch completes.
  }, [orgId, tenantId, marketplaceConnectors]);

  const selectedWorkflow = workflows.find((w) => w.id === selectedWorkflowId);

  /* RC-46 + RC-50 — Bidirectional sync between linkedAgentId and the
   * active workflow's agentId.
   *
   * Original RC-46 intent: when an operator opens a CEG via the
   * workflow picker (only ?workflowId=… in URL, no ?agentId=…), the
   * "Back to Agent" banner should still appear because the loaded
   * workflow row carries an agentId we can recover. So we fill
   * linkedAgentId from selectedWorkflow.agentId post-load.
   *
   * RC-50 — the original implementation was one-shot: an early-out
   * `if (linkedAgentId) return;` prevented the sync from ever
   * firing again once set. That created a stale-state bug: open a
   * CEG → linkedAgentId set → operator clicks "+ New blank workflow"
   * → canvas resets to a standalone workflow with no agentId → but
   * linkedAgentId is still pinned to the OLD CEG's agent → banner
   * misleads the operator into thinking the blank canvas is linked
   * to the previous agent.
   *
   * Fix: drop the early-out. Always sync linkedAgentId to whatever
   * the currently-selected workflow declares — including clearing
   * to null when the workflow has no agentId (i.e. it's a plain
   * standalone workflow). The URL-hint entry path is preserved by
   * the `!selectedWorkflowId` guard below: until ANY workflow is
   * selected, we don't touch linkedAgentId, so /canvas?agentId=X
   * still gets to auto-select the agent's CEG via the existing
   * useEffect at line ~1480 before this sync runs.
   *
   * State transitions covered:
   *   CEG opened from picker (no URL agentId)
   *      → selectedWorkflow.agentId = X → linkedAgentId set to X    ✓
   *   /canvas?agentId=X opened (URL hint, no workflow yet)
   *      → !selectedWorkflowId guard returns early; URL value
   *        preserved until the agent-link useEffect auto-selects
   *        the agent's workflow, at which point this effect re-runs
   *        and confirms wfAgentId === linkedAgentId — no-op.        ✓
   *   Switch CEG → different CEG
   *      → wfAgentId changes, linkedAgentId updates                  ✓
   *   Switch CEG → blank standalone workflow (the RC-50 case)
   *      → wfAgentId is null, linkedAgentId clears to null           ✓
   *   Blank standalone → blank standalone
   *      → both stay null                                            ✓ */
  useEffect(() => {
    if (!selectedWorkflowId) return;                 // no workflow loaded — preserve URL hint
    const wfAgentId = selectedWorkflow?.agentId || null;
    if (wfAgentId !== linkedAgentId) {
      setLinkedAgentId(wfAgentId);
    }
  }, [selectedWorkflowId, selectedWorkflow?.agentId, linkedAgentId]);

  // V169 / Wave 3 — AutoLet CEG read-only mode (GOLDEN_CEG_RULES.md §1).
  //
  // The CEG of a runbook-managed AutoLet is immutable — no Save, no
  // Save As, no palette, no +Task button, no per-task +, no ⌘E
  // shortcut, no drag-drop, no recipes. Backend 409s on the Save call
  // anyway (W1.4: WorkflowService.updateWorkflow guards on
  // is_runbook_managed via assertWorkflowMutable), but surfacing
  // controls that always error is bad UX. We render the canvas in a
  // pan-zoom-only viewer mode and post a banner.
  //
  // Two signals must BOTH be true to flip read-only:
  //   1. The workflow is a CEG (workflow.isCanonical === true), and
  //   2. The owning agent is runbook-managed.
  //
  // When the workflow has no agent_id or the agent isn't loaded yet,
  // we DON'T flip read-only — better to allow editing on a workflow
  // we're unsure about than lock a manual workflow by mistake. The
  // backend stays the source-of-truth.
  //
  // Sandbox workflows (isSandbox=true) explicitly stay editable, even
  // when their parent is an AutoLet — that's the whole point of
  // sandboxes (§2). The check below is is_canonical-only; sandboxes
  // never hit it.
  /* G385 — workflow.kind is now the authoritative source for canvas
   * mutability. Replaces the old (isCanonical AND isRunbookManaged) AND-pair
   * that failed open if either flag drifted. The legacy AND-pair is kept
   * as a fallback for pre-V177 rows that haven't been backfilled yet
   * (defensive — backfill should have caught everything).
   *
   * V221 — split the gate into two flags that mirror the two design
   * categories the backend now enforces:
   *
   *   isImmutableKindReadOnly  → fully immutable (autolet_ceg + syslet_canvas).
   *                              No one can edit via this endpoint, Platform
   *                              Admin included. Rebuild path is to re-register
   *                              via the source (runbook upload for AutoLet,
   *                              SystemWorkflowRegistry boot for SysLet CEG).
   *
   *   isStandaloneSystemAdminOnly → mutable, but only by Platform Admin.
   *                                  Platform utility workflows (scheduler
   *                                  kickers, sweepers, enforcement entries).
   *                                  Everyone else sees a read-only banner.
   *
   * Uses isPlatformAdmin(), NOT isSuperAdmin() — the latter deliberately
   * conflates PA+SA, while WorkflowService.updateWorkflow gates on Platform
   * Admin alone. With the conflated predicate a customer-scoped Super Admin
   * was shown an editable canvas and refused with 403 on save.
   *
   * Legacy variable name {@code isAutoLetCegReadOnly} is kept for backward
   * compatibility with the dozens of references throughout this file; it now
   * aliases isImmutableKindReadOnly. A future cleanup pass can rename. */
  const workflowKind = selectedWorkflow?.kind;
  const isImmutableKindReadOnly = (
       workflowKind === 'autolet_ceg'
    || workflowKind === 'syslet_canvas'
    || (selectedWorkflow?.isCanonical === true && linkedAgent?.isRunbookManaged === true)
  );
  const isStandaloneSystemAdminOnly = (
    workflowKind === 'standalone_system' && !isPlatformAdmin()
  );
  /* Aliased for backward compatibility — many downstream references key off
   * this exact name. V221 expanded the lock surface to include
   * standalone_system + non-admin so all the same chrome-hiding kicks in
   * (palette, +Task, save, etc.) without renaming every reference. */
  const isAutoLetCegReadOnly = isImmutableKindReadOnly || isStandaloneSystemAdminOnly;

  /* G240 — Let canvas phase lock per LET_CANVAS_GOLDEN_RULES §1.
   *
   *   Build  = free authoring         (no lock)
   *   Decide = structural lock        (no add/remove skill nodes; per-node
   *                                    autonomy/inference still editable;
   *                                    palette + +Task hidden)
   *   Act    = fully locked           (pan + zoom only; Save disabled;
   *                                    Sync from agent skills hidden)
   *
   * Only applies to canvases linked to a Let agent (i.e. linkedAgent is
   * loaded AND archetype === 'Let'). AutoLet read-only is enforced
   * separately by isAutoLetCegReadOnly above; ExteLet / SysLet never reach
   * this canvas authoring path. */
  const linkedLetArchetype = (() => {
    const a = linkedAgent;
    if (!a) return null;
    if (a.archetype) return a.archetype;
    if (a.isSystemAgent === true) return 'SysLet';
    if (a.agentClassification === 'EXTERNAL' || a.isExternal === true) return 'ExteLet';
    if (a.isRunbookManaged === true ||
        (typeof a.name === 'string' && a.name.startsWith('AutoLets-'))) return 'AutoLet';
    return 'Let';
  })();
  const linkedAgentPhase = (linkedAgent?.phase || '').toLowerCase();
  const isLetCanvas = linkedLetArchetype === 'Let';
  const isLetStructuralLock = isLetCanvas && linkedAgentPhase === 'decide';
  const isLetFullLock = isLetCanvas && linkedAgentPhase === 'act';
  const isCanvasFullyLocked = isAutoLetCegReadOnly || isLetFullLock;
  /* Disable structural mutations whenever any kind of structural-lock fires. */
  const isCanvasStructuralLock = isCanvasFullyLocked || isLetStructuralLock;

  /* The Act-phase canvas lock keys on PHASE, not publication — an agent can sit in Act
   * un-published (Act→Published gate not yet cleared). Copy must not claim it "is published"
   * or tell the operator to "unpublish" something that was never published; the real
   * remediation is to regress. Only mention unpublish when it actually IS published. */
  const linkedAgentPublished = !!linkedAgent?.published;
  const phaseLockTooltip = isLetFullLock
    ? (linkedAgentPublished
        ? `Canvas is locked because ${linkedAgent?.name || 'this agent'} is in Act phase and published. Unpublish, then regress to Decide to make changes.`
        : `Canvas is locked because ${linkedAgent?.name || 'this agent'} is in Act phase. Regress to Decide to make changes.`)
    : isLetStructuralLock
    ? `Canvas is in Decide phase — structural changes (add/remove skills) are blocked. Per-node autonomy + inference are still editable. Regress to Build to add or remove skills.`
    : isAutoLetCegReadOnly
    ? `${linkedAgent?.name || 'This AutoLet'}'s CEG is immutable. Upload a revised runbook to change it.`
    : '';

  // Push selected workflow name to breadcrumb trail
  useEffect(() => {
    if (selectedWorkflow) {
      nav.replaceContext([{ label: selectedWorkflow.name, key: 'workflow' }]);
      // Record in the precise recent-history trail so the operator can jump
      // straight back to this specific workflow via the top-bar "Recent" trail.
      if (selectedWorkflow.id && selectedWorkflow.name) {
        nav.recordEntity({
          type: 'Workflow',
          id: selectedWorkflow.id,
          name: selectedWorkflow.name,
          path: `/canvas?workflowId=${selectedWorkflow.id}`,
        });
      }
    } else {
      nav.clearContext();
    }
  }, [selectedWorkflow?.id, selectedWorkflow?.name]); // eslint-disable-line

  // Right-panel tab auto-switch (Conductor OSS parity, #136):
  //   • select a node/connection → jump to Task tab
  //   • BUT never interrupt a user actively on the Code tab (they may be
  //     mid-paste / mid-edit and clicks on the canvas shouldn't pull them
  //     away from their JSON buffer).
  useEffect(() => {
    if (!canvas.selected) return;
    if (rightPanelTab === 'code') return;
    if (rightPanelTab !== 'task') setRightPanelTab('task');
  }, [canvas.selected, canvas.selectedType]); // eslint-disable-line

  // RC-CanvasV3-PaletteCanvasSync — when a canvas node is selected, point
  // the left palette at it: expand the parent category, highlight the
  // matching row (decays after 1.8 s), scroll the row into view.
  //
  // Non-destructive: only expands (never collapses other categories),
  // skips sentinel/paletteHidden types (START/END/terminate), and
  // respects an explicit collapse — if the operator collapses a
  // category, we don't keep re-expanding it on every click in that
  // category. The "explicit collapse memory" is per-category and resets
  // when the operator selects a node in a DIFFERENT category, since the
  // expectation changes (manual collapse of Tasks doesn't imply
  // suppression of AI/RAG auto-expand).
  const prevSelectedCategoryRef = useRef(null);
  const prevHighlightedNodeRef = useRef(null);
  useEffect(() => {
    if (!canvas.selected) {
      setPaletteHighlightType(null);
      prevHighlightedNodeRef.current = null;
      return undefined;
    }
    const node = (canvas.nodes || []).find((n) => n.id === canvas.selected);
    if (!node) return undefined;
    const def = getNodeTypeDef(node.type);
    // Sentinel nodes have no palette entry (paletteHidden:true) — graceful no-op.
    if (!def || def.paletteHidden) return undefined;
    const categoryId = def.category;
    if (!categoryId) return undefined;

    // RC-PaletteSync-EffectDeps — only re-run the visible side effects
    // (highlight + scroll + expand) when the SELECTED NODE actually
    // changes. canvas.nodes is in the dep array so the effect fires
    // when nodes load after selection (workflow-load race) — but we
    // skip the work after the first run for the same node so editing
    // the selected node's config doesn't re-trigger the highlight
    // flash on every keystroke.
    const selectedNodeKey = `${canvas.selected}|${node.type}`;
    const sameAsPrevious = prevHighlightedNodeRef.current === selectedNodeKey;

    // Category change → reset the explicit-collapse memory.
    if (prevSelectedCategoryRef.current && prevSelectedCategoryRef.current !== categoryId) {
      setPaletteUserCollapsedSince(new Set());
    }
    prevSelectedCategoryRef.current = categoryId;

    if (sameAsPrevious) {
      // Already highlighted this node; nothing to do (avoid re-flashing
      // the ring on every canvas mutation under this selection).
      return undefined;
    }
    prevHighlightedNodeRef.current = selectedNodeKey;

    // Auto-expand unless the operator just collapsed this category.
    if (!paletteUserCollapsedSince.has(categoryId)) {
      setPaletteExpanded((prev) => (prev[categoryId] ? prev : { ...prev, [categoryId]: true }));
    }

    // RC-PaletteSync-PersistentHighlight — highlight stays on until the
    // operator selects a DIFFERENT node (or deselects). The earlier
    // 1.8 s decay timer was operator-rejected — it made the highlight
    // feel transient and the visual link between "this node on canvas"
    // and "this tile in palette" disappeared mid-thought. The
    // prevHighlightedNodeRef gate above ensures we don't re-flash on
    // every canvas re-render under the same selection; the early-
    // return at the top of the effect clears highlight when
    // canvas.selected becomes null.
    //
    // RC-PaletteSync-AliasHighlightMismatch — use the CANONICAL type
    // from the resolved def, not node.type. When the AI emits an
    // alias (e.g. variable_assignment → data_transform via
    // TYPE_ALIASES), node.type is the alias key and the palette row
    // is rendered for the canonical (data_transform). Comparing
    // alias against canonical never matches → no highlight even
    // though the category auto-expand fires. def.type is always
    // the canonical key (NODE_TYPES entries always carry their own
    // canonical type field), so highlight matches the rendered row.
    setPaletteHighlightType(def.type);
    return undefined;
    // canvas.nodes IS in deps (RC-PaletteSync-EffectDeps) so we catch
    // the workflow-load race where canvas.selected is set before
    // canvas.nodes finishes hydrating. Idempotence is handled by
    // prevHighlightedNodeRef above so editing the selected node's
    // config doesn't spam re-highlights.
  }, [canvas.selected, canvas.nodes, paletteUserCollapsedSince]); // eslint-disable-line

  const loadedWorkflowRef = useRef(null);       // tracks workflow ID
  const loadedDefFingerprintRef = useRef(null);  // tracks definition content (detects reconversion)
  /* G237 — snapshot of skill IDs present in the loaded canvas. The
   * reverse-sync diff checker (in onSave) compares the current canvas
   * skillIds against this snapshot to detect adds/removes and reject
   * structural changes that violate the agent's current B-D-A phase. */
  const originalSkillIdsRef = useRef(new Set());
  // Set to a workflowId when its scoped detail read returned 404 (out of the
  // current scope / archived / deleted). Drives the "Not available in your
  // current scope" panel over the canvas instead of a silent blank; reset to
  // null whenever a valid, in-scope workflow loads.
  const [unavailableWorkflowId, setUnavailableWorkflowId] = useState(null);
  useEffect(() => {
    if (!selectedWorkflowId) return;

    // Determine if we have a definition with actual nodes
    const def = selectedWorkflow?.definition;
    const hasNodes = def && Array.isArray(def.nodes) ? def.nodes.length > 0
                   : def?.nodes?.length > 0; // handles JsonNode from backend

    // Build a lightweight fingerprint of the definition to detect reconversion.
    // After backend reconversion, the definition changes (new nodes/connections)
    // even though the workflowId stays the same.
    const defFingerprint = hasNodes
      ? `${selectedWorkflowId}|n=${def.nodes.length}|c=${(def.connections || []).length}|${def.nodes.map(n => n.id).sort().join(',')}`
      : `${selectedWorkflowId}|empty`;

    const alreadyLoaded = loadedWorkflowRef.current === selectedWorkflowId;
    const sameDefinition = loadedDefFingerprintRef.current === defFingerprint;

    // Already loaded this EXACT definition — skip
    // (uses fingerprint, not just ID, so reconverted workflows get reloaded)
    if (alreadyLoaded && hasNodes && sameDefinition) return;

    // Clear execution view state when switching workflows
    if (!alreadyLoaded) {
      designSnapshotRef.current = null;
      setSelectedRunId(null);
      setLeftPanelMode('design');
    }

    if (hasNodes) {
      // Workflow list has the definition with nodes — load it
      setUnavailableWorkflowId(null);
      loadedWorkflowRef.current = selectedWorkflowId;
      loadedDefFingerprintRef.current = defFingerprint;
      console.log(`[CanvasPage] Loading workflow definition (fingerprint: ${defFingerprint})`);
      canvas.loadCanvasData(def, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
      /* G237 — snapshot skill IDs for reverse-sync diff check. Read both
       * `agentSkillId` (new canonical) and legacy `skillId` so existing
       * workflows authored before G239b still participate in diff. */
      const snap = new Set();
      (def.nodes || []).forEach((n) => {
        const sid = n?.config?.agentSkillId || n?.config?.skillId;
        if (sid) snap.add(sid);
      });
      originalSkillIdsRef.current = snap;
    } else if (!hasNodes) {
      // Not present in the (scope-filtered) list store with nodes — resolve the
      // workflow via a single SCOPED detail read. One fetch covers three cases:
      //   • in-store but empty definition (list returned a stale/empty def),
      //   • not yet in the store (list still loading) but IN the current scope,
      //   • NOT accessible in the current scope (another tenant / archived /
      //     deleted) — the backend now 404s (getWorkflow is scope-pinned).
      // 200 → load it; 404 → show the "not available" panel instead of a silent
      // blank and self-heal the Recent trail. This never bypasses scope: the
      // backend's scoped read is the authority.
      let cancelled = false;
      const capturedId = selectedWorkflowId;
      (async () => {
        try {
          const res = await getWorkflow(capturedId);
          // Guard: if user switched workflows while we were fetching, discard result
          if (cancelled || loadedWorkflowRef.current !== null && loadedWorkflowRef.current !== capturedId) return;
          if (res?.ok && res.data?.definition) {
            const freshDef = res.data.definition;
            const freshHasNodes = freshDef.nodes && freshDef.nodes.length > 0;
            setUnavailableWorkflowId(null);
            if (freshHasNodes) {
              loadedWorkflowRef.current = capturedId;
              const freshFp = `${capturedId}|n=${freshDef.nodes.length}|c=${(freshDef.connections || []).length}|${freshDef.nodes.map(n => n.id).sort().join(',')}`;
              loadedDefFingerprintRef.current = freshFp;
              canvas.loadCanvasData(freshDef, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
            } else {
              // Truly empty workflow (in scope) — show empty canvas
              loadedWorkflowRef.current = capturedId;
              loadedDefFingerprintRef.current = `${capturedId}|empty`;
              canvas.loadCanvasData({ nodes: [], connections: [] });
            }
          } else {
            // Not accessible in the current scope (404) or unreadable — surface
            // a clear "unavailable" panel rather than a blank canvas, and drop
            // the stale entry from the Recent trail so it stops resurfacing.
            setUnavailableWorkflowId(capturedId);
            canvas.loadCanvasData({ nodes: [], connections: [] });
            if (res?.status === 404 && nav?.removeRecentEntity) {
              nav.removeRecentEntity({ id: capturedId, path: `/canvas?workflowId=${capturedId}` });
            }
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('Workflow load fetch failed:', err);
            setUnavailableWorkflowId(capturedId);
            canvas.loadCanvasData({ nodes: [], connections: [] });
          }
        }
      })();
      // Cleanup: cancel stale fetch if effect re-runs (user switched workflows)
      return () => { cancelled = true; };
    }
  }, [selectedWorkflowId, selectedWorkflow]);

  // P0-B ported: track unresolved playbook / legacy connector slugs across
  // every workflow load (not just templates). Runtime fallback in the picker
  // already handles selection; this effect drives the yellow banner so the
  // user knows which connectors need installing. Bails when the set hasn't
  // changed to avoid render churn.
  useEffect(() => {
    if (!installedConnectors) return;
    const haveInstalled = Object.keys(installedConnectors).length > 0;
    if (!haveInstalled) return;
    const nodes = canvas.nodes || [];
    if (nodes.length === 0) {
      if (unresolvedConnectorSlugs.length > 0) setUnresolvedConnectorSlugs([]);
      return;
    }
    const unresolved = new Set();
    for (const n of nodes) {
      const cfg = n?.config;
      if (!cfg) continue;
      if (cfg.connectorId) continue;
      const slug = cfg.connector;
      if (typeof slug !== 'string' || !slug.trim()) continue;
      const hit = findInstalledBySlug(slug, installedConnectors);
      if (!hit) unresolved.add(slug.trim().toLowerCase());
    }
    const arr = Array.from(unresolved);
    const same = arr.length === unresolvedConnectorSlugs.length
      && arr.every((s, i) => s === unresolvedConnectorSlugs[i]);
    if (!same) setUnresolvedConnectorSlugs(arr);
  }, [installedConnectors, canvas.nodes, unresolvedConnectorSlugs]);

  useEffect(() => {
    if (selectedWorkflowId) {
      loadWorkflowRuns(selectedWorkflowId);
      // Load RBAC assignments
      getWorkflowAssignments(selectedWorkflowId).then((r) => {
        if (r?.ok) setWorkflowAssignments(r.data || []);
      });
      // Load audit log
      getWorkflowAuditLog(selectedWorkflowId).then((r) => {
        if (r?.ok) setWorkflowAuditLog(r.data || []);
      });
    }
  }, [selectedWorkflowId]);

  // Auto-refresh execution history every 15s when on Execution tab to catch
  // background runs (scheduled, agent-triggered) and update their status
  useEffect(() => {
    if (!selectedWorkflowId || leftPanelMode !== 'execution') return;
    const intervalId = setInterval(() => {
      loadWorkflowRuns(selectedWorkflowId);
    }, 15000);
    return () => clearInterval(intervalId);
  }, [selectedWorkflowId, leftPanelMode, loadWorkflowRuns]);

  // =========================================================================
  // Unsaved Changes Guard
  // =========================================================================

  /**
   * Check for unsaved canvas changes before a destructive action (switching
   * workflows, creating new ones, etc.). Returns a Promise that resolves
   * `true` when it's safe to proceed (no dirty changes, or user chose Save
   * or Discard) and `false` when the user cancelled.
   *
   * RC-20a (task #1) — Replaced the previous `window.confirm()` with a
   * platform-styled modal. The native confirm dialog has three problems on
   * this platform: (1) it's visually jarring against the V2 chrome,
   * (2) browser-native styling can't render the workflow name or surface
   * a "Save & Continue" path — it's binary OK/Cancel only, forcing the
   * user to abandon the action even when they would have happily saved,
   * and (3) some browsers/extensions suppress repeated confirms entirely,
   * silently allowing the destructive action through.
   *
   * The new flow lifts the choice into React state (`unsavedChangesPrompt`)
   * and the modal at the JSX root reads it. The Promise returned here is
   * resolved by whichever modal button the user clicks — Save, Discard,
   * or Cancel — keeping callers' async/await sites unchanged.
   */
  const confirmUnsavedChanges = useCallback(() => {
    if (!canvas.dirty) return Promise.resolve(true);
    return new Promise((resolve) => {
      setUnsavedChangesPrompt({
        onDiscard: () => {
          canvas.clearDirty();
          setUnsavedChangesPrompt(null);
          resolve(true);
        },
        onCancel: () => {
          setUnsavedChangesPrompt(null);
          resolve(false);
        },
        onSave: async () => {
          try {
            await canvas.save();
            setUnsavedChangesPrompt(null);
            resolve(true);
          } catch (e) {
            // Save failed → keep the modal open so the user can pick
            // Discard or Cancel as fallback. Toast the error so they
            // know why Save didn't work.
            addToast(`Save failed: ${e?.message || e}`, 'error');
          }
        },
      });
    });
  }, [canvas, addToast]);

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleCreateWorkflow = async (fromTemplate = null, overrides = null) => {
    if (!canCreateWorkflow) { addToast('Permission denied: workflow.create required', 'error'); return; }
    // Guard: check for unsaved changes on the current workflow
    if (!(await confirmUnsavedChanges())) return;
    // Use overrides (from template auto-populate) or fall back to form state
    const name = overrides?.name || workflowForm.name;
    const description = overrides?.description || workflowForm.description;
    const agentId = overrides?.agentId || workflowForm.agentId;

    if (!name.trim()) {
      addToast('Workflow name required', 'warn');
      return;
    }

    const payload = {
      name: name.trim(),
      description,
      orgId,
      tenantId,
      status: 'draft',
      ...(agentId ? { agentId } : {}),
      createdBy: userId,
      ...(fromTemplate ? { definition: fromTemplate } : {}),
    };

    try {
      const result = await createWorkflow(payload);
      if (result?.ok) {
        addToast(`Workflow "${name}" created`, 'success');

        // ── Seed canvas with Start + End nodes (Conductor OSS-style blank canvas) ──
        loadedWorkflowRef.current = null; // reset so load effect will fire

        const seed = makeFreshSentinelPayload();
        const seedNodes = fromTemplate ? [] : seed.nodes;
        const seedConns = fromTemplate ? [] : seed.connections;
        canvas.loadCanvasData(
          fromTemplate
            ? (typeof fromTemplate === 'object' && fromTemplate.nodes ? fromTemplate : { nodes: [], connections: [] })
            : seed
        );

        // Switch to the new workflow and persist seed data.
        // CRITICAL: Save DIRECTLY to the new workflow ID using saveCanvasApi,
        // NOT canvas.saveData() — because saveData's onSave closure still
        // captures the OLD selectedWorkflowId (React state update is async).
        const newId = result.data?.id;
        setSelectedWorkflowId(newId);
        if (!fromTemplate && seedNodes.length && newId) {
          try {
            await saveCanvasApi(newId, { nodes: seedNodes, connections: seedConns });
          } catch (e) {
            console.warn('Seed save for new workflow failed:', e);
          }
        }
        loadWorkflows(); // refresh so new workflow appears in selector
      } else if (result?.duplicate) {
        addToast(`A workflow named "${name}" already exists — choose a different name`, 'error');
        return; // keep the dialog open
      } else {
        addToast(`Failed: ${result?.error}`, 'error');
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }

    setShowNewWorkflow(false);
    setShowTemplateModal(false);
    setWorkflowForm({ name: '', description: '', agentId: '' });
  };

  const handleAddNode = (nodeType) => {
    const nodeDef = getNodeTypeDef(nodeType);
    if (!nodeDef) {
      addToast('Unknown node type', 'error');
      return;
    }

    // ── Multi-entry guard ─────────────────────────────────────────────
    // A workflow can have at most ONE Start-class entry node. Same set
    // is enforced by every other insertion surface (palette drop, picker,
    // legacy SVG drop). See SINGLE_INSTANCE_ENTRY_TYPES at module top.
    if (SINGLE_INSTANCE_ENTRY_TYPES.has(nodeType)) {
      const existing = (canvas.nodes || []).find((n) =>
        SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
      if (existing) {
        addToast(
          `This workflow already has an entry node ("${existing.label || existing.type}"). `
          + `A workflow can have only one Start. Delete the existing entry first if you want to replace it.`,
          'warn');
        return;
      }
    }

    // W130 — every user-initiated insertion routes through the single
    // addTaskToFlow primitive in useCanvas. This guarantees the new node
    // is reachable from Start (so flatToIR doesn't drop it) regardless
    // of canvas shape: empty / Start-only / Start+End / mid-build.
    const cx = (400 - canvas.pan.x) / canvas.zoom;
    const cy = (300 - canvas.pan.y) / canvas.zoom;
    const result = canvas.addTaskToFlow(nodeType, nodeDef.label, {}, null, { x: cx, y: cy });
    if (result.id) addToast(result.toast, 'success');
  };

  // RC-20e — commandPaletteItems memo deleted alongside V3CommandPalette.
  // Insert/Save/Execute/Switch surfaces remain available via:
  //   • ⌘E global TaskPicker (insert)
  //   • Save / Execute toolbar buttons
  //   • Workflow tab strip across the top (switch)

  // Handle sequential insertion — driven by the new TaskPicker (and the
  // legacy 8-shortcut popup until Phase 6 retires the flag). The picker
  // passes a `ChosenItem` shape (kind + type/recipeId + optional config).
  // The legacy popup just calls this with a bare string nodeType — we
  // accept both shapes at the boundary so neither code path breaks.
  //
  // `insertionTarget` is now a discriminated union:
  //   { kind: 'edge',       connectionId }
  //   { kind: 'branch',     branchEdgeId, switchNodeId, branchLabel }
  //   { kind: 'after-node', nodeId }
  //   { kind: 'palette',    sourceType? }
  // Legacy `string` shape (raw connection id) is preserved for back-compat.
  const handleInsertNodeType = (chosenOrType) => {
    const target = insertionTarget;
    if (!target) return;
    const item = typeof chosenOrType === 'string'
      ? { kind: 'type', type: chosenOrType }
      : (chosenOrType || {});

    // Phase 4: recipe insertion. Routes to canvas.applyRecipe for atomic
    // multi-node splice on edge targets.
    if (item.kind === 'recipe' && item.recipeId) {
      const recipe = getRecipeById(item.recipeId);
      if (!recipe) {
        addToast('Recipe not found', 'error');
        setInsertionTarget(null);
        return;
      }
      // Resolve an edge to splice into:
      //   1. Explicit edge target (legacy / programmatic) — use it.
      //   2. After-node (the canonical insert path now — "+" on a task
      //      card, or ⌘E with a task selected): use the first outgoing
      //      edge of that node. If the node is a true leaf (no outgoing
      //      edges), recipes can't apply because they need an entry AND
      //      exit edge to splice between — fall back to the actionable
      //      hint.
      //   3. Palette mode (⌘E with no selection, or "+ Task" toolbar
      //      with no selection): prefer the edge whose target is END
      //      ("append before END"); else the only edge of a single-edge
      //      graph; else hint.
      let edgeTarget = (typeof target === 'string')
        ? { kind: 'edge', connectionId: target }
        : (target.kind === 'edge' ? target : null);
      if (!edgeTarget && target.kind === 'after-node') {
        const outEdge = (canvas.connections || []).find((c) => c.from === target.nodeId);
        if (outEdge) {
          edgeTarget = { kind: 'edge', connectionId: outEdge.id };
        }
      }
      if (!edgeTarget) {
        const conns = canvas.connections || [];
        const nodes = canvas.nodes || [];
        const endNode = nodes.find((n) => n.type === 'end' || n.type === 'terminate');
        // Edge into END is the canonical "append" point.
        let defaultEdge = endNode
          ? conns.find((c) => c.target === endNode.id || c.to === endNode.id)
          : null;
        // Single-edge graphs: just use the only edge.
        if (!defaultEdge && conns.length === 1) defaultEdge = conns[0];
        if (defaultEdge) {
          edgeTarget = { kind: 'edge', connectionId: defaultEdge.id };
        } else {
          addToast(
            'Recipes need a connection to splice into. Click "+" on a task that has a step after it, or insert a single task first.',
            'info'
          );
          setInsertionTarget(null);
          return;
        }
      }
      const ids = canvas.applyRecipe(edgeTarget, recipe);
      if (!ids) {
        addToast('Could not apply recipe — the target edge may have moved. Try clicking + on the edge again.', 'error');
      } else {
        addToast(`Inserted recipe: ${recipe.name} (${ids.length} nodes)`, 'success');
      }
      setInsertionTarget(null);
      return;
    }

    if (!item.type) { setInsertionTarget(null); return; }

    const nodeDef = getNodeTypeDef(item.type);
    if (!nodeDef) {
      addToast('Unknown node type', 'error');
      setInsertionTarget(null);
      return;
    }
    const label = item.label || nodeDef.label;
    const presetConfig = item.config || {};

    // ── Multi-entry guard ─────────────────────────────────────────────
    // A workflow can have at most ONE entry/trigger node (Start-class).
    // The TaskPicker (⌘E shortcut, "+ Task" toolbar, per-task "+") routes
    // here for every insertion target — palette / edge / branch / after-node.
    // Without this guard, picking "Manual Start" from the picker when one
    // already exists produces a multi-root graph that crashes ELK's
    // flatToIR. Mirror the same refusal toast as the palette-drop path so
    // both insertion surfaces enforce the invariant identically.
    if (SINGLE_INSTANCE_ENTRY_TYPES.has(item.type)) {
      const existing = (canvas.nodes || []).find((n) =>
        SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
      if (existing) {
        addToast(
          `This workflow already has an entry node ("${existing.label || existing.type}"). `
          + `A workflow can have only one Start. Delete the existing entry first if you want to replace it.`,
          'warn');
        setInsertionTarget(null);
        return;
      }
    }

    // W130 — every TaskPicker insertion routes through addTaskToFlow.
    // Translate the discriminated-union `target` shape into the primitive's
    // `anchor` shape; the primitive owns the actual splice/append decision
    // and orphan-prevention. Legacy bare-string target (raw connection id)
    // is normalised to the edge anchor.
    let anchor = null;
    if (typeof target === 'string') {
      anchor = { kind: 'edge', connectionId: target };
    } else if (target.kind === 'edge') {
      anchor = { kind: 'edge', connectionId: target.connectionId };
    } else if (target.kind === 'branch') {
      anchor = { kind: 'branch', branchEdgeId: target.branchEdgeId };
    } else if (target.kind === 'after-node') {
      anchor = { kind: 'after-node', nodeId: target.nodeId };
    } else if (target.kind === 'palette') {
      anchor = null; // primitive resolves to "append before End" / "after Start" / free
    } else {
      // RC-125 — defense in depth. An unknown target.kind should never reach
      // here (TaskPicker only emits the discriminated-union shapes above),
      // but if it ever does, give the operator an actionable message instead
      // of leaking the internal kind string.
      addToast(`Couldn't place that task here. Try adding it from the palette on the left, or click the + on an existing edge.`, 'error');
      setInsertionTarget(null);
      return;
    }

    const result = canvas.addTaskToFlow(item.type, label, presetConfig, anchor, { x: 200, y: 200 });
    if (!result.id) {
      // RC-125 — the addTaskToFlow primitive now falls through stale
      // anchors to its before-End / after-Start / free-placement cascade,
      // so this branch is essentially unreachable. Kept as defense in
      // depth — if it ever fires (e.g. a degenerate canvas with no Start,
      // no End, and an unrenderable position), tell the operator something
      // actionable instead of "anchor missing or invalid".
      addToast(`Couldn't place that task automatically. Add a Start node first, or drag the task from the palette to position it.`, 'error');
      setInsertionTarget(null);
      return;
    }
    addToast(result.toast, 'success');
    setInsertionTarget(null);
  };

  // Execution state polling
  const executionPollRef = useRef(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  // v5.3.1 — Execution source filter: 'all' | 'manual' | 'agentlet'
  const [executionSourceFilter, setExecutionSourceFilter] = useState('all');

  // Poll node execution states during a run
  const pollCountRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 150; // 5 minutes at 2s intervals

  const pollExecutionState = useCallback(async (runId) => {
    try {
      pollCountRef.current += 1;

      // Safety: stop polling after max attempts
      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        addToast('Execution polling timed out — check the Logs tab for details', 'warn');
        setIsExecuting(false);
        setActiveRunId(null);
        loadWorkflowRuns(selectedWorkflowId);
        return true; // stop polling
      }

      // ---- Check run-level status first (catches Temporal OSS start failures) ----
      const runResult = await getWorkflowRun(runId);
      if (runResult?.ok && runResult.data) {
        const runStatus = runResult.data.status;
        if (runStatus === 'failed' || runStatus === 'cancelled') {
          // Workflow ended — fetch ACTUAL per-node statuses from the backend first,
          // then fill in any remaining pending/running nodes with the appropriate fallback status.
          // This preserves "skipped" status for nodes that were never executed.
          try {
            const nodeResult = await getNodeExecutions(runId);
            if (nodeResult?.ok && Array.isArray(nodeResult.data)) {
              const stateMap = {};
              nodeResult.data.forEach((nodeExec) => {
                stateMap[nodeExec.nodeId] = {
                  status: nodeExec.status,
                  output: nodeExec.output,
                  error: nodeExec.error,
                  startedAt: nodeExec.startedAt,
                  completedAt: nodeExec.completedAt,
                  durationMs: nodeExec.durationMs,
                };
              });
              // For any canvas nodes not in the backend response, mark as skipped (never reached)
              canvas.nodes.forEach((n) => {
                if (!stateMap[n.id]) {
                  stateMap[n.id] = {
                    status: runStatus === 'cancelled' ? 'cancelled' : 'skipped',
                    error: runStatus === 'cancelled' ? 'Cancelled by user' : `Skipped: workflow ${runStatus} before reaching this node`,
                  };
                }
              });
              canvas.setExecutionStates(stateMap);
            }
          } catch (nodeErr) {
            // Fallback: mark unfinished nodes as failed/cancelled if we can't fetch node states
            const fallbackStates = {};
            canvas.nodes.forEach((n) => {
              const existing = canvas.executionState.get(n.id);
              if (!existing || existing.status === 'pending' || existing.status === 'running') {
                fallbackStates[n.id] = {
                  status: runStatus === 'cancelled' ? 'cancelled' : 'failed',
                  error: runResult.data.error || `Workflow ${runStatus}`,
                };
              }
            });
            canvas.setExecutionStates(fallbackStates);
          }

          addToast(`Workflow ${runStatus}: ${runResult.data.error || 'Unknown error'}`, 'error');
          setIsExecuting(false);
          setActiveRunId(null);
          loadWorkflowRuns(selectedWorkflowId);
          return true; // done
        }
      }

      // ---- Then check node-level statuses ----
      const result = await getNodeExecutions(runId);
      if (result?.ok && Array.isArray(result.data)) {
        const stateMap = {};
        let allDone = true;
        result.data.forEach((nodeExec) => {
          stateMap[nodeExec.nodeId] = {
            status: nodeExec.status,
            output: nodeExec.output,
            error: nodeExec.error,
            startedAt: nodeExec.startedAt,
            completedAt: nodeExec.completedAt,
            durationMs: nodeExec.durationMs,
          };
          if (nodeExec.status === 'pending' || nodeExec.status === 'running') {
            allDone = false;
          }
        });

        // Update canvas execution states for visual feedback
        canvas.setExecutionStates(stateMap);

        // Check if all nodes are done
        if (allDone && result.data.length > 0) {
          const failedCount = result.data.filter((n) => n.status === 'failed').length;
          if (failedCount > 0) {
            addToast(`Workflow completed with ${failedCount} failed node(s)`, 'warn');
          } else {
            addToast('Workflow execution completed successfully!', 'success');
          }
          setIsExecuting(false);
          setActiveRunId(null);
          loadWorkflowRuns(selectedWorkflowId);
          return true; // done
        }
      }
      return false; // still running
    } catch (err) {
      console.error('Polling error:', err);
      // If the run is not found or API is unreachable, stop polling to avoid
      // infinite "Running..." state. Increment poll count so max-attempts guard
      // will eventually stop it even on repeated transient errors.
      pollCountRef.current += 5; // accelerate toward max-attempts
      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        addToast('Execution polling stopped — workflow run may not exist', 'warn');
        setIsExecuting(false);
        setActiveRunId(null);
        canvas.clearExecutionState();
        loadWorkflowRuns(selectedWorkflowId);
        return true; // stop polling
      }
      return false;
    }
  }, [canvas, selectedWorkflowId, addToast, loadWorkflowRuns]);

  // Start/stop polling effect
  useEffect(() => {
    if (activeRunId && isExecuting) {
      // Poll every 2 seconds
      const poll = async () => {
        const done = await pollExecutionState(activeRunId);
        if (!done && isExecuting) {
          executionPollRef.current = setTimeout(poll, 2000);
        }
      };
      // Initial poll after 1 second
      executionPollRef.current = setTimeout(poll, 1000);

      return () => {
        if (executionPollRef.current) clearTimeout(executionPollRef.current);
      };
    }
  }, [activeRunId, isExecuting, pollExecutionState]);

  // ── Required-input prompt state ──
  // The workflow's trigger node (manual_start / form_trigger / etc.)
  // declares config.inputParameters as a list of names the workflow
  // expects at invocation. If those exist and the user clicked Execute
  // without filling them, we MUST collect values up front — without them
  // the run starts and immediately fails at the first ${workflow.input.X}
  // resolution. The pending-execute pattern: stash the trigger context,
  // open the modal, the modal's Submit calls runExecuteWithInput().
  const [executeInputPrompt, setExecuteInputPrompt] = useState(null);
  // executeInputPrompt shape:
  //   { params: [{name, required, type, description?}, ...],
  //     values: {<name>: "", ...} }
  // Resolved by /api/v1/workflows/{id}/required-inputs which merges canvas
  // trigger inputParameters AND the linked external workflow's input_schema
  // (DiscoveredWorkflow.inputSchema) so GEL Protected wrappers prompt for
  // their wrapped workflow's required fields.

  // Local-only scan of the canvas for trigger node inputParameters. Fast and
  // sufficient for native canvas workflows where the author declared inputs
  // on the start node. Misses GEL Protected wrappers because their canvas
  // trigger has no inputParameters — those workflows need the backend lookup
  // (see resolveRequiredInputs below) which reads the linked
  // DiscoveredWorkflow's input_schema.
  //
  // RC-7 (task #187) — restricted to trigger types whose semantics INCLUDE
  // "operator fills values at Execute click". Real event-driven triggers
  // (webhook, schedule, siem_alert, email_trigger, system_event_trigger)
  // fire from external events (HTTP POST to a path, cron tick, inbound
  // alert, etc.) — the Workflow Inputs modal is semantically wrong for
  // them. Webhook input testing belongs to a separate "send test request"
  // flow that shows the operator the actual invocation contract (curl
  // example + sample body), not a generic input modal. See
  // detectRealTriggerNode() + the test-invocation surface for the
  // trigger-correct path.
  const MANUAL_INPUT_TRIGGER_TYPES = new Set([
    'manual_start', 'manual_trigger', 'start', 'trigger', 'form_trigger',
  ]);
  const REAL_TRIGGER_TYPES = new Set([
    'webhook', 'schedule', 'siem_alert', 'email_trigger', 'system_event_trigger',
  ]);
  const collectRequiredInputParamsLocal = () => {
    const startNode = (canvas.nodes || []).find((n) => n && MANUAL_INPUT_TRIGGER_TYPES.has(n.type));
    if (!startNode) return [];
    const ip = startNode.config?.inputParameters;
    if (!Array.isArray(ip)) return [];
    return ip
      .filter((s) => typeof s === 'string' && s.trim().length > 0)
      .map((name) => ({ name, required: true, type: 'string' }));
  };

  /** Returns the workflow's real-event-driven trigger node when present,
   *  else null. Used by handleExecute to route real-trigger workflows to
   *  the trigger-specific test-invocation prompt rather than the generic
   *  manual-input modal. */
  const detectRealTriggerNode = () => {
    return (canvas.nodes || []).find((n) => n && REAL_TRIGGER_TYPES.has(n.type)) || null;
  };

  // Authoritative resolver — defers to the backend's WorkflowInputSchemaService
  // which merges trigger-node parameters AND external workflow input schemas.
  // Falls back to the local scan if the endpoint isn't available so the canvas
  // keeps working in dev without a fresh backend.
  const resolveRequiredInputs = async () => {
    if (!selectedWorkflowId) return [];
    try {
      const res = await getWorkflowRequiredInputs(selectedWorkflowId);
      if (res?.ok && Array.isArray(res.data?.inputs)) {
        return res.data.inputs;
      }
    } catch (e) {
      console.warn('[Execute] required-inputs lookup failed, falling back to local scan', e);
    }
    return collectRequiredInputParamsLocal();
  };

  const runExecuteWithInput = async (inputValues) => {
    setExecuteInputPrompt(null);
    return doExecute(inputValues);
  };

  const handleExecute = async () => {
    if (!canExecuteWorkflow) { addToast('Permission denied: workflow.execute required', 'error'); return; }
    if (!selectedWorkflowId) {
      addToast('No workflow selected', 'warn');
      return;
    }
    // P1-D ported: re-entrant Execute guard. The button-level `disabled`
    // already prevents duplicate clicks for the common case, but a
    // programmatic invocation (keyboard shortcut, AI Builder, test harness)
    // could still hit this function while a run is in flight. Bail loudly.
    if (isExecuting) {
      addToast('Workflow is already running — wait for it to finish or clear before executing again.', 'warn');
      return;
    }

    // RC-7 (task #187) — Real event-driven triggers (webhook / schedule /
    // siem_alert / email_trigger / system_event_trigger) fire from external
    // events, not from operator Execute clicks. Route them to a trigger-
    // specific test-invocation prompt that shows the operator the actual
    // invocation contract (webhook curl example, schedule "run now",
    // etc.) instead of conflating them with manual_start's input modal.
    const realTrigger = detectRealTriggerNode();
    if (realTrigger) {
      // Open the trigger-test prompt. The component (TriggerTestPrompt)
      // renders different bodies per trigger type:
      //   webhook  → "POST {host}{path} with this sample JSON body. Sample
      //              fields come from config.inputParameters. Or click
      //              'Send test request' to dry-run inline."
      //   schedule → "This workflow fires on cron '{cron}'. Click 'Run now'
      //              to invoke it immediately as a test."
      //   siem_alert / email_trigger / system_event_trigger →
      //              "This workflow fires on inbound {kind} events. Send
      //              one to invoke it; or click 'Run with sample event'."
      // Until that surface lands, surface a clear toast that explains the
      // mismatch and prevents the wrong-modal regression — production-
      // grade explicit-refusal is better than the previous silent
      // conflation with manual_start.
      const triggerKind = realTrigger.type;
      const triggerPath = realTrigger.config?.path;
      const triggerMethod = realTrigger.config?.method || 'POST';
      let hint;
      if (triggerKind === 'webhook') {
        hint = `This workflow is triggered by a webhook (${triggerMethod} ${triggerPath || '<no path configured>'}). `
             + `Send a real HTTP request to invoke it — the Workflow Inputs modal is only for manual_start / form_trigger workflows. `
             + `To make this workflow testable via Execute, swap the trigger to manual_start.`;
      } else if (triggerKind === 'schedule') {
        hint = `This workflow is triggered by a schedule. It fires on its cron tick, not on Execute clicks. `
             + `To make this workflow testable via Execute, swap the trigger to manual_start.`;
      } else {
        hint = `This workflow is triggered by an inbound ${triggerKind} event. It fires on external events, not on Execute clicks. `
             + `To make this workflow testable via Execute, swap the trigger to manual_start.`;
      }
      addToast(hint, 'warn');
      return;
    }

    // Pre-flight: if the workflow declares required inputs, gate Execute
    // behind a small input prompt. The backend resolver handles BOTH native
    // trigger inputParameters AND external workflow input schemas (for GEL
    // Protected wrappers) — without this, GEL workflows whose canvas trigger
    // has no inputParameters would skip the prompt and fail Conductor schema
    // validation with "$.URL is missing but it is required".
    const required = await resolveRequiredInputs();
    if (required.length > 0) {
      const seed = {};
      required.forEach((p) => { seed[p.name] = ''; });
      setExecuteInputPrompt({ params: required, values: seed });
      return; // doExecute runs from the modal's Submit
    }
    return doExecute(null);
  };

  const doExecute = async (providedInput) => {
    try {
      // Execute the workflow exactly as authored. Governance node injection
      // is now an explicit operator action via the Protect toolbar button —
      // we no longer auto-insert on Execute. This puts the workflow author
      // in full control of WHAT runs (their nodes only) and lets the
      // platform admin / workflow owner decide WHEN to add governance via
      // the Protect button. Removing auto-insertion-on-Execute also stops
      // the silent canvas mutation that previously surprised authors who
      // saved an unprotected workflow and saw it grow new nodes after the
      // first run.
      await canvas.save();

      // Clear previous execution state
      canvas.clearExecutionState();
      pollCountRef.current = 0;
      setIsExecuting(true);

      // Initialize all nodes as "pending"
      const pendingStates = {};
      (canvas.nodes || []).forEach((n) => {
        pendingStates[n.id] = { status: 'pending' };
      });
      canvas.setExecutionStates(pendingStates);

      const result = await executeWorkflowApi(selectedWorkflowId, {
        userId,
        ...(providedInput && Object.keys(providedInput).length > 0 ? { input: providedInput } : {}),
      });
      if (result?.ok) {
        const runData = result.data;
        const runId = runData?.id;
        const runStatus = runData?.status;

        if (runStatus === 'pending_approval') {
          // Workflow is gated behind human approval
          addToast(
            'Human approval required — workflow is held until approved. Check the Approval Center.',
            'info'
          );
          canvas.clearExecutionState();
          setIsExecuting(false);
        } else {
          addToast(`Workflow run started (${runId?.substring(0, 8)}...)`, 'success');
        }
        setActiveRunId(runId);
        loadWorkflowRuns(selectedWorkflowId);
      } else {
        addToast(`Execute failed: ${result?.error}`, 'error');
        setIsExecuting(false);
        canvas.clearExecutionState();
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
      setIsExecuting(false);
      canvas.clearExecutionState();
    }
  };

  const [enforcementReport, setEnforcementReport] = useState(null);
  const [showEnforcementReport, setShowEnforcementReport] = useState(false);

  // ── Governance Protection ──────────────────────────────────────
  // Check whether the current canvas already has enforcement nodes. Three
  // signals count as "protected" — kept in sync with stripEnforcementNodes
  // so the toggle and the strip operation always agree:
  //   - frontend auto-inserted (`_autoInserted: true`) — what the Protect
  //     button itself adds (DPG_INTERNODE scans)
  //   - platform enforcement node TYPES (`isPlatformEnforcementNode`) —
  //     dpg_scan / policy_enforcement_gate / token_vault / etc.
  //   - GEL-baked scaffolding (`config.gelPhase` set, excluding the
  //     external-execution node itself which is the workflow's purpose,
  //     not enforcement)
  // User-authored switches/audit_records/approval_gates without gelPhase
  // are NOT considered platform enforcement.
  const isWorkflowProtected = useCallback(() => {
    const nodes = canvas.nodes || [];
    const PRESERVED_GEL_PHASES = new Set(['EXECUTE_EXTERNAL', 'INLINE_EXTERNAL_TASK']);
    return nodes.some((n) => {
      if (isAutoInsertedNode(n) || isPlatformEnforcementNode(n)) return true;
      const phase = n && n.config && typeof n.config.gelPhase === 'string'
        ? n.config.gelPhase
        : null;
      return phase != null && !PRESERVED_GEL_PHASES.has(phase);
    });
  }, [canvas.nodes]);

  // P1-A ported: confirmation gate for auto-protect injections.
  //
  // Wraps `autoInsertEnforcementNodes` so every code path that mutates the
  // canvas with governance nodes routes through one user-visible diff modal.
  // No-ops short-circuit immediately. The promise NEVER rejects — Cancel
  // resolves with confirmed:false so call sites stay non-throwing.
  const requestAutoProtect = useCallback(
    (canvasData, opts = {}) => new Promise((resolve) => {
      if (!canvasData || !Array.isArray(canvasData.nodes) || canvasData.nodes.length === 0) {
        resolve({ canvasData: canvasData || { nodes: [], connections: [] }, confirmed: true, governed: null });
        return;
      }
      let governed;
      try { governed = autoInsertEnforcementNodes(canvasData); }
      catch (err) {
        console.error('[requestAutoProtect] enforcement injection failed', err);
        addToast(`Could not analyse governance requirements: ${err.message || 'unknown error'}`, 'error');
        resolve({ canvasData, confirmed: true, governed: null });
        return;
      }
      if (governed.alreadyGoverned || !governed.insertionReport || governed.insertionReport.length === 0) {
        resolve({ canvasData, confirmed: true, governed });
        return;
      }
      setPendingEnforcement({
        original: canvasData,
        governed,
        reason: opts.reason || 'protect',
        contextLabel: opts.contextLabel || '',
        // V328 — onConfirm accepts the operator-selected data-protection
        // default action (REDACT/TOKENIZE/MASK/BLOCK) captured from the
        // slideout's radio group at click time. Threaded back to
        // handleProtectWorkflow for the protection_policy PUT.
        onConfirm: (defaultAction) => {
          setPendingEnforcement(null);
          // RC-CanvasV3-ProtectManifest — embed the manifest in the
          // canvas data so Unprotect can replay it exactly. The
          // manifest survives BE round-trip because Workflow.definition
          // is JSONB; underscore-prefixed keys (none used here) and
          // unknown top-level keys are preserved.
          resolve({
            canvasData: {
              nodes: governed.nodes,
              connections: governed.connections,
              protectManifest: governed.protectManifest || null,
            },
            confirmed: true,
            governed,
            defaultAction: defaultAction || 'MASK',
          });
        },
        onCancel: () => {
          setPendingEnforcement(null);
          resolve({ canvasData, confirmed: false, governed });
        },
      });
    }),
    [addToast]
  );

  // "Protect Workflow" — toggles governance protection on the canvas. This is
  // the SINGLE source of truth for whether enforcement nodes (DPG scans, token
  // vault, policy gates, audit recorders) appear in the workflow. Execute no
  // longer auto-inserts; templates load as-authored. Click once to add, click
  // again to strip.
  //
  //   Not protected → click → enforcement nodes inserted around every regular
  //                             node, canvas re-laid-out, workflow saved.
  //   Protected     → click → all auto-inserted / platform-enforcement nodes
  //                             removed, surviving edges re-stitched
  //                             (upstream→downstream around each removed
  //                             enforcement node), canvas re-laid-out, saved.
  //
  // The button's RED → GREEN/'✓ Protected' visual transition (rendered below
  // in the toolbar) reflects state from {@link #isWorkflowProtected}, so the
  // toggle is fully reactive to canvas mutations from any source (manual node
  // delete, undo, template load, etc.).
  const handleProtectWorkflow = useCallback(async () => {
    const currentNodes = canvas.nodes || [];
    if (currentNodes.length === 0) {
      addToast('Add nodes to the canvas first before protecting', 'warn');
      return;
    }
    // RC-CanvasV3-ProtectManifest — thread the persisted protectManifest
    // (if any) into the strip input. Without this, stripEnforcementNodes
    // can't see the manifest and falls back to the legacy fuzzy strip
    // that the operator reported as destructive. The manifest lives on
    // canvas state itself (set when Protect ran in this session) AND
    // potentially in the loaded workflow definition (set when Protect
    // ran in a prior session and was saved). Prefer the live canvas
    // state since it's authoritative for the current edit.
    const currentCanvas = {
      nodes: currentNodes,
      connections: canvas.connections || [],
      protectManifest:
        canvas.protectManifest
        || (canvas.definition && canvas.definition.protectManifest)
        || null,
    };

    if (isWorkflowProtected()) {
      // ── Toggle OFF — exact-replay via manifest when available,
      // conservative fallback when not. See stripEnforcementNodes
      // for the dispatch. ──
      const stripped = stripEnforcementNodes(currentCanvas);
      if (stripped.removedNodeCount === 0) {
        addToast('No enforcement nodes detected to remove', 'info');
        return;
      }
      // RC-CanvasV3-ProtectManifest — explicitly clear the manifest on
      // unprotect so a future Protect starts fresh. Without this,
      // canvas state would keep a stale manifest pointing at nodes
      // that no longer exist.
      const cleanedData = {
        nodes: stripped.nodes,
        connections: stripped.connections,
        protectManifest: null,
      };
      canvas.loadCanvasData(cleanedData, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
      setShowEnforcementReport(false);
      setEnforcementReport(null);
      addToast(
        `Protection removed — ${stripped.removedNodeCount} enforcement node${stripped.removedNodeCount !== 1 ? 's' : ''} stripped`,
        'success',
      );
      if (selectedWorkflowId) {
        // V328 — clear the workflow's protection_policy so the DPG stops
        // applying the operator-selected default action on this workflow's
        // form submissions / canvas outputs. Best-effort: if this fails,
        // the canvas save still proceeds (governance nodes are already
        // stripped from the canvas payload, so the visible protection
        // state matches the operator's Unprotect intent). afterCommit
        // hook in WorkflowService.updateWorkflow will invalidate the
        // 120s policy cache on success.
        try {
          await updateWorkflow(selectedWorkflowId, { protectionPolicy: null });
        } catch (e) {
          console.warn('[Unprotect] clearing protection_policy failed — canvas save will still proceed', e);
          addToast('Governance nodes stripped, but clearing the data-protection policy failed. '
                 + 'Retry Unprotect to fully release protection.', 'warn', 7000);
        }
        canvas.saveData(cleanedData);
      }
      return;
    }

    // ── Toggle ON — confirm via P1-A diff modal before mutating ──
    const { canvasData, confirmed, governed, defaultAction } = await requestAutoProtect(currentCanvas, {
      reason: 'protect',
      contextLabel: 'Protect Workflow',
    });
    if (!confirmed) {
      addToast('Protect cancelled — workflow was not modified.', 'info');
      return;
    }
    if (!governed || governed.alreadyGoverned || governed.insertionReport.length === 0) {
      addToast('Nothing to protect — no eligible nodes detected', 'info');
      return;
    }
    // V328 (2026-07-07) — save the data-protection policy BEFORE mutating
    // the canvas. Rationale: fail-fast atomicity. If the policy PUT fails
    // (RBAC / network / server 500), we abort with no visible change to
    // the canvas so operator sees a coherent "not protected" state instead
    // of a half-protected workflow (governance nodes without a policy).
    // The policy payload is tiny; the canvas payload is heavy — do the
    // cheap fail-fast check first. WorkflowService.updateWorkflow's
    // afterCommit hook invalidates the 120s policy cache on success.
    if (selectedWorkflowId) {
      try {
        const policyRes = await updateWorkflow(selectedWorkflowId, {
          protectionPolicy: {
            defaultAction: defaultAction || 'MASK',
            scope: 'workflow',
            // Encryption is always-on when Protect is enabled (operator
            // decision — see WORKFLOW_PROTECT_ENCRYPTION_AND_MASKING.md §8.1).
            // Stamped here so the persisted policy is self-describing for
            // future audits / policy inspectors.
            encryption: 'P7E1',
            version: 1,
          },
        });
        if (policyRes && policyRes.ok === false) {
          const errMsg = policyRes.error || policyRes.status || 'save failed';
          addToast(`Could not save data-protection policy: ${errMsg}. Workflow was NOT protected.`, 'error');
          return;
        }
      } catch (e) {
        console.error('[Protect] protection_policy save failed', e);
        addToast(`Could not save data-protection policy: ${e.message || 'unknown error'}. Workflow was NOT protected.`, 'error');
        return;
      }
    }
    canvas.loadCanvasData(canvasData, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
    setEnforcementReport(governed);
    setShowEnforcementReport(true);
    addToast(
      `Governance enabled: ${governed.stats.enforcementNodesAdded} enforcement nodes inserted · Default action: ${defaultAction || 'MASK'}`,
      'success',
    );
    if (selectedWorkflowId) {
      canvas.saveData(canvasData);
    }
  }, [canvas, isWorkflowProtected, selectedWorkflowId, addToast, requestAutoProtect, updateWorkflow]);

  // ── Demo Mode State ─────────────────────────────────────────
  const [demoMode, setDemoMode] = useState(false);
  const [enforcementEvents, setEnforcementEvents] = useState([]);
  const [complianceEvents, setComplianceEvents] = useState([]);
  const [demoTokens, setDemoTokens] = useState([]);
  const [demoCertificate, setDemoCertificate] = useState(null);
  const [demoExecutionDuration, setDemoExecutionDuration] = useState(null);
  const [lastDemoRunId, setLastDemoRunId] = useState(null);
  const demoExecutionStartRef = useRef(null);
  const workflowWsRef = useRef(null);

  // Connect workflow WebSocket when executing in demo mode
  useEffect(() => {
    if (demoMode && activeRunId && isExecuting) {
      demoExecutionStartRef.current = Date.now();
      // Clear previous events
      setEnforcementEvents([]);
      setComplianceEvents([]);
      setDemoTokens([]);

      workflowWsRef.current = connectWorkflowWebSocket(activeRunId, {
        onNodeUpdate: (data) => {
          // Feed real-time node updates to the canvas execution state
          if (data.nodeId && data.status) {
            canvas.setExecutionStates({ [data.nodeId]: {
              status: data.status,
              output: data.output,
              error: data.error,
              startedAt: data.startedAt,
              completedAt: data.completedAt,
              durationMs: data.durationMs,
            }});
          }
        },
        onEnforcementEvent: (data) => {
          setEnforcementEvents(prev => [...prev, data]);
          // Extract tokens from tokenization events
          if (data.type === 'TOKENIZATION_EVENT' && data.token) {
            setDemoTokens(prev => [...prev, data.token]);
          }
        },
        onComplianceEvent: (data) => {
          setComplianceEvents(prev => [...prev, data]);
        },
        onWorkflowComplete: (data) => {
          setDemoExecutionDuration(Date.now() - (demoExecutionStartRef.current || Date.now()));
          setIsExecuting(false);
          // Keep activeRunId so certificate generation can use it
          setLastDemoRunId(activeRunId);
          addToast('Workflow execution completed!', 'success');
          if (selectedWorkflowId) loadWorkflowRuns(selectedWorkflowId);
        },
      });

      return () => {
        if (workflowWsRef.current) {
          workflowWsRef.current.disconnect();
          workflowWsRef.current = null;
        }
      };
    }
  }, [demoMode, activeRunId, isExecuting]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      disconnectWorkflowWebSocket();
    };
  }, []);

  const handleGenerateDemoCertificate = async () => {
    const runId = activeRunId || lastDemoRunId;
    if (!runId) {
      addToast('No workflow run available for certificate generation', 'warn');
      return;
    }
    try {
      const result = await generateSoc2Certificate(runId);
      if (result?.ok && result.data) {
        setDemoCertificate(result.data);
        addToast('SOC 2 compliance certificate generated', 'success');
      } else {
        addToast(`Certificate generation failed: ${result?.error || 'Unknown'}`, 'error');
      }
    } catch (err) {
      addToast(`Certificate error: ${err.message}`, 'error');
    }
  };

  // Handle simulated demo execution (fires scripted WebSocket events)
  const handleDemoSimulateExecution = async () => {
    // Use a synthetic run ID for the demo simulation
    const demoRunId = crypto.randomUUID ? crypto.randomUUID() : 'demo-' + Date.now();
    setActiveRunId(demoRunId);
    setIsExecuting(true);
    demoExecutionStartRef.current = Date.now();

    // Clear previous events
    setEnforcementEvents([]);
    setComplianceEvents([]);
    setDemoTokens([]);

    // Connect WebSocket for this demo run
    workflowWsRef.current = connectWorkflowWebSocket(demoRunId, {
      onNodeUpdate: (data) => {
        if (data.nodeId && data.status) {
          canvas.setExecutionStates({ [data.nodeId]: {
            status: data.status,
            output: data.output,
            error: data.error,
            durationMs: data.durationMs,
          }});
        }
      },
      onEnforcementEvent: (data) => {
        setEnforcementEvents(prev => [...prev, data]);
        if (data.type === 'TOKENIZATION_EVENT') {
          setDemoTokens(prev => [...prev, {
            tokenId: data.tokenId,
            fieldName: data.fieldName,
            dataType: data.dataType,
            encryptionAlgorithm: data.encryptionAlgorithm,
            keyProvider: data.keyProvider,
            kmsWrapped: data.kmsWrapped,
            createdAt: data.timestamp,
          }]);
        }
      },
      onComplianceEvent: (data) => {
        setComplianceEvents(prev => [...prev, data]);
      },
      onWorkflowComplete: (data) => {
        setDemoExecutionDuration(Date.now() - (demoExecutionStartRef.current || Date.now()));
        setLastDemoRunId(demoRunId);
        setIsExecuting(false);
        addToast('Demo workflow execution completed!', 'success');
      },
    });

    // Fire the backend simulation
    try {
      await simulateDemoFullSequence(demoRunId);
      addToast('Demo simulation started — watch the canvas!', 'success');
    } catch (err) {
      addToast(`Demo simulation failed: ${err.message}`, 'error');
      setIsExecuting(false);
      setActiveRunId(null);
    }
  };

  const handleLoadTemplate = (playbook) => {
    setShowTemplateModal(false);
    // RC-111a — also close the New Workflow slideout and reset its view
    // back to 'form' so the next open lands on the blank-author form,
    // not on the template browser. No-op when called from outside the
    // slideout (legacy Modal callers); zero regression.
    setShowNewWorkflow(false);
    setNewWorkflowView('form');
    // Templates load AS-AUTHORED. Governance node insertion is now a single
    // explicit action via the Protect toolbar button — this is consistent
    // with Execute (which also no longer auto-protects). The author/operator
    // sees the playbook's intent first and chooses to apply protection on
    // their schedule. After load, the Protect button glows RED to signal
    // "click me to add governance."
    const rawTemplate = { nodes: playbook.nodes || [], connections: playbook.connections || [] };

    // P0-B ported: resolve playbook connector slugs to installed UUIDs so
    // the Config picker pre-selects the right connector. Without this, the
    // playbook's `config.connector: 'email-security'` slug never maps to a
    // tenant connector UUID and the Config tab silently shows "No
    // configuration available." Surface unresolved slugs as a yellow banner.
    const resolved = resolveWorkflowConnectorRefs(rawTemplate, installedConnectors);
    setUnresolvedConnectorSlugs(resolved.unresolvedSlugs.length > 0 ? resolved.unresolvedSlugs : []);
    const templateData = { nodes: resolved.nodes, connections: resolved.connections };

    addToast(
      'Template loaded — click Protect to add governance nodes (DPG scans, policy gates, audit trail).',
      'info',
    );

    // Pass template definition AND name/description directly — no reliance on async state
    handleCreateWorkflow(templateData, { name: playbook.name, description: playbook.description || '' });
    // Load into canvas WITH auto-layout and zoom-to-fit computed inline (avoids stale-closure timing issues)
    canvas.loadCanvasData(templateData, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
  };

  const canDeleteWorkflow = selectedWorkflowId && workflowRuns.length === 0;

  const handleDeleteWorkflow = async (workflowIdOverride) => {
    if (!hasDeletePerm) { addToast('Permission denied: workflow.delete required', 'error'); return; }
    // Guard: onClick passes a SyntheticEvent — ignore non-string args
    const raw = (typeof workflowIdOverride === 'string') ? workflowIdOverride : selectedWorkflowId;
    if (!raw) return;
    const targetId = raw;
    if (!targetId) return;

    // For the currently selected workflow, we know the run count from state
    if (targetId === selectedWorkflowId && workflowRuns.length > 0) {
      addToast('Cannot delete a workflow that has been executed. Archive it instead.', 'warn');
      setShowDeleteConfirm(false);
      return;
    }

    try {
      const result = await deleteWorkflow(targetId);
      if (result?.ok) {
        addToast('Workflow deleted', 'success');
        if (targetId === selectedWorkflowId) {
          setSelectedWorkflowId(null);
          // Reset to a fresh sentinel-only canvas — Start → End — instead
          // of dumping the user into an empty `{nodes:[], connections:[]}`
          // orphan with a dangling "Select a workflow…" dropdown. The
          // canvas is now immediately usable for hand-building a new
          // workflow without first clicking "+ New Workflow".
          loadedWorkflowRef.current = null;
          canvas.loadCanvasData(makeFreshSentinelPayload());
        }
        // Reload workflow list (V132 — archived:'all' so the archived
        // tab on the list view stays fresh after a delete)
        loadWorkflows({ orgId, tenantId, archived: 'all' });
      } else {
        addToast(`Delete failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      addToast(`Delete error: ${err.message}`, 'error');
    }
    setShowDeleteConfirm(false);
  };

  // =========================================================================
  // Archive / Unarchive
  // =========================================================================

  const handleArchiveWorkflow = async (workflowIdOverride) => {
    const hasEditPerm = hasPermission('workflow.edit');
    if (!hasEditPerm) { addToast('Permission denied: workflow.edit required', 'error'); return; }
    const targetId = (typeof workflowIdOverride === 'string') ? workflowIdOverride : selectedWorkflowId;
    if (!targetId) return;
    try {
      const result = await archiveWorkflow(targetId);
      if (result?.ok) {
        addToast('Workflow archived', 'success');
        // V132: backend snapshots previousStatus so unarchive can restore it.
        // Mirror the server state locally.
        const prevStatus = result.data?.previousStatus ?? null;
        updateWorkflow(targetId, { status: 'archived', previousStatus: prevStatus });
        if (targetId === selectedWorkflowId) {
          setSelectedWorkflowId(null);
          canvas.loadCanvasData({ nodes: [], connections: [] });
        }
        // Refresh list so the archived row moves to the Archived partition
        // when the user toggles the view.
        if (orgId && tenantId) {
          loadWorkflows({ orgId, tenantId, archived: 'all' });
        }
      } else {
        addToast(`Archive failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      addToast(`Archive error: ${err.message}`, 'error');
    }
  };

  const handleUnarchiveWorkflow = async (workflowIdOverride) => {
    const hasEditPerm = hasPermission('workflow.edit');
    if (!hasEditPerm) { addToast('Permission denied: workflow.edit required', 'error'); return; }
    const targetId = (typeof workflowIdOverride === 'string') ? workflowIdOverride : selectedWorkflowId;
    if (!targetId) return;
    try {
      const result = await unarchiveWorkflow(targetId);
      if (result?.ok) {
        addToast('Workflow restored from archive', 'success');
        // V132: backend restores to previous_status (draft/paused/active/…),
        // not always 'paused'. Use server-returned status.
        const restoredStatus = result.data?.status || 'paused';
        updateWorkflow(targetId, { status: restoredStatus, previousStatus: null });
        // Refresh the list so Archived/Active partitions are accurate.
        if (orgId && tenantId) {
          loadWorkflows({ orgId, tenantId, archived: 'all' });
        }
      } else {
        addToast(`Unarchive failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      addToast(`Unarchive error: ${err.message}`, 'error');
    }
  };

  // =========================================================================
  // Execution Controls
  // =========================================================================

  const handleRetryFromFailed = async (runId) => {
    if (!canExecuteWorkflow) { addToast('Permission denied: workflow.execute required', 'error'); return; }
    try {
      const result = await retryFromFailedStep(runId, { userId });
      if (result?.ok) {
        addToast('Retry run started', 'success');
        setActiveRunId(result.data?.id);
        setIsExecuting(true);
        loadWorkflowRuns(selectedWorkflowId);
      } else {
        addToast(`Retry failed: ${result?.error}`, 'error');
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
  };

  /* RC-120b — Replay-with-stored-inputs handler. Fires the BE /replay
   * endpoint which re-runs the workflow from the START using the
   * stored input envelope of the prior run. Distinct from retry which
   * resumes from the failed step. Used by FailureDeepDiveBanner's
   * "Replay with same inputs" button.
   *
   * Busy-state tracks the originating runId (not a global boolean) so
   * the button correctly disables ONLY on its own row — if a future
   * UI shows multiple deep-dive banners simultaneously, each one
   * tracks its own busy state. */
  const [replayBusyRunId, setReplayBusyRunId] = useState(null);
  const handleReplayRun = useCallback(async (runId) => {
    if (!runId) return;
    if (!canExecuteWorkflow) {
      addToast('You do not have permission to execute this workflow.', 'error');
      return;
    }
    setReplayBusyRunId(runId);
    try {
      const result = await replayWorkflowRun(runId, { userId });
      if (result?.ok) {
        const newRunId = result.data?.id;
        addToast('Replay started with the same inputs', 'success');
        if (newRunId) {
          setActiveRunId(newRunId);
          setIsExecuting(true);
        }
        loadWorkflowRuns(selectedWorkflowId);
      } else {
        addToast(`Could not replay this run: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      addToast(`Could not replay this run: ${err?.message || err}`, 'error');
    } finally {
      setReplayBusyRunId(null);
    }
  }, [canExecuteWorkflow, userId, addToast, loadWorkflowRuns, selectedWorkflowId]);

  const handleResumeRun = async (runId) => {
    if (!canExecuteWorkflow) { addToast('Permission denied: workflow.execute required', 'error'); return; }
    try {
      const result = await resumeWorkflowRun(runId, { userId });
      if (result?.ok) {
        addToast('Run resumed', 'success');
        setActiveRunId(runId);
        setIsExecuting(true);
        loadWorkflowRuns(selectedWorkflowId);
      } else {
        addToast(`Resume failed: ${result?.error}`, 'error');
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleTerminateRun = async (runId) => {
    if (!canCancelWorkflow) { addToast('Permission denied: workflow.cancel required', 'error'); return; }
    try {
      const result = await terminateWorkflowRun(runId);
      if (result?.ok) {
        addToast('Run terminated', 'success');
      } else {
        // Terminate failed (e.g., run not found) — still reset UI state
        // so the user isn't stuck in "Running..." indefinitely
        addToast(`Terminate failed: ${result?.error}`, 'error');
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
    // Always reset execution state — whether terminate succeeded or not,
    // the user clicked "terminate" and expects the UI to stop showing "Running..."
    setIsExecuting(false);
    setActiveRunId(null);
    canvas.clearExecutionState();
    loadWorkflowRuns(selectedWorkflowId);

    // If this is a scheduled workflow, also disable the schedule so the backend
    // stops firing new executions. Without this, the scheduler keeps creating
    // new runs every cron interval even though the user terminated the current one.
    if (selectedWorkflow?.triggerType === 'schedule' && selectedWorkflow?.status === 'active') {
      try {
        await updateWorkflow(selectedWorkflowId, { status: 'paused' });
        try { await disableSchedule(selectedWorkflowId); } catch (_) {}
        addToast('Schedule also disabled to prevent new runs', 'info');
      } catch (schedErr) {
        addToast('Run terminated, but failed to disable schedule — use "Disable Schedule" button', 'warning');
      }
    }
  };

  const handleStopRun = async (runId) => {
    try {
      const result = await cancelWorkflowRun(runId);
      if (result?.ok) {
        addToast('Run stopped', 'success');
        setIsExecuting(false);
        setActiveRunId(null);
        loadWorkflowRuns(selectedWorkflowId);
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Schedule Enable/Disable ──────────────────────────────────────────
  const handleDisableSchedule = async () => {
    if (!selectedWorkflowId) return;
    try {
      // 1. Update workflow status to "paused" via PlatformContext — this updates
      //    the local workflows array immediately so the UI re-renders.
      const updateResult = await updateWorkflow(selectedWorkflowId, { status: 'paused' });
      if (!updateResult?.ok) {
        addToast(`Failed to pause workflow: ${updateResult?.error || 'Unknown error'}`, 'error');
        return;
      }

      // 2. Call the dedicated disable endpoint to also clear the scheduler's
      //    in-memory cache and cancel any currently-running executions.
      //    This is best-effort — even if it fails, the status change above
      //    is enough to stop the scheduler from firing new runs.
      try {
        await disableSchedule(selectedWorkflowId);
      } catch (schedErr) {
        console.warn('Schedule disable endpoint failed (non-critical):', schedErr);
      }

      addToast('Schedule disabled — workflow paused', 'success');
      setIsExecuting(false);
      setActiveRunId(null);
      canvas.clearExecutionState();
      loadWorkflowRuns(selectedWorkflowId);
    } catch (err) {
      addToast(`Error disabling schedule: ${err.message}`, 'error');
    }
  };

  const handleEnableSchedule = async () => {
    if (!selectedWorkflowId) return;
    try {
      // 1. Update workflow status to "active" via PlatformContext — updates local state.
      const updateResult = await updateWorkflow(selectedWorkflowId, { status: 'active' });
      if (!updateResult?.ok) {
        addToast(`Failed to activate workflow: ${updateResult?.error || 'Unknown error'}`, 'error');
        return;
      }

      // 2. Call enable endpoint to clear scheduler's lastFiredAt cache (best-effort).
      try {
        await enableSchedule(selectedWorkflowId);
      } catch (schedErr) {
        console.warn('Schedule enable endpoint failed (non-critical):', schedErr);
      }

      addToast('Schedule enabled — workflow active', 'success');
    } catch (err) {
      addToast(`Error enabling schedule: ${err.message}`, 'error');
    }
  };

  // ── Activate / Pause (lifecycle) ──────────────────────────────────────
  //
  // Generic Draft → Active promotion (and the inverse). Distinct from
  // {handleEnableSchedule} / {handleDisableSchedule}: those are
  // schedule-trigger specific and additionally clear the scheduler's
  // lastFiredAt cache. These two handlers work for ANY trigger type
  // (manual, webhook, alert, schedule) and only flip the status field
  // — they do not touch scheduler state. For a schedule-triggered
  // workflow the side-panel still surfaces the schedule-aware controls;
  // the header buttons remain the universal lifecycle toggle.
  //
  // Why separate handlers and not just "edit status in the URL bar":
  //   • RBAC + workflow-access checks live in the PUT /workflows/{id}
  //     handler — calling updateWorkflow goes through the same gate.
  //   • Optimistic local update via PlatformContext.updateWorkflow
  //     keeps the badge in sync immediately, no manual reload needed.
  //   • A single confirm() prompt prevents accidental Pause on a
  //     production workflow that's actively executing.
  const handleActivateWorkflow = async () => {
    if (!selectedWorkflowId || !selectedWorkflow) return;
    const isResume = selectedWorkflow.status === 'paused';
    try {
      const updateResult = await updateWorkflow(selectedWorkflowId, { status: 'active' });
      if (!updateResult?.ok) {
        addToast(`Failed to activate: ${updateResult?.error || 'Unknown error'}`, 'error');
        return;
      }
      // For schedule-triggered workflows, also clear the scheduler's
      // lastFiredAt cache so future runs evaluate from scratch — same
      // best-effort pattern as handleEnableSchedule.
      if (selectedWorkflow.triggerType === 'schedule') {
        try { await enableSchedule(selectedWorkflowId); }
        catch (e) { console.warn('Schedule enable best-effort failed:', e); }
      }
      addToast(isResume ? 'Workflow resumed — status active' : 'Workflow promoted — status active', 'success');
    } catch (err) {
      addToast(`Error activating workflow: ${err.message}`, 'error');
    }
  };

  const handlePauseWorkflow = async () => {
    if (!selectedWorkflowId || !selectedWorkflow) return;
    if (!window.confirm(
      `Pause "${selectedWorkflow.name}"?\n\n` +
      `• New executions will be blocked.\n` +
      `• Currently running executions are NOT cancelled — use Terminate for that.\n` +
      `• Reactivate any time from this same button.`
    )) return;
    try {
      const updateResult = await updateWorkflow(selectedWorkflowId, { status: 'paused' });
      if (!updateResult?.ok) {
        addToast(`Failed to pause: ${updateResult?.error || 'Unknown error'}`, 'error');
        return;
      }
      // For schedule-triggered workflows, also explicitly disable scheduling
      // so the scheduler stops firing new runs at cron interval.
      if (selectedWorkflow.triggerType === 'schedule') {
        try { await disableSchedule(selectedWorkflowId); }
        catch (e) { console.warn('Schedule disable best-effort failed:', e); }
      }
      addToast('Workflow paused', 'success');
    } catch (err) {
      addToast(`Error pausing workflow: ${err.message}`, 'error');
    }
  };

  const handleSelectRun = async (runId) => {
    setSelectedRunId(runId);
    setLeftPanelMode('execution');

    // Preserve the current design-mode nodes & connections so node edits
    // in design view don't bleed into (or get lost during) execution viewing
    if (!designSnapshotRef.current) {
      designSnapshotRef.current = {
        nodes: JSON.parse(JSON.stringify(canvas.nodes)),
        connections: JSON.parse(JSON.stringify(canvas.connections)),
      };
    }

    // Fetch the frozen DAG snapshot for this run (falls back to live definition for pre-v6.0.1 runs)
    // IMPORTANT: Use setNodes/setConnections directly — do NOT run loadCanvasData here,
    // because that runs validateAndRepairDAG which can drop connections from the raw snapshot.
    // The execution view should show the exact topology that was executed, unmodified.
    const dagRes = await getRunDagSnapshot(runId);
    const dagData = dagRes?.ok ? (dagRes.data || dagRes) : null;
    if (dagData && dagData.nodes) {
      // Normalize connections (source/target → from/to) without validation
      const normConns = (dagData.connections || [])
        .filter((c) => (c.from || c.source) && (c.to || c.target))
        .map((c) => ({
          id: c.id || `c_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          from: c.from || c.source,
          fromPort: c.fromPort || c.sourcePort || 'default',
          to: c.to || c.target,
          toPort: c.toPort || c.targetPort || 'default',
        }));

      // Auto-layout the frozen DAG snapshot. Snapshots can be stored with
      // stale positions (or none at all) — which renders the execution view
      // as a single stacked column. Running Sugiyama here yields the same
      // clean layout the user sees in design mode without mutating topology.
      let layoutNodes = dagData.nodes;
      try {
        const totalNodes = dagData.nodes.length;
        const rankSep = totalNodes <= 8 ? 140 : totalNodes <= 20 ? 120 : 100;
        const nodeSep = totalNodes <= 8 ? 80 : totalNodes <= 20 ? 60 : 40;
        const positioned = runLayout(dagData.nodes, normConns, {
          nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep, nodeSep, padding: 80, rankDir: 'TB',
        });
        const posMap = new Map(positioned.map((n) => [n.id, { x: n.x, y: n.y }]));
        layoutNodes = dagData.nodes.map((n) => {
          const pos = posMap.get(n.id);
          return pos ? { ...n, x: pos.x, y: pos.y } : n;
        });
      } catch (err) {
        console.warn('[handleSelectRun] layout failed, using snapshot positions', err);
      }

      canvas.setNodes(layoutNodes);
      canvas.setConnections(normConns);
      // Zoom to fit after React renders the new nodes
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { canvas.zoomToFit(); });
      });
    }

    // Load node executions for this run
    const result = await getNodeExecutions(runId);
    let runIsStillActive = false;
    if (result?.ok) {
      const nodeExecs = result.data || [];
      setRunNodeExecutions(nodeExecs);
      // Populate canvas execution state so the right-panel Execution tab + Debug button appear
      const stateMap = {};
      nodeExecs.forEach((ne) => {
        stateMap[ne.nodeId] = {
          status: ne.status?.toLowerCase() || 'unknown',
          output: ne.output,
          error: ne.error,
          input: ne.input,
          startedAt: ne.startedAt,
          completedAt: ne.completedAt,
          durationMs: ne.durationMs,
        };
      });
      // Mark canvas nodes not in the snapshot as skipped
      const currentNodes = dagData?.nodes || canvas.nodes;
      currentNodes.forEach((n) => {
        if (!stateMap[n.id]) {
          stateMap[n.id] = { status: 'skipped', error: 'Node was not reached during this run' };
        }
      });
      canvas.setExecutionStates(stateMap);

      // Check if ANY node is still pending/running — the run is still active
      runIsStillActive = nodeExecs.some(
        (ne) => ne.status?.toLowerCase() === 'running' || ne.status?.toLowerCase() === 'pending'
      );
    }

    // Load audit for this run
    const auditResult = await getRunAuditLog(runId);
    if (auditResult?.ok) setRunAuditLog(auditResult.data || []);

    // ── Auto-poll for in-progress runs (scheduled, agent-triggered, etc.) ──
    // If the run is still active, start polling so the user sees real-time
    // node state updates — not just a frozen snapshot from the initial load.
    if (runIsStillActive) {
      setActiveRunId(runId);
      setIsExecuting(true);
      pollCountRef.current = 0;
    }
  };

  // =========================================================================
  // Clear Execution State — return to editable design mode
  // =========================================================================
  const hasExecutionState = canvas.executionState && canvas.executionState.size > 0;

  const handleClearExecution = () => {
    canvas.clearExecutionState();
    setIsExecuting(false);
    setActiveRunId(null);
    // Restore the design-mode nodes/connections that were preserved before switching to execution view
    if (designSnapshotRef.current) {
      canvas.setNodes(designSnapshotRef.current.nodes);
      canvas.setConnections(designSnapshotRef.current.connections);
      designSnapshotRef.current = null;
    }
    setSelectedRunId(null);
    setLeftPanelMode('design');
    // Recalculate viewport to frame all restored design nodes
    // Use requestAnimationFrame to ensure React has rendered the restored nodes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { canvas.zoomToFit(); });
    });
    addToast('Execution state cleared — you can now edit and re-execute', 'success');
  };

  // =========================================================================
  // RBAC Handlers
  // =========================================================================

  // Load users, groups, agents when assign modal opens
  useEffect(() => {
    if (!showAssignModal) return;
    (async () => {
      const [uRes, gRes, aRes] = await Promise.all([
        getUsers().catch(() => null),
        getGroups().catch(() => null),
        getAgents().catch(() => null),
      ]);
      if (uRes?.ok) setRbacUsers(uRes.data || []);
      if (gRes?.ok) setRbacGroups(gRes.data || []);
      if (aRes?.ok) setRbacAgents(aRes.data || []);
    })();
  }, [showAssignModal]);

  const handleCreateAssignment = async () => {
    if (!selectedWorkflowId) return;
    try {
      const body = {
        accessLevel: assignForm.accessLevel,
        assignedBy: userId,
      };
      if (assignForm.userId) body.userId = assignForm.userId;
      if (assignForm.groupId) body.groupId = assignForm.groupId;
      if (assignForm.agentId) body.agentId = assignForm.agentId;

      const result = await createWorkflowAssignment(selectedWorkflowId, body);
      if (result?.ok) {
        addToast('Assignment created', 'success');
        setShowAssignModal(false);
        setAssignForm({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' });
        // Reload
        const r = await getWorkflowAssignments(selectedWorkflowId);
        if (r?.ok) setWorkflowAssignments(r.data || []);
      } else {
        addToast(`Failed: ${result?.error}`, 'error');
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      const result = await removeWorkflowAssignment(assignmentId);
      if (result?.ok) {
        addToast('Assignment removed', 'success');
        const r = await getWorkflowAssignments(selectedWorkflowId);
        if (r?.ok) setWorkflowAssignments(r.data || []);
      }
    } catch (err) {
      addToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleAutoLayout = () => {
    canvas.autoLayout();
    setTimeout(() => canvas.zoomToFit(), 50);
    addToast('Layout applied', 'success');
  };

  const handleZoomFit = () => {
    canvas.zoomToFit();
    addToast('Zoomed to fit', 'info');
  };

  const handleValidate = () => {
    const result = canvas.validateDAG();
    if (result.valid) {
      addToast('Workflow is valid', 'success');
    } else {
      addToast(`Validation errors: ${result.errors.length}`, 'error');
    }
  };

  // =========================================================================
  // Canvas SVG Handlers
  // =========================================================================

  const svgContainerRef = useRef(null);

  const getSVGCoords = (e) => {
    const rect = svgContainerRef.current?.getBoundingClientRect();
    if (!rect) return { x: e.clientX, y: e.clientY };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleSVGMouseDown = (e) => {
    if (e.target === e.currentTarget || (e.target.tagName === 'rect' && e.target.getAttribute('fill') === 'url(#grid)')) {
      const { x, y } = getSVGCoords(e);
      canvas.startPan(x, y);
      // Don't clear node selection when panning in execution mode — keeps properties panel stable
      if (leftPanelMode !== 'execution') {
        canvas.clearSelection();
      }
    }
  };

  const handleSVGMouseMove = (e) => {
    const { x, y } = getSVGCoords(e);
    if (canvas.dragging || canvas.dragPending) canvas.drag(x, y);
    else if (canvas.connecting) canvas.moveConnect(x, y);
    else canvas.doPan(x, y);
  };

  const handleSVGMouseUp = () => {
    if (canvas.dragging || canvas.dragPending) canvas.endDrag();
    if (canvas.connecting) canvas.cancelConnect();
    canvas.endPan();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    canvas.doZoom(e.deltaY > 0 ? -1 : 1);
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      canvas.deleteSelected();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      canvas.save();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault();
      canvas.nodes.forEach((n) => canvas.toggleMultiSelect(n.id));
    }
    /* RC-118 — Undo (⌘Z) / Redo (⌘⇧Z, also ⌘Y on Windows muscle memory).
     *
     * Skipped when focus is in any editable surface so the OS-native
     * undo for input/textarea/contenteditable still works inside the
     * Inspector — the canvas history is for canvas mutations, not for
     * un-typing a character in a config field.
     *
     * Also skipped on the AutoLet CEG read-only surface: that canvas is
     * locked, so there's nothing the operator could undo. */
    if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
      const ae = document.activeElement;
      const inEditable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      if (!inEditable && !isAutoLetCegReadOnly) {
        e.preventDefault();
        if (e.shiftKey) {
          try { canvas.redo(); } catch { /* swallow — guard against transient hook timing */ }
        } else {
          try { canvas.undo(); } catch { /* swallow */ }
        }
      }
    }
    // ⌘Y is the Windows convention for redo. Bind for muscle memory.
    if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) {
      const ae = document.activeElement;
      const inEditable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      if (!inEditable && !isAutoLetCegReadOnly) {
        e.preventDefault();
        try { canvas.redo(); } catch { /* swallow */ }
      }
    }
    // RC-20e — ⌘⇧P (V3CommandPalette) hotkey deleted. ⌘E opens the global
    // TaskPicker (canonical insert path). ⌘⇧F opens Jump-to-Node (rebound
    // from ⌘K in V2CanvasView). ⌘K is reserved for the platform top-bar.
    //
    // ⌘E / Ctrl+E opens the global Task Picker.
    //
    // History: we tried ⌘T first ("T for Task") but Chrome/Edge/Safari hard-
    // reserve ⌘T at the browser-chrome level for "New Tab" — preventDefault
    // inside the page never fires because the keystroke is consumed before
    // it reaches the document. ⌘E has no browser/OS reservation on macOS,
    // Windows, or Linux, and is free in every text-input context that
    // matters here.
    //
    // ⌘K stays bound to the platform-level command/search palette and is
    // intentionally NOT this shortcut. ⌘A stays bound to "select all nodes"
    // (line above). ⌘E is the picker. Three orthogonal shortcuts, one user.
    //
    // We bail when focus is in any editable surface (the user is typing,
    // not summoning).
    if ((e.metaKey || e.ctrlKey) && (e.key === 'e' || e.key === 'E')) {
      const ae = document.activeElement;
      const inEditable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      // V169 / Wave 3 — read-only mode for AutoLet CEGs: skip the
      // shortcut entirely so it can't open a picker that would fire
      // a backend mutation that would 409. preventDefault is also
      // skipped so the keystroke falls through (Chrome's default for
      // ⌘E is no-op, so this is safe).
      /* G240 — Picker / dbl-click adds blocked in Decide + Act (structural). */
      if (!inEditable && isCanvasPickerEnabled() && !isCanvasStructuralLock) {
        e.preventDefault();
        if (!insertionTarget) {
          // Selection-aware: ⌘E with a task selected = "insert below THIS
          // task" (same as clicking + on the card). Without selection,
          // fall back to palette mode (which the picker resolves to
          // "append before END" via the recipe/single-task heuristic).
          if (canvas.selected && canvas.nodes.some((n) => n.id === canvas.selected)) {
            setInsertionTarget({ kind: 'after-node', nodeId: canvas.selected });
          } else {
            setInsertionTarget({ kind: 'palette' });
          }
        }
      }
    }
    if (e.key === 'Escape') {
      canvas.cancelConnect();
      canvas.clearSelection();
    }
  }, [canvas, insertionTarget, isAutoLetCegReadOnly, isCanvasStructuralLock]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // RC-14 (task #194) — Drag-and-drop palette → canvas REMOVED.
  // Pre-RC-14, the canvas wrapper accepted HTML5 drag-drop and the palette
  // items emitted onDragStart, with point-to-segment distance picking the
  // splice slot. That path was the source of a long regression chain:
  // wrong-slot splices, silent failures (operator dropped, nothing
  // appeared), nodes spliced "after the switch" when the operator
  // intended a branch arm. Click-to-add via the inline TaskPicker is the
  // only insertion path now — see handleAddNode (palette click → orphan,
  // recipe insert), the per-node "+" overlay (onAddAfter prop on
  // V2CanvasView), and the branch "+" buttons. All three paths route
  // through canvas.addTaskToFlow with an explicit anchor; no proximity
  // guessing means no wrong-slot bugs.
  //
  // The deleted handlers were handleCanvasDragOver and handleCanvasDrop.
  // The associated SINGLE_INSTANCE_ENTRY_TYPES guard, edge-proximity
  // math, and selection-aware fallback have all moved into / are already
  // present in handleAddNode + handleInsertNodeType, so no logic was lost.

  // =========================================================================
  // Per-Node Debug — trace data flow through upstream chain
  // =========================================================================

  const handleDebugNode = async (nodeId) => {
    const targetNode = (canvas.nodes || []).find((n) => n.id === nodeId);
    if (!targetNode) {
      addToast('Node not found', 'warn');
      return null;
    }

    const upstreamIds = new Set();
    const queue = [nodeId];
    const allConns = canvas.connections || [];
    while (queue.length > 0) {
      const current = queue.shift();
      allConns.forEach((c) => {
        if (c.to === current && !upstreamIds.has(c.from)) {
          upstreamIds.add(c.from);
          queue.push(c.from);
        }
      });
    }

    const downstreamIds = new Set();
    allConns.forEach((c) => {
      if (c.from === nodeId) downstreamIds.add(c.to);
    });

    const relevantIds = new Set([...upstreamIds, nodeId, ...downstreamIds]);
    const subgraphNodes = (canvas.nodes || []).filter((n) => relevantIds.has(n.id));
    const subgraphConns = allConns.filter((c) => relevantIds.has(c.from) && relevantIds.has(c.to));

    let executionResults = null;
    if (canvas.executionState && canvas.executionState.size > 0) {
      executionResults = {};
      canvas.executionState.forEach((state, nid) => {
        if (relevantIds.has(nid)) executionResults[nid] = state;
      });
    }

    const subgraphCanvas = { nodes: subgraphNodes, connections: subgraphConns };
    const connectorIds = Object.keys(installedConnectors || {});

    const focusedPrompt = [
      `FOCUS NODE: "${targetNode.label}" (id=${nodeId}, type=${targetNode.type}).`,
      `Upstream chain: [${[...upstreamIds].map((id) => {
        const n = (canvas.nodes || []).find((nd) => nd.id === id);
        return n ? `"${n.label}" (${n.type})` : id;
      }).join(' → ')}] → TARGET → [${[...downstreamIds].map((id) => {
        const n = (canvas.nodes || []).find((nd) => nd.id === id);
        return n ? `"${n.label}" (${n.type})` : id;
      }).join(', ')}].`,
      '',
      'Analyze this SINGLE node in extreme detail:',
      '1. DATA RECEIVED: What inputMapping resolves to? Trace each ${...} variable to its source node output.',
      '2. CONFIG APPLIED: What action/endpoint/params does this node use? Are the field names correct for the connector API?',
      '3. EXECUTION: If execution data is available, what was the actual HTTP request (URL, method, headers, body)? What response came back?',
      '4. OUTPUT PRODUCED: What data does this node put into its outputVariable for downstream nodes?',
      '5. CHAIN BREAKS: Where exactly does the data chain break? Is the upstream node producing the expected output? Is this node consuming it with the correct variable path?',
      '',
      'Return detailed configFixes with exact key-value pairs for this node.',
    ].join('\n');

    try {
      const res = await aiCanvasDebug(focusedPrompt, subgraphCanvas, executionResults, connectorIds);
      if (res?.ok && res.data) {
        if (res.data.error) return { error: res.data.error };
        return res.data;
      }
      // apiFetch returns { ok: false, error: "message" } on HTTP errors
      const errMsg = res?.data?.error || res?.error || 'Debug analysis failed';
      console.error('[debug] aiCanvasDebug failed:', errMsg, res);
      return { error: errMsg };
    } catch (err) {
      console.error('[debug] Network error:', err);
      return { error: `Network error: ${err.message}` };
    }
  };

  // =========================================================================
  // Render: LIST MODE
  // =========================================================================

  if (viewMode === 'list') {
    return (
      <div style={{ padding: 24, background: 'var(--v2-bg-elevated)', minHeight: '100vh' }}>
        <CanvasPageHeader viewMode={viewMode} setViewMode={setViewMode} onNew={async () => { if (await confirmUnsavedChanges()) setShowNewWorkflow(true); }} />
        <WorkflowListView
          workflows={workflows}
          selectedId={selectedWorkflowId}
          onSelect={async (id) => {
            if (!(await confirmUnsavedChanges())) return;
            setSelectedWorkflowId(id);
            setViewMode('canvas');
          }}
          onDelete={handleDeleteWorkflow}
          onArchive={handleArchiveWorkflow}
          onUnarchive={handleUnarchiveWorkflow}
          showArchived={showArchived}
          onToggleArchived={() => setShowArchived((prev) => !prev)}
        />
        {renderNewWorkflowModal()}
      </div>
    );
  }

  // =========================================================================
  // Render: ORCHESTRATION MODE
  // =========================================================================

  if (viewMode === 'orchestration') {
    return (
      <div style={{ padding: 24, background: 'var(--v2-bg-elevated)', minHeight: '100vh' }}>
        <CanvasPageHeader viewMode={viewMode} setViewMode={setViewMode} onNew={async () => { if (await confirmUnsavedChanges()) setShowNewWorkflow(true); }} />
        <OrchestrationDashboard
          stats={orchestrationStats}
          workflows={workflows}
          workflowRuns={workflowRuns}
          onRefresh={() => {
            loadOrchestrationStats(orgId, tenantId);
            if (selectedWorkflowId) loadWorkflowRuns(selectedWorkflowId);
          }}
          onCancelRun={cancelWorkflowRun}
        />
        {renderNewWorkflowModal()}
      </div>
    );
  }

  // =========================================================================
  // Render: CANVAS MODE (Main Editor)
  // =========================================================================

  // RC-111 — New Workflow rendered as V2Slideout (not Modal) per
  // `feedback_v2_inline_over_modal`. Same form fields, same handlers,
  // same back-compat function name so call sites don't need to change.
  // Uses V2 form primitives per `feedback_v2_form_primitives_mandatory`.
  //
  // RC-111a — "From Template" now renders the template browser IN-PLACE
  // inside the same Slideout (newWorkflowView toggles between 'form'
  // and 'template'), instead of popping the separate Load Template
  // modal. The standalone Load Template Modal still exists for the
  // OTHER triggers (left-panel "Load Playbook" button, Learn cards)
  // that aren't inside any slideout — zero regression there.
  //
  // ZERO REGRESSION: showNewWorkflow / workflowForm / handleCreateWorkflow /
  // handleLoadTemplate / agents source / playbookFilter+playbookCategoryFilter
  // state and SOAR_PLAYBOOKS source all untouched. Only chrome + the
  // intra-slideout view-toggle changed.
  function renderNewWorkflowModal() {
    const isTemplateView = newWorkflowView === 'template';
    const handleCancel = () => {
      setShowNewWorkflow(false);
      setNewWorkflowView('form');
    };
    const goToTemplates = () => {
      setPlaybookFilter('');
      setPlaybookCategoryFilter(null);
      setNewWorkflowView('template');
    };
    const goBackToForm = () => setNewWorkflowView('form');

    // Inline template browser — same SOAR_PLAYBOOKS source and same
    // filter mechanics as renderTemplateModal(). Slimmer chrome since
    // it lives inside the slideout (no top-level title; the slideout
    // header carries it).
    const templateCount = SOAR_PLAYBOOKS.filter((p) =>
      (!playbookCategoryFilter || p.category === playbookCategoryFilter)
      && (playbookFilter === ''
          || p.name.toLowerCase().includes(playbookFilter.toLowerCase())
          || p.description.toLowerCase().includes(playbookFilter.toLowerCase()))
    ).length;

    return (
      <V2Slideout
        isOpen={showNewWorkflow}
        onClose={handleCancel}
        ariaLabel="New Workflow"
        width={isTemplateView ? 640 : 520}
        header={isTemplateView ? (
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={goBackToForm}
                className="text-[12px] text-v2-ink-secondary hover:text-v2-ink-primary inline-flex items-center gap-1 cursor-pointer"
                aria-label="Back to New Workflow form"
                style={{ background: 'none', border: 'none', padding: 0 }}
              >
                <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
                Back
              </button>
            </div>
            <h2 className="text-[16px] font-semibold tracking-tight m-0 mt-1">
              Load Template
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1 m-0">
              {templateCount} matching {templateCount === 1 ? 'playbook' : 'playbooks'}. Select one to author a workflow from it.
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight m-0">
              New Workflow
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1 m-0">
              Author a blank canvas or start from a Playbook template.
            </p>
          </div>
        )}
        footer={isTemplateView ? (
          <div className="flex items-center gap-2 justify-end">
            <V2Button variant="ghost" size="sm" onClick={goBackToForm}>
              Back
            </V2Button>
            <V2Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </V2Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 justify-end">
            <V2Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </V2Button>
            <V2Button
              variant="secondary"
              size="sm"
              onClick={goToTemplates}
            >
              From Template
            </V2Button>
            <V2Button
              variant="primary"
              size="sm"
              onClick={() => handleCreateWorkflow()}
              // Disable Create when the name is empty — prevents the
              // BE 400 on blank-name workflows. Same UX intent as the
              // pre-RC-111 modal but enforced at the button level.
              disabled={!workflowForm.name || !workflowForm.name.trim()}
            >
              Create Blank
            </V2Button>
          </div>
        )}
      >
        {!isTemplateView && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            <V2FieldGroup label="Workflow Name" required>
              <V2Input
                value={workflowForm.name}
                onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                placeholder="e.g. Threat Response Pipeline"
                autoFocus
              />
            </V2FieldGroup>

            <V2FieldGroup label="Description">
              <V2Textarea
                rows={4}
                value={workflowForm.description}
                onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                placeholder="Describe what this workflow does"
              />
            </V2FieldGroup>

            <V2FieldGroup
              label="Link to Agent (optional)"
              hint="When linked, this workflow appears in the agent's skill catalog and inherits the agent's permissions."
            >
              <V2Select
                value={workflowForm.agentId}
                onChange={(e) => setWorkflowForm({ ...workflowForm, agentId: e.target.value })}
              >
                <option value="">None (standalone workflow)</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </V2Select>
            </V2FieldGroup>
          </div>
        )}

        {isTemplateView && (
          <div className="space-y-3">
            {/* Filter input — V2 primitive */}
            <V2Input
              value={playbookFilter}
              onChange={(e) => { setPlaybookFilter(e.target.value); setPlaybookCategoryFilter(null); }}
              placeholder="Filter templates by name or description..."
              autoFocus
            />

            {/* Category chips */}
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setPlaybookCategoryFilter(null)}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer"
                style={{
                  border: !playbookCategoryFilter ? '1px solid var(--v2-action)' : '1px solid var(--v2-border-default)',
                  background: !playbookCategoryFilter ? 'var(--v2-action-soft)' : 'var(--v2-bg-surface)',
                  color: !playbookCategoryFilter ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                }}
              >
                All
              </button>
              {PLAYBOOK_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setPlaybookCategoryFilter(cat.id)}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer"
                  style={{
                    border: playbookCategoryFilter === cat.id ? '1px solid var(--v2-action)' : '1px solid var(--v2-border-default)',
                    background: playbookCategoryFilter === cat.id ? 'var(--v2-action-soft)' : 'var(--v2-bg-surface)',
                    color: playbookCategoryFilter === cat.id ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                  }}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            {/* Playbook cards grouped by category — same filter logic as renderTemplateModal */}
            {PLAYBOOK_CATEGORIES.map((category) => {
              if (playbookCategoryFilter && category.id !== playbookCategoryFilter) return null;
              const categoryPlaybooks = SOAR_PLAYBOOKS.filter(
                (p) => p.category === category.id
                  && (playbookFilter === ''
                      || p.name.toLowerCase().includes(playbookFilter.toLowerCase())
                      || p.description.toLowerCase().includes(playbookFilter.toLowerCase()))
              );
              if (categoryPlaybooks.length === 0) return null;
              return (
                <div key={category.id} className="space-y-2">
                  <h3 className="text-[12px] font-bold m-0 mt-3 text-v2-ink-primary uppercase tracking-wider">
                    {category.name}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {categoryPlaybooks.map((playbook) => (
                      <div
                        key={playbook.id}
                        className="rounded border border-v2-border-subtle bg-white p-3"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <h4 className="text-[13px] font-semibold m-0 text-v2-ink-primary">
                              {playbook.name}
                            </h4>
                            <p className="text-[11.5px] text-v2-ink-secondary m-0 mt-1 leading-snug">
                              {playbook.description}
                            </p>
                          </div>
                          <Badge text={playbook.severity} variant={playbook.severity} />
                        </div>
                        <div className="text-[11px] text-v2-ink-muted mb-2">
                          {playbook.nodes?.length || 0} nodes · {playbook.requiredConnectors?.join(', ') || 'No connectors'}
                        </div>
                        <V2Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleLoadTemplate(playbook)}
                        >
                          Use Template
                        </V2Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {templateCount === 0 && (
              <div className="text-[12px] text-v2-ink-secondary text-center py-6">
                No templates match your filter. Try clearing the filter or picking a different category.
              </div>
            )}
          </div>
        )}
      </V2Slideout>
    );
  }

  function renderSaveAsModal() {
    // P1-E ported: inline error rendering, submitting state, hard length cap,
    // double-click guard. Modal stays open until 2xx success.
    const closeAndReset = () => {
      setShowSaveAs(false);
      setSaveAsName('');
      setSaveAsError(null);
      setSaveAsSubmitting(false);
      // RC-96a — clear picker selection so the next Save-As starts fresh.
      setSaveAsPickedTenantId('');
    };
    /* RC-96a — Tenant resolution.
     *
     * RBAC invariant: every workflow row has a (org_id, tenant_id) pair —
     * those columns are NOT NULL by schema and are what every downstream
     * list/read query filters on. A Platform Admin session is correctly
     * tenant-less at the platform level (PA operates cross-customer),
     * but a *write* must still attribute the row to a specific tenant.
     *
     * Resolution order:
     *   1. session.tenantId — Tenant Admin / scoped SA / TA with multi-
     *      tenant membership: their JWT pins a tenant and that's the
     *      authoring scope. orgId from session pairs with it.
     *   2. operator-picked tenantId from the dialog Select (PA / no-
     *      tenant SA path). We look the picked tenant up in
     *      platform.tenants to derive its org_id, so the saved row gets
     *      a consistent (org, tenant) pair even when the session orgId
     *      points at a customer-level scope rather than the tenant's
     *      actual org.
     *
     * NEVER auto-pick "first tenant" on a write — that's the RC-85
     * read-path fallback and would silently mis-attribute the workflow.
     * Picker UX preserves operator intent. */
    const effectiveTenantId = tenantId || saveAsPickedTenantId || null;
    const pickedTenantObj = !tenantId && saveAsPickedTenantId
      ? (Array.isArray(tenants) ? tenants.find(t => t?.id === saveAsPickedTenantId) : null)
      : null;
    const effectiveOrgId = tenantId ? orgId : (pickedTenantObj?.orgId || pickedTenantObj?.org_id || null);
    const needsTenantPicker = !tenantId;

    const handleSaveAs = async () => {
      if (saveAsSubmitting) return;
      if (!canCreateWorkflow) {
        setSaveAsError('Permission denied: workflow.create required');
        return;
      }
      const trimmed = (saveAsName || '').trim();
      if (!trimmed) { setSaveAsError('Workflow name is required.'); return; }
      if (trimmed.length > 200) { setSaveAsError('Workflow name must be 200 characters or fewer.'); return; }
      // RC-96a — Picker is required when session has no tenant. Without
      // a pick we'd hit the BE's TENANT_REQUIRED guard anyway; short-
      // circuit so the operator gets immediate feedback on the field
      // they need to touch.
      if (needsTenantPicker && !saveAsPickedTenantId) {
        setSaveAsError('Pick a target tenant — your Platform Admin session is platform-wide and does not pin a single tenant, so we need to know where this workflow should live.');
        return;
      }
      if (!effectiveTenantId || !effectiveOrgId) {
        setSaveAsError('Could not resolve a (tenant, org) target for this workflow. Reload the page and try again — if it persists, file an RC.');
        return;
      }
      setSaveAsSubmitting(true);
      setSaveAsError(null);
      try {
        const canvasData = canvas.getCanvasData();
        const nodes = canvasData?.nodes || [];
        const connections = canvasData?.connections || [];

        if (nodes.length === 0) {
          setSaveAsError('Canvas is empty — add at least one node before saving.');
          setSaveAsSubmitting(false);
          return;
        }

        const result = await createWorkflow({
          name: trimmed,
          description: selectedWorkflow?.description || 'Saved from AI Workflow Builder',
          // RC-96a — Use resolved (effectiveOrgId, effectiveTenantId)
          // not raw (orgId, tenantId). For Tenant Admin paths these are
          // identical to the session values; for PA-picker paths these
          // come from the picked tenant + its derived org.
          orgId: effectiveOrgId,
          tenantId: effectiveTenantId,
          status: 'draft',
          createdBy: userId,
          definition: { nodes, connections },
        });

        if (result?.ok && result.data?.id) {
          const targetTenantName = pickedTenantObj?.name;
          const successMsg = targetTenantName
            ? `Workflow "${trimmed}" saved to tenant "${targetTenantName}"`
            : `Workflow "${trimmed}" saved successfully`;
          addToast(successMsg, 'success');
          loadedWorkflowRef.current = result.data.id;
          setSelectedWorkflowId(result.data.id);
          closeAndReset();
          return;
        }
        if (result?.duplicate) {
          setSaveAsError(`A workflow named "${trimmed}" already exists in this tenant. Choose a different name.`);
          setSaveAsSubmitting(false);
          return;
        }
        /* RC-96 — Branch on the BE's structured TENANT_REQUIRED code.
         * With the RC-96a picker this should never fire on the happy
         * path, but it's the last-line defense if some edge case slips
         * through (e.g. session race that loses tenant mid-submit). */
        const errStr = String(result?.error || '');
        if (errStr.includes('TENANT_REQUIRED') || errStr.toLowerCase().includes('tenant_id')) {
          setSaveAsError('Tenant context dropped between dialog open and submit. Pick the target tenant again and try once more.');
          setSaveAsSubmitting(false);
          return;
        }
        /* A server error that isn't one of the cases above. Prefer the
         * server's own `message` over `error`: on a conflict the latter is
         * just "Conflict" while the former says what happened. The errorId
         * is what correlates this failure with the server log, so show it —
         * without it the operator has nothing to quote, which is how a
         * not-null violation on insert spent its life being reported as a
         * duplicate name. */
        const serverMsg = String(result?.message || errStr || '');
        const ref = result?.errorId ? ` (reference ${result.errorId})` : '';
        setSaveAsError(
          serverMsg
            ? `${serverMsg}${ref}`
            : 'Save failed for an unknown reason. Please try again.'
        );
        setSaveAsSubmitting(false);
      } catch (err) {
        setSaveAsError(err?.message || 'Network error while saving. Please try again.');
        setSaveAsSubmitting(false);
      }
    };

    /* RC-96a — Lazy-load the tenant list the first time the picker is
     * needed for this session. We don't load eagerly on mount because
     * most sessions (Tenant Admin path) never see the picker. The
     * existing PlatformContext.loadTenants is idempotent — calling it
     * again here is safe and refreshes if a tenant was added in
     * another tab since the page loaded. Guard the call so we only
     * fire when the dialog is visible AND we actually need it AND we
     * don't already have data. */
    if (showSaveAs && needsTenantPicker && (!Array.isArray(tenants) || tenants.length === 0) && typeof loadTenants === 'function') {
      // Fire-and-forget; the Select will re-render when `tenants` arrives.
      loadTenants();
    }

    return (
      <Modal isOpen={showSaveAs} title="Save Workflow As" onClose={closeAndReset}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Workflow Name</label>
            <input
              value={saveAsName}
              onChange={(e) => {
                setSaveAsName(e.target.value);
                if (saveAsError) setSaveAsError(null);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !saveAsSubmitting) handleSaveAs(); }}
              style={{ ...inputStyle, borderColor: saveAsError ? 'var(--v2-danger)' : inputStyle?.borderColor }}
              maxLength={200}
              placeholder="e.g. ServiceNow Ticket Creator"
              autoFocus
              disabled={saveAsSubmitting}
              aria-invalid={!!saveAsError}
              aria-describedby={saveAsError ? 'save-as-error' : undefined}
            />
          </div>

          {/* RC-96a — Inline Target Tenant picker. Renders ONLY when the
              session has no tenant binding (Platform Admin sessions
              always; Super Admin sessions that haven't pinned a tenant).
              For every other role the JWT carries a tenant and this
              entire block stays unmounted, preserving the original UX.
              The Select sources from /api/v1/tenants which is already
              RBAC-scoped per caller (TenantController:363) — PA sees
              all, SA sees their customer's, Tenant Admin sees their
              grant-union. We never expose tenants the caller can't
              author into. */}
          {needsTenantPicker && (
            <div>
              <label style={labelStyle}>
                Target Tenant <span style={{ color: 'var(--v2-danger)' }}>*</span>
              </label>
              <div style={{
                fontSize: 11.5, color: 'var(--v2-text-muted)', marginTop: -4, marginBottom: 6, lineHeight: 1.45,
              }}>
                Your session is platform-wide (no tenant pinned). Pick the tenant this workflow should live under — once saved it&apos;s editable by anyone with workflow.edit on that tenant.
              </div>
              <select
                value={saveAsPickedTenantId}
                onChange={(e) => {
                  setSaveAsPickedTenantId(e.target.value);
                  if (saveAsError) setSaveAsError(null);
                }}
                disabled={saveAsSubmitting}
                style={{
                  ...inputStyle,
                  borderColor: (saveAsError && !saveAsPickedTenantId) ? 'var(--v2-danger)' : inputStyle?.borderColor,
                  cursor: saveAsSubmitting ? 'wait' : 'pointer',
                }}
                aria-invalid={!!(saveAsError && !saveAsPickedTenantId)}
              >
                <option value="">
                  {Array.isArray(tenants) && tenants.length > 0
                    ? '— Select a tenant —'
                    : 'Loading tenants…'}
                </option>
                {Array.isArray(tenants) && tenants
                  // Stable sort for predictable picker order. Mirrors the
                  // ordering the scope chip uses elsewhere.
                  .slice()
                  .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
                  .map(t => (
                    <option key={t.id} value={t.id}>{t.name || t.id}</option>
                  ))}
              </select>
            </div>
          )}

          {saveAsError && (
            <div id="save-as-error" role="alert" style={{
              fontSize: 12, color: 'var(--v2-danger)',
              display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.45,
              background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)', borderRadius: 6,
              padding: '8px 10px',
            }}>
              <span aria-hidden="true">⚠</span>
              <span>{saveAsError}</span>
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
            This will create a new workflow with the current canvas contents.
            {selectedWorkflowId && ' The original workflow will not be modified.'}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={closeAndReset} style={btnBase} disabled={saveAsSubmitting}>
              Cancel
            </button>
            <button
              onClick={handleSaveAs}
              style={{ ...btnPrimary, ...(saveAsSubmitting ? { opacity: 0.7, cursor: 'wait' } : {}) }}
              disabled={saveAsSubmitting}
            >
              {saveAsSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  /**
   * RC-97a — Canvas Issues panel.
   *
   * Renders every issue from `v4ValidationReport` as a scrollable list.
   * Each row shows:
   *   - severity badge (red/amber/blue per error/warning/info)
   *   - code (small, monospace, for support triage)
   *   - message
   *   - the node label (resolved from canvas.nodes by issue.nodeId)
   *   - suggest hint when present
   *   - "Focus on canvas" button when nodeId is set — closes panel
   *     and calls canvas.selectNode(nodeId) so the operator lands
   *     directly on the offending node with the V2 highlight ring.
   *
   * NO REGRESSION SURFACE: this is purely additive. The chip's
   * existing tooltip behavior is preserved (RC-97a edit added the
   * click affordance + cursor:pointer; the tooltip text is the same
   * + "Click to see all issues"). Clean canvas: chip doesn't render,
   * panel never opens. Modal closes via backdrop click, X button, or
   * Esc (Modal component handles Esc).
   *
   * Defensive: if the validator throws or returns unexpected shape,
   * the panel renders an empty-state. Focus button no-ops gracefully
   * when canvas.selectNode isn't available (older canvas versions).
   */
  function renderIssuesPanelModal() {
    if (!showIssuesPanel) return null;
    const report = v4ValidationReport || { issues: [], stats: { error: 0, warning: 0, info: 0, total: 0 } };
    const issues = Array.isArray(report.issues) ? report.issues : [];
    const stats = report.stats || { error: 0, warning: 0, info: 0, total: 0 };

    // Lookup map: node id → node, used to render human-readable
    // labels next to issue messages. Falls back to the raw id when
    // a node has been deleted between validate-run and panel-open.
    const nodesById = new Map(
      (canvas.nodes || []).map((n) => [n.id, n])
    );

    const severityTone = (sev) => {
      if (sev === 'error')   return { bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)', text: 'var(--v2-danger)', label: 'ERROR' };
      if (sev === 'warning') return { bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)', text: 'var(--v2-warning)', label: 'WARN' };
      return                       { bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)', text: 'var(--v2-action)', label: 'INFO' };
    };

    const focusNode = (nodeId) => {
      if (!nodeId) return;
      // Defensive: canvas hook may not expose selectNode on older renders.
      if (typeof canvas?.selectNode === 'function') {
        try { canvas.selectNode(nodeId); } catch { /* swallow — focus is best-effort */ }
      }
      /* RC-130 — Bump the canvas focus target so V2CanvasView's
       * useEffect fires and pans/zooms the viewport onto the
       * offending node. Pre-RC-130 the click only set the selection
       * state — if the node was off-screen the operator saw nothing
       * change. Using a fresh `key` (Date.now()) makes the object
       * identity change every click so repeat-focusing the same
       * node still re-fires the centering effect. */
      setCanvasFocusTarget({ nodeId, key: Date.now() });
      // RC-97e — Route through the cleanup helper (defined just below)
      // so any in-flight AI-fix request gets aborted before the panel
      // closes. Forward-reference is fine inside this closure — the
      // arrow function only runs on user click.
      closeIssuesPanel();
    };

    /* RC-97e — Ask AI to fix the open issues.
     *
     * Synthesizes a fix-this-list prompt from the validator output and
     * routes it through the existing aiCanvasBuild path. Because the
     * current canvas IS sent as currentCanvas, the BE detects this as
     * a MODIFY operation (auto-detect in CanvasAIController:91) and
     * routes through modifyWorkflow → RC-97c retry loop → RC-97d
     * connector drop → result. The whole pipeline already exists; we
     * just feed it a focused fix prompt.
     *
     * Defensive guards:
     *   - No-op if no issues (button is hidden in that case but
     *     belt + braces).
     *   - Cap issue list in the prompt at 50 items so a runaway
     *     validator can't blow the prompt size.
     *   - Abort in-flight request when panel closes or another fix
     *     click happens (issuesFixAbortRef).
     *   - On success: reload canvas with the fixed payload AND close
     *     the panel so the operator sees the result.
     *   - On failure: surface the error inline; don't close the panel
     *     (operator stays oriented). */
    const askAIToFixIssues = async () => {
      if (issuesFixBusy) return;
      if (issues.length === 0) return;
      // Abort any prior in-flight request (rare — button is disabled
      // while busy — but defensive).
      try { issuesFixAbortRef.current?.abort(); } catch { /* swallow */ }
      const ac = new AbortController();
      issuesFixAbortRef.current = ac;
      setIssuesFixBusy(true);
      setIssuesFixError(null);
      // RC-97e3 — Clear any prior AI fix trace so this attempt's
      // trace (set in the success path below) is the one shown.
      // Without this, the banner would still display the *previous*
      // fix's summary even though we're computing a new one.
      setAiFixTrace(null);

      try {
        const currentCanvas = typeof canvas?.getCanvasData === 'function'
          ? canvas.getCanvasData()
          : { nodes: canvas?.nodes || [], connections: canvas?.connections || [] };
        if (!currentCanvas.nodes || currentCanvas.nodes.length === 0) {
          setIssuesFixError('Canvas is empty — nothing for AI to fix. Add at least one node first.');
          setIssuesFixBusy(false);
          return;
        }
        // Cap to keep prompt bounded
        const ISSUE_CAP = 50;
        const issuesCapped = issues.slice(0, ISSUE_CAP);
        const truncated = issues.length - issuesCapped.length;
        const issueLines = issuesCapped.map((i) => {
          const sev = (i.severity || 'warning').toUpperCase();
          const code = i.code || '(no-code)';
          const node = i.nodeId ? ` (node: ${i.nodeId})` : '';
          const suggest = i.suggest ? ` Suggestion: ${i.suggest}` : '';
          return `- [${sev}] ${code}${node}: ${i.message}${suggest}`;
        }).join('\n');
        const fixPrompt = [
          'Fix ONLY the following validation issues in the current workflow. Make MINIMAL changes — do NOT redesign or add new features.',
          '',
          'CRITICAL RULES:',
          '- Preserve ALL existing node IDs (do NOT regenerate IDs).',
          '- Preserve ALL existing nodes that are not directly cited in the issues below.',
          '- Return the COMPLETE updated workflow (full nodes + connections arrays), not a delta.',
          '- If an issue cannot be mechanically fixed (e.g. missing required config that needs a real value), leave that node alone and surface a warning in the workflow `warnings` array.',
          '',
          'OPEN ISSUES:',
          issueLines,
          truncated > 0 ? `\n(... and ${truncated} more issue(s) not shown)` : '',
        ].join('\n');

        const connectorIds = Object.keys(installedConnectors || {});
        const skills = Array.isArray(linkedAgent?.skills) ? linkedAgent.skills : [];
        const res = await aiCanvasBuild(fixPrompt, currentCanvas, connectorIds, ac.signal, skills);

        // If the request was aborted, the response may be undefined or
        // an aborted-flag payload depending on apiFetch — bail without
        // mutating UI state.
        if (ac.signal.aborted) {
          setIssuesFixBusy(false);
          return;
        }

        if (res?.ok && res.data?.nodes && res.data.nodes.length > 0) {
          if (typeof canvas?.loadCanvasData === 'function') {
            /* RC-117c — Tag the next save as "ai_fix" so the Versions
             * slideout narrates this as an AI-driven fix, not a manual
             * save. The hint is consumed once by the next onSave call
             * and cleared, so operator edits made after this load
             * correctly revert to "manual_save". */
            canvas.loadCanvasData(res.data, { autoLayoutOnLoad: false, zoomToFitOnLoad: false, changeKind: 'ai_fix' });
          }
          /* RC-97e3 — Capture the AI's structured response surface so
           * the operator has a TRACE of what changed, mirroring how
           * the BE auto-repair banner (canvas.orphanRepairReport)
           * surfaces its structural fixes. Both summary and warnings
           * are AI-generated and may be empty; the banner renders
           * something useful even when only one is present.
           *
           * String coercion is defensive — the system prompt asks for
           * a string summary but Claude occasionally returns null or
           * a nested object. We render whatever it gave us as text
           * rather than crashing on shape drift. */
          const summary = typeof res.data.summary === 'string' ? res.data.summary : '';
          const warningsRaw = Array.isArray(res.data.warnings) ? res.data.warnings : [];
          const warnings = warningsRaw
            .map((w) => (typeof w === 'string' ? w : JSON.stringify(w)))
            .filter((w) => w && w.length > 0);
          setAiFixTrace({
            summary,
            warnings,
            appliedAt: new Date().toISOString(),
          });
          addToast?.('AI applied fixes — see the banner above the canvas for what changed.', 'success');
          setShowIssuesPanel(false);
          setIssuesFixBusy(false);
          return;
        }
        // Surface BE error (validator rejection, parse fail, API err)
        const beErr = res?.data?.error || res?.error;
        setIssuesFixError(beErr
          ? `AI fix failed: ${beErr}`
          : 'AI fix returned no usable result — try again, or fix the issues manually using the Focus buttons below.');
        setIssuesFixBusy(false);
      } catch (e) {
        // AbortError → user cancelled, no toast.
        if (e?.name === 'AbortError' || ac.signal.aborted) {
          setIssuesFixBusy(false);
          return;
        }
        setIssuesFixError(e?.message || 'Network error while asking AI to fix issues.');
        setIssuesFixBusy(false);
      }
    };

    /* RC-97e — Cancel any in-flight AI fix request when the panel
     * closes, so a click-then-close doesn't race against the next
     * user action. The next askAIToFixIssues call replaces the
     * AbortController reference, so a successful flow naturally
     * supersedes the prior signal. */
    const closeIssuesPanel = () => {
      try { issuesFixAbortRef.current?.abort(); } catch { /* swallow */ }
      setIssuesFixBusy(false);
      setIssuesFixError(null);
      setShowIssuesPanel(false);
    };

    /* RC-97e2 — V2 Slideout layout.
     *
     * The pre-RC-97e2 implementation rendered as a centered Modal
     * which obscured the canvas and violated the V2 design rule
     * (feedback_v2_inline_over_modal). V2 Slideout right-docks the
     * panel so the operator can keep the canvas visible while
     * triaging issues — also matches every other "triage" surface
     * in the V2 system (slideouts from inventory rows, etc.).
     *
     * Layout:
     *   - header (sticky): title, stat chips, Ask AI button, hint
     *   - body: inline error (if any) + issue cards
     *   - footer (sticky): the policy note that was at the bottom
     *
     * Width 560px — wider than Slideout's 480 default because issue
     * messages can be long ("Branch 'Critical' on switch_case ...").
     * Still leaves >50% of viewport for canvas on a 1280+ display. */
    const headerNode = (
      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
          Canvas Issues ({stats.total})
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--v2-text-secondary)' }}>
            {stats.error > 0   && <span style={{ color: 'var(--v2-danger)' }}>● {stats.error} error{stats.error === 1 ? '' : 's'}</span>}
            {stats.warning > 0 && <span style={{ color: 'var(--v2-warning)' }}>● {stats.warning} warning{stats.warning === 1 ? '' : 's'}</span>}
            {stats.info > 0    && <span style={{ color: 'var(--v2-accent-cyan)' }}>● {stats.info} info</span>}
          </div>
          {issues.length > 0 && (
            <button
              type="button"
              onClick={askAIToFixIssues}
              disabled={issuesFixBusy}
              title={issuesFixBusy
                ? 'AI is working on the fix — usually 5–15 seconds, up to ~30s if self-correction kicks in.'
                : 'Send the open issues to the AI builder. AI will return a corrected workflow that you can review on the canvas.'}
              style={{
                marginLeft: 'auto',
                background: issuesFixBusy ? 'var(--v2-text-disabled)' : 'var(--v2-action)',
                color: 'var(--v2-bg-surface)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: issuesFixBusy ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {issuesFixBusy ? (
                <>
                  {/* RC-97e2 — Inline spinner so the operator sees the
                      AI call is alive (not stuck). CSS animation in a
                      style tag below; keyframes scoped via animation
                      property naming. */}
                  <span aria-hidden="true" style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: 'var(--v2-bg-surface)',
                    borderRadius: '50%',
                    animation: 'p7-spin 0.9s linear infinite',
                  }} />
                  <style>{`@keyframes p7-spin { to { transform: rotate(360deg); } }`}</style>
                  Asking AI…
                </>
              ) : (
                <>✨ Ask AI to fix these</>
              )}
            </button>
          )}
        </div>
        {issuesFixBusy && (
          <div style={{ fontSize: 11.5, color: 'var(--v2-text-muted)', marginTop: 6 }}>
            AI is rewriting the workflow — typically 5–15 seconds. Up to ~30s if self-correction (RC-97c) is triggered.
          </div>
        )}
      </div>
    );

    const footerNode = (
      <div style={{
        fontSize: 11.5,
        color: 'var(--v2-text-muted)',
        lineHeight: 1.45,
      }}>
        Errors prevent the workflow from running as authored. Warnings let it run but flag review-worthy patterns
        (orphan branches, missing config, etc.). Info items are suggestions only.
      </div>
    );

    return (
      <V2Slideout
        isOpen={showIssuesPanel}
        onClose={closeIssuesPanel}
        ariaLabel="Canvas issues panel"
        header={headerNode}
        footer={footerNode}
        width={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Inline error from a failed AI fix. Distinct visual from
              per-issue cards so the operator knows it's about the fix
              attempt, not a new validator finding. */}
          {issuesFixError && (
            <div role="alert" style={{
              border: '1px solid var(--v2-danger-soft)',
              background: 'var(--v2-danger-soft)',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 12,
              color: 'var(--v2-danger)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              lineHeight: 1.45,
            }}>
              <span aria-hidden="true">⚠</span>
              <span>{issuesFixError}</span>
            </div>
          )}

          {issues.length === 0 ? (
            <div style={{
              padding: 24, textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: 13,
              border: '1px dashed var(--v2-border-default)', borderRadius: 8,
            }}>
              No issues to display. (Canvas may have changed since the panel opened — close and re-open to re-validate.)
            </div>
          ) : (
            <div style={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingRight: 4,
            }}>
              {issues.map((issue) => {
                const tone = severityTone(issue.severity);
                const node = issue.nodeId ? nodesById.get(issue.nodeId) : null;
                const nodeLabel = node?.label || node?.type || issue.nodeId;
                return (
                  <div
                    key={issue.id || `${issue.code}-${issue.nodeId || ''}-${issue.message}`}
                    style={{
                      border: `1px solid ${tone.border}`,
                      background: tone.bg,
                      borderRadius: 8,
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                      <span style={{
                        color: tone.text,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                      }}>{tone.label}</span>
                      {issue.code && (
                        <span style={{
                          color: 'var(--v2-text-muted)',
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                          fontSize: 10.5,
                        }}>{issue.code}</span>
                      )}
                      {issue.nodeId && (
                        <span style={{ color: 'var(--v2-text-secondary)', fontSize: 11.5, marginLeft: 'auto' }}>
                          Node: <strong>{nodeLabel}</strong>
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--v2-text-primary)', lineHeight: 1.5 }}>
                      {issue.message}
                    </div>
                    {issue.suggest && (
                      <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', fontStyle: 'italic' }}>
                        Suggestion: {issue.suggest}
                      </div>
                    )}
                    {issue.nodeId && (
                      <div style={{ marginTop: 4 }}>
                        <button
                          type="button"
                          onClick={() => focusNode(issue.nodeId)}
                          style={{
                            background: 'var(--v2-action)',
                            color: 'var(--v2-bg-surface)',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 11.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Focus on canvas →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {/* RC-97e2 — Footer note moved to the V2Slideout `footer`
              prop above; renders as a sticky bar at the bottom of
              the panel per Slideout convention. */}
        </div>
      </V2Slideout>
    );
  }

  /**
   * Modal that prompts the operator for the workflow's required inputs
   * before kicking off Execute. The {@code params} array carries one
   * descriptor per input ({@code {name, required, type, description}}) —
   * sourced from either the trigger node's {@code config.inputParameters}
   * or the linked external workflow's {@code input_schema}. Required
   * fields gate Submit; optional fields are allowed empty (and stripped
   * before submission so the backend doesn't see noise).
   */
  function renderExecuteInputModal() {
    if (!executeInputPrompt) return null;
    const { params, values } = executeInputPrompt;
    const requiredParams = params.filter((p) => p.required !== false);
    const allFilled = requiredParams.every(
      (p) => values[p.name] && values[p.name].toString().trim().length > 0,
    );
    const submit = () => {
      // Strip empty optional fields so they don't end up as empty strings
      // in the workflow input payload (Conductor often validates "must
      // not be empty" on optionals, which would defeat the purpose).
      const trimmed = {};
      params.forEach((p) => {
        const v = values[p.name];
        if (v != null && v.toString().trim().length > 0) trimmed[p.name] = v;
      });
      runExecuteWithInput(trimmed);
    };
    return (
      <Modal isOpen title="Workflow Inputs" onClose={() => setExecuteInputPrompt(null)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
            This workflow declares {params.length} input{params.length === 1 ? '' : 's'}
            {requiredParams.length !== params.length
              ? ` (${requiredParams.length} required)`
              : ''}
            . Provide a value for each — they're available downstream as
            <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
              {'${workflow.input.<name>}'}
            </code>
            references in node config / inputMapping.
          </div>
          {params.map((p, idx) => (
            <div key={p.name}>
              <label style={labelStyle}>
                {p.name}
                {p.required !== false && (
                  <span style={{ color: 'var(--v2-action)', marginLeft: 4 }}>*</span>
                )}
                {p.type && p.type !== 'string' && (
                  <span style={{ color: 'var(--v2-text-disabled)', fontSize: 10, marginLeft: 6,
                    fontFamily: 'ui-monospace, monospace' }}>
                    {p.type}
                  </span>
                )}
              </label>
              {p.description && (
                <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)', marginTop: 2, marginBottom: 4 }}>
                  {p.description}
                </div>
              )}
              <input
                value={values[p.name] || ''}
                onChange={(e) => setExecuteInputPrompt((prev) => prev && ({
                  ...prev,
                  values: { ...prev.values, [p.name]: e.target.value },
                }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && allFilled) submit();
                }}
                style={{
                  ...inputStyle,
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: 12,
                }}
                placeholder={p.description || `Value for ${p.name}`}
                autoFocus={idx === 0}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setExecuteInputPrompt(null)} style={btnBase}>
              Cancel
            </button>
            <button
              onClick={submit}
              style={{ ...btnPrimary, opacity: allFilled ? 1 : 0.5, cursor: allFilled ? 'pointer' : 'not-allowed' }}
              disabled={!allFilled}
            >
              Run Workflow
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  function renderTemplateModal() {
    return (
      <Modal isOpen={showTemplateModal} title="Load Template" onClose={() => { setShowTemplateModal(false); setPlaybookCategoryFilter(null); setPlaybookFilter(''); }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <input
              type="text"
              placeholder="Filter templates..."
              value={playbookFilter}
              onChange={(e) => { setPlaybookFilter(e.target.value); setPlaybookCategoryFilter(null); }}
              style={inputStyle}
            />
          </div>

          {/* Category quick-filter chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              onClick={() => setPlaybookCategoryFilter(null)}
              style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                border: !playbookCategoryFilter ? '1px solid var(--v2-action)' : '1px solid var(--v2-border-default)',
                background: !playbookCategoryFilter ? 'var(--v2-action-soft)' : 'var(--v2-bg-surface)',
                color: !playbookCategoryFilter ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                cursor: 'pointer',
              }}
            >
              All
            </button>
            {PLAYBOOK_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setPlaybookCategoryFilter(cat.id)}
                style={{
                  padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                  border: playbookCategoryFilter === cat.id ? '1px solid var(--v2-action)' : '1px solid var(--v2-border-default)',
                  background: playbookCategoryFilter === cat.id ? 'var(--v2-action-soft)' : 'var(--v2-bg-surface)',
                  color: playbookCategoryFilter === cat.id ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                  cursor: 'pointer',
                }}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          {PLAYBOOK_CATEGORIES.map((category) => {
            // Apply both category filter and text filter
            if (playbookCategoryFilter && category.id !== playbookCategoryFilter) return null;

            const categoryPlaybooks = SOAR_PLAYBOOKS.filter(
              (p) =>
                p.category === category.id &&
                (playbookFilter === '' ||
                  p.name.toLowerCase().includes(playbookFilter.toLowerCase()) ||
                  p.description.toLowerCase().includes(playbookFilter.toLowerCase()))
            );

            if (categoryPlaybooks.length === 0) return null;

            return (
              <div key={category.id}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--v2-text-primary)' }}>
                  {category.name}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {categoryPlaybooks.map((playbook) => (
                    <div key={playbook.id} style={cardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                        <div>
                          <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px 0', color: 'var(--v2-text-primary)' }}>
                            {playbook.name}
                          </h4>
                          <p style={{ fontSize: 12, color: 'var(--v2-text-secondary)', margin: 0, marginBottom: 6 }}>
                            {playbook.description}
                          </p>
                        </div>
                        <Badge text={playbook.severity} variant={playbook.severity} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 8 }}>
                        {playbook.nodes?.length || 0} nodes · {playbook.requiredConnectors?.join(', ') || 'No connectors'}
                      </div>
                      <button
                        onClick={() => handleLoadTemplate(playbook)}
                        style={btnPrimary}
                      >
                        Use Template
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    );
  }

  return (
    <CanvasErrorBoundary>
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: 'var(--v2-bg-elevated)' }}>
      {/* ===== TOP BAR =====
           #133: flex-wrap + row-gap so buttons wrap to a second line on narrow
           viewports instead of triggering a page-level horizontal scrollbar. */}
      <div style={{
        borderBottom: '1px solid var(--v2-border-default)',
        background: 'var(--v2-bg-surface)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        rowGap: 10,
        flexWrap: 'wrap',
        minWidth: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['canvas', 'list', 'orchestration'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  ...miniBtn,
                  background: viewMode === mode ? 'var(--v2-text-primary)' : 'var(--v2-bg-surface)',
                  color: viewMode === mode ? 'var(--v2-bg-surface)' : 'var(--v2-text-secondary)',
                  fontWeight: viewMode === mode ? 600 : 400,
                }}
                title={`Switch to ${mode} view`}
              >
                {mode === 'canvas' && <Eye size={14} />}
                {mode === 'list' && <Layers size={14} />}
                {mode === 'orchestration' && <Activity size={14} />}
              </button>
            ))}
          </div>

          {/* Auto-Create — always-present CTA to draft a workflow from process
              files (documents / spreadsheets) via the AI Workflow Builder.
              Sits left of the workflow selector, independent of any selection.
              Gated by workflow.create (same permission as Save). */}
          {canCreateWorkflow && (
            <button
              onClick={openAutoCreateFromFiles}
              disabled={autoCreateFromFilesBusy}
              title="Auto-Create — draft a workflow from documents or spreadsheets"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: autoCreateFromFilesBusy ? 'not-allowed' : 'pointer',
                background: 'var(--v2-action)', color: 'var(--v2-bg-surface)',
                border: '1px solid var(--v2-action)', whiteSpace: 'nowrap',
                opacity: autoCreateFromFilesBusy ? 0.6 : 1,
              }}
            >
              <Sparkles size={14} /> {autoCreateFromFilesBusy ? 'Drafting…' : 'Auto-Create'}
            </button>
          )}

          {/* Searchable Workflow Dropdown */}
          <div ref={wfDropdownRef} style={{ position: 'relative', minWidth: 280 }}>
            <div
              onClick={() => setWfDropdownOpen((o) => !o)}
              style={{
                padding: '7px 10px',
                borderRadius: 6,
                border: `1px solid ${wfDropdownOpen ? 'var(--v2-action)' : 'var(--v2-border-default)'}`,
                fontSize: 13,
                fontFamily: 'inherit',
                background: 'var(--v2-bg-surface)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 34,
              }}
            >
              {selectedWorkflowId ? (
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(() => { const sw = workflows.find((w) => w.id === selectedWorkflowId); return sw ? `${sw.name} v${sw.version || 1}` : 'Select...'; })()}
                </span>
              ) : (
                <span style={{ flex: 1, color: 'var(--v2-text-disabled)' }}>Select a workflow...</span>
              )}
              <ChevronDown size={14} style={{ color: 'var(--v2-text-muted)', flexShrink: 0, transform: wfDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </div>

            {wfDropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                marginTop: 4, background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)',
                borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 320, display: 'flex', flexDirection: 'column',
              }}>
                {/* Search input */}
                <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid var(--v2-bg-muted)' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--v2-text-disabled)' }} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search workflows..."
                      value={wfSearchTerm}
                      onChange={(e) => setWfSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%', padding: '7px 8px 7px 28px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                    {wfSearchTerm && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setWfSearchTerm(''); }}
                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--v2-text-disabled)' }}
                      ><X size={12} /></button>
                    )}
                  </div>
                </div>

                {/* Workflow list */}
                <div style={{ overflowY: 'auto', maxHeight: 260, padding: '4px 0' }}>
                  {filteredWorkflows.length === 0 ? (
                    <div style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--v2-text-disabled)', fontSize: 12 }}>
                      {wfSearchTerm ? `No workflows matching "${wfSearchTerm}"` : 'No workflows found'}
                    </div>
                  ) : (
                    filteredWorkflows.map((w) => (
                      <div
                        key={w.id}
                        onClick={async () => {
                          if (w.id === selectedWorkflowId) { setWfDropdownOpen(false); return; }
                          if (!(await confirmUnsavedChanges())) return;
                          setSelectedWorkflowId(w.id); setWfDropdownOpen(false); setWfSearchTerm('');
                        }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                          background: w.id === selectedWorkflowId ? 'var(--v2-action-soft)' : 'transparent',
                          borderLeft: w.id === selectedWorkflowId ? '3px solid var(--v2-action)' : '3px solid transparent',
                        }}
                        onMouseEnter={(e) => { if (w.id !== selectedWorkflowId) e.currentTarget.style.background = 'var(--v2-bg-muted)'; }}
                        onMouseLeave={(e) => { if (w.id !== selectedWorkflowId) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: w.id === selectedWorkflowId ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--v2-text-primary)' }}>
                            {w.name || 'Untitled'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 1 }}>
                            v{w.version || 1} · {w.status}{w.isSystem ? ' · ★ system' : ''}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 500, flexShrink: 0,
                          background: w.status === 'active' ? 'var(--v2-success-soft)' : w.status === 'published' ? 'var(--v2-action-soft)' : 'var(--v2-bg-muted)',
                          color: w.status === 'active' ? 'var(--v2-success)' : w.status === 'published' ? 'var(--v2-action)' : 'var(--v2-text-secondary)',
                        }}>{w.status}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer with count */}
                <div style={{ padding: '6px 12px', borderTop: '1px solid var(--v2-bg-muted)', fontSize: 11, color: 'var(--v2-text-disabled)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{filteredWorkflows.length} of {nonArchivedWorkflows.length} workflows</span>
                  {selectedWorkflowId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedWorkflowId(null); setWfDropdownOpen(false); setWfSearchTerm(''); }}
                      style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--v2-action)', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                    >Clear selection</button>
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedWorkflow && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, borderLeft: '1px solid var(--v2-border-default)' }}>
              {/* Badge colour comes from statusColors[selectedWorkflow.status]
                  in the Badge component — variant only switches solid vs.
                  outline. Pass solid (the default) so the lifecycle palette
                  (draft/active/paused/archived) renders consistently. */}
              <Badge text={selectedWorkflow.status} />
              {/*
                Lifecycle toggle — Activate / Pause / Resume. Sits next to
                the status badge so the action affects the same field the
                user is reading. Hidden for archived workflows (the Archive
                button next to Delete handles that path) and for system
                workflows that read-only-ly project from manifests.
                See handleActivateWorkflow / handlePauseWorkflow for the
                semantics; this button is intentionally generic across all
                trigger types — the schedule-side-panel control remains
                for schedule-aware operators who want the additional cache
                clearing.
              */}
              {/* G388 — Hide Activate / Pause / Resume for AutoLet CEGs.
                  Per GOLDEN_CEG_RULES §1.4, a CEG never executes directly —
                  only the parent AutoLet does. Letting the operator activate
                  the workflow row creates a parallel scheduler entry that
                  can fire independently of the agent. */}
              {selectedWorkflow.status !== 'archived' && !selectedWorkflow.isSystem && !isAutoLetCegReadOnly && (
                selectedWorkflow.status === 'active' ? (
                  <button
                    onClick={handlePauseWorkflow}
                    title="Pause workflow — blocks new executions, leaves running ones alone"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 4,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      background: 'var(--v2-warning-soft)', border: '1px solid var(--v2-warning-soft)', color: 'var(--v2-warning)',
                    }}
                  >
                    Pause
                  </button>
                ) : (
                  <button
                    onClick={handleActivateWorkflow}
                    title={selectedWorkflow.status === 'paused'
                      ? 'Resume workflow — re-enables executions'
                      : 'Activate workflow — promote from Draft so executions can run'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 4,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)', color: 'var(--v2-success)',
                    }}
                  >
                    {selectedWorkflow.status === 'paused' ? 'Resume' : 'Activate'}
                  </button>
                )
              )}
              <span style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
                {canvas.nodes.length} nodes · {canvas.connections.length} connections
              </span>
              {/* System Workflow toggle — SuperAdmin only */}
              {isSuperAdmin() && (
                <button
                  onClick={async () => {
                    const next = !selectedWorkflow.isSystem;
                    const confirmed = next
                      ? window.confirm(`Promote "${selectedWorkflow.name}" to a System Workflow?\n\nSystem workflows are platform-managed and visible across all orgs/tenants.`)
                      : window.confirm(`Demote "${selectedWorkflow.name}" from System Workflow status?`);
                    if (!confirmed) return;
                    try {
                      const res = await intelligenceToggleSystemWorkflow(selectedWorkflow.id, next);
                      if (res?.ok !== false) {
                        addToast(next ? 'Promoted to System Workflow' : 'Demoted from System Workflow', 'success');
                        // V132 — archived:'all' keeps both tabs of the list view fresh
                        loadWorkflows({ orgId, tenantId, archived: 'all' });
                      } else {
                        addToast('Failed: ' + (res?.error || 'Unknown error'), 'error');
                      }
                    } catch (e) { addToast('Error: ' + e.message, 'error'); }
                  }}
                  title={selectedWorkflow.isSystem ? 'Click to demote from System Workflow' : 'Click to promote to System Workflow'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                    borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: selectedWorkflow.isSystem ? '1px solid var(--v2-action)' : '1px dashed var(--v2-text-disabled)',
                    background: selectedWorkflow.isSystem ? 'var(--v2-action-soft)' : 'transparent',
                    color: selectedWorkflow.isSystem ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (!selectedWorkflow.isSystem) { e.currentTarget.style.borderColor = 'var(--v2-action)'; e.currentTarget.style.color = 'var(--v2-action)'; } }}
                  onMouseLeave={(e) => { if (!selectedWorkflow.isSystem) { e.currentTarget.style.borderColor = 'var(--v2-text-disabled)'; e.currentTarget.style.color = 'var(--v2-text-muted)'; } }}
                >
                  {selectedWorkflow.isSystem ? '★ System' : '☆ System'}
                </button>
              )}
              {/* Non-admin system indicator (read-only) */}
              {!isSuperAdmin() && selectedWorkflow.isSystem && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px',
                  borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: 'var(--v2-action-soft)', color: 'var(--v2-action)', border: '1px solid var(--v2-action)',
                }}>★ System</span>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 8, minWidth: 0 }}>
          <button onClick={handleValidate} style={miniBtn} title="Validate workflow">
            {canvas.dagValidation.valid ? (
              <>
                <CheckCircle size={14} style={{ color: 'var(--v2-text-secondary)' }} />
              </>
            ) : (
              <>
                <AlertTriangle size={14} style={{ color: 'var(--v2-danger)' }} />
              </>
            )}
          </button>

          {/* V169 / Wave 3 — Save / Save As / Protect are mutations
              (Save and Protect change definition; Save As creates a
              standalone copy that would orphan from the AutoLet's
              symbiotic relationship). All three are hidden in the
              AutoLet CEG read-only mode. Execute stays — it's runtime,
              not edit. See GOLDEN_CEG_RULES.md §1, §9.2. */}
          {/* G240 — Save hidden in any full lock (Act-phase Lets + AutoLet CEGs).
            * Save As stays available so the operator can fork an Act-phase
            * canvas into a fresh draft if needed. */}
          {!isCanvasFullyLocked && (
            <button
              onClick={() => { if (!codeTabError) canvas.save(); }}
              disabled={!!codeTabError}
              style={{
                ...btnBase,
                ...(!selectedWorkflowId || codeTabError ? { color: 'var(--v2-text-disabled)', cursor: 'not-allowed', borderColor: 'var(--v2-border-default)', opacity: codeTabError ? 0.55 : 1 } : {}),
              }}
              title={codeTabError
                ? `Fix JSON errors in the Code tab first — ${codeTabError}`
                : selectedWorkflowId ? 'Save workflow' : 'No workflow selected — use Save As'}
            >
              <Save size={14} style={{ marginRight: 4 }} />
              Save
            </button>
          )}

          {!isAutoLetCegReadOnly && (
            <button
              onClick={() => {
                setSaveAsName(selectedWorkflow?.name ? selectedWorkflow.name + ' (copy)' : '');
                setShowSaveAs(true);
              }}
              style={btnBase}
              title="Save workflow with a new name"
            >
              <Copy size={14} style={{ marginRight: 4 }} />
              Save As
            </button>
          )}

          {/* RC-118 — Undo / Redo. canvas.canUndo / canvas.canRedo drive
              the enabled state; both buttons are always rendered so the
              affordance stays where the operator's muscle memory expects
              it, just disabled when there's nothing to undo/redo. Cmd+Z /
              Cmd+Shift+Z keyboard handler is installed in the effect
              below this toolbar — clicking the buttons and hitting the
              shortcuts hit the same hook. */}
          {!isAutoLetCegReadOnly && (
            <>
              {/* RC-118b — Tooltip surfaces the next undoable / redoable
                  operation when the canvas can derive a label (e.g.
                  "Undo Add 'Slack notify' (⌘Z)"). Falls back to bare
                  "Undo (⌘Z)" for baseline / replay-restore entries
                  where no label could be derived. */}
              <button
                onClick={() => { try { canvas.undo(); } catch {/* noop */} }}
                disabled={!canvas.canUndo}
                style={{ ...btnBase, opacity: canvas.canUndo ? 1 : 0.4, cursor: canvas.canUndo ? 'pointer' : 'not-allowed' }}
                title={canvas.canUndo
                  ? (canvas.undoLabel ? `Undo ${canvas.undoLabel} (⌘Z)` : 'Undo (⌘Z)')
                  : 'Nothing to undo'}
              >
                <Undo2 size={14} style={{ marginRight: 4 }} />
                Undo
              </button>
              <button
                onClick={() => { try { canvas.redo(); } catch {/* noop */} }}
                disabled={!canvas.canRedo}
                style={{ ...btnBase, opacity: canvas.canRedo ? 1 : 0.4, cursor: canvas.canRedo ? 'pointer' : 'not-allowed' }}
                title={canvas.canRedo
                  ? (canvas.redoLabel ? `Redo ${canvas.redoLabel} (⌘⇧Z)` : 'Redo (⌘⇧Z)')
                  : 'Nothing to redo'}
              >
                <Redo2 size={14} style={{ marginRight: 4 }} />
                Redo
              </button>
            </>
          )}

          {/* RC-117 Phase 1 — Versions opens the read-only history
              slideout. Available only when the canvas has a persisted
              workflow id (a fresh unsaved canvas has no history yet).
              Same display gate the other workflow-scoped buttons use.
              Phase 2 (RC-117b) adds diff + rollback inside the same
              slideout component. */}
          {selectedWorkflow?.id && (
            <button
              onClick={() => setShowVersionsOpen(true)}
              style={btnBase}
              title="View this workflow's version history"
            >
              <History size={14} style={{ marginRight: 4 }} />
              Versions
            </button>
          )}

          {/* #133: Top-bar "Layout" button removed — duplicates the Layout
              button in the in-canvas zoom panel and was contributing to the
              top-bar overflow. `handleAutoLayout` is still kept and wired
              into the zoom-panel button so no functionality is lost. */}

          {/* Governance Protection button — toggles enforcement node insertion.
              This is the SINGLE place protection turns on or off; Execute no
              longer auto-protects, templates load as-authored. RED (gradient)
              = unprotected and ready to add governance; GREEN with checkmark
              = protected, click again to remove. */}
          {!isAutoLetCegReadOnly && (
            <button
              onClick={handleProtectWorkflow}
              style={{
                ...btnBase,
                ...(isWorkflowProtected()
                  ? { color: 'var(--v2-success)', borderColor: 'var(--v2-success-soft)', background: 'var(--v2-success-soft)' }
                  : (canvas.nodes || []).length > 0
                    ? { color: 'var(--v2-bg-surface)', background: 'linear-gradient(135deg, var(--v2-action), var(--v2-action-strong))', borderColor: 'var(--v2-action)' }
                    : { color: 'var(--v2-text-disabled)', borderColor: 'var(--v2-border-default)' }),
              }}
              title={isWorkflowProtected()
                ? 'Click to remove governance nodes — DPG scans, policy gates, and audit trail will be stripped from the canvas'
                : 'Click to add DPG scans, policy gates, and audit trail nodes that protect this workflow'}
            >
              <Shield size={14} style={{ marginRight: 4 }} />
              {isWorkflowProtected() ? '✓ Protected' : 'Protect'}
            </button>
          )}

          <button
            onClick={handleExecute}
            style={{
              ...btnPrimary,
              ...(!selectedWorkflowId || isExecuting || codeTabError ? { opacity: 0.5, cursor: isExecuting ? 'wait' : 'not-allowed' } : {}),
            }}
            disabled={!selectedWorkflowId || isExecuting || !!codeTabError}
            title={codeTabError
              ? `Fix JSON errors in the Code tab first — ${codeTabError}`
              : !selectedWorkflowId ? 'Save workflow first'
              : isExecuting ? 'Workflow running...'
              : 'Execute workflow'}
          >
            {isExecuting ? (
              <Loader size={14} style={{ marginRight: 4, animation: 'spin 1s linear infinite' }} />
            ) : (
              <Play size={14} style={{ marginRight: 4 }} />
            )}
            {isExecuting ? 'Running...' : 'Execute'}
          </button>

          {/* Audit & Runs — opens the unified Workflow Audit page (under
              Governance) pre-filtered to the current workflow. The page
              shows the run table for both archetypes (Native + External
              GEL Protected) with a per-run drill-in to the 7-stage timeline.

              Why this and not /workflows/:id: that detail route is gated by
              WorkflowAccessService (Teams assignments) and returns 403 for
              users who can author workflows but aren't on the access list.
              The audit page is gated only by Tenant Admin / RoleGuard so
              the action is reachable from the canvas without a 403. */}
          <button
            onClick={() => {
              if (selectedWorkflowId) {
                navigate(`/workflow-audit?workflowId=${selectedWorkflowId}`);
              }
            }}
            disabled={!selectedWorkflowId}
            style={{
              ...btnBase,
              color: selectedWorkflowId ? 'var(--v2-accent-cyan)' : 'var(--v2-text-disabled)',
              borderColor: selectedWorkflowId ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-border-default)',
              background: selectedWorkflowId ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-bg-surface)',
              ...(selectedWorkflowId ? {} : { cursor: 'not-allowed' }),
            }}
            title={selectedWorkflowId
              ? 'Open the Workflow Audit page filtered to this workflow’s runs'
              : 'Save the workflow first'}
          >
            <Activity size={14} style={{ marginRight: 4 }} />
            Audit & Runs
          </button>

          {hasExecutionState && !isExecuting && (
            <button
              onClick={handleClearExecution}
              style={{ ...btnBase, color: 'var(--v2-warning)', borderColor: 'var(--v2-warning-soft)', background: 'var(--v2-warning-soft)' }}
              title="Clear execution results and return to editable design mode"
            >
              <RotateCw size={14} style={{ marginRight: 4 }} />
              Edit Mode
            </button>
          )}

          {/* Demo Mode toggle — launches the 7-step governance demo sequence */}
          <button
            onClick={() => {
              setDemoMode(!demoMode);
              if (!demoMode) {
                // Reset demo state when entering demo mode
                setEnforcementEvents([]);
                setComplianceEvents([]);
                setDemoTokens([]);
                setDemoCertificate(null);
                setDemoExecutionDuration(null);
              }
            }}
            style={{
              ...btnBase,
              ...(demoMode
                ? { background: 'linear-gradient(135deg, var(--v2-action), var(--v2-action-strong))', color: 'var(--v2-bg-surface)', borderColor: 'var(--v2-action)' }
                : { color: 'var(--v2-action-strong)', borderColor: 'var(--v2-border-default)', background: 'var(--v2-action-soft)' }),
            }}
            title={demoMode ? 'Exit demo mode' : 'Launch 15-minute governance demo'}
          >
            <Sparkles size={14} style={{ marginRight: 4 }} />
            {demoMode ? 'Exit Demo' : 'Demo Mode'}
          </button>

          <button onClick={async () => { if (await confirmUnsavedChanges()) setShowNewWorkflow(true); }} style={btnBase} title="Create new workflow">
            <Plus size={14} />
          </button>

          {/* G388 — Hide Delete + Archive for AutoLet CEGs and SysLet canvases.
              Backend already refuses these with IMMUTABLE_KIND but the UI
              shouldn't tempt the operator. autolet_ceg gets deleted via
              the agent-delete cascade (GOLDEN_CEG_RULES §1.1); syslet_canvas
              is platform-managed. autolet_sandbox stays freely deletable. */}
          {selectedWorkflowId && !isAutoLetCegReadOnly && (
            // Delete: promoted from icon-only miniBtn to a labelled btnBase button
            // because users were missing the affordance entirely (the 14px trash
            // sandwiched between Save/Save As/Protect/Execute/Demo Mode reads as
            // decorative). Disabled state still uses the same icon but in a
            // muted color + helper toast on click — keeps the affordance
            // visible so people know delete is gated by execution history.
            <button
              onClick={() => {
                if (!canDeleteWorkflow) {
                  addToast('Cannot delete a workflow that has been executed. Archive it instead.', 'warn');
                } else {
                  // Pre-fetch linked skills to show in the confirmation dialog
                  if (selectedWorkflowId) {
                    getWorkflowLinkedSkills(selectedWorkflowId).then((res) => {
                      setDeleteLinkedSkills(res?.ok ? (res.data || []) : []);
                    }).catch(() => setDeleteLinkedSkills([]));
                  }
                  setShowDeleteConfirm(true);
                }
              }}
              style={{
                ...btnBase,
                gap: 6,
                ...(canDeleteWorkflow
                  ? { color: 'var(--v2-danger)', borderColor: 'var(--v2-danger-soft)', background: 'var(--v2-danger-soft)' }
                  : { color: 'var(--v2-text-disabled)', cursor: 'not-allowed', borderColor: 'var(--v2-border-default)', background: 'var(--v2-bg-elevated)' }),
              }}
              title={canDeleteWorkflow ? 'Delete workflow (never executed)' : 'Cannot delete — workflow has execution history. Archive it instead.'}
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}

          {selectedWorkflowId && selectedWorkflow?.status !== 'archived' && !isAutoLetCegReadOnly && (
            // Archive: matches the labelled-button treatment of Delete so the
            // pair reads as a discoverable lifecycle group rather than two
            // hidden icons. G388 — hidden for autolet_ceg / syslet_canvas.
            <button
              onClick={() => handleArchiveWorkflow(selectedWorkflowId)}
              style={{ ...btnBase, gap: 6, color: 'var(--v2-text-secondary)', borderColor: 'var(--v2-border-default)' }}
              title="Archive workflow — hides from list, preserves history"
            >
              <Archive size={14} />
              Archive
            </button>
          )}

          {selectedWorkflowId && selectedWorkflow?.status === 'archived' && (
            <button
              onClick={() => handleUnarchiveWorkflow(selectedWorkflowId)}
              style={{ ...btnBase, gap: 6, color: 'var(--v2-accent-trust)', borderColor: 'var(--v2-accent-trust-soft)', background: 'var(--v2-accent-trust-soft)' }}
              title="Restore workflow from archive"
            >
              <ArchiveRestore size={14} />
              Unarchive
            </button>
          )}
        </div>
      </div>

      {/* ───── Agent-link banner (forward Agent → Canvas materialisation) ─────
          Renders only when /canvas was opened with ?agentId=<uuid>. The
          [Open in Canvas to Test & Verify] CTA on the agent's ACT-phase
          panel sets that param so the analyst doesn't lose context when
          they jump from the agent record to the sandbox canvas.
          User can dismiss the banner; we don't persist the dismissal —
          a hard reload brings it back, which is the desired behaviour
          for a navigation-state cue. */}
      {/* RC-54 — Tombstone banner. Mutually exclusive with the
          AgentCanvasLinkBanner below (linkedAgent is null when
          linkedAgentRetired is true, by construction in the fetch
          effect). Renders when the selected workflow has an agentId
          but /api/v1/agents/{id} returned 404 (soft-deleted, retired,
          or out-of-scope per RC-52's now-converged behavior). Gives
          the operator a clear "Parent agent retired" explanation
          plus the agentId for forensic correlation, instead of
          leaving them to wonder why the back-to-agent chip is
          missing. Dismissable like the normal banner. */}
      {linkedAgentRetired && linkedAgentId && !linkedAgentDismissed && (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 14px',
            margin: '0 16px 12px 16px',
            border: '1px solid var(--v2-warning-soft)',
            background: 'var(--v2-warning-soft)',
            borderRadius: 8,
            fontSize: 12, lineHeight: 1.5, color: 'var(--v2-warning)',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }}>⚠</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 2, color: 'var(--v2-warning)' }}>
              Parent agent is no longer accessible
            </div>
            <div>
              This CEG was created for an agent that has since been retired,
              soft-deleted, or moved out of your scope. The canvas is
              read-only and shown for audit / forensic purposes. The
              parent agent UUID is{' '}
              <code style={{
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: 11,
                background: 'var(--v2-warning-soft)', padding: '1px 5px', borderRadius: 3,
                color: 'var(--v2-warning)',
              }}>{linkedAgentId}</code>.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setLinkedAgentDismissed(true)}
            title="Dismiss"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 14, color: 'var(--v2-warning)', lineHeight: 1, padding: '2px 4px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {linkedAgent && !linkedAgentDismissed && (
        <AgentCanvasLinkBanner
          agent={linkedAgent}
          workflowKind={workflowKind}
          onClose={() => setLinkedAgentDismissed(true)}
          /* G236 — Show Sync only for Let agents (the ones whose canvas
           * we manage via skill-pipeline materialisation). For AutoLets
           * / ExteLets / SysLets the canvas is sourced elsewhere
           * (runbook / external endpoint / platform), so a sync from
           * agent.skills would be either disallowed (AutoLet CEG is
           * immutable) or meaningless. Detection mirrors the auto-create
           * archetype logic above so the two stay in lockstep. */
          {...(() => {
            /* G240 — Sync from agent skills is hidden in Act phase (locked
             * canvas). Available in Build + Decide for Let agents only.
             * Surface the phase-lock state on the banner so operators see
             * WHY controls below are disabled before clicking them. */
            const lockMsg = isLetFullLock
              ? (linkedAgentPublished
                  ? `Act phase — ${linkedAgent?.name || 'agent'} is published. Unpublish, then regress to Decide to edit.`
                  : `Act phase — regress to Decide to edit. (${linkedAgent?.name || 'agent'} is not published yet.)`)
              : isLetStructuralLock
              ? `Decide phase — autonomy/inference editable; add/remove skills blocked. Regress to Build for structural changes.`
              : '';
            const lockKind = isLetFullLock ? 'full' : isLetStructuralLock ? 'partial' : '';
            if (linkedLetArchetype !== 'Let') {
              return { phaseLockMessage: '', phaseLockKind: '' };
            }
            if (isLetFullLock) {
              /* Act = fully locked. Sync/advance stay hidden, but REGRESS must be offered —
               * it is the exact remedy this banner's message prescribes, and it was
               * previously unreachable from any surface (see handleRegressPhaseFromBanner). */
              return {
                phaseLockMessage: lockMsg,
                phaseLockKind: lockKind,
                regressing: regressingPhase,
                onRegressPhase: handleRegressPhaseFromBanner,
              };
            }
            return {
              syncing: syncingFromAgent,
              onSync: handleSyncFromAgent,
              phaseLockMessage: lockMsg,
              phaseLockKind: lockKind,
              /* Decide → Regress to Build for structural edits. */
              regressing: regressingPhase,
              onRegressPhase: handleRegressPhaseFromBanner,
              /* G258 — Advance phase from canvas. Mirror of the agent
               * detail page's gate-advance button; same backend call
               * (POST /agents/{id}/advance-phase). On success we
               * re-pull the agent (so the phase chip + lock state
               * refresh) and re-pull the policy bindings (which can
               * shift between Build / Decide / Act per §1). */
              advancing: advancingPhase,
              onAdvancePhase: handleAdvancePhase,
              /* G264 — Clickable execution-mode chip. */
              cyclingExecutionMode,
              onCycleExecutionMode: handleCycleExecutionMode,
            };
          })()}
        />
      )}

      {/* ===== 3-PANEL LAYOUT ===== */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ===== LEFT PANEL: Design / Execution Split =====
            When collapsed, renders a thin 40px vertical strip with a single
            expand chevron so the center canvas gets the full horizontal width.

            V169 / Wave 3 — read-only AutoLet CEGs force-collapse the
            palette to a 40px strip with NO expand chevron (since
            expanding it would expose drag-drop affordances that all
            error). The expand button below is hidden in that case;
            the rest of the collapsed-panel chrome (mode toggle, etc.)
            stays visible because it's pure read-only. */}
        {(leftPanelCollapsed || isAutoLetCegReadOnly) ? (
          <div style={{
            width: 40,
            background: 'var(--v2-bg-surface)',
            borderRight: '1px solid var(--v2-border-default)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '10px 0',
            gap: 8,
          }}>
            {/* Expand chevron — hidden in AutoLet CEG read-only mode
                because the palette would only show drag-drop targets
                that all error. The user pans / zooms / inspects only. */}
            {!isAutoLetCegReadOnly && (
              <button
                onClick={() => setLeftPanelCollapsed(false)}
                title="Expand panel"
                style={{
                  width: 28, height: 28, padding: 0,
                  border: '1px solid var(--v2-border-default)',
                  borderRadius: 6,
                  background: 'var(--v2-bg-surface)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--v2-text-secondary)',
                }}
              >
                <ChevronRight size={14} />
              </button>
            )}
            <div style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 11,
              color: 'var(--v2-text-muted)',
              marginTop: 12,
              fontWeight: 500,
              letterSpacing: 0.3,
              userSelect: 'none',
            }}>
              {leftPanelMode === 'execution' ? 'Execution' : 'Design'}
            </div>
          </div>
        ) : (
        <div style={{
          width: 300,
          background: 'var(--v2-bg-surface)',
          borderRight: '1px solid var(--v2-border-default)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Panel Mode Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid var(--v2-border-default)',
            background: 'var(--v2-bg-elevated)',
          }}>
            {[
              { id: 'design', label: 'Design', icon: PenTool },
              { id: 'execution', label: 'Execution', icon: Activity },
            ].map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                onClick={() => {
                  setLeftPanelMode(id);
                  if (id === 'design') {
                    // Clear any selected run so highlight state drops.
                    setSelectedRunId(null);
                    /* G396 — Restore the original Design DAG.
                     *
                     * Preferred: replay from the in-memory snapshot captured in
                     * handleSelectRun. If that's gone (page refresh after viewing
                     * a run; OR the workflow-load effect cleared it as part of a
                     * fingerprint mismatch), fall back to the workflow's stored
                     * definition. Previously this branch was a no-op when the
                     * ref was null, leaving the user staring at an empty or
                     * stale execution canvas (the user-reported "blank canvas"). */
                    if (designSnapshotRef.current) {
                      canvas.setNodes(designSnapshotRef.current.nodes);
                      canvas.setConnections(designSnapshotRef.current.connections);
                    } else if (selectedWorkflow?.definition) {
                      canvas.loadCanvasData(selectedWorkflow.definition, {
                        autoLayoutOnLoad: true,
                        zoomToFitOnLoad: true,
                      });
                    }
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => { canvas.zoomToFit(); });
                    });
                  }
                }}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  background: leftPanelMode === id ? 'var(--v2-bg-surface)' : 'transparent',
                  border: 'none',
                  borderBottom: leftPanelMode === id ? '2px solid var(--v2-text-primary)' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: leftPanelMode === id ? 700 : 400,
                  color: leftPanelMode === id ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <TabIcon size={13} />
                {label}
              </button>
            ))}
            {/* Collapse toggle — claims horizontal canvas real estate for large workflows */}
            <button
              onClick={() => setLeftPanelCollapsed(true)}
              title="Collapse panel"
              style={{
                width: 32,
                padding: '10px 0',
                background: 'transparent',
                border: 'none',
                borderBottom: '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--v2-text-muted)',
              }}
            >
              <PanelLeftClose size={14} />
            </button>
          </div>

          {/* DESIGN MODE */}
          {leftPanelMode === 'design' && (
            <>
              {/* Search */}
              <div style={{ padding: '12px', borderBottom: '1px solid var(--v2-border-default)' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--v2-text-disabled)',
                  }} />
                  <input
                    type="text"
                    placeholder="Search nodes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      ...inputStyle,
                      paddingLeft: 32,
                      padding: '8px 10px 8px 32px',
                    }}
                  />
                </div>
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {searchQuery ? (
                  <SearchNodeResults query={searchQuery} onAddNode={handleAddNode} />
                ) : (
                  <>
                    {/* Gap #2 — inline "start a workflow" affordance.
                        When no workflow is selected, every palette node
                        (Agents & Skills included) renders disabled via the
                        `disabled={!selectedWorkflowId}` prop below. Pre-fix
                        the operator got a wall of greyed tiles with no
                        explanation or next step. This CTA surfaces the
                        required first action (create a workflow) and routes
                        to the existing New-Workflow slideout — the same
                        entry point as the toolbar "＋" and header "New".
                        Purely additive: shows ONLY when selectedWorkflowId
                        is null, so the workflow-selected path is unchanged. */}
                    {!selectedWorkflowId && (
                      <div style={{
                        margin: '0 8px 12px',
                        padding: '12px',
                        background: 'var(--v2-bg-muted)',
                        border: '1px solid var(--v2-border-default)',
                        borderRadius: 8,
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
                          Start a workflow to add nodes
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', lineHeight: 1.45, marginBottom: 10 }}>
                          Agents, skills, and every node below attach to a workflow. Create one to begin — then add nodes from this palette or the “＋” on the canvas.
                        </div>
                        <button
                          type="button"
                          onClick={async () => { if (await confirmUnsavedChanges()) setShowNewWorkflow(true); }}
                          style={{ ...btnPrimary, width: '100%', justifyContent: 'center', fontSize: 12 }}
                        >
                          ＋ New workflow
                        </button>
                      </div>
                    )}
                    {/* Node Palette by Category */}
                    <div style={{ padding: '0 8px', marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', padding: '8px 4px', textTransform: 'uppercase' }}>
                        Node Palette
                      </div>
                      {NODE_CATEGORIES.map((category) => (
                        <PaletteCategory
                          key={category.id}
                          category={category}
                          onAddNode={handleAddNode}
                          disabled={!selectedWorkflowId}
                          expanded={paletteExpanded[category.id]}
                          onToggle={() => {
                            const willCollapse = !!paletteExpanded[category.id];
                            // RC-CanvasV3-PaletteCanvasSync — when the
                            // operator explicitly collapses a category,
                            // remember it so the auto-expand effect doesn't
                            // immediately re-open it on the next selection
                            // change in that same category. Cleared when
                            // selection moves to a different category.
                            setPaletteUserCollapsedSince((prev) => {
                              const next = new Set(prev);
                              if (willCollapse) next.add(category.id);
                              else next.delete(category.id);
                              return next;
                            });
                            setPaletteExpanded({
                              ...paletteExpanded,
                              [category.id]: !paletteExpanded[category.id],
                            });
                          }}
                          installedConnectors={category.id === 'connector' ? installedConnectors : null}
                          installedConnectorsMkIndex={category.id === 'connector' ? installedConnectorsMkIndex : null}
                          highlightTypeId={paletteHighlightType}
                        />
                      ))}
                    </div>

                    {/* Playbook Templates */}
                    <div style={{ padding: '0 8px', marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', padding: '8px 4px', textTransform: 'uppercase' }}>
                        Templates
                      </div>
                      <button
                        onClick={() => { setPlaybookFilter(''); setPlaybookCategoryFilter(null); setShowTemplateModal(true); }}
                        style={{
                          ...btnBase,
                          width: '100%',
                          marginTop: 4,
                        }}
                      >
                        <Copy size={13} style={{ marginRight: 6 }} />
                        Load Playbook
                      </button>
                    </div>

                    {/* ── Learn / Getting Started ── */}
                    <div style={{ padding: '0 8px', marginBottom: 16 }}>
                      <div
                        onClick={() => setLearnExpanded(prev => !prev)}
                        style={{
                          fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', padding: '8px 4px',
                          textTransform: 'uppercase', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'space-between', userSelect: 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Eye size={11} /> Learn
                        </span>
                        {learnExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </div>
                      {learnExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {[
                            { label: 'Phishing Triage', desc: 'Auto-classify & escalate phishing alerts', cat: 'incident-response' },
                            { label: 'Suspicious Login', desc: 'Detect & respond to anomalous auth events', cat: 'identity-access' },
                            { label: 'Access Review', desc: 'Periodic user access certification workflow', cat: 'identity-access' },
                            { label: 'Compliance Check', desc: 'Automated evidence collection & audit', cat: 'compliance-audit' },
                          ].map((item) => {
                            const playbook = SOAR_PLAYBOOKS.find(p => p.category === item.cat);
                            return (
                              <button
                                key={item.label}
                                onClick={() => {
                                  // Open template modal filtered to this category
                                  setPlaybookFilter('');
                                  setPlaybookCategoryFilter(item.cat);
                                  setShowTemplateModal(true);
                                }}
                                style={{
                                  ...btnBase,
                                  width: '100%',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '8px 10px',
                                  gap: 2,
                                }}
                              >
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--v2-text-primary)' }}>{item.label}</span>
                                <span style={{ fontSize: 10, color: 'var(--v2-text-muted)', lineHeight: '1.3' }}>{item.desc}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Access Control moved to fixed footer below scrollable area */}
                  </>
                )}
              </div>

              {/* ===== BEGIN v5.3.0 — Linked Agent dropdown (rollback: remove this block) ===== */}
              {selectedWorkflowId && (
                <div style={{
                  borderTop: '1px solid var(--v2-border-default)',
                  padding: '8px 12px',
                  background: 'var(--v2-bg-elevated)',
                  flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Bot size={13} style={{ color: 'var(--v2-text-muted)' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', textTransform: 'uppercase' }}>
                      Linked Agent
                    </span>
                  </div>
                  <select
                    value={selectedWorkflow?.agentId || ''}
                    onChange={async (e) => {
                      const newAgentId = e.target.value;
                      try {
                        const result = await updateWorkflow(selectedWorkflowId, {
                          agentId: newAgentId || null,
                          updatedBy: userId,
                        });
                        if (result?.ok) {
                          addToast(newAgentId ? 'Workflow linked to agent' : 'Agent unlinked', 'success');
                          // Reload workflows to refresh derived selectedWorkflow
                          // (V132 — archived:'all' keeps the Archived tab fresh too)
                          loadWorkflows({ orgId, tenantId, archived: 'all' });
                        } else {
                          addToast('Failed to update linked agent', 'error');
                        }
                      } catch (err) {
                        addToast('Error updating linked agent: ' + err.message, 'error');
                      }
                    }}
                    style={{
                      ...inputStyle,
                      fontSize: 11,
                      padding: '5px 8px',
                    }}
                  >
                    <option value="">None (standalone workflow)</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}{a.status !== 'active' ? ` (${a.status})` : ''}</option>
                    ))}
                  </select>
                  {selectedWorkflow?.agentId && (
                    <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 4 }}>
                      This workflow is owned by the linked agent and discoverable via its skill registry.
                    </div>
                  )}
                </div>
              )}
              {/* ===== END v5.3.0 — Linked Agent dropdown ===== */}

              {/* ===== ACCESS CONTROL — Fixed footer, always visible in Design mode ===== */}
              <div style={{
                borderTop: '1px solid var(--v2-border-default)',
                padding: '8px 12px',
                background: 'var(--v2-bg-elevated)',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Shield size={13} style={{ color: 'var(--v2-text-muted)' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', textTransform: 'uppercase' }}>
                      Access Control
                    </span>
                  </div>
                  {selectedWorkflowId && (
                    <a
                      href="/team"
                      style={{ fontSize: 10, color: 'var(--v2-action)', textDecoration: 'none', fontWeight: 600 }}
                      title="Manage in Team → Assignments"
                    >
                      Manage →
                    </a>
                  )}
                </div>
                {!selectedWorkflowId ? (
                  <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>
                    Select a workflow to view access
                  </div>
                ) : workflowAssignments.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)' }}>
                    No users, groups, or agents assigned
                  </div>
                ) : (
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {workflowAssignments.map((a) => {
                      const matchedUser = rbacUsers.find((u) => u.id === a.userId);
                      const matchedGroup = rbacGroups.find((g) => g.id === a.groupId);
                      const matchedAgent = rbacAgents.find((ag) => ag.id === a.agentId);
                      let label = 'Unknown';
                      let TypeIcon = Users;
                      if (a.userId) { label = matchedUser ? (matchedUser.name || matchedUser.email) : `User ${a.userId.substring(0, 8)}...`; TypeIcon = UserPlus; }
                      else if (a.groupId) { label = matchedGroup ? matchedGroup.name : `Group ${a.groupId.substring(0, 8)}...`; TypeIcon = Users; }
                      else if (a.agentId) { label = matchedAgent ? matchedAgent.name : `Agent ${a.agentId.substring(0, 8)}...`; TypeIcon = Bot; }
                      return (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 0', fontSize: 11, borderBottom: '1px solid var(--v2-bg-muted)',
                        }}>
                          <TypeIcon size={12} style={{ color: 'var(--v2-text-muted)' }} />
                          <span style={{ flex: 1, color: 'var(--v2-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {label}
                          </span>
                          <Badge status={a.accessLevel || 'active'} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Schedule Control — show enable/disable when workflow is schedule-triggered */}
              {selectedWorkflow && selectedWorkflow.triggerType === 'schedule' && (
                <div style={{
                  borderTop: '1px solid var(--v2-border-default)', padding: '8px 12px',
                  background: selectedWorkflow.status === 'active' ? 'var(--v2-success-soft)' : 'var(--v2-warning-soft)',
                  flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: selectedWorkflow.status === 'active' ? 'var(--v2-success)' : 'var(--v2-warning)' }}>
                      {selectedWorkflow.status === 'active' ? '● Schedule Active' : '○ Schedule Paused'}
                    </div>
                    {selectedWorkflow.status === 'active' ? (
                      <button
                        onClick={handleDisableSchedule}
                        style={{
                          fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
                          background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)', color: 'var(--v2-danger)',
                          cursor: 'pointer',
                        }}
                        title="Disable schedule — pauses the workflow and cancels any running executions"
                      >
                        Disable Schedule
                      </button>
                    ) : (
                      <button
                        onClick={handleEnableSchedule}
                        style={{
                          fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
                          background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)', color: 'var(--v2-success)',
                          cursor: 'pointer',
                        }}
                        title="Enable schedule — reactivates the workflow for scheduled execution"
                      >
                        Enable Schedule
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Version info */}
              {selectedWorkflow && (
                <div style={{ borderTop: '1px solid var(--v2-border-default)', padding: '6px 12px', fontSize: 11, color: 'var(--v2-text-muted)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Version: {selectedWorkflow.version || 1}{selectedWorkflow.versionLabel ? ` (${selectedWorkflow.versionLabel})` : ''}</span>
                    <span>{selectedWorkflow.status}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* EXECUTION MODE — Left panel shows run list only; detail moved to split-canvas center */}
          {leftPanelMode === 'execution' && (
            <>
              {/* Execution Controls Bar */}
              {isExecuting && activeRunId && (
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--v2-border-default)',
                  background: 'var(--v2-bg-muted)',
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                }}>
                  <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--v2-action)' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-action-strong)', flex: 1 }}>Running...</span>
                  <button
                    onClick={() => handleStopRun(activeRunId)}
                    style={{ ...miniBtn, background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)', padding: '4px 8px' }}
                    title="Stop execution"
                  >
                    <Square size={10} />
                  </button>
                  <button
                    onClick={() => handleTerminateRun(activeRunId)}
                    style={{ ...miniBtn, background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)', padding: '4px 8px' }}
                    title="Terminate execution"
                  >
                    <StopCircle size={10} />
                  </button>
                </div>
              )}

              {/* Run History — v5.3.1 with source filter */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', textTransform: 'uppercase' }}>
                      Execution History
                    </div>
                    {workflowRuns.length > 0 && (() => {
                      const agentCount = workflowRuns.filter((r) => r.triggerType === 'agentlet').length;
                      const manualCount = workflowRuns.filter((r) => !r.triggerType || r.triggerType === 'manual').length;
                      return (
                        <div style={{ display: 'flex', gap: 3 }}>
                          {manualCount > 0 && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 4,
                              background: executionSourceFilter === 'manual' ? 'var(--v2-text-muted)' : 'var(--v2-bg-muted)',
                              color: executionSourceFilter === 'manual' ? 'var(--v2-bg-surface)' : 'var(--v2-text-muted)',
                              cursor: 'pointer', fontWeight: 600,
                            }}
                            onClick={() => setExecutionSourceFilter((f) => f === 'manual' ? 'all' : 'manual')}
                            >
                              Canvas {manualCount}
                            </span>
                          )}
                          {agentCount > 0 && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 4,
                              background: executionSourceFilter === 'agentlet' ? 'var(--v2-action)' : 'var(--v2-action-soft)',
                              color: executionSourceFilter === 'agentlet' ? 'var(--v2-bg-surface)' : 'var(--v2-action)',
                              cursor: 'pointer', fontWeight: 600,
                              border: `1px solid ${executionSourceFilter === 'agentlet' ? 'var(--v2-action)' : 'var(--v2-accent-trust-soft)'}`,
                            }}
                            onClick={() => setExecutionSourceFilter((f) => f === 'agentlet' ? 'all' : 'agentlet')}
                            >
                              Agent {agentCount}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {(() => {
                    // v5.3.1 — Apply source filter
                    const filteredRuns = executionSourceFilter === 'all'
                      ? workflowRuns
                      : workflowRuns.filter((r) => {
                          if (executionSourceFilter === 'manual') return !r.triggerType || r.triggerType === 'manual';
                          return r.triggerType === executionSourceFilter;
                        });
                    return filteredRuns.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--v2-text-disabled)', textAlign: 'center', padding: 16 }}>
                      {workflowRuns.length === 0
                        ? 'No executions yet. Click Execute to start.'
                        : `No ${executionSourceFilter === 'agentlet' ? 'agent' : 'canvas'} executions found.`}
                    </div>
                  ) : (
                    pgExecutionRuns.paginate(filteredRuns).map((run) => {
                      // v5.3.1 — Execution source visual differentiation
                      const isAgentlet = run.triggerType === 'agentlet';
                      const isSchedule = run.triggerType === 'schedule';
                      const isApi = run.triggerType === 'api';
                      const sourceColor = isAgentlet ? 'var(--v2-action)' : isSchedule ? 'var(--v2-warning)' : isApi ? 'var(--v2-accent-cyan)' : 'var(--v2-text-muted)';
                      const sourceBg = isAgentlet ? 'var(--v2-action-soft)' : isSchedule ? 'var(--v2-action-soft)' : isApi ? 'var(--v2-success-soft)' : 'var(--v2-bg-surface)';
                      const sourceBorder = isAgentlet ? 'var(--v2-accent-trust-soft)' : isSchedule ? 'var(--v2-warning)' : isApi ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-border-default)';
                      const sourceLabel = isAgentlet ? 'AGENT' : isSchedule ? 'SCHEDULED' : isApi ? 'API' : 'MANUAL';

                      return (
                      <div
                        key={run.id}
                        onClick={() => handleSelectRun(run.id)}
                        style={{
                          padding: '10px',
                          marginBottom: 4,
                          border: `1px solid ${selectedRunId === run.id ? 'var(--v2-action)' : isAgentlet ? 'var(--v2-border-default)' : 'var(--v2-border-default)'}`,
                          borderRadius: 6,
                          background: selectedRunId === run.id ? 'var(--v2-bg-muted)' : isAgentlet ? 'var(--v2-accent-trust-soft)' : 'var(--v2-bg-surface)',
                          borderLeft: `3px solid ${sourceColor}`,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <RunStatusIcon status={run.status} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: run.status === 'pending_approval' ? 'var(--v2-warning)' : run.status === 'rejected' ? 'var(--v2-danger)' : 'var(--v2-text-primary)', flex: 1 }}>
                            {run.status === 'pending_approval' ? 'Pending Approval' : run.status === 'rejected' ? 'Rejected' : run.status}
                          </span>
                          {/* Trigger source badge */}
                          <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
                            padding: '1px 6px', borderRadius: 4,
                            background: sourceBg, color: sourceColor,
                            border: `1px solid ${sourceBorder}`,
                          }}>
                            {sourceLabel}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>
                            v{run.workflowVersion || '?'}
                          </span>
                        </div>
                        {/* Agent info line for agentlet-triggered runs */}
                        {isAgentlet && (run.triggerAgentName || run.triggerSkillName) && (
                          <div style={{
                            fontSize: 10, color: 'var(--v2-action-strong)', marginBottom: 3,
                            display: 'flex', alignItems: 'center', gap: 4,
                          }}>
                            <span style={{ fontSize: 11 }}>🤖</span>
                            <span style={{ fontWeight: 500 }}>
                              {run.triggerAgentName || 'Agent'}
                              {run.triggerSkillName ? ` → ${run.triggerSkillName}` : ''}
                            </span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--v2-text-muted)' }}>
                          <span>#{run.executionNumber || run.id?.substring(0, 8)}</span>
                          <span>{run.createdAt && new Date(run.createdAt).toLocaleString()}</span>
                        </div>
                        {run.durationMs && (
                          <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2 }}>
                            Duration: {(run.durationMs / 1000).toFixed(1)}s
                          </div>
                        )}
                        {/* Pending approval badge */}
                        {run.status === 'pending_approval' && (
                          <div style={{
                            marginTop: 4, padding: '3px 8px', borderRadius: 4,
                            background: 'var(--v2-warning-soft)', border: '1px solid var(--v2-warning-soft)',
                            fontSize: 10, color: 'var(--v2-warning)', display: 'flex', alignItems: 'center', gap: 4
                          }}>
                            <Shield size={10} /> Awaiting Human Approval
                          </div>
                        )}
                        {run.status === 'rejected' && (
                          <div style={{
                            marginTop: 4, padding: '3px 8px', borderRadius: 4,
                            background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)',
                            fontSize: 10, color: 'var(--v2-danger)', display: 'flex', alignItems: 'center', gap: 4
                          }}>
                            <XCircle size={10} /> Approval Rejected
                          </div>
                        )}
                        {/* Run-level controls */}
                        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                          {(run.status === 'failed') && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRetryFromFailed(run.id); }}
                                style={{ ...miniBtn, fontSize: 10, padding: '3px 6px' }}
                                title="Retry from failed step"
                              >
                                <RefreshCw size={10} style={{ marginRight: 2 }} />
                                Retry
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleResumeRun(run.id); }}
                                style={{ ...miniBtn, fontSize: 10, padding: '3px 6px' }}
                                title="Resume run"
                              >
                                <SkipForward size={10} style={{ marginRight: 2 }} />
                                Resume
                              </button>
                            </>
                          )}
                          {(run.status === 'running') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleTerminateRun(run.id); }}
                              style={{ ...miniBtn, fontSize: 10, padding: '3px 6px', background: 'var(--v2-danger-soft)' }}
                              title="Terminate"
                            >
                              <StopCircle size={10} style={{ marginRight: 2 }} />
                              Terminate
                            </button>
                          )}
                          {/* Always show a Clear button to reset stuck execution state */}
                          {(run.status === 'failed' || run.status === 'cancelled' || run.status === 'running') && selectedRunId === run.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                canvas.clearExecutionState();
                                setIsExecuting(false);
                                setActiveRunId(null);
                                addToast('Execution state cleared', 'success');
                              }}
                              style={{ ...miniBtn, fontSize: 10, padding: '3px 6px', color: 'var(--v2-warning)', borderColor: 'var(--v2-warning-soft)', background: 'var(--v2-warning-soft)' }}
                              title="Clear execution state and return to edit mode"
                            >
                              <X size={10} style={{ marginRight: 2 }} />
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })
                  );
                  })()}
                  <Pagination totalItems={(executionSourceFilter === 'all' ? workflowRuns : workflowRuns.filter((r) => executionSourceFilter === 'manual' ? (!r.triggerType || r.triggerType === 'manual') : r.triggerType === executionSourceFilter)).length} page={pgExecutionRuns.page} pageSize={pgExecutionRuns.pageSize} onPageChange={pgExecutionRuns.setPage} onPageSizeChange={pgExecutionRuns.setPageSize} noun="runs" />
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* ===== CENTER PANEL: SVG Canvas / Split Execution View ===== */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* --- Enforcement Auto-Insertion Report Banner --- */}
          {showEnforcementReport && enforcementReport && (
            <div style={{
              background: 'var(--v2-bg-surface)',
              color: 'var(--v2-text-primary)',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
              borderBottom: '2px solid var(--v2-action)',
              zIndex: 10,
            }}>
              <Shield size={18} style={{ color: 'var(--v2-action)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: 'var(--v2-action)' }}>Protection Enabled</span>
                <span style={{ margin: '0 6px', color: 'var(--v2-text-disabled)' }}>|</span>
                <span style={{ color: 'var(--v2-text-secondary)' }}>
                  {enforcementReport.stats.enforcementNodesAdded} enforcement nodes auto-inserted:
                </span>
                {enforcementReport.stats.dpgInboundCount > 0 && (
                  <span style={{ marginLeft: 8, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-border-default)', padding: '2px 8px', borderRadius: 4, fontSize: 11, color: 'var(--v2-action-strong)', fontWeight: 600 }}>
                    DPG Scan x{enforcementReport.stats.dpgInboundCount}
                  </span>
                )}
                {enforcementReport.stats.dpgOutboundCount > 0 && (
                  <span style={{ marginLeft: 4, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-border-default)', padding: '2px 8px', borderRadius: 4, fontSize: 11, color: 'var(--v2-action-strong)', fontWeight: 600 }}>
                    LLM Guard x{enforcementReport.stats.dpgOutboundCount}
                  </span>
                )}
                {enforcementReport.stats.policyGateCount > 0 && (
                  <span style={{ marginLeft: 4, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-border-default)', padding: '2px 8px', borderRadius: 4, fontSize: 11, color: 'var(--v2-action-strong)', fontWeight: 600 }}>
                    Gate x{enforcementReport.stats.policyGateCount}
                  </span>
                )}
                {enforcementReport.stats.autonomyGateCount > 0 && (
                  <span style={{ marginLeft: 4, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-border-default)', padding: '2px 8px', borderRadius: 4, fontSize: 11, color: 'var(--v2-action-strong)', fontWeight: 600 }}>
                    Trust Gate x{enforcementReport.stats.autonomyGateCount}
                  </span>
                )}
                {enforcementReport.stats.auditCount > 0 && (
                  <span style={{ marginLeft: 4, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-border-default)', padding: '2px 8px', borderRadius: 4, fontSize: 11, color: 'var(--v2-action-strong)', fontWeight: 600 }}>
                    Audit Trail x{enforcementReport.stats.auditCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowEnforcementReport(false)}
                style={{ background: 'none', border: 'none', color: 'var(--v2-text-disabled)', cursor: 'pointer', padding: 4 }}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* P1-B ported: orphan / repair banner. */}
          {canvas.orphanRepairReport && canvas.orphanRepairReport.count > 0 && (
            <div style={{
              background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)',
              padding: '10px 16px', display: 'flex', alignItems: 'flex-start',
              gap: 12, fontSize: 13, borderBottom: '1px solid var(--v2-warning-soft)', zIndex: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.3 }} aria-hidden="true">🔧</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>Workflow auto-repaired</div>
                <div style={{ color: 'var(--v2-warning)', lineHeight: 1.5 }}>
                  Agentlets fixed {canvas.orphanRepairReport.count} structural issue{canvas.orphanRepairReport.count === 1 ? '' : 's'} when loading this workflow
                  (orphan nodes, dangling edges, duplicate connections, or cycles).
                  These changes are <em>not yet saved</em> — review the canvas, then save when satisfied.
                </div>
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--v2-warning)', fontWeight: 600 }}>
                    Show repair log
                  </summary>
                  <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12, color: 'var(--v2-warning)', lineHeight: 1.6 }}>
                    {canvas.orphanRepairReport.repairs.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </details>
              </div>
              <button
                onClick={canvas.dismissOrphanRepairReport}
                style={{ background: 'none', border: 'none', color: 'var(--v2-warning)', cursor: 'pointer', padding: 4 }}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* RC-97e3 — "AI applied fix" trace banner. Mirrors the
              "Workflow auto-repaired" banner above (same layout, same
              dismiss affordance) but uses a Prove7 navy palette to
              distinguish operator-initiated AI fixes from server-side
              auto-repair. Renders only when aiFixTrace is set; cleared
              when operator clicks X or starts a new fix attempt. */}
          {aiFixTrace && (
            <div style={{
              background: 'var(--v2-bg-muted)', color: 'var(--v2-text-primary)',
              padding: '10px 16px', display: 'flex', alignItems: 'flex-start',
              gap: 12, fontSize: 13, borderBottom: '1px solid var(--v2-border-default)', zIndex: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.3 }} aria-hidden="true">✨</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2, color: 'var(--v2-action)' }}>AI applied a fix</div>
                <div style={{ color: 'var(--v2-text-secondary)', lineHeight: 1.5 }}>
                  {aiFixTrace.summary && aiFixTrace.summary.length > 0
                    ? aiFixTrace.summary
                    : 'AI returned an updated workflow but did not include a summary.'}
                  {' '}
                  These changes are <em>not yet saved</em> — review the canvas, then save when satisfied.
                </div>
                {aiFixTrace.warnings && aiFixTrace.warnings.length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--v2-action)', fontWeight: 600 }}>
                      Show {aiFixTrace.warnings.length} warning{aiFixTrace.warnings.length === 1 ? '' : 's'} from AI
                    </summary>
                    <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
                      {aiFixTrace.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </details>
                )}
              </div>
              <button
                onClick={() => setAiFixTrace(null)}
                style={{ background: 'none', border: 'none', color: 'var(--v2-text-secondary)', cursor: 'pointer', padding: 4 }}
                title="Dismiss"
                aria-label="Dismiss AI fix trace"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* RC-134 — Concurrent-edit conflict banner.
              Fires when the BE returns 412 because someone else saved
              this workflow since we loaded it. The banner LOCKS the
              operator into a deliberate choice (Reload vs Save anyway)
              — there's no dismiss-without-acting affordance, because
              ignoring it means the next save will hit the same 412.
              Reload: discards local edits, reloads the workflow from
                      the server, re-anchors canvasVersionRef.
              Save anyway: re-fires the save with If-Match: "*" so the
                      BE skips the check; operator has explicitly
                      acknowledged the overwrite. */}
          {versionConflict && (
            <div style={{
              background: 'var(--v2-danger-soft)', color: 'var(--v2-danger)',
              padding: '10px 16px', display: 'flex', alignItems: 'flex-start',
              gap: 12, fontSize: 13, borderBottom: '1px solid var(--v2-accent-rose-soft)', zIndex: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.3 }} aria-hidden="true">⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  Save blocked — someone else updated this workflow
                </div>
                <div style={{ color: 'var(--v2-text-secondary)', lineHeight: 1.5 }}>
                  {versionConflict.attemptedVersion != null && versionConflict.currentVersion != null
                    ? <>You're editing version {versionConflict.attemptedVersion}; the server is now on version {versionConflict.currentVersion}.</>
                    : <>The server has a newer version of this workflow than the one you loaded.</>}
                  {' '}Reload to see what changed and re-apply your edits, or save anyway to overwrite their work.
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Drop the pending payload and re-load the workflow.
                      // The version-anchor effect picks up the new version
                      // automatically once the workflows list refreshes.
                      const pending = versionConflict;
                      setVersionConflict(null);
                      try { loadWorkflows && loadWorkflows(); } catch { /* soft */ }
                      addToast(
                        `Reloaded — your edits to v${pending.attemptedVersion ?? '?'} were discarded.`,
                        'warn',
                      );
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 4, fontWeight: 600, fontSize: 12,
                      background: 'var(--v2-bg-elevated)', color: 'var(--v2-text-primary)',
                      border: '1px solid var(--v2-border-default)', cursor: 'pointer',
                    }}
                  >
                    Reload (discard my edits)
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      // Re-fire the save with If-Match: "*" so the BE skips
                      // the version check. The operator has explicitly
                      // chosen to overwrite — that's a one-shot ack.
                      const pending = versionConflict;
                      setVersionConflict(null);
                      try {
                        const overrideOpts = { ifMatchVersion: '*' };
                        if (pending.changeKind) overrideOpts.changeKind = pending.changeKind;
                        const result = await saveCanvasApi(
                          selectedWorkflowId, pending.pendingData, overrideOpts,
                        );
                        if (result?.ok) {
                          // BE accepted; bump the local anchor to whatever
                          // version it just wrote (server-authoritative).
                          if (result?.data?.version != null) {
                            canvasVersionRef.current = result.data.version;
                          }
                          addToast('Saved — overwrote the concurrent edit.', 'success');
                        } else {
                          addToast(
                            'Save anyway failed: ' + (result?.error || 'Unknown error'),
                            'error',
                          );
                        }
                      } catch (err) {
                        addToast(`Save anyway error: ${err?.message || err}`, 'error');
                      }
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 4, fontWeight: 600, fontSize: 12,
                      background: 'var(--v2-danger)', color: 'var(--v2-bg-surface)',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    Save anyway (overwrite their edits)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* RC-135 — Autosave restore-on-load banner.
              Fires when a workflow load detects an autosave blob with
              unsaved edits. Operator picks Restore (apply the snapshot)
              or Discard (drop it, keep server's loaded definition).
              Stale-detection: if baseVersion < loadedVersion, a
              concurrent save landed during the disconnect — we surface
              that explicitly so Restore is a deliberate overwrite. */}
          {unsavedRestore && (() => {
            const fmtAgo = (ts) => {
              if (!ts) return 'a while ago';
              const sec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
              if (sec < 60) return `${sec}s ago`;
              const min = Math.floor(sec / 60);
              if (min < 60) return `${min}m ago`;
              const hr = Math.floor(min / 60);
              return `${hr}h ago`;
            };
            const stale = unsavedRestore.baseVersion != null
              && unsavedRestore.loadedVersion != null
              && unsavedRestore.baseVersion < unsavedRestore.loadedVersion;
            return (
              <div style={{
                background: stale ? 'var(--v2-danger-soft)' : 'var(--v2-bg-muted)',
                color: stale ? 'var(--v2-danger)' : 'var(--v2-text-primary)',
                padding: '10px 16px', display: 'flex', alignItems: 'flex-start',
                gap: 12, fontSize: 13,
                borderBottom: '1px solid ' + (stale ? 'var(--v2-accent-rose-soft)' : 'var(--v2-border-default)'),
                zIndex: 10,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.3 }} aria-hidden="true">
                  {stale ? '⚠️' : '💾'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>
                    {stale ? 'Unsaved edits found — but the workflow has changed since' : 'Restore unsaved edits?'}
                  </div>
                  <div style={{ color: stale ? 'var(--v2-danger)' : 'var(--v2-text-secondary)', lineHeight: 1.5 }}>
                    {stale ? (
                      <>
                        We kept your edits from {fmtAgo(unsavedRestore.savedAt)} (built on v{unsavedRestore.baseVersion}),
                        but someone else has saved the workflow since — it's now on v{unsavedRestore.loadedVersion}.
                        Restoring will overwrite their save.
                      </>
                    ) : (
                      <>
                        Your last canvas edits from {fmtAgo(unsavedRestore.savedAt)} weren't saved before this tab reloaded.
                        Restore to pick up where you left off, or discard to use the version on the server.
                      </>
                    )}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => {
                        // Apply the snapshot. loadCanvasData resets history
                        // and re-anchors the baseline; the change_kind hint
                        // doesn't apply here (this isn't AI / import — it's
                        // a recovery). Operator still needs to click Save
                        // to persist upstream; we don't auto-save because
                        // the canvas may need review first.
                        const snap = unsavedRestore.snapshot;
                        setUnsavedRestore(null);
                        try {
                          canvas.loadCanvasData(snap, { autoLayoutOnLoad: false, zoomToFitOnLoad: false });
                          addToast('Restored your unsaved edits — review and click Save when ready.', 'success');
                        } catch (err) {
                          addToast(`Could not restore: ${err?.message || err}`, 'error');
                        }
                      }}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontWeight: 600, fontSize: 12,
                        background: stale ? 'var(--v2-danger)' : 'var(--v2-action)',
                        color: 'var(--v2-bg-surface)', border: 'none', cursor: 'pointer',
                      }}
                    >
                      {stale ? 'Restore (overwrite their save)' : 'Restore my edits'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUnsavedRestore(null);
                        try {
                          canvas.clearUnsavedFor && canvas.clearUnsavedFor(selectedWorkflowId);
                        } catch { /* soft */ }
                        addToast('Discarded the autosaved edits.', 'warn');
                      }}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontWeight: 600, fontSize: 12,
                        background: 'var(--v2-bg-elevated)', color: 'var(--v2-text-primary)',
                        border: '1px solid var(--v2-border-default)', cursor: 'pointer',
                      }}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* P0-B ported: unresolved-connector banner. */}
          {unresolvedConnectorSlugs.length > 0 && (
            <div style={{
              background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)',
              padding: '10px 16px', display: 'flex', alignItems: 'center',
              gap: 12, fontSize: 13, borderBottom: '1px solid var(--v2-warning-soft)', zIndex: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true">⚠️</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700 }}>Missing connectors</span>
                <span style={{ margin: '0 6px', color: 'var(--v2-warning)' }}>·</span>
                <span>
                  {unresolvedConnectorSlugs.length === 1
                    ? `1 node references a connector that isn't installed: `
                    : `${unresolvedConnectorSlugs.length} nodes reference connectors that aren't installed: `}
                </span>
                <span style={{ fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
                  {unresolvedConnectorSlugs.join(', ')}
                </span>
              </div>
              <a href="/connectors" style={{
                background: 'var(--v2-warning)', color: 'var(--v2-bg-surface)',
                padding: '5px 12px', borderRadius: 6, textDecoration: 'none',
                fontSize: 12, fontWeight: 600, flexShrink: 0,
              }}>Install</a>
              <button
                onClick={() => setUnresolvedConnectorSlugs([])}
                style={{ background: 'none', border: 'none', color: 'var(--v2-warning)', cursor: 'pointer', padding: 4 }}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* RC-CanvasV3-ProtectModal-Slideout — Protect/Unprotect confirmation
              moved Modal → V2 Slideout. The diff table needs horizontal room
              for "Between Set Hello World Message → Compliance Audit"-style
              anchor strings (RC-CanvasV3-ProtectModal-AnchorColumn). Modal at
              ~600px wrapped them; slideout at 640px keeps them on a single
              line and matches the canonical RC-97e2 pattern (every other
              diff/preview surface — Issues, Versions, AI Fix Trace — is a
              slideout). Footer holds Cancel / Confirm so the action bar stays
              pinned even with long insertion reports. */}
          {pendingEnforcement && (
            <Slideout
              isOpen={!!pendingEnforcement}
              onClose={pendingEnforcement.onCancel}
              width={640}
              ariaLabel="Confirm governance injection"
              header={
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--v2-text-primary)' }}>
                    Confirm protection injection
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', marginTop: 2 }}>
                    {(pendingEnforcement.governed?.stats?.enforcementNodesAdded || 0)} governance
                    node{(pendingEnforcement.governed?.stats?.enforcementNodesAdded || 0) === 1 ? '' : 's'}
                    {' '}will be inserted
                  </div>
                </div>
              }
              footer={
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={pendingEnforcement.onCancel} style={btnBase}>Cancel</button>
                  {/* V328 — inject the operator-selected default action
                      captured from the radio group below. Fallback 'MASK'
                      matches the useState default. */}
                  <button onClick={() => pendingEnforcement.onConfirm(protectAction)} style={btnPrimary}>
                    Confirm &amp; apply
                  </button>
                </div>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{
                  fontSize: 13, color: 'var(--v2-text-secondary)', lineHeight: 1.55,
                  padding: '10px 12px', background: 'var(--v2-action-soft)',
                  border: '1px solid var(--v2-action-soft)', borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 700, color: 'var(--v2-action-strong)', marginBottom: 4 }}>
                    {pendingEnforcement.contextLabel || 'Apply governance'}
                  </div>
                  These nodes run before connector calls and LLM responses to enforce
                  data-protection, policy, and autonomy controls. Nothing is saved until you click
                  <em> Confirm &amp; apply</em>.
                </div>

                {/* V328 (2026-07-07) — Data-protection action selector.
                    Sets Workflow.protection_policy.defaultAction, consumed
                    by DataProtectionGate.scan() as the workflow-level
                    override BELOW per-node overrides. Persisted values
                    are always encrypted (P7E1 / AES-256-GCM) regardless
                    of action; the action controls how DPG treats a
                    detected PII value in scan / display / audit.
                    Action enum matches DataClassificationRule.Action on the
                    backend — REDACT / TOKENIZE / MASK / BLOCK. */}
                <div style={{
                  border: '1px solid var(--v2-border-default)', borderRadius: 8,
                  padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v2-text-primary)' }}>
                      Data-protection default action
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
                      Applied when the DPG detects a classified PII / secret value in a form
                      submission, connector output, or LLM prompt on this workflow. Per-node
                      overrides (set in Inspector) still take precedence over this default.
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { value: 'MASK',     label: 'Mask',     help: 'Replace with a length-preserving mask (e.g. ●●●●1234). Reveal available with justification.' },
                      { value: 'REDACT',   label: 'Redact',   help: 'Replace with a category tag (e.g. [REDACTED:email]). Reveal available with justification.' },
                      { value: 'TOKENIZE', label: 'Tokenize', help: 'Swap with a stable opaque token stored in the vault. Reveal available with justification.' },
                      { value: 'BLOCK',    label: 'Block',    help: 'Fail-close: reject the submission / call. No reveal option.' },
                    ].map((opt) => {
                      const selected = protectAction === opt.value;
                      return (
                        <label
                          key={opt.value}
                          style={{
                            display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6,
                            border: `1px solid ${selected ? 'var(--v2-action-strong)' : 'var(--v2-border-default)'}`,
                            background: selected ? 'var(--v2-action-soft)' : 'transparent',
                            cursor: 'pointer', alignItems: 'flex-start',
                          }}
                        >
                          <input
                            type="radio"
                            name="protectAction"
                            value={opt.value}
                            checked={selected}
                            onChange={() => setProtectAction(opt.value)}
                            style={{ marginTop: 3, accentColor: 'var(--v2-action-strong)' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: 13, fontWeight: 600,
                              color: selected ? 'var(--v2-action-strong)' : 'var(--v2-text-primary)',
                            }}>{opt.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', marginTop: 2, lineHeight: 1.45 }}>
                              {opt.help}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{
                    fontSize: 12, color: 'var(--v2-text-secondary)', lineHeight: 1.5,
                    padding: '8px 10px', background: 'var(--v2-bg-muted)', borderRadius: 6,
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                  }}>
                    <span aria-hidden="true" style={{ flexShrink: 0 }}>🔒</span>
                    <span>
                      <strong style={{ color: 'var(--v2-text-primary)' }}>Encryption enabled</strong>
                      {' — '}
                      persisted form_task submissions and canvas outputs are encrypted with
                      per-tenant AES-256-GCM (<code style={{ fontSize: 11 }}>P7E1</code>). Applies
                      to every action above regardless of DPG detection.
                    </span>
                  </div>
                </div>
                <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ background: 'var(--v2-bg-elevated)' }}>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--v2-border-default)', fontWeight: 700, color: 'var(--v2-text-secondary)' }}>Kind</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--v2-border-default)', fontWeight: 700, color: 'var(--v2-text-secondary)' }}>Anchor</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--v2-border-default)', fontWeight: 700, color: 'var(--v2-text-secondary)' }}>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* RC-CanvasV3-ProtectModal-AnchorColumn — resolve node-id
                          anchors to human labels via the pre-injection node
                          list. Producer (`enforcementAutoInsertion.js`) emits
                          rows with `beforeNode` / `afterNode` (single anchor)
                          or `sourceNode` + `targetNode` (between-pair, for
                          DPG_INTERNODE). */}
                      {(pendingEnforcement.governed?.insertionReport || []).map((row, i) => {
                        const labelById = new Map(
                          (pendingEnforcement.original?.nodes || []).map((n) => [n.id, n.label || n.type || n.id])
                        );
                        const lbl = (id) => labelById.get(id) || id || '—';
                        let anchor = '—';
                        if (row.sourceNode && row.targetNode) {
                          anchor = `Between ${lbl(row.sourceNode)} → ${lbl(row.targetNode)}`;
                        } else if (row.beforeNode) {
                          anchor = `Before ${lbl(row.beforeNode)}`;
                        } else if (row.afterNode) {
                          anchor = `After ${lbl(row.afterNode)}`;
                        }
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid var(--v2-bg-muted)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--v2-text-primary)', whiteSpace: 'nowrap' }}>{row.kind || row.type || '—'}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--v2-text-secondary)' }}>{anchor}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--v2-text-muted)' }}>{row.reason || row.purpose || '—'}</td>
                          </tr>
                        );
                      })}
                      {(!pendingEnforcement.governed?.insertionReport
                        || pendingEnforcement.governed.insertionReport.length === 0) && (
                        <tr>
                          <td colSpan={3} style={{ padding: '16px 12px', color: 'var(--v2-text-disabled)', textAlign: 'center' }}>
                            No insertions queued.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Slideout>
          )}

          {/* Phase 6 ported: first-time picker tooltip.
              Styled with the Agentlets neutral-grey palette (stone-100 bg
              / stone-700 text) so it blends with page chrome rather than
              competing with canvas content. The original sky-blue banner
              read as a system alert; this new tone reads as a quiet,
              dismissible tip. */}
          {/* V169 / Wave 3 + V221 — Immutable / admin-only canvas banner.
              Always shown at the top of the canvas region when the loaded
              workflow is one of:
                (a) the CEG of a runbook-managed AutoLet (autolet_ceg)
                (b) a SysLet's behavioral CEG (syslet_canvas)
                (c) a platform utility workflow viewed by a non-admin
                    (standalone_system + !isSuperAdmin)
              Copy branches across the three cases so the operator sees the
              right rebuild path. Banner overrides the first-time picker tip
              below — there's nothing to insert in a locked canvas. See
              GOLDEN_CEG_RULES.md §1, §9.2 and V221 migration notes. */}
          {isAutoLetCegReadOnly && (
            <div style={{
              background: 'var(--v2-bg-muted)', color: 'var(--v2-text-secondary)',
              padding: '10px 16px', display: 'flex', alignItems: 'center',
              gap: 12, fontSize: 12, lineHeight: 1.5,
              borderBottom: '1px solid var(--v2-border-default)', zIndex: 9,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'var(--v2-bg-surface)', color: 'var(--v2-text-secondary)',
                border: '1px solid var(--v2-border-default)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 500, flexShrink: 0,
              }} aria-hidden="true">🔒</span>
              <span style={{ flex: 1 }}>
                {workflowKind === 'autolet_ceg' ? (
                  <>
                    <strong style={{ fontWeight: 500, color: 'var(--v2-text-primary)' }}>
                      Canonical Execution Graph (read-only)
                    </strong>
                    {' — '}
                    This is the CEG of <strong style={{ fontWeight: 500 }}>{linkedAgent?.name || 'an AutoLet'}</strong>
                    {' '}(source v{linkedAgent?.runbookVersion ?? 1}). It&rsquo;s a snapshot — to change it,
                    upload a revised source document to create v{(linkedAgent?.runbookVersion ?? 1) + 1}.
                    You can pan, zoom, and inspect; saves and edits are disabled.
                    {linkedAgent?.executionMode === 'cognitive' && (
                      <>{' '}<em style={{ color: 'var(--v2-text-secondary)' }}>
                        This agent runs in cognitive mode — at runtime the LLM reasons over these skills as a
                        tool catalog and picks what to call; it does not execute this graph step by step.
                      </em></>
                    )}
                  </>
                ) : workflowKind === 'syslet_canvas' ? (
                  <>
                    <strong style={{ fontWeight: 500, color: 'var(--v2-text-primary)' }}>
                      SysLet Canvas (read-only)
                    </strong>
                    {' — '}
                    This is the behavioral CEG of <strong style={{ fontWeight: 500 }}>{linkedAgent?.name || 'a System Agent'}</strong>.
                    Platform-managed and immutable post-creation. To change it, update the
                    SystemWorkflowRegistry definition and re-bootstrap. You can pan, zoom, and inspect.
                  </>
                ) : workflowKind === 'standalone_system' ? (
                  <>
                    <strong style={{ fontWeight: 500, color: 'var(--v2-text-primary)' }}>
                      Platform utility workflow — view only
                    </strong>
                    {' — '}
                    {selectedWorkflow?.name ? <><strong style={{ fontWeight: 500 }}>{selectedWorkflow.name}</strong> is part of</> : 'This workflow is part of'} the
                    platform's own machinery, shared across every customer on this deployment,
                    so its definition, cadence and trigger settings are maintained by your
                    Platform Administrator. You can pan, zoom and inspect the full graph here.
                    If you need its schedule or behaviour changed, contact your Platform
                    Administrator with the workflow name above.
                  </>
                ) : (
                  /* Legacy fallback — pre-V177 rows where workflowKind is null
                     but the (isCanonical AND isRunbookManaged) heuristic fires.
                     Stick with the original AutoLet copy. */
                  <>
                    <strong style={{ fontWeight: 500, color: 'var(--v2-text-primary)' }}>
                      Canonical Execution Graph (read-only)
                    </strong>
                    {' — '}
                    This is the CEG of <strong style={{ fontWeight: 500 }}>{linkedAgent?.name || 'an AutoLet'}</strong>
                    {' '}(source v{linkedAgent?.runbookVersion ?? 1}). It&rsquo;s a snapshot — to change it,
                    upload a revised source document to create v{(linkedAgent?.runbookVersion ?? 1) + 1}.
                    You can pan, zoom, and inspect; saves and edits are disabled.
                    {linkedAgent?.executionMode === 'cognitive' && (
                      <>{' '}<em style={{ color: 'var(--v2-text-secondary)' }}>
                        This agent runs in cognitive mode — at runtime the LLM reasons over these skills as a
                        tool catalog and picks what to call; it does not execute this graph step by step.
                      </em></>
                    )}
                  </>
                )}
              </span>
            </div>
          )}

          {/* First-time picker tip — suppressed on read-only AutoLet
              CEGs because there's no insert affordance to discover. */}
          {!isAutoLetCegReadOnly && showPickerTip && canvas.nodes && canvas.nodes.length > 0 && (
            <div style={{
              background: 'var(--v2-bg-muted)', color: 'var(--v2-text-secondary)',
              padding: '8px 14px', display: 'flex', alignItems: 'center',
              gap: 12, fontSize: 12, borderBottom: '1px solid var(--v2-border-default)', zIndex: 9,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%',
                background: 'var(--v2-bg-surface)', color: 'var(--v2-text-secondary)',
                border: '1px solid var(--v2-border-default)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 500, flexShrink: 0,
              }} aria-hidden="true">+</span>
              <span style={{ flex: 1 }}>
                <strong style={{ fontWeight: 500, color: 'var(--v2-text-primary)' }}>New: </strong>
                Click the <strong style={{ fontWeight: 500 }}>+</strong> below any task to insert a step after it. Or select a task and press <span style={{ fontFamily: 'SFMono-Regular, Menlo, monospace', background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)', padding: '1px 5px', borderRadius: 3, color: 'var(--v2-text-primary)' }}>⌘E</span>; the <strong style={{ fontWeight: 500 }}>+ Task</strong> toolbar button works too.
              </span>
              <button
                onClick={dismissPickerTip}
                aria-label="Dismiss tip"
                style={{ background: 'none', border: 'none', color: 'var(--v2-text-muted)', cursor: 'pointer', padding: 4 }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* --- AI Workflow Builder Chat Bar --- */}
          {leftPanelMode !== 'execution' && (
            <AIWorkflowChatBar
              canvas={canvas}
              installedConnectors={installedConnectors}
              addToast={addToast}
              createWorkflow={createWorkflow}
              setSelectedWorkflowId={setSelectedWorkflowId}
              selectedWorkflowId={selectedWorkflowId}
              loadedWorkflowRef={loadedWorkflowRef}
              orgId={orgId}
              tenantId={tenantId}
              userId={userId}
              // RC-42 — Plumb the immutable-kind read-only flag through.
              // The const is declared inside CanvasPage's scope (~line 2474)
              // but AIWorkflowChatBar is a sibling function component; its
              // body referenced the bare identifier and the minified bundle
              // resolved it against window → ReferenceError at runtime when
              // the user clicked the AI submit button on an AutoLet CEG /
              // SysLet canvas. Passing as a prop keeps both call sites
              // (shouldSpawnNew gate + pre-save guard) working without
              // duplicating the kind-check logic.
              isImmutableKindReadOnly={isImmutableKindReadOnly}
              /* RC-97f — Linked agent's skill catalog. Forwarded to
                 the BE so the AI prompt can list real skill IDs for
                 skill_task / agent_task nodes. Falls back to empty
                 array when canvas is standalone (no linked agent)
                 OR when linkedAgent hasn't been fully loaded yet.
                 BE treats empty/missing as "no agent context" and
                 omits the prompt block — identical to pre-RC-97f
                 behavior on the standalone path. */
              linkedAgentSkills={Array.isArray(linkedAgent?.skills) ? linkedAgent.skills : []}
            />
          )}
          {/* --- TOP HALF: Canvas or Graphical Timeline --- */}
          <div
            style={{
              flex: (leftPanelMode === 'execution' && selectedRunId) ? '0 0 55%' : 1,
              position: 'relative',
              overflow: 'hidden',
              background: 'var(--v2-bg-elevated)',
              borderBottom: (leftPanelMode === 'execution' && selectedRunId) ? '2px solid var(--v2-border-default)' : 'none',
            }}
            ref={svgContainerRef}
            /* RC-14 (task #194) — onDragOver / onDrop wrappers removed;
             * drag-and-drop palette → canvas is no longer supported. The
             * canvas accepts insertions only via handleAddNode (palette
             * click), the per-node "+" overlay, and the branch "+" button. */
          >
            {/* Execution status overlay header (shown when viewing a run) */}
            {leftPanelMode === 'execution' && selectedRunId && (
              <ExecutionStatusOverlay
                nodeExecutions={runNodeExecutions}
                selectedRun={workflowRuns.find((r) => r.id === selectedRunId)}
              />
            )}

            {/* Scoped detail read returned 404 (out of current scope / archived /
                deleted). Show a clear panel over the canvas instead of a silent
                blank. Tied to the current selection so it never lingers. */}
            {unavailableWorkflowId && unavailableWorkflowId === selectedWorkflowId && (
              <div
                role="status"
                style={{
                  position: 'absolute', inset: 0, zIndex: 20,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--v2-bg-surface)',
                }}
              >
                <div style={{ maxWidth: 440, textAlign: 'center', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <Lock size={30} style={{ color: 'var(--v2-text-disabled)' }} />
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--v2-text-primary)' }}>
                    This workflow isn’t available in your current scope
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--v2-text-secondary)' }}>
                    It may belong to another tenant, or it has been archived or deleted. Switch to its tenant from the scope selector, or pick a workflow from your list.
                  </div>
                  <button
                    type="button"
                    onClick={() => { setUnavailableWorkflowId(null); setSelectedWorkflowId(null); setViewMode('list'); }}
                    style={{ marginTop: 4, padding: '8px 16px', fontSize: 13, fontWeight: 500, color: 'var(--v2-bg-surface)', background: 'var(--v2-text-primary)', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Back to Workflows
                  </button>
                </div>
              </div>
            )}

            {/* #182 — V2 hierarchical-ELK DAG renderer takes the centre.
                We hide the legacy SVG (display:none) rather than delete
                it: keeping it in the React tree means useCanvas hooks
                (svgRef, drag handlers, port positions) keep their
                contract. The legacy svg never paints, never receives
                events. V2CanvasView paints on top of the same container.
                Selection sync: V2 calls onSelectNode(sourceId) → host
                calls canvas.selectNode(id) so the right Task panel
                lights up; canvas.selected drives the V2 highlight ring. */}
            <V2CanvasView
              payload={{ nodes: canvas.nodes, connections: canvas.connections }}
              executionState={canvas.executionState}
              selectedNodeId={canvas.selected}
              focusTarget={canvasFocusTarget}  /* RC-130 — pan/zoom on Issues panel focus clicks */
              onSelectNode={(id) => {
                if (id == null) {
                  // Pane click — clear selection (both design and execution).
                  // Note: pane click does NOT clear the AI Explainer trigger;
                  // the explainer stays pinned to the last double-clicked node
                  // until the user explicitly double-clicks a different one.
                  if (canvas.clearSelection) canvas.clearSelection();
                  if (leftPanelMode === 'execution') setSelectedExecNodeId(null);
                  return;
                }
                canvas.selectNode(id);
                if (leftPanelMode === 'execution') setSelectedExecNodeId(id);
              }}
              onNodeDoubleClick={(id) => {
                // #191 — double-click is the explicit "explain this node"
                // trigger. Single clicks no longer pump the AI; this means
                // a casual click never resets a fresh analysis.
                if (!id) return;
                /* G265 / AGENT_EXECUTION_MODEL §4 — Double-click on a
                 * cognitive_loop node opens the Cognitive Brief editor
                 * slideout instead of the generic AI explainer. The
                 * brief is the LLM-driving config (systemPrompt,
                 * maxIterations, toolSelectionStrategy, cost cap,
                 * fallbackBehavior) so it deserves a dedicated editor. */
                const dblNode = canvas.nodes?.find((n) => n && n.id === id);
                if (dblNode && dblNode.type === 'cognitive_loop') {
                  setCognitiveBriefOpen(true);
                  return;
                }
                setExplainerTriggerNodeId(id);
              }}
              /* RC-14 (task #194) — onPaletteDrop callback REMOVED.
               * Palette items no longer emit drag events post-RC-14, so
               * V2CanvasView's internal drop machinery is never reached
               * from V3. The callback was the routing hop that used to
               * splice palette-dropped types into an edge — that role
               * now belongs to the inline TaskPicker triggered by the
               * per-node "+" overlay (data.onAddAfter). The
               * SINGLE_INSTANCE_ENTRY_TYPES guard, edge-proximity
               * splicing, and selected-node anchor promotion that used
               * to live in this callback are all present in
               * handleAddNode / handleInsertNodeType — no logic lost.
               * V2 page (CanvasPageV2) keeps its drag-drop wiring
               * intact because V2CanvasView itself is unchanged. */
              // Bottom-toolbar "+ Task" button → mirrors the ⌘E shortcut.
              // Same one-shot guard as the keybinding so we don't stack
              // multiple pickers if a user mashes the button. Gated on
              // the feature flag so the button vanishes cleanly when the
              // picker is disabled.
              //
              // V169 / Wave 3 — also gated on !isAutoLetCegReadOnly so
              // the toolbar button (and its "+ Task" pill) vanishes on
              // read-only AutoLet CEGs. Same gate is applied to the per-
              // task "+" buttons via onAddAfter below.
              //
              // Selected-node aware: if the user has a task selected,
              // anchor the picker as "insert after THIS task" — same
              // mental model as clicking the + on the card itself. Falls
              // back to palette mode (which the recipe handler resolves
              // to "append before END") when nothing is selected.
              /* G240 — Structural mutators (palette / +Task / +Add after) blocked
                * in Decide + Act for Lets. */
              onOpenPicker={(isCanvasPickerEnabled() && !isCanvasStructuralLock) ? (() => {
                if (insertionTarget) return;
                if (canvas.selected && canvas.nodes.some((n) => n.id === canvas.selected)) {
                  setInsertionTarget({ kind: 'after-node', nodeId: canvas.selected });
                } else {
                  setInsertionTarget({ kind: 'palette' });
                }
              }) : undefined}
              // Per-task "+" button on each leaf card. Always opens the
              // picker as "insert after this node" regardless of
              // selection state. Stable callback identity isn't critical
              // here — V2CanvasView re-decorates nodes each layout pass
              // anyway.
              //
              // Read-only AutoLet CEG: don't pass onAddAfter at all so
              // the per-task "+" button vanishes (V2Leaf hides the button
              // when data.onAddAfter is missing — that's the contract
              // already established for /canvas-v2 read-only viewers).
              onAddAfter={(isCanvasPickerEnabled() && !isCanvasStructuralLock) ? ((sourceNodeId) => {
                if (!insertionTarget) setInsertionTarget({ kind: 'after-node', nodeId: sourceNodeId });
              }) : undefined}
              // Canvas Round 1 Patch A — persist node drag.
              // Gated by `!isCanvasStructuralLock` (same lock-set the
              // insertion path uses): if the canvas is structurally
              // locked the drag still shows visually but doesn't persist,
              // which matches user expectation (locked = no changes).
              onNodeMove={!isCanvasStructuralLock ? (({ sourceNodeId, x, y }) => {
                if (!sourceNodeId || typeof canvas.updateNode !== 'function') return;
                // manualPosition=true tells layout passes (autoLayout +
                // load-time hierarchical relayout) to preserve these
                // coords verbatim. canvas.updateNode also calls
                // markDirty() so auto-save kicks in naturally.
                canvas.updateNode(sourceNodeId, { x, y, manualPosition: true });
              }) : undefined}
              // Canvas Round 1 Patch B — restore manual edge drawing.
              // Gated identically to onNodeMove. canvas.addConnection
              // returns the new connection id on success, or null when
              // canConnect rejects (cycle, type mismatch, duplicate).
              // We surface the rejection as a warning toast so the user
              // knows why their drag-to-connect didn't take.
              onConnectEdge={!isCanvasStructuralLock ? (({ sourceNodeId, targetNodeId, sourceHandle, targetHandle }) => {
                if (!sourceNodeId || !targetNodeId) return;
                if (typeof canvas.addConnection !== 'function') return;
                const id = canvas.addConnection(
                  sourceNodeId,
                  sourceHandle || 'default',
                  targetNodeId,
                  targetHandle || 'default',
                );
                if (id == null) {
                  addToast('Connection not allowed (cycle, type mismatch, or duplicate)', 'warning');
                }
              }) : undefined}
            />
            {/* Legacy SVG — kept in tree for hook compatibility, hidden
                from view. Will be removed when /canvas-v3 absorbs the
                final cutover (#175). */}
            <svg
              ref={canvas.svgRef}
              width="100%"
              height="100%"
              style={{
                display: 'none',
                cursor: leftPanelMode === 'execution' ? 'default' : (canvas.dragging ? 'grabbing' : 'grab'),
                marginTop: 0,
              }}
              onMouseDown={handleSVGMouseDown}
              onMouseMove={handleSVGMouseMove}
              onMouseUp={handleSVGMouseUp}
              onMouseLeave={handleSVGMouseUp}
              onWheel={handleWheel}
            >
              {/* Spin animation for execution running state */}
              <defs>
                <style>{`
                  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                  .insert-plus-btn:hover .insert-plus-bg { opacity: 1 !important; }
                  .insert-plus-btn:hover .insert-plus-icon { opacity: 1 !important; }
                  .insert-plus-btn:hover .insert-plus-bg { fill: var(--v2-success-soft); stroke: var(--v2-success); }
                  .insert-plus-btn:hover .insert-plus-icon { stroke: var(--v2-success); }
                `}</style>
              </defs>
              <defs>
                <pattern
                  id="grid"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                  patternTransform={`translate(${canvas.pan.x},${canvas.pan.y}) scale(${canvas.zoom})`}
                >
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e7e5e4" strokeWidth="0.5" />
                </pattern>
              </defs>

              <rect width="100%" height="100%" fill="url(#grid)" />

              <g>
                {canvas.connections.map((conn) => (
                  <CanvasConnection
                    key={conn.id}
                    connection={conn}
                    nodes={canvas.nodes}
                    connections={canvas.connections}
                    getPortPosition={canvas.getPortPosition}
                    pan={canvas.pan}
                    zoom={canvas.zoom}
                    selected={canvas.selected === conn.id}
                    onSelect={() => {
                      if (leftPanelMode === 'execution') return; // read-only in execution
                      canvas.selectConnection(conn.id);
                    }}
                    onDelete={(connId) => {
                      canvas.deleteConnection(connId);
                    }}
                    onInsertNode={(connId) => {
                      // Phase 3: detect branch edges so the picker context
                      // bar can show "Inserting on the <label> branch of …".
                      const c = canvas.connections.find((x) => x.id === connId);
                      const fromNode = c ? canvas.nodes.find((n) => n.id === c.from) : null;
                      const BRANCH_TYPES = new Set(['switch', 'condition', 'fork', 'fork_join_dynamic']);
                      const isBranch = !!fromNode
                        && BRANCH_TYPES.has(fromNode.type)
                        && c.fromPort
                        && c.fromPort !== 'default';
                      if (isCanvasPickerEnabled()) {
                        if (isBranch) {
                          setInsertionTarget({
                            kind: 'branch',
                            branchEdgeId: connId,
                            switchNodeId: fromNode.id,
                            branchLabel: c.fromPort,
                          });
                        } else {
                          setInsertionTarget({ kind: 'edge', connectionId: connId });
                        }
                      } else {
                        // Legacy popup path expects a bare connection id.
                        setInsertionTarget(connId);
                      }
                    }}
                    isExecutionMode={leftPanelMode === 'execution'}
                    executionState={canvas.executionState}
                  />
                ))}
                {canvas.connecting && (
                  <line
                    x1={(canvas.connecting.fromX || 0) * canvas.zoom + canvas.pan.x}
                    y1={(canvas.connecting.fromY || 0) * canvas.zoom + canvas.pan.y}
                    x2={canvas.connecting.mouseX}
                    y2={canvas.connecting.mouseY}
                    style={{ stroke: 'var(--v2-action)' }}
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                )}
              </g>

              <g>
                {canvas.nodes.map((node) => (
                  <CanvasNode
                    key={node.id}
                    node={node}
                    selected={
                      leftPanelMode === 'execution'
                        ? selectedExecNodeId === node.id
                        : (canvas.selected === node.id || canvas.multiSelect.has(node.id))
                    }
                    onSelect={() => {
                      if (leftPanelMode === 'execution') {
                        setSelectedExecNodeId(node.id);
                      }
                      canvas.selectNode(node.id);
                    }}
                    onMultiSelect={() => {
                      if (leftPanelMode !== 'execution') canvas.toggleMultiSelect(node.id);
                    }}
                    onStartDrag={(e) => {
                      if (leftPanelMode === 'execution') return; // read-only
                      const { x, y } = getSVGCoords(e);
                      canvas.startDrag(node.id, x, y);
                    }}
                    onEndDrag={() => canvas.endDrag()}
                    isConnecting={!!canvas.connecting}
                    onStartConnect={(portName, side) => {
                      if (leftPanelMode === 'execution') return; // read-only
                      const portPos = canvas.getPortPosition(node.id, portName, side);
                      canvas.startConnect(node.id, portName, portPos.x, portPos.y);
                    }}
                    onEndConnect={(toNodeId, toPortName) => {
                      canvas.endConnect(toNodeId, toPortName);
                    }}
                    pan={canvas.pan}
                    zoom={canvas.zoom}
                    getPortPosition={canvas.getPortPosition}
                    nodeDef={getNodeTypeDef(node.type)}
                    connections={canvas.connections}
                    executionState={canvas.executionState.get(node.id)}
                  />
                ))}
              </g>

              {/* Phase 3 ported: terminal-leaf "+" buttons. */}
              {leftPanelMode !== 'execution' && isCanvasPickerEnabled() && (() => {
                const TERMINAL_TYPES = new Set(['end', 'terminate']);
                const outgoingByFrom = new Set((canvas.connections || []).map((c) => c.from));
                const leaves = (canvas.nodes || []).filter((n) =>
                  n && !TERMINAL_TYPES.has(n.type) && !outgoingByFrom.has(n.id)
                );
                return (
                  <g>
                    {leaves.map((n) => {
                      const cx = ((n.x ?? 0) + NODE_W / 2) * canvas.zoom + canvas.pan.x;
                      const cy = ((n.y ?? 0) + NODE_H + 18) * canvas.zoom + canvas.pan.y;
                      return (
                        <g
                          key={`leaf-plus-${n.id}`}
                          transform={`translate(${cx}, ${cy})`}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showPickerTip) dismissPickerTip();
                            setInsertionTarget({ kind: 'after-node', nodeId: n.id });
                          }}
                        >
                          <circle r={16} fill="transparent" />
                          <circle r={11} fill="#1c1917" />
                          <line x1={-5} y1={0} x2={5} y2={0} stroke="#ffffff" strokeWidth={2} strokeLinecap="round" />
                          <line x1={0} y1={-5} x2={0} y2={5} stroke="#ffffff" strokeWidth={2} strokeLinecap="round" />
                        </g>
                      );
                    })}
                  </g>
                );
              })()}
            </svg>

            {/* ── Task Picker — feature-flagged (CANVAS_PICKER_INLINE_DESIGN.md) ──
                When `features.canvasPicker` is on (dev/test default), the new
                inline TaskPicker component renders. When off, the legacy
                centred popup below renders. Both paths share `handleInsertNodeType`. */}
            {insertionTarget && isCanvasPickerEnabled() && (() => {
              // Best-effort anchor: place the picker just to the right of
              // the source node of the targeted edge.
              let anchorX = 200;
              let anchorY = 100;
              const target = insertionTarget;
              if (typeof target === 'object' && target !== null) {
                if (target.kind === 'edge') {
                  const conn = canvas.connections.find((c) => c.id === target.connectionId);
                  const fromNode = conn ? canvas.nodes.find((n) => n.id === conn.from) : null;
                  if (fromNode) {
                    anchorX = (fromNode.x + NODE_W + 24) * canvas.zoom + canvas.pan.x;
                    anchorY = (fromNode.y) * canvas.zoom + canvas.pan.y;
                  }
                } else if (target.kind === 'after-node') {
                  const n = canvas.nodes.find((x) => x.id === target.nodeId);
                  if (n) {
                    anchorX = (n.x + NODE_W + 24) * canvas.zoom + canvas.pan.x;
                    anchorY = (n.y) * canvas.zoom + canvas.pan.y;
                  }
                } else if (target.kind === 'branch') {
                  const sw = canvas.nodes.find((n) => n.id === target.switchNodeId);
                  if (sw) {
                    anchorX = (sw.x + NODE_W + 24) * canvas.zoom + canvas.pan.x;
                    anchorY = (sw.y) * canvas.zoom + canvas.pan.y;
                  }
                }
              }
              return (
                <TaskPicker
                  isOpen
                  target={typeof insertionTarget === 'string'
                    ? { kind: 'edge', connectionId: insertionTarget }
                    : insertionTarget}
                  anchor={{ x: anchorX, y: anchorY }}
                  nodes={canvas.nodes}
                  connections={canvas.connections}
                  installedConnectors={installedConnectors}
                  marketplaceConnectors={marketplaceConnectors}
                  agents={agents}
                  recipes={getCuratedRecipes()}
                  onChoose={(item) => handleInsertNodeType(item)}
                  onClose={() => setInsertionTarget(null)}
                />
              );
            })()}

            {/* ── Legacy popup — only renders when the flag is OFF ── */}
            {insertionTarget && !isCanvasPickerEnabled() && (
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onClick={() => setInsertionTarget(null)}
              >
                {/* Backdrop */}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.15)' }} />
                {/* Picker card */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'relative', background: 'var(--v2-bg-surface)', borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: '16px 20px',
                    width: 340, maxHeight: 420, overflow: 'auto',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Plus size={16} style={{ color: 'var(--v2-success)' }} />
                    Insert Task
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 12 }}>
                    Choose a task type to insert between nodes
                  </div>
                  {/* Quick-pick common task types — uses actual soarNodes type keys */}
                  {[
                    { type: 'http_task', label: 'HTTP Request', desc: 'REST API call', color: 'var(--v2-action)' },
                    { type: 'llm_task', label: 'AI / LLM Task', desc: 'LLM prompt or GenAI', color: 'var(--v2-accent-trust)' },
                    { type: 'script_task', label: 'Script', desc: 'Python / JS code', color: 'var(--v2-accent-trust)' },
                    { type: 'condition', label: 'Condition', desc: 'If/else branch', color: 'var(--v2-warning)' },
                    { type: 'approval_gate', label: 'Approval Gate', desc: 'Human approval step', color: 'var(--v2-success)' },
                    { type: 'notification_task', label: 'Notification', desc: 'Email / Slack alert', color: 'var(--v2-accent-rose)' },
                    { type: 'connector_task', label: 'Connector Task', desc: 'Run installed connector', color: 'var(--v2-action)' },
                    { type: 'terminate', label: 'Terminate', desc: 'End workflow branch', color: 'var(--v2-text-muted)' },
                  ].map((item) => (
                    <button
                      key={item.type}
                      onClick={() => handleInsertNodeType(item.type)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', padding: '8px 10px', marginBottom: 4,
                        border: '1px solid var(--v2-border-default)', borderRadius: 8,
                        background: 'var(--v2-bg-surface)', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--v2-bg-muted)'; e.currentTarget.style.borderColor = item.color; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--v2-bg-surface)'; e.currentTarget.style.borderColor = 'var(--v2-border-default)'; }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, background: item.color + '15',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--v2-text-primary)' }}>{item.label}</div>
                        <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>{item.desc}</div>
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => setInsertionTarget(null)}
                    style={{
                      width: '100%', padding: '6px 0', marginTop: 8,
                      border: 'none', background: 'transparent',
                      fontSize: 11, color: 'var(--v2-text-muted)', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Legacy minimap REMOVED in V3 — V2CanvasView ships its own
                ReactFlow MiniMap that's positioned, themed and zoom-aware
                consistently with the rest of the V2 chrome. The legacy SVG
                minimap (`<Minimap/>` at the old #5 position) was painting
                a stale snapshot of `canvas.nodes` / `canvas.pan` /
                `canvas.zoom` that the V2 ReactFlow viewport doesn't keep
                in sync — so it was a misleading artefact. Hidden by
                deletion rather than a flag because there's nothing to
                bring back; V2's MiniMap is strictly better. */}

            {/* Bottom Status Bar (always shown) */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'var(--v2-bg-surface)',
              borderTop: '1px solid var(--v2-border-default)',
              padding: '6px 12px',
              fontSize: 11,
              color: 'var(--v2-text-muted)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', gap: 16 }}>
                <span>{canvas.nodes.length} nodes</span>
                <span>{canvas.connections.length} connections</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {canvas.dirty && <span style={{ color: 'var(--v2-warning)' }}>Unsaved changes</span>}
                {canvas.dagValidation.valid ? (
                  <span style={{ color: 'var(--v2-text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle size={12} /> Valid
                  </span>
                ) : (
                  <span style={{ color: 'var(--v2-danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <AlertTriangle size={12} /> {canvas.dagValidation.errors.length} error(s)
                  </span>
                )}
                {/* RC-18c (task #201) — v4Validate soft-check chip, runs
                 *  alongside the legacy dagValidation. Only renders when
                 *  there's something to report; clean canvas stays silent.
                 *
                 *  RC-97a — Chip is now CLICKABLE. Tooltip still shows
                 *  the first 3 issues as a quick preview (back-compat
                 *  with operators who learned that behavior). Clicking
                 *  opens the full issues panel where every issue is
                 *  listed with a "Focus on canvas" button per item.
                 *
                 *  role="button" + keyboard handler keep this
                 *  accessible. Chip stays hidden when stats.total=0
                 *  (clean canvas), so the click affordance only
                 *  appears when there's something to act on. */}
                {v4ValidationReport.stats.total > 0 && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Open issues panel: ${v4ValidationReport.stats.total} issue(s)`}
                    onClick={() => setShowIssuesPanel(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowIssuesPanel(true);
                      }
                    }}
                    title={v4ValidationReport.issues.slice(0, 3)
                      .map((i) => `${i.severity.toUpperCase()}: ${i.message}`).join('\n')
                      + (v4ValidationReport.stats.total > 3 ? `\n…and ${v4ValidationReport.stats.total - 3} more` : '')
                      + '\n\nClick to see all issues'}
                    style={{
                      color: 'var(--v2-text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      padding: '2px 6px',
                      borderRadius: 4,
                      transition: 'background-color 120ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--v2-bg-muted)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {v4ValidationReport.stats.error > 0 && (
                      <span style={{ color: 'var(--v2-danger)' }}>● {v4ValidationReport.stats.error} err</span>
                    )}
                    {v4ValidationReport.stats.warning > 0 && (
                      <span style={{ color: 'var(--v2-warning)' }}>● {v4ValidationReport.stats.warning} warn</span>
                    )}
                    {v4ValidationReport.stats.info > 0 && (
                      <span style={{ color: 'var(--v2-accent-cyan)' }}>● {v4ValidationReport.stats.info} info</span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Legacy floating zoom toolbar REMOVED in V3 — V2CanvasView's
                ReactFlow `<Controls/>` already exposes zoom-in/out/fit
                buttons, and ELK auto-lays-out on every payload change so
                a "GitBranch" auto-layout button is redundant. The legacy
                toolbar was wired to `canvas.zoom` / `canvas.doZoom` /
                `handleZoomFit` / `handleAutoLayout` — all SVG-world
                primitives that don't sync with V2's ReactFlow viewport,
                so it was visually duplicating UI while doing nothing
                functional. */}
          </div>

          {/* --- BOTTOM HALF: Tabbed Detail Panel (only in execution split view) --- */}
          {leftPanelMode === 'execution' && selectedRunId && (
            <ExecutionDetailPanel
              nodeExecutions={runNodeExecutions}
              selectedNodeId={selectedExecNodeId}
              onSelectNode={setSelectedExecNodeId}
              activeTab={execDetailTab}
              onTabChange={setExecDetailTab}
              auditLog={runAuditLog}
              workflowAuditLog={workflowAuditLog}
              selectedRun={workflowRuns.find((r) => r.id === selectedRunId)}
              workflowNodes={canvas.nodes}
              workflowConnections={canvas.connections}
              workflowMeta={selectedWorkflow}
            />
          )}
        </div>

        {/* ===== RIGHT PANEL: Properties or Demo Sequence ===== */}
        {demoMode ? (
          <DemoSequencePanel
            enforcementReport={enforcementReport}
            isExecuting={isExecuting}
            activeRunId={activeRunId}
            enforcementEvents={enforcementEvents}
            tokens={demoTokens}
            certificate={demoCertificate}
            onGenerateCertificate={handleGenerateDemoCertificate}
            complianceEvents={complianceEvents}
            agentData={null}
            workflowName={selectedWorkflow?.name || 'Phishing Email Triage'}
            executionDurationMs={demoExecutionDuration}
            onClose={() => setDemoMode(false)}
            onTriggerExecution={selectedWorkflowId ? handleExecute : handleDemoSimulateExecution}
            onSimulateExecution={handleDemoSimulateExecution}
            workflowRunId={activeRunId || lastDemoRunId}
          />
        ) : rightPanelCollapsed ? (
          <div style={{
            width: 40,
            background: 'var(--v2-bg-surface)',
            borderLeft: '1px solid var(--v2-border-default)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '10px 0',
            gap: 8,
          }}>
            <button
              onClick={() => setRightPanelCollapsed(false)}
              title="Expand panel"
              style={{
                width: 28, height: 28, padding: 0,
                border: '1px solid var(--v2-border-default)',
                borderRadius: 6,
                background: 'var(--v2-bg-surface)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--v2-text-secondary)',
              }}
            >
              <ChevronLeft size={14} />
            </button>
            <div style={{
              writingMode: 'vertical-rl',
              fontSize: 11,
              color: 'var(--v2-text-muted)',
              marginTop: 12,
              fontWeight: 500,
              letterSpacing: 0.3,
              userSelect: 'none',
            }}>
              {canvas.selected ? 'Properties' : 'Inspector'}
            </div>
          </div>
        ) : (
        <div style={{
          // RC-10 (task #190) — Task and Code share the wider 520px width.
          // The Task panel hosts node-config forms whose Input Parameters
          // section renders `${workflow.input.X}` expression strings,
          // textarea prompts, and JSON parameter blobs that need horizontal
          // room. At the prior 320px width those inputs wrapped raggedly
          // ("${workflow.input." landed on one line, the closing "}" on
          // the next) and JSON / textarea fields scrolled horizontally,
          // making operators copy-paste-edit through a peephole. Matching
          // Code's 520px gives every config field full breathing room
          // while keeping the Workflow overview compact at 320px.
          width: (rightPanelTab === 'code' || rightPanelTab === 'task') ? 520 : 320,
          background: 'var(--v2-bg-surface)',
          borderLeft: '1px solid var(--v2-border-default)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          transition: 'width 180ms ease',
        }}>
          {/* ─── Tab strip (Conductor OSS-parity, #136) ─────────────────────────── */}
          {/* >> collapse on far left, then Workflow | Task | Code. Behind the
              `codeTab` feature flag so QA can A/B the header change on day one. */}
          {isCodeTabEnabled() ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: 40,
              borderBottom: '1px solid var(--v2-bg-muted)',
              padding: '0 4px',
              flexShrink: 0,
            }}>
              <button
                onClick={() => setRightPanelCollapsed(true)}
                title="Collapse panel"
                style={{
                  width: 28, height: 28, padding: 0,
                  border: 'none', background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--v2-text-muted)',
                  borderRadius: 6,
                }}
              >
                <PanelRightClose size={14} />
              </button>
              {[
                { key: 'workflow', label: 'Workflow', disabled: false, title: 'Workflow context — feeds into the AI Explainer' },
                { key: 'task',     label: 'Task',     disabled: false, title: 'Selected task properties' },
                { key: 'code',     label: 'Code',     disabled: false, title: 'Full-workflow JSON — bidirectional' },
              ].map((t) => {
                const active = rightPanelTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => !t.disabled && setRightPanelTab(t.key)}
                    disabled={t.disabled}
                    title={t.title}
                    style={{
                      padding: '6px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: active ? '2px solid var(--v2-action)' : '2px solid transparent',
                      color: t.disabled ? 'var(--v2-border-default)' : active ? 'var(--v2-text-primary)' : 'var(--v2-text-secondary)',
                      fontSize: 12,
                      fontWeight: active ? 600 : 500,
                      cursor: t.disabled ? 'not-allowed' : 'pointer',
                      fontFamily: 'Lexend, Inter, system-ui, sans-serif',
                      marginBottom: -1,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
              {codeTabError && rightPanelTab !== 'code' && (
                <button
                  onClick={() => setRightPanelTab('code')}
                  title="Code tab has JSON errors — click to fix"
                  style={{
                    marginLeft: 'auto',
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px',
                    border: '1px solid var(--v2-danger-soft)',
                    borderRadius: 10,
                    background: 'var(--v2-danger-soft)',
                    color: 'var(--v2-danger)',
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <AlertTriangle size={11} />
                  JSON error
                </button>
              )}
            </div>
          ) : (
            // Flag off — keep the legacy absolute-positioned collapse toggle.
            <button
              onClick={() => setRightPanelCollapsed(true)}
              title="Collapse panel"
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 10,
                width: 24, height: 24, padding: 0,
                border: '1px solid var(--v2-border-default)',
                borderRadius: 6,
                background: 'var(--v2-bg-surface)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--v2-text-muted)',
              }}
            >
              <PanelRightClose size={14} />
            </button>
          )}

          {/* ─── Tab content ────────────────────────────────────────────── */}
          {isCodeTabEnabled() && rightPanelTab === 'code' ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <WorkflowCodeTab
                nodes={canvas.nodes}
                connections={canvas.connections}
                workflowMeta={selectedWorkflow}
                readOnly={leftPanelMode === 'execution'}
                onValidityChange={setCodeTabError}
                onApply={(parsed) => {
                  // Mirror Conductor OSS: a valid paste/edit re-renders the DAG but
                  // we deliberately don't re-run auto-layout — users expect
                  // the positions they typed to be honored verbatim.
                  canvas.loadCanvasData(
                    { nodes: parsed.nodes, connections: parsed.connections },
                    { autoLayoutOnLoad: false, zoomToFitOnLoad: false },
                  );
                }}
              />
            </div>
          ) : isCodeTabEnabled() && rightPanelTab === 'workflow' ? (
            (() => {
              // Workflow tab — workflow-level context the AI Explainer
              // and AI Builder use to ground their output. Anything
              // typed here is persisted per workflow id and pumped into
              // the explainer's prompt as additional context.
              const ctx = workflowContext || {};
              const stoneInput = {
                width: '100%', padding: '8px 10px', fontSize: 12,
                fontFamily: 'inherit', color: 'var(--v2-text-primary)', background: 'var(--v2-bg-surface)',
                border: '1px solid var(--v2-border-default)', borderRadius: 6, outline: 'none',
                resize: 'vertical', boxSizing: 'border-box',
              };
              const Label = ({ children }) => (
                <div style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--v2-text-muted)',
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  marginBottom: 4, marginTop: 14,
                }}>{children}</div>
              );
              return (
                <div style={{
                  padding: 16, fontSize: 12,
                  fontFamily: 'Lexend, Inter, system-ui, sans-serif',
                  overflowY: 'auto',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
                    {selectedWorkflow?.name || 'Untitled workflow'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)', marginBottom: 12 }}>
                    Version {selectedWorkflow?.version ?? '—'} · {(canvas.nodes || []).length} nodes · {(canvas.connections || []).length} edges
                  </div>
                  {/* Phase B-fix.3 — right-panel Schedule editor REMOVED.
                    * Per operator design (see SYSTEM_WORKFLOW_RULES.md §2.6),
                    * the canonical edit surface for trigger config is the
                    * trigger NODE on the canvas — click the Schedule node →
                    * right-panel Task tab → Config to edit cron/timezone.
                    * Single source of truth; no duplication across panels. */}
                  <div style={{
                    padding: 10,
                    background: 'linear-gradient(135deg, var(--v2-action-glow), var(--v2-action-glow))',
                    border: '1px solid var(--v2-danger-soft)',
                    borderRadius: 6,
                    fontSize: 11, color: 'var(--v2-warning)', lineHeight: 1.5,
                  }}>
                    Anything you fill in here is fed to the <b>AI Explainer</b> on
                    every node — so explanations ground themselves in your
                    audience, regulatory environment, and downstream systems
                    instead of guessing.
                  </div>

                  <Label>Workflow purpose</Label>
                  <textarea
                    rows={2}
                    placeholder={selectedWorkflow?.description || 'In one or two sentences, what is the business outcome of this workflow?'}
                    value={ctx.description || ''}
                    onChange={(e) => updateWorkflowContext({ description: e.target.value })}
                    style={stoneInput}
                  />

                  <Label>Business / domain context</Label>
                  <textarea
                    rows={3}
                    placeholder="e.g. US health insurance prior-authorization for outpatient procedures; serves claims adjusters; volume is ~5k/day."
                    value={ctx.businessContext || ''}
                    onChange={(e) => updateWorkflowContext({ businessContext: e.target.value })}
                    style={stoneInput}
                  />

                  <Label>Primary audience</Label>
                  <input
                    type="text"
                    placeholder="e.g. Claims adjusters, fraud-ops analysts, compliance reviewers"
                    value={ctx.audience || ''}
                    onChange={(e) => updateWorkflowContext({ audience: e.target.value })}
                    style={{ ...stoneInput, resize: 'none' }}
                  />

                  <Label>Regulatory / compliance environment</Label>
                  <input
                    type="text"
                    placeholder="e.g. HIPAA, SOC 2, PCI-DSS scope, GDPR data residency in EU"
                    value={ctx.regulatoryEnv || ''}
                    onChange={(e) => updateWorkflowContext({ regulatoryEnv: e.target.value })}
                    style={{ ...stoneInput, resize: 'none' }}
                  />

                  <Label>Downstream systems / consumers</Label>
                  <textarea
                    rows={2}
                    placeholder="e.g. ServiceNow case manager, payer claims engine, customer email, Slack #ops-incidents"
                    value={ctx.downstreamSystems || ''}
                    onChange={(e) => updateWorkflowContext({ downstreamSystems: e.target.value })}
                    style={stoneInput}
                  />

                  <div style={{
                    marginTop: 16, padding: 10,
                    background: 'var(--v2-bg-muted)',
                    borderRadius: 6,
                    fontSize: 10, color: 'var(--v2-text-muted)', lineHeight: 1.5,
                  }}>
                    Saved locally per workflow. Backend persistence (so the
                    context follows the workflow across browsers / users)
                    ships with the next workflow-metadata write — until then,
                    each browser keeps its own copy.
                  </div>
                </div>
              );
            })()
          ) : canvas.selected && canvas.selectedType === 'node' ? (() => {
            const selNode = canvas.nodes.find((n) => n.id === canvas.selected);
            const selType = selNode?.type || '';
            const selShape = getNodeShapeType(selType);
            // Conductor OSS parity: Start / End / Terminate / Convergence are decorative
            // DAG boundary markers — they don't need the full NodePropertiesPanel.
            // Render a lightweight summary card instead so the DAG stays elegant
            // and operable without the heavy config pane clutter.
            const isSelLabelOnly =
              selShape === 'circle' ||
              selType === 'convergence' ||
              selType === 'terminate' ||
              selType === 'end' ||
              selType === 'terminal';
            if (isSelLabelOnly) {
              // Contextual subtitle based on node role
              const subtitle =
                selShape === 'circle' ? 'Workflow boundary marker' :
                selType === 'convergence' ? 'Branch convergence point' :
                (selType === 'terminate' || selType === 'terminal') ? 'Early workflow termination' :
                selType === 'end' ? 'Workflow end' :
                'Workflow marker';
              const termStatus = selNode?.config?.terminationStatus || selNode?.config?.status;
              const termReason = selNode?.config?.terminationReason || selNode?.config?.reason;
              return (
                <div style={{ padding: 20, fontFamily: 'Lexend, Inter, system-ui, sans-serif' }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 6 }}>
                    {selNode?.label || selType}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginBottom: 16 }}>
                    {subtitle}
                  </div>
                  {(selType === 'terminate' || selType === 'terminal') && (termStatus || termReason) && (
                    <div style={{
                      padding: 12,
                      borderRadius: 8,
                      background: 'var(--v2-bg-elevated)',
                      border: '1px solid var(--v2-border-default)',
                      marginBottom: 16,
                      fontSize: 12,
                      color: 'var(--v2-text-secondary)',
                    }}>
                      {termStatus && (
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: 'var(--v2-text-muted)', fontWeight: 500 }}>Status:</span>{' '}
                          <span style={{ fontWeight: 600 }}>{termStatus}</span>
                        </div>
                      )}
                      {termReason && (
                        <div>
                          <span style={{ color: 'var(--v2-text-muted)', fontWeight: 500 }}>Reason:</span>{' '}
                          {termReason}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)', marginBottom: 16, padding: '8px 0', borderTop: '1px solid var(--v2-bg-muted)' }}>
                    Node ID: {selNode?.id}
                  </div>
                  {/* G385 — Hide Delete Node when the canvas is locked.
                      Previously only `leftPanelMode !== 'execution'` gated
                      this button — which meant an AutoLet CEG (an immutable
                      runbook-derived blueprint) had a fully functional
                      red Delete Node button on every node. The hard
                      `isAutoLetCegReadOnly` check now also covers
                      autolet_ceg / syslet_canvas via workflow.kind. */}
                  {leftPanelMode !== 'execution' && !isAutoLetCegReadOnly && !isLetFullLock && (
                    <button
                      onClick={() => canvas.deleteNode(canvas.selected)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', border: '1px solid var(--v2-danger-soft)', borderRadius: 6,
                        background: 'var(--v2-bg-surface)', color: 'var(--v2-danger)', fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', width: '100%', justifyContent: 'center',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--v2-danger-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--v2-bg-surface)'; }}
                    >
                      <Trash2 size={13} /> Delete Node
                    </button>
                  )}
                  {/* G385 — Lock indicator replaces the button when the
                      canvas is read-only so the operator sees WHY the
                      action is unavailable rather than thinking it's a bug. */}
                  {(isAutoLetCegReadOnly || isLetFullLock) && leftPanelMode !== 'execution' && (
                    <div style={{
                      padding: '8px 14px', border: '1px dashed var(--v2-warning-soft)', borderRadius: 6,
                      background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)', fontSize: 11, lineHeight: 1.5,
                    }}>
                      {isAutoLetCegReadOnly
                        // RC-48 — Operator-facing tooltip; rule citation
                        // moved to engineering comments only.
                        ? 'This canvas is read-only. AutoLet CEGs are immutable — edits happen via the agent’s runbook.'
                        : 'Save is fully locked while the agent is in Act phase. Unpublish + regress to Build to delete nodes.'}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <CanvasErrorBoundary>
              {/* #191 — flex layout. The Explainer (when present) is
                  flexShrink:0 with its OWN internal scroll, so clicking
                  Input / Output / Attributes sub-tabs inside the
                  NodePropertiesPanel below scrolls THAT panel only;
                  the Explainer stays pinned at the top of the right
                  Task tab and the user never has to scroll back up to
                  re-find it. The Explainer itself is driven by
                  `explainerTriggerNode` (only updates on double-click),
                  not by `selNode` (single-click selection), so casual
                  clicks around the canvas don't reset it. */}
              <div style={{
                display: 'flex', flexDirection: 'column',
                height: '100%', minHeight: 0,
              }}>
                {isAIFeatureEnabled('explainerPanel') && (
                  <div style={{ flexShrink: 0 }}>
                    <AIExplainerPanel
                      node={explainerTriggerNode}
                      allNodes={canvas.nodes}
                      allConnections={canvas.connections}
                      executionState={canvas.executionState}
                      installedConnectors={installedConnectors}
                      workflowContext={workflowContext}
                      workflowId={selectedWorkflowId}
                      selectedNode={selNode}
                      onTriggerSelected={() => {
                        if (selNode && selNode.id) setExplainerTriggerNodeId(selNode.id);
                      }}
                    />
                  </div>
                )}
                {/* Bug #161 — Manual node-type swap. Renders only when a
                  * node is selected and the canvas isn't immutable. The
                  * component is internally guarded; passing null props is
                  * safe. */}
                <NodeTypeSwapControl
                  selectedNode={selNode}
                  canvas={canvas}
                  isReadOnly={isAutoLetCegReadOnly || isLetFullLock}
                  addToast={addToast}
                />
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <NodePropertiesPanel
                    node={selNode}
                    nodeDef={getNodeTypeDef(selType)}
                    /* G385 — Thread the lock-state through so the panel's
                       inputs and Delete button know to no-op. updateNode +
                       deleteNode bypass any kind of internal gating from
                       child components, so we have to neuter them here at
                       the boundary.
                       onDelete is set to null (not a no-op fn) when locked
                       so GeneralTab can hide the Delete Node button entirely
                       rather than render a button that silently does nothing. */
                    onUpdate={(isAutoLetCegReadOnly || isLetFullLock)
                      ? () => {}
                      : (updates) => canvas.updateNode(canvas.selected, updates)}
                    onDelete={(leftPanelMode === 'execution' || isAutoLetCegReadOnly || isLetFullLock)
                      ? null
                      : () => canvas.deleteNode(canvas.selected)}
                    executionState={canvas.executionState.get(canvas.selected)}
                    installedConnectors={installedConnectors}
                    installedConnectorsMkIndex={installedConnectorsMkIndex}
                    agents={agents}
                    getNodePorts={canvas.getNodePorts}
                    nodes={canvas.nodes}
                    connections={canvas.connections}
                    onDebugNode={handleDebugNode}
                    isExecutionMode={leftPanelMode === 'execution'}
                    /* RC-120b — Thread the currently-selected run + the
                     * replay handler so the failed-node deep-dive banner
                     * can offer "Replay with same inputs". Only valid in
                     * execution mode (otherwise selectedRunId is null
                     * and the banner won't render its replay button). */
                    selectedRunId={selectedRunId}
                    onReplayRun={handleReplayRun}
                    replayBusy={replayBusyRunId === selectedRunId}
                    /* RC-152c-1c — see prop docs on NodePropertiesPanel
                     * signature. Drilled down through V3Inspector → per-
                     * type inspectors so deep-link buttons work even
                     * when the URL doesn't carry ?workflowId=. */
                    selectedWorkflowId={selectedWorkflowId}
                  />
                </div>
              </div>
              </CanvasErrorBoundary>
            );
          })() : canvas.selected && canvas.selectedType === 'connection' ? (
            <ConnectionPropertiesPanel
              connection={canvas.connections.find((c) => c.id === canvas.selected)}
              /* G385 — Edge delete is a structural mutation. Refuse when
                 the canvas is locked. */
              onDelete={(isAutoLetCegReadOnly || isLetFullLock || isLetStructuralLock)
                ? () => {}
                : () => canvas.deleteConnection(canvas.selected)}
              onReroute={(newFrom, newFromPort, newTo, newToPort) => {
                // P1-C ported: atomic delete + add. canConnect runs inside
                // addConnection and returns null on cycle / self-loop /
                // duplicate, so the old edge is preserved if invalid.
                const oldId = canvas.selected;
                const probe = canvas.canConnect(newFrom, newFromPort, newTo, newToPort);
                if (!probe) {
                  addToast('Cannot reroute: would create a cycle, self-loop, or duplicate edge.', 'error');
                  return false;
                }
                const newId = canvas.addConnection(newFrom, newFromPort, newTo, newToPort);
                if (!newId) { addToast('Reroute failed', 'error'); return false; }
                canvas.deleteConnection(oldId);
                canvas.selectConnection(newId);
                addToast('Connection rerouted', 'success');
                return true;
              }}
              nodes={canvas.nodes}
              connections={canvas.connections}
              getNodePorts={canvas.getNodePorts}
            />
          ) : (
            <EmptyPropertiesPanel />
          )}
        </div>
        )}
      </div>

      {/* ===== MODALS ===== */}
      {renderNewWorkflowModal()}
      {renderSaveAsModal()}
      {renderExecuteInputModal()}
      {renderTemplateModal()}
      {renderIssuesPanelModal()}

      {/* RC-117 Phase 1 — Workflow Versions slideout. Self-contained
          component; fetches on open via listWorkflowVersions and shows
          the history with per-version metadata + a read-only JSON view.
          Phase 2 (RC-117b) layers diff + rollback inside this same
          component — the storage substrate (V282 + capture-on-save)
          ships with Phase 1 so the UI evolution doesn't need a schema
          change. */}
      {selectedWorkflow?.id && (
        <WorkflowVersionsSlideout
          isOpen={showVersionsOpen}
          onClose={() => setShowVersionsOpen(false)}
          workflowId={selectedWorkflow.id}
          workflowName={selectedWorkflow.name}
          /* RC-117b — Rollback reload hook.
           *
           * The BE rollback POST creates a new version equal to the
           * target (a forward-step in version numbering, not a
           * destructive rewind). We force a fresh canvas load so the
           * operator sees the rolled-back definition immediately
           * rather than the stale in-memory state. The toggle-null-
           * then-restore pattern fires the existing canvas load
           * effect that's wired to selectedWorkflowId; it's the same
           * mechanism Save As + workflow switching use, so we
           * don't introduce a new state-mutation path. */
          onRolledBack={({ newVersion, target }) => {
            const wfId = selectedWorkflow.id;
            addToast(`Rolled back to v${target}. Now at v${newVersion}.`, 'success');
            setSelectedWorkflowId(null);
            // Defer the re-select to the next tick so React commits
            // the null first; otherwise some load effects collapse
            // both into a no-op.
            setTimeout(() => setSelectedWorkflowId(wfId), 0);
          }}
        />
      )}

      {/* Auto-Create from process files — single dialog with an inline
          multi-file dropzone (no stacked slideouts). Stages files, then drafts
          a workflow via the AI Workflow Builder and loads it onto the canvas. */}
      <Modal
        isOpen={autoCreateFromFilesOpen}
        title="Auto-Create — from process files"
        onClose={() => { if (!autoCreateFromFilesBusy) setAutoCreateFromFilesOpen(false); }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 12.5, color: 'var(--v2-text-secondary)', margin: 0 }}>
            Upload one or more documents or spreadsheets. The AI Workflow Builder drafts a workflow
            from their steps and renders it on the canvas to review and Save As.
          </p>
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '22px',
            border: '2px dashed var(--v2-border-default)', borderRadius: 10, color: 'var(--v2-text-secondary)',
            background: 'var(--v2-bg-elevated)', fontSize: 13,
            cursor: (autoCreateUploading || autoCreateFromFilesBusy) ? 'not-allowed' : 'pointer',
          }}>
            <Upload size={16} />
            {autoCreateUploading ? 'Uploading…' : 'Click to choose files (PDF, DOCX, TXT, MD, XLSX)'}
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.html,.htm,.md,.txt,.xlsx"
              onChange={handleAutoCreateUpload}
              disabled={autoCreateUploading || autoCreateFromFilesBusy}
            />
          </label>
          {autoCreateUploads.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {autoCreateUploads.map((u) => (
                <div key={u.ref} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  border: '1px solid var(--v2-border-default)', borderRadius: 6,
                  padding: '7px 10px', background: 'var(--v2-bg-surface)',
                }}>
                  {u.isSpreadsheet
                    ? <FileSpreadsheet size={15} style={{ color: 'var(--v2-success)', flexShrink: 0 }} />
                    : <FileText size={15} style={{ color: 'var(--v2-text-muted)', flexShrink: 0 }} />}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--v2-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.filename}</span>
                  <button type="button" onClick={() => removeAutoCreateUpload(u.ref)} title={`Remove ${u.filename}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--v2-text-muted)', padding: 2, display: 'inline-flex' }}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              onClick={() => setAutoCreateFromFilesOpen(false)}
              disabled={autoCreateFromFilesBusy}
              style={{ padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-surface)', color: 'var(--v2-text-secondary)', cursor: autoCreateFromFilesBusy ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleAutoCreateDraft}
              disabled={autoCreateUploads.length === 0 || autoCreateUploading || autoCreateFromFilesBusy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6,
                fontSize: 13, fontWeight: 600, border: '1px solid var(--v2-action)',
                background: 'var(--v2-action)', color: 'var(--v2-bg-surface)',
                cursor: (autoCreateUploads.length === 0 || autoCreateUploading || autoCreateFromFilesBusy) ? 'not-allowed' : 'pointer',
                opacity: (autoCreateUploads.length === 0 || autoCreateUploading || autoCreateFromFilesBusy) ? 0.6 : 1,
              }}
            >
              <Sparkles size={14} /> {autoCreateFromFilesBusy ? 'Drafting…' : 'Draft workflow'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} title="Delete Workflow" onClose={() => setShowDeleteConfirm(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Linked skills block — prevents deletion */}
          {deleteLinkedSkills.length > 0 ? (
            <>
              <div style={{
                padding: '12px 16px',
                background: 'var(--v2-danger-soft)',
                border: '1px solid var(--v2-danger-soft)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <AlertTriangle size={18} style={{ color: 'var(--v2-danger)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 13, color: 'var(--v2-danger)' }}>
                  <strong>Cannot delete</strong> — this workflow is linked to <strong>{deleteLinkedSkills.length} skill(s)</strong> that depend on it:
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                    {deleteLinkedSkills.map((s, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <strong>{s.name}</strong>
                        {s.skillPhase && <span style={{
                          marginLeft: 6,
                          padding: '1px 6px',
                          borderRadius: 3,
                          fontSize: 11,
                          background: s.skillPhase === 'act' ? 'var(--v2-success-soft)' : s.skillPhase === 'decide' ? 'var(--v2-warning-soft)' : 'var(--v2-bg-muted)',
                          color: s.skillPhase === 'act' ? 'var(--v2-success)' : s.skillPhase === 'decide' ? 'var(--v2-warning)' : 'var(--v2-text-secondary)',
                        }}>{s.skillPhase}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
                Unlink or re-assign these skills to a different workflow before deleting. Go to <strong>Agent Lifecycle → Edit Skill</strong> to change the linked workflow.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowDeleteConfirm(false)} style={btnBase}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{
                padding: '12px 16px',
                background: 'var(--v2-danger-soft)',
                border: '1px solid var(--v2-danger-soft)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <AlertTriangle size={18} style={{ color: 'var(--v2-danger)', flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--v2-danger)' }}>
                  This will permanently delete <strong>{(selectedWorkflow?.name || '').length > 60 ? (selectedWorkflow.name.substring(0, 57) + '...') : (selectedWorkflow?.name || 'this workflow')}</strong> and all its configuration. This action cannot be undone.
                </div>
              </div>

              <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
                This workflow has never been executed and has no linked skills, so it is safe to delete.
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowDeleteConfirm(false)} style={btnBase}>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteWorkflow}
                  style={{
                    ...btnBase,
                    background: 'var(--v2-danger)',
                    color: 'var(--v2-bg-surface)',
                    border: '1px solid var(--v2-danger)',
                  }}
                >
                  <Trash2 size={14} style={{ marginRight: 4 }} />
                  Delete Workflow
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* v5.4.0 — RBAC Assignment modal removed; all assignment CRUD now lives in Team → Assignments */}

      {/* G268 / LET_CANVAS_GOLDEN_RULES §5.4 — Pre-save diff slideout.
        *
        * Opens when a canvas save is rejected by the backend phase-allowlist
        * (either WorkflowPhaseAllowlistGuard on PUT /workflows/{id} or
        * CanvasReconcileService on POST /canvas-reconcile). Renders the
        * server-supplied diff (attemptedAdds / attemptedRemoves /
        * attemptedMutates) and the rejection reason; the operator can
        * Cancel or click Regress to demote the agent's phase and retry
        * the save automatically.
        *
        * Same payload shape on both error sources means one component
        * handles both lock kinds — STRUCTURAL_LOCK (Decide → Build) and
        * FULL_LOCK (Act → Decide or Build, depending on whether the
        * change was structural).
        */}
      {diffModal?.open && (
        <>
          <div
            aria-hidden="true"
            onClick={regressingFromModal ? undefined : () => setDiffModal(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 80,
              background: 'rgba(15, 23, 42, 0.30)',
            }}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Canvas save blocked — phase allowlist"
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 600, maxWidth: '94vw', zIndex: 90,
              background: 'var(--v2-bg-surface)',
              boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.16)',
              display: 'flex', flexDirection: 'column',
            }}>
            <header style={{
              padding: '16px 20px 12px 20px',
              borderBottom: '1px solid var(--v2-border-default)',
              flexShrink: 0,
              background: diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  color: diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger)' : 'var(--v2-warning)',
                  background: diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)',
                  padding: '2px 7px', borderRadius: 4,
                  border: '1px solid ' + (diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)'),
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {diffModal.code === 'FULL_LOCK' ? 'Full lock' : 'Structural lock'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                  LET_CANVAS_GOLDEN_RULES §1 · {String(diffModal.phase || '').toUpperCase()} phase
                </span>
              </div>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--v2-text-primary)' }}>
                Save blocked — agent is in {String(diffModal.phase || '').toUpperCase()}
              </h2>
              <p style={{ fontSize: 12, color: 'var(--v2-text-secondary)', margin: '6px 0 0 0', lineHeight: 1.5 }}>
                {diffModal.error || 'The backend phase-allowlist rejected this save.'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={handleRegressFromDiffModal}
                  disabled={regressingFromModal || !diffModal.regressTarget}
                  style={{
                    padding: '6px 14px', borderRadius: 6,
                    border: '1px solid ' + (diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger)' : 'var(--v2-warning)'),
                    background: diffModal.code === 'FULL_LOCK' ? 'var(--v2-danger)' : 'var(--v2-warning)',
                    color: 'var(--v2-bg-surface)', fontWeight: 600, fontSize: 12,
                    cursor: regressingFromModal ? 'wait' : 'pointer',
                    opacity: regressingFromModal ? 0.6 : 1,
                  }}>
                  {regressingFromModal
                    ? 'Regressing…'
                    : `Regress to ${diffModal.regressTarget === 'build' ? 'Build' : 'Decide'} and retry`}
                </button>
                <button
                  type="button"
                  onClick={() => setDiffModal(null)}
                  disabled={regressingFromModal}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid var(--v2-border-default)',
                    background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)', fontWeight: 500, fontSize: 12,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setDiffModal(null)}
                  aria-label="Close"
                  style={{
                    marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--v2-border-default)',
                    background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', fontSize: 12, cursor: 'pointer',
                  }}>
                  <X size={14} />
                </button>
              </div>
            </header>

            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
              {/* Adds */}
              <section style={{ marginBottom: 18 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--v2-success)', background: 'var(--v2-success-soft)',
                    padding: '2px 7px', borderRadius: 4, border: '1px solid var(--v2-success-soft)',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>+ Add</span>
                  <span style={{ fontSize: 12, color: 'var(--v2-text-secondary)' }}>
                    {diffModal.attemptedAdds.length} skill{diffModal.attemptedAdds.length === 1 ? '' : 's'}
                  </span>
                </div>
                {diffModal.attemptedAdds.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>None.</div>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--v2-text-primary)' }}>
                    {diffModal.attemptedAdds.map((name, i) => (
                      <li key={`add_${i}`}>{name}</li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Removes */}
              <section style={{ marginBottom: 18 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--v2-danger)', background: 'var(--v2-danger-soft)',
                    padding: '2px 7px', borderRadius: 4, border: '1px solid var(--v2-danger-soft)',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>− Remove</span>
                  <span style={{ fontSize: 12, color: 'var(--v2-text-secondary)' }}>
                    {diffModal.attemptedRemoves.length} skill{diffModal.attemptedRemoves.length === 1 ? '' : 's'}
                  </span>
                </div>
                {diffModal.attemptedRemoves.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>None.</div>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--v2-text-primary)' }}>
                    {diffModal.attemptedRemoves.map((name, i) => (
                      <li key={`rem_${i}`}>{name}</li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Mutates */}
              <section style={{ marginBottom: 18 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--v2-accent-trust)', background: 'var(--v2-action-soft)',
                    padding: '2px 7px', borderRadius: 4, border: '1px solid var(--v2-accent-trust-soft)',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>~ Mutate</span>
                  <span style={{ fontSize: 12, color: 'var(--v2-text-secondary)' }}>
                    {diffModal.attemptedMutates.length} skill{diffModal.attemptedMutates.length === 1 ? '' : 's'}
                  </span>
                </div>
                {diffModal.attemptedMutates.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>None.</div>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--v2-text-primary)' }}>
                    {diffModal.attemptedMutates.map((entry, i) => (
                      <li key={`mut_${i}`}>{entry}</li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Invariant footer + escape hatch to the agent detail page. */}
              <div style={{
                marginTop: 14, padding: 12, borderRadius: 6,
                background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-border-default)',
                fontSize: 11.5, color: 'var(--v2-text-secondary)', lineHeight: 1.55,
              }}>
                <div style={{ fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
                  What "Regress" does
                </div>
                <div>
                  Demotes the agent from <code>{String(diffModal.phase || '').toUpperCase()}</code>
                  {' '}to <code>{String(diffModal.regressTarget || '').toUpperCase()}</code>, cascades
                  affected skills back through their lifecycle (one audit event per skill via
                  <code> AgentService.regressSkill</code>), and then re-fires this save. If
                  the agent was published from Act, the published flag is cleared and the
                  gateway is re-synced so the agentlet drops the stale bundle. To preview
                  these changes per-skill before they fire, open{' '}
                  <a href={linkedAgentId ? `/agents/${linkedAgentId}#act` : '#'}
                     style={{ color: 'var(--v2-action)', textDecoration: 'underline' }}>
                    the agent detail page
                  </a>.
                </div>
              </div>
            </div>
          </aside>
        </>
      )}

      {/* G265 / AGENT_EXECUTION_MODEL §2.1 — Cognitive Brief editor.
        * Right-edge slideout opened from double-click on the cognitive_loop
        * node. Edits agent.cognitiveBrief which is read at runtime to
        * configure the LLM-driven loop (system prompt, iteration cap,
        * tool selection strategy, cost cap, fallback behaviour). */}
      {cognitiveBriefOpen && (
        <>
          <div
            aria-hidden="true"
            onClick={savingCognitiveBrief ? undefined : () => setCognitiveBriefOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 80,
              background: 'rgba(15, 23, 42, 0.30)',
            }}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Cognitive Brief editor"
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 560, maxWidth: '92vw', zIndex: 90,
              background: 'var(--v2-bg-surface)',
              boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.16)',
              display: 'flex', flexDirection: 'column',
            }}>
            <header style={{
              padding: '16px 20px 12px 20px',
              borderBottom: '1px solid var(--v2-border-default)',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--v2-accent-trust)', background: 'var(--v2-accent-trust-soft)',
                  padding: '2px 7px', borderRadius: 4, border: '1px solid var(--v2-accent-trust-soft)',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>Cognitive</span>
                <span style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                  AGENT_EXECUTION_MODEL §2.1 · per-agent LLM brief
                </span>
              </div>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--v2-text-primary)' }}>Cognitive Brief</h2>
              <p style={{ fontSize: 12, color: 'var(--v2-text-muted)', margin: '4px 0 0 0' }}>
                {linkedAgent?.name || 'Agent'} — drives the LLM loop. Changes apply on the next execution.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={handleSaveCognitiveBrief}
                  disabled={savingCognitiveBrief}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid var(--v2-accent-trust)',
                    background: 'var(--v2-accent-trust)', color: 'var(--v2-bg-surface)', fontWeight: 600, fontSize: 12,
                    cursor: savingCognitiveBrief ? 'wait' : 'pointer',
                    opacity: savingCognitiveBrief ? 0.6 : 1,
                  }}>
                  {savingCognitiveBrief ? 'Saving…' : 'Save brief'}
                </button>
                <button
                  type="button"
                  onClick={() => setCognitiveBriefOpen(false)}
                  disabled={savingCognitiveBrief}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid var(--v2-border-default)',
                    background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)', fontWeight: 500, fontSize: 12,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setCognitiveBriefOpen(false)}
                  aria-label="Close"
                  style={{
                    marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--v2-border-default)',
                    background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', fontSize: 12, cursor: 'pointer',
                  }}>
                  <X size={14} />
                </button>
              </div>
            </header>

            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  System prompt <span style={{ color: 'var(--v2-danger)' }}>*</span>
                </label>
                <textarea
                  value={cognitiveBriefForm.systemPrompt}
                  onChange={(e) => setCognitiveBriefForm({ ...cognitiveBriefForm, systemPrompt: e.target.value })}
                  rows={8}
                  placeholder={'You are PolicyAuditor. When given an inbound request, …'}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 6,
                    border: '1px solid var(--v2-border-default)', fontSize: 12.5, lineHeight: 1.45,
                    fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
                    resize: 'vertical', boxSizing: 'border-box',
                  }}
                  disabled={savingCognitiveBrief}
                />
                <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)', marginTop: 4 }}>
                  Shown to the model on every iteration. Keep it precise; the catalog of available skills is appended automatically.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Tool selection strategy
                  </label>
                  <select
                    value={cognitiveBriefForm.toolSelectionStrategy}
                    onChange={(e) => setCognitiveBriefForm({ ...cognitiveBriefForm, toolSelectionStrategy: e.target.value })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid var(--v2-border-default)', fontSize: 12.5, background: 'var(--v2-bg-surface)',
                    }}
                    disabled={savingCognitiveBrief}>
                    <option value="auto">auto — model picks freely</option>
                    <option value="forced_sequence">forced_sequence — try skills in declared order</option>
                    <option value="react">react — reason then act</option>
                    <option value="tot">tot — tree of thoughts</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Max iterations
                  </label>
                  <input
                    type="number" min={1} max={200}
                    value={cognitiveBriefForm.maxIterations}
                    onChange={(e) => setCognitiveBriefForm({ ...cognitiveBriefForm, maxIterations: e.target.value })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid var(--v2-border-default)', fontSize: 12.5,
                    }}
                    disabled={savingCognitiveBrief}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Cost cap (USD/run)
                  </label>
                  <input
                    type="text" inputMode="decimal"
                    value={cognitiveBriefForm.iterationCostCapUsd}
                    placeholder="leave blank for org default"
                    onChange={(e) => setCognitiveBriefForm({ ...cognitiveBriefForm, iterationCostCapUsd: e.target.value })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid var(--v2-border-default)', fontSize: 12.5,
                    }}
                    disabled={savingCognitiveBrief}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Fallback behaviour
                  </label>
                  <select
                    value={cognitiveBriefForm.fallbackBehavior}
                    onChange={(e) => setCognitiveBriefForm({ ...cognitiveBriefForm, fallbackBehavior: e.target.value })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid var(--v2-border-default)', fontSize: 12.5, background: 'var(--v2-bg-surface)',
                    }}
                    disabled={savingCognitiveBrief}>
                    <option value="stop">stop — abort cleanly</option>
                    <option value="escalate">escalate — page on-call</option>
                    <option value="ask_human">ask_human — pause for approval</option>
                  </select>
                </div>
              </div>

              {cognitiveBriefError && (
                <div style={{
                  padding: '8px 12px', borderRadius: 6,
                  background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)',
                  color: 'var(--v2-danger)', fontSize: 12,
                }}>
                  {cognitiveBriefError}
                </div>
              )}

              <div style={{
                marginTop: 16, padding: '10px 12px', borderRadius: 6,
                background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)',
                fontSize: 11, color: 'var(--v2-text-muted)', lineHeight: 1.55,
              }}>
                <strong style={{ color: 'var(--v2-text-primary)' }}>Invariant:</strong>{' '}
                mode + brief are immutable across a published version. In Act phase
                the agent's execution mode chip is locked; regress + republish to
                change. The brief itself can still be edited in Build / Decide.
              </div>
            </div>
          </aside>
        </>
      )}
      {/* RC-20e — V3CommandPalette render removed. See import-section header. */}

      {/* RC-20a (task #1) — Platform-styled Unsaved Changes modal. Replaces
       *  the previous window.confirm() prompt. Three actions: Save & Continue
       *  (save current, then proceed), Discard (drop changes, proceed),
       *  Cancel (stay where you are). Dismiss-by-backdrop is intentionally
       *  disabled so the user has to make an explicit choice — silent
       *  backdrop dismissal was the failure mode the old window.confirm()
       *  protected against and we don't want to regress on that. */}
      {unsavedChangesPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Unsaved changes"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
        >
          <div
            style={{
              width: 'min(480px, calc(100vw - 32px))',
              background: 'var(--v2-bg-surface)',
              borderRadius: 10,
              boxShadow: '0 24px 60px -16px rgba(15, 23, 42, 0.32), 0 6px 20px -6px rgba(15, 23, 42, 0.18)',
              padding: '20px 22px 18px',
              fontFamily: 'inherit',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 10,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14,
                background: 'var(--v2-warning-soft)',
                color: 'var(--v2-warning)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800,
              }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--v2-text-primary)' }}>
                Unsaved changes
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--v2-text-secondary)', lineHeight: 1.55, marginBottom: 18 }}>
              You have unsaved edits on the current workflow. Choose how to proceed
              before opening the next one — Save commits and continues, Discard
              drops the edits and continues, Cancel keeps you here.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={unsavedChangesPrompt.onCancel}
                style={{
                  padding: '7px 14px',
                  fontSize: 12.5, fontWeight: 600,
                  color: 'var(--v2-text-secondary)',
                  background: 'var(--v2-bg-surface)',
                  border: '1px solid var(--v2-border-default)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={unsavedChangesPrompt.onDiscard}
                style={{
                  padding: '7px 14px',
                  fontSize: 12.5, fontWeight: 600,
                  color: 'var(--v2-danger)',
                  background: 'var(--v2-bg-surface)',
                  border: '1px solid var(--v2-danger-soft)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >Discard changes</button>
              <button
                type="button"
                onClick={unsavedChangesPrompt.onSave}
                style={{
                  padding: '7px 14px',
                  fontSize: 12.5, fontWeight: 700,
                  color: 'var(--v2-bg-surface)',
                  background: 'linear-gradient(135deg, var(--v2-action) 0%, var(--v2-action) 100%)',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >Save &amp; continue</button>
            </div>
          </div>
        </div>
      )}

      {/* RC-18b (task #200) — Pre-save Governance Diff Modal. Fires when
       *  the BE rejects a save with 409/423 + a structured diff body
       *  (WorkflowPhaseAllowlistGuard). Operator sees the rejected
       *  adds/removes/mutates + a "Regress to {phase}" button.
       *
       *  Intercept point — the host save handler should call
       *  setGovernanceDiff({ diff, agent, regressTarget, onConfirm })
       *  when an api.updateWorkflow / api.saveCanvas response returns
       *  status 409 or 423. Until that intercept is wired into useCanvas.save
       *  (next pass), this modal stays dormant. Operator's pending edits
       *  remain unsaved on cancel — the BE never wrote them. */}
      {governanceDiff && (
        <V3GovernanceDiffModal
          open
          /* Diff payload from BE rejection. Modal reads .attemptedAdds /
           * .attemptedRemoves / .attemptedMutates / .regressTarget from this. */
          diff={governanceDiff.diff}
          /* Linked agent — modal reads .phase from it to label the Regress
           * button ("Regress to {phase}"). Null is fine; button just disables. */
          agent={governanceDiff.agent}
          /* In-flight flags for the host save loop. Default false because
           * the host hasn't yet wired the BE-rejection intercept (RC-18b
           * tail-end work). Modal disables Esc + Cancel while either is true. */
          regressing={false}
          resaving={false}
          /* Cancel = close modal without acting. Modal calls this on
           * Esc / X click / backdrop click. */
          onCancel={() => setGovernanceDiff(null)}
          /* Regress = downgrade the agent to the diff.regressTarget phase
           * and retry the save. Host delegates the actual API call to the
           * onConfirm closure that was stored alongside the diff. */
          onRegress={async (target) => {
            try {
              if (typeof governanceDiff.onConfirm === 'function') {
                await governanceDiff.onConfirm(target);
              }
              setGovernanceDiff(null);
            } catch (e) {
              addToast(`Phase regress failed: ${e?.message || e}`, 'error');
            }
          }}
        />
      )}
    </div>
    </CanvasErrorBoundary>
  );
}

// ============================================================================
// AI WORKFLOW CHAT BAR
// ============================================================================

function AIWorkflowChatBar({ canvas, installedConnectors, addToast, createWorkflow, setSelectedWorkflowId, selectedWorkflowId, loadedWorkflowRef, orgId, tenantId, userId,
  // RC-42 — see render-site comment. Default false so the component is
  // still safe to render when the parent omits the prop (e.g. legacy
  // tests / dev shells). False = "treat as mutable" = same behaviour
  // as a normal Let canvas, which is the right baseline.
  isImmutableKindReadOnly = false,
  // RC-97f — Linked agent's skill catalog (array of {id, name, description}).
  // Default empty array means "no agent context" — the api helper omits
  // the field from the wire body and the BE skips the prompt block.
  // Identical to pre-RC-97f behavior on the standalone canvas path.
  linkedAgentSkills = [],
}) {
  const [expanded, setExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugging, setDebugging] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [lastDebugData, setLastDebugData] = useState(null); // raw debug API response for Fix flow
  // #182-followup — Tier-S #2: inline recommendation chips above the AI
  // input. Each `lastDebugData.quickFixes[i]` becomes a one-click chip.
  // appliedChipFixes hides chips that have been successfully applied;
  // dismissedChipFixes hides chips the user explicitly dismissed (X).
  // Both sets reset whenever a fresh Debug analysis completes so the
  // chip rail always reflects the most recent recommendations.
  const [appliedChipFixes, setAppliedChipFixes] = useState(() => new Set());
  const [dismissedChipFixes, setDismissedChipFixes] = useState(() => new Set());
  const [messages, setMessages] = useState([]);
  const [pendingPrompt, setPendingPrompt] = useState(null); // awaiting save/discard confirmation
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null); // AbortController for in-flight AI requests

  const isRunning = loading || debugging || fixing;

  const handleStopOrClear = () => {
    if (isRunning) {
      // Abort in-flight request
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      setLoading(false);
      setDebugging(false);
      setFixing(false);
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Stopped by user.', isError: true }]);
    } else {
      // Clear chat history
      setMessages([]);
      setLastDebugData(null);
      setPendingPrompt(null);
      setPrompt('');
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, pendingPrompt]);

  useEffect(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded]);

  /* Proceed with AI build (called directly or after confirmation).
   *
   * Bug #159 — the second param `forceNew` is set TRUE by the
   * Save/Discard confirm handlers below to unambiguously declare "this
   * is a fresh build; ignore prompt heuristics and current workflow
   * selection." Without it, a benign prompt like "Hello World" (which
   * doesn't match the new-workflow regex at line ~7974) could still
   * route the AI's output back into the previously-loaded workflow's
   * save slot, even though the user just explicitly elected to start
   * fresh via the modal. The forceNew flag short-circuits both the
   * prompt regex AND the auto-save-into-selected branch. */
  const doBuild = async (userMsg, forceNew = false) => {
    setMessages((prev) => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      // ── JSON Import Detection ──
      // If the user pastes a Conductor-style workflow JSON, parse and load it directly
      // without calling the AI service.
      const pastedJSON = extractWorkflowJSON(userMsg);
      if (pastedJSON) {
        const parsed = parseWorkflowJSON(pastedJSON);
        if (parsed && parsed.nodes.length > 0) {
          /* RC-117c — JSON-paste path is the "import" narrative.
           * Operator pasted a Conductor-style workflow JSON; the next
           * save records this as an import, not a manual save. */
          canvas.loadCanvasData({ nodes: parsed.nodes, connections: parsed.connections }, { autoLayoutOnLoad: true, changeKind: 'import' });
          requestAnimationFrame(() => { requestAnimationFrame(() => { canvas.zoomToFit && canvas.zoomToFit(); }); });

          const taskCount = parsed.nodes.length;
          const wfName = parsed.name || 'Imported Workflow';
          setMessages((prev) => [...prev, {
            role: 'assistant',
            text: `Imported "${wfName}" — ${taskCount} tasks loaded onto the canvas from JSON definition. You can now Save or Execute this workflow.`,
          }]);

          // Auto-save if a workflow is already selected
          if (selectedWorkflowId) {
            try { await canvas.saveData({ nodes: parsed.nodes, connections: parsed.connections }); }
            catch (e) { console.warn('Auto-save after JSON import failed:', e); }
          } else if (createWorkflow) {
            try {
              const result = await createWorkflow({
                name: `AI: ${wfName}`,
                description: parsed.description || 'Imported from JSON definition',
                orgId, tenantId, status: 'draft', createdBy: userId,
                definition: { nodes: parsed.nodes, connections: parsed.connections },
              });
              if (result?.ok && result.data?.id) {
                loadedWorkflowRef.current = result.data.id;
                setSelectedWorkflowId(result.data.id);
              }
            } catch (e) { console.warn('Auto-create after JSON import failed:', e); }
          }

          setLoading(false);
          return; // Skip AI call — JSON was loaded directly
        }
      }

      // Build the current canvas snapshot.
      // If the prompt signals a new/fresh workflow, send empty canvas so the AI
      // doesn't get confused by existing nodes from a different workflow.
      // Bug #159 — `forceNew` (set by the Save/Discard confirm handlers)
      // unconditionally treats the request as a fresh build, regardless
      // of whether the prompt happens to match the new-workflow regex.
      const lowerPrompt = userMsg.toLowerCase();
      const isNewWorkflowRequest = forceNew
        || /\b(new workflow|from scratch|fresh|blank|create a|build a|design a|start over|discard|ignore current)\b/.test(lowerPrompt);
      const currentCanvas = isNewWorkflowRequest
        ? { nodes: [], connections: [] }
        : { nodes: canvas.nodes || [], connections: canvas.connections || [] };

      // Get installed connector IDs
      const connectorIds = Object.keys(installedConnectors || {});

      const ac = new AbortController();
      abortRef.current = ac;
      // RC-97f — 5th arg: linked agent's skill catalog (empty array
      // when canvas is standalone; api helper omits the field when
      // empty so wire body is identical to pre-RC-97f for that case).
      const res = await aiCanvasBuild(userMsg, currentCanvas, connectorIds, ac.signal, linkedAgentSkills);


      if (res?.ok && res.data) {
        const data = res.data;

        // Check for error from the service
        if (data.error) {
          /* Bug #160 — the BE response carries a structured issues[] array
           * (severity, path, message) explaining exactly what failed in
           * validation. The previous handler dropped that detail and only
           * showed the generic banner — making it impossible for the
           * operator to know which node id was dangling, which skill was
           * unresolved, etc. Surface up to 5 issues so the operator can
           * see (and fix or report) the specific problem. */
          let errText = `Error: ${data.error}`;
          if (Array.isArray(data.issues) && data.issues.length > 0) {
            const issueLines = data.issues.slice(0, 5).map(iss => {
              const sev = iss.severity ? `[${iss.severity}]` : '';
              const path = iss.path ? ` ${iss.path}:` : '';
              return `  • ${sev}${path} ${iss.message || '(no detail)'}`;
            });
            errText += `\n\nIssues:\n${issueLines.join('\n')}`;
            if (data.issues.length > 5) {
              errText += `\n  …and ${data.issues.length - 5} more`;
            }
          }
          setMessages((prev) => [...prev, { role: 'assistant', text: errText, isError: true }]);
          setLoading(false);
          return;
        }

        const nodes = data.nodes || [];
        const connections = data.connections || [];
        const summary = data.summary || 'Workflow generated.';
        const warnings = data.warnings || [];

        if (nodes.length > 0) {
          /* RC-117c — Initial full-prompt build path. The next save
           * records this as "ai_build" so the Versions slideout reads
           * the right narrative; the operator's subsequent edits will
           * each be tagged "manual_save" because the ref clears after
           * the first consume. */
          canvas.loadCanvasData({ nodes, connections }, { autoLayoutOnLoad: true, changeKind: 'ai_build' });

          // Zoom to fit after a render cycle
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              canvas.zoomToFit && canvas.zoomToFit();
            });
          });

          // Auto-create or auto-save workflow so Save and Execute work immediately.
          //
          // BUGFIX — Bug #158: never auto-save AI output into the current
          // workflow when:
          //   (a) the user's prompt asks for a new/fresh workflow
          //       ("new workflow", "from scratch", "discard", etc.), OR
          //   (b) the current workflow is immutable per GOLDEN_CEG_RULES
          //       §1.3 (autolet_ceg / syslet_canvas, or a canonical
          //       workflow on a runbook-managed agent).
          //
          // Without this guard, asking the AI to "create a new workflow"
          // while a CEG is loaded silently attempts to mutate the CEG;
          // RunbookManagedGuard 409s the save, the FE catches the error
          // into console.warn, and the canvas is left in a desync'd state
          // with the new nodes but no toolbar (because the canvas state
          // diverged from the loaded workflow row). The right behaviour
          // is to spawn a brand-new standalone workflow row instead.
          const shouldSpawnNew = isNewWorkflowRequest || isImmutableKindReadOnly;

          if (selectedWorkflowId && !shouldSpawnNew) {
            // Workflow already selected and mutation is legal — auto-save
            // the AI modifications. Use saveData() with the AI data
            // directly to avoid stale closure in getCanvasData().
            try {
              await canvas.saveData({ nodes, connections });
            } catch (saveErr) {
              console.warn('Auto-save after AI modification failed:', saveErr);
            }
          } else if (createWorkflow) {
            /* Bug #158 — when shouldSpawnNew, clear the current selection
             * BEFORE attempting create so a failure path leaves the canvas
             * in a clean "unsaved new content" state (toolbar's Save As is
             * available, no association with the previously-loaded CEG). */
            if (shouldSpawnNew && selectedWorkflowId) {
              setSelectedWorkflowId(null);
            }
            try {
              // Derive a name from the user's prompt (first 50 chars)
              const autoName = userMsg.length > 50 ? userMsg.substring(0, 47) + '...' : userMsg;
              const wfResult = await createWorkflow({
                name: `AI: ${autoName}`,
                description: `Auto-generated by AI Workflow Builder`,
                orgId,
                tenantId,
                status: 'draft',
                createdBy: userId,
                definition: { nodes, connections },
              });
              if (wfResult?.ok && wfResult.data?.id) {
                // Pre-set ref so the parent's load effect skips redundant canvas reload
                if (loadedWorkflowRef) loadedWorkflowRef.current = wfResult.data.id;
                setSelectedWorkflowId(wfResult.data.id);
              } else {
                // API returned failure — canvas has nodes but no workflow record
                addToast('Workflow generated on canvas — use "Save As" to name and save it', 'warn');
              }
            } catch (wfErr) {
              console.warn('Auto-create workflow failed:', wfErr);
              // Non-fatal — canvas still has the nodes, user can save manually via "Save As"
              addToast('Workflow generated but auto-save failed — use "Save As" to save it', 'warn');
            }
          }

          let assistantMsg = `✓ ${summary}`;
          if (nodes.length > 0) {
            assistantMsg += `\n${nodes.length} node${nodes.length > 1 ? 's' : ''}, ${connections.length} connection${connections.length !== 1 ? 's' : ''}`;
          }
          if (warnings.length > 0) {
            assistantMsg += `\n⚠ ${warnings.join('; ')}`;
          }

          setMessages((prev) => [...prev, { role: 'assistant', text: assistantMsg }]);
          addToast(`AI Builder: ${summary}`, 'success');
        } else {
          setMessages((prev) => [...prev, { role: 'assistant', text: summary || 'No nodes generated. Try rephrasing your request.', isError: true }]);
        }
      } else {
        const errMsg = res?.data?.error || res?.data?.message || res?.error || 'Failed to generate workflow';
        setMessages((prev) => [...prev, { role: 'assistant', text: errMsg, isError: true }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: `Network error: ${err.message}`, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    const userMsg = prompt.trim();
    setPrompt('');

    /* Bug #159 — gate the Save/Discard confirmation on EITHER existing
     * canvas content OR a selected workflow. Previous gate only checked
     * nodeCount; that missed the case where a workflow was loaded but
     * the user hadn't yet added nodes (e.g. a freshly-loaded empty
     * draft, or a CEG that the user just opened). The contract per
     * design clarification: any time AI Build runs against a context
     * that has a workflow association, prompt the user first. */
    const nodeCount = (canvas.nodes || []).length;
    const hasExistingContext = nodeCount > 0 || !!selectedWorkflowId;
    console.log('[AIBuilder] handleSubmit: nodeCount=' + nodeCount
      + ', selectedWorkflowId=' + selectedWorkflowId
      + ', hasExistingContext=' + hasExistingContext);
    if (hasExistingContext) {
      console.log('[AIBuilder] Showing save/discard confirmation for: ' + userMsg);
      setPendingPrompt(userMsg);
      return;
    }

    // No existing work — build immediately on a clean slate (forceNew=true
    // because the user explicitly came in with no context AND wants a build).
    doBuild(userMsg, true);
  };

  const handleConfirmSave = async () => {
    const userMsg = pendingPrompt;
    setPendingPrompt(null);

    /* Bug #159 — Save the current workflow IF it's mutable. CEGs are
     * read-only per GOLDEN_CEG_RULES §1.3; calling save() on a CEG
     * 409s on the backend and would surface as a confusing error to
     * the operator. Skip the save in the immutable case — the AutoLet
     * already has its sealed CEG snapshot, there's nothing useful to
     * save here. The new standalone is what the operator actually wants. */
    if (!isImmutableKindReadOnly) {
      try {
        await canvas.save();
        addToast('Previous workflow saved', 'success');
      } catch (err) {
        console.warn('Save before new build failed:', err);
      }
    } else {
      addToast('Current workflow is an immutable AutoLet CEG — starting fresh standalone', 'info');
    }

    // Clear canvas and deselect workflow so a NEW one is created
    canvas.loadCanvasData({ nodes: [], connections: [] });
    if (loadedWorkflowRef) loadedWorkflowRef.current = null;
    setSelectedWorkflowId(null);

    // Clear chat history and start fresh. forceNew=true makes doBuild
    // treat this as a brand-new standalone build regardless of prompt text.
    setMessages([]);
    doBuild(userMsg, true);
  };

  const handleConfirmDiscard = () => {
    const userMsg = pendingPrompt;
    setPendingPrompt(null);

    // Clear canvas and deselect workflow so a NEW one is created
    canvas.loadCanvasData({ nodes: [], connections: [] });
    if (loadedWorkflowRef) loadedWorkflowRef.current = null;
    setSelectedWorkflowId(null);

    // Clear chat history and start fresh. forceNew=true makes doBuild
    // treat this as a brand-new standalone build regardless of prompt text.
    setMessages([]);
    doBuild(userMsg, true);
  };

  const handleConfirmCancel = () => {
    // Put the prompt back so the user doesn't lose it
    setPrompt(pendingPrompt || '');
    setPendingPrompt(null);
  };

  // ─── Debug / Analyze workflow ──────────────────────────────────────────
  const handleDebug = async () => {
    const nodeCount = (canvas.nodes || []).length;
    if (nodeCount === 0) {
      addToast('No workflow to debug — build one first', 'warn');
      return;
    }

    if (!expanded) setExpanded(true);

    const userQuestion = prompt.trim();
    setPrompt('');

    if (userQuestion) {
      setMessages((prev) => [...prev, { role: 'user', text: `🔍 Debug: ${userQuestion}` }]);
    } else {
      setMessages((prev) => [...prev, { role: 'user', text: '🔍 Analyze & debug this workflow' }]);
    }
    setDebugging(true);

    try {
      const currentCanvas = {
        nodes: canvas.nodes || [],
        connections: canvas.connections || [],
      };

      // Collect execution results if available
      let executionResults = null;
      if (canvas.executionState && canvas.executionState.size > 0) {
        executionResults = {};
        canvas.executionState.forEach((state, nodeId) => {
          executionResults[nodeId] = state;
        });
      }

      const connectorIds = Object.keys(installedConnectors || {});

      const ac = new AbortController();
      abortRef.current = ac;
      const res = await aiCanvasDebug(userQuestion || null, currentCanvas, executionResults, connectorIds, ac.signal);

      if (res?.ok && res.data) {
        const data = res.data;

        if (data.error) {
          /* Bug #160 — surface structured issues[] like the Build handler does. */
          let errText = `Error: ${data.error}`;
          if (Array.isArray(data.issues) && data.issues.length > 0) {
            const issueLines = data.issues.slice(0, 5).map(iss => {
              const sev = iss.severity ? `[${iss.severity}]` : '';
              const path = iss.path ? ` ${iss.path}:` : '';
              return `  • ${sev}${path} ${iss.message || '(no detail)'}`;
            });
            errText += `\n\nIssues:\n${issueLines.join('\n')}`;
            if (data.issues.length > 5) {
              errText += `\n  …and ${data.issues.length - 5} more`;
            }
          }
          setMessages((prev) => [...prev, { role: 'assistant', text: errText, isError: true }]);
          setDebugging(false);
          return;
        }

        // Format the debug report
        let report = '';

        if (data.summary) {
          const healthIcon = data.overallHealth === 'critical' ? '🔴' : data.overallHealth === 'warning' ? '🟡' : '🟢';
          report += `${healthIcon} ${data.summary}\n\n`;
        }

        if (data.quickFixes && data.quickFixes.length > 0) {
          report += '⚡ Quick Fixes:\n';
          data.quickFixes.forEach((fix, i) => {
            report += `${i + 1}. ${fix}\n`;
          });
          report += '\n';
        }

        if (data.nodeAnalysis && data.nodeAnalysis.length > 0) {
          report += '📋 Node Analysis:\n';
          data.nodeAnalysis.forEach((na) => {
            const icon = na.status === 'error' ? '❌' : na.status === 'warning' ? '⚠️' : '✅';
            report += `${icon} ${na.nodeLabel || na.nodeId}`;
            if (na.issues && na.issues.length > 0) {
              report += `: ${na.issues.join('; ')}`;
            }
            if (na.recommendations && na.recommendations.length > 0) {
              report += `\n   → ${na.recommendations.join('\n   → ')}`;
            }
            report += '\n';
          });
        }

        if (data.flowIssues && data.flowIssues.length > 0) {
          report += '\n🔗 Flow Issues:\n';
          data.flowIssues.forEach((issue) => {
            report += `• ${issue}\n`;
          });
        }

        // Fallback: if response was plain text analysis
        if (!report.trim() && data.analysis) {
          report = data.analysis;
        }

        if (!report.trim()) {
          report = 'Analysis complete — no issues found.';
        }

        // Store raw debug data for Fix flow
        const hasIssues = (data.overallHealth === 'critical' || data.overallHealth === 'warning') ||
          (data.quickFixes && data.quickFixes.length > 0) ||
          (data.nodeAnalysis && data.nodeAnalysis.some(n => n.status === 'error' || n.status === 'warning'));
        if (hasIssues) {
          setLastDebugData(data);
        } else {
          setLastDebugData(null);
        }
        // Fresh debug analysis → reset chip applied/dismissed state so
        // every quickFix surfaces in the rail above the input.
        setAppliedChipFixes(new Set());
        setDismissedChipFixes(new Set());

        setMessages((prev) => [...prev, { role: 'assistant', text: report.trim(), isDebug: true, hasFixableIssues: hasIssues }]);
      } else {
        const errMsg = res?.data?.error || res?.data?.message || res?.error || 'Debug analysis failed';
        console.error('[ai-debug] Debug failed:', errMsg, res);
        setMessages((prev) => [...prev, { role: 'assistant', text: errMsg, isError: true }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: `Network error: ${err.message}`, isError: true }]);
    } finally {
      setDebugging(false);
    }
  };

  // ─── Fix workflow based on debug recommendations ────────────────────
  const handleFix = async () => {
    if (!lastDebugData) {
      addToast('No debug data — run Debug first to identify issues', 'warn');
      return;
    }

    if (!expanded) setExpanded(true);

    setMessages((prev) => [...prev, { role: 'user', text: '🔧 Apply AI-recommended fixes to workflow' }]);
    setFixing(true);

    try {
      // Compose a detailed fix prompt from the debug data
      const fixInstructions = [];

      // Include root cause summary
      if (lastDebugData.summary) {
        fixInstructions.push('ROOT CAUSE: ' + lastDebugData.summary);
      }

      // Include quick fixes
      if (lastDebugData.quickFixes && lastDebugData.quickFixes.length > 0) {
        fixInstructions.push('PRIORITY FIXES (apply these first):\n' + lastDebugData.quickFixes.map((f, i) => `${i + 1}. ${f}`).join('\n'));
      }

      // Include per-node fixes with deep analysis data
      if (lastDebugData.nodeAnalysis && lastDebugData.nodeAnalysis.length > 0) {
        const nodeFixLines = lastDebugData.nodeAnalysis
          .filter(na => (na.status === 'error' || na.status === 'warning') && (na.recommendations?.length > 0 || na.configFixes))
          .map(na => {
            let line = `Node "${na.nodeLabel || na.nodeId}" (${na.nodeId}):`;
            if (na.issues?.length > 0) line += `\n  Issues: ${na.issues.join('; ')}`;
            if (na.inputAnalysis) line += `\n  Input analysis: ${na.inputAnalysis}`;
            if (na.configAnalysis) line += `\n  Config analysis: ${na.configAnalysis}`;
            if (na.outputAnalysis) line += `\n  Output analysis: ${na.outputAnalysis}`;
            if (na.recommendations?.length > 0) line += `\n  Recommended fixes: ${na.recommendations.join('; ')}`;
            if (na.configFixes && Object.keys(na.configFixes).length > 0) {
              line += `\n  APPLY THESE EXACT CONFIG CHANGES: ${JSON.stringify(na.configFixes)}`;
            }
            return line;
          });
        if (nodeFixLines.length > 0) {
          fixInstructions.push('NODE-SPECIFIC FIXES (apply configFixes directly to the node config):\n' + nodeFixLines.join('\n\n'));
        }
      }

      // Include flow-level issues
      if (lastDebugData.flowIssues && lastDebugData.flowIssues.length > 0) {
        fixInstructions.push('FLOW-LEVEL FIXES:\n' + lastDebugData.flowIssues.map(f => `• ${f}`).join('\n'));
      }

      const fixPrompt = [
        'Apply the following debug-recommended fixes to the existing workflow.',
        'CRITICAL RULES:',
        '- Preserve ALL existing node IDs (do NOT regenerate IDs).',
        '- Apply configFixes by merging into the existing node config — do not replace the entire config.',
        '- If a configFixes says {"assignments": [...]}, SET that assignments array in the node config.',
        '- If a configFixes says {"inputMapping": {...}}, SET that inputMapping in the node config.',
        '- If a configFixes says {"action": "..."}, UPDATE the action field in the node config.',
        '- Do NOT remove nodes unless explicitly recommended in flow-level fixes.',
        '- Do NOT change node IDs, connection IDs, or node types unless the fix specifically requires it.',
        '- For data_transform nodes: ALWAYS include assignments array with name+expression for each variable to extract.',
        '- For connector_task nodes: ALWAYS include action, connectorId, connectorType, and skillParams.',
        '',
        fixInstructions.join('\n\n'),
      ].join('\n');

      // Send as a modify request (canvas has nodes, so backend auto-detects modify mode)
      const currentCanvas = {
        nodes: canvas.nodes || [],
        connections: canvas.connections || [],
      };
      const connectorIds = Object.keys(installedConnectors || {});

      const ac = new AbortController();
      abortRef.current = ac;
      // RC-97f — see same-file aiCanvasBuild call notes; identical pattern.
      const res = await aiCanvasBuild(fixPrompt, currentCanvas, connectorIds, ac.signal, linkedAgentSkills);

      if (res?.ok && res.data) {
        const data = res.data;

        if (data.error) {
          setMessages((prev) => [...prev, { role: 'assistant', text: `Fix failed: ${data.error}`, isError: true }]);
          setFixing(false);
          return;
        }

        const nodes = data.nodes || [];
        const connections = data.connections || [];
        const summary = data.summary || 'Fixes applied.';
        const warnings = data.warnings || [];

        if (nodes.length > 0) {
          /* RC-117c — AI Debug → Fix path. Tag the next save as
           * "ai_fix" so Versions history correctly narrates this as
           * AI-driven, not operator-driven. */
          canvas.loadCanvasData({ nodes, connections }, { autoLayoutOnLoad: true, changeKind: 'ai_fix' });

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              canvas.zoomToFit && canvas.zoomToFit();
            });
          });

          // Auto-save if workflow exists — use saveData() with the AI data directly
          // because canvas.save() would capture stale pre-fix data from getCanvasData()
          if (selectedWorkflowId) {
            try {
              await canvas.saveData({ nodes, connections });
            } catch (saveErr) {
              console.warn('Auto-save after AI fix failed:', saveErr);
            }
          }

          // Clear execution state since we've modified the workflow
          if (canvas.clearExecutionState) {
            canvas.clearExecutionState();
          }
          // Also clear isExecuting/activeRunId so Execute button is re-enabled
          try { setIsExecuting(false); setActiveRunId(null); } catch (_) { /* guard */ }

          let assistantMsg = `✅ Fixes applied: ${summary}`;
          assistantMsg += `\n${nodes.length} node${nodes.length > 1 ? 's' : ''}, ${connections.length} connection${connections.length !== 1 ? 's' : ''}`;
          if (warnings.length > 0) {
            assistantMsg += `\n⚠ ${warnings.join('; ')}`;
          }
          assistantMsg += '\n\n💡 Review the updated workflow, then Save & Execute to test.';

          setMessages((prev) => [...prev, { role: 'assistant', text: assistantMsg, isFix: true }]);
          addToast('AI fixes applied — review and execute', 'success');

          // Clear debug data since fixes have been applied
          setLastDebugData(null);
        } else {
          setMessages((prev) => [...prev, { role: 'assistant', text: 'Fix attempt generated no nodes — try Debug again for updated recommendations.', isError: true }]);
        }
      } else {
        const errMsg = res?.data?.error || res?.data?.message || res?.error || 'Fix generation failed';
        setMessages((prev) => [...prev, { role: 'assistant', text: errMsg, isError: true }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: `Network error: ${err.message}`, isError: true }]);
    } finally {
      setFixing(false);
    }
  };

  // Suggestions are derived from the loaded workflow rather than
  // hardcoded so the chip rail nudges the user toward changes that are
  // actually relevant to what's on canvas. Three buckets:
  //   1. Empty canvas — "build" prompts (start something new).
  //   2. Single-node / very small canvas — "extend" prompts that
  //      reference the node label / type the user has actually placed.
  //   3. Larger workflow — "harden" prompts focused on resilience,
  //      observability, and the gaps we can detect from the node mix
  //      (no error handler? no notification? long linear chain?).
  // We deliberately keep this client-side and heuristic — server-side
  // suggestion generation is a backlog item but every round-trip costs
  // perceived UX latency on a chip rail, and the heuristic gets ~80% of
  // the way there for free.
  const suggestions = useMemo(() => {
    const nodes = (canvas && canvas.nodes) || [];
    const conns = (canvas && canvas.connections) || [];
    const types = new Set(nodes.map((n) => (n && n.type) || ''));
    const labelOf = (n) => (n && (n.label || n.id)) || 'this node';
    const nodeCount = nodes.length;

    // ── Empty canvas ──
    if (nodeCount === 0) {
      return [
        'Create a phishing triage workflow that checks threat intel and escalates to Slack',
        'Build an incident response playbook with an approval gate before isolation',
        'Generate a customer onboarding workflow with KYC + ServiceNow ticket creation',
        'Draft a vulnerability triage flow that scores CVEs and pages on critical findings',
      ];
    }

    // ── 1 or 2 nodes — encourage growth around what the user placed ──
    if (nodeCount <= 2) {
      const tail = nodes[nodes.length - 1];
      const tailLabel = labelOf(tail);
      const out = [];
      out.push(`Add an error-handling branch after ${tailLabel}`);
      out.push(`Add a Slack notification step after ${tailLabel}`);
      out.push(`Add a human approval gate before ${tailLabel}`);
      out.push(`Wire ${tailLabel}'s output as input to a downstream task`);
      return out;
    }

    // ── Larger workflow — propose targeted improvements ──
    const hasCondition  = types.has('condition') || types.has('decision') || types.has('switch');
    const hasFork       = types.has('fork') || types.has('fork_join') || types.has('fork_join_dynamic') || types.has('parallel');
    const hasApproval   = types.has('approval') || types.has('approval_gate') || types.has('human_approval');
    const hasNotify     = nodes.some((n) => /slack|email|notify|notification|sms|teams/i.test((n && (n.type || n.label || '')) || ''));
    const hasErrorPath  = nodes.some((n) => /error|fail|exception|retry/i.test((n && (n.type || n.label || '')) || ''));
    const hasAudit      = nodes.some((n) => /audit|log|trail/i.test((n && (n.type || n.label || '')) || ''));

    const out = [];
    if (!hasErrorPath) out.push('Add a global error-handling branch with retry + dead-letter');
    if (!hasNotify) out.push('Add a Slack/email notification on terminal success and failure');
    if (!hasApproval && nodes.length >= 4) out.push('Insert a human approval gate before the final action');
    if (!hasCondition && nodes.length >= 4) out.push('Add a decision branch to skip downstream work when input is empty');
    if (!hasFork && nodes.length >= 5) out.push('Run independent steps in parallel with a fork/join to cut latency');
    if (!hasAudit) out.push('Add an audit-log task so every run is traceable for compliance');

    // Always offer at least one shape-of-it improvement so the rail isn't empty.
    if (out.length === 0) {
      out.push(`Tighten timeouts and retries across all ${nodeCount} steps`);
      out.push('Add an output schema to the last node so downstream consumers can subscribe');
      out.push('Document each node with a one-sentence Description for future engineers');
    }

    // Cap at 4 chips — more than that and the rail starts wrapping.
    return out.slice(0, 4);
  }, [canvas?.nodes, canvas?.connections]);

  // ─── Apply a single chip-fix from the Debug recommendations rail ───
  // Sends a focused build prompt that includes ONLY this one fix
  // (vs `handleFix` which bundles every quickFix + nodeAnalysis +
  // flowIssues into one mega-prompt). The motivation is that the rail
  // surfaces 3 chips, each independent, so the LLM can apply them one
  // at a time and the user can review the workflow between fixes.
  // After success: marks this chip as applied (hidden from the rail),
  // pushes a confirmation message into the chat, and toasts.
  const handleApplyChipFix = useCallback(async (fixText) => {
    if (!fixText || !canvas) return;
    if (!expanded) setExpanded(true);
    setMessages((prev) => [...prev, { role: 'user', text: `🔧 Apply: ${fixText}` }]);
    setFixing(true);
    try {
      const currentCanvas = { nodes: canvas.nodes, connections: canvas.connections };
      const connectorIds = Object.keys(installedConnectors || {});
      const fixPrompt = [
        'Apply ONLY the following AI-recommended fix to the existing workflow.',
        'CRITICAL RULES:',
        '- Preserve ALL existing node IDs (do NOT regenerate IDs).',
        '- Do NOT remove nodes unless this fix explicitly requires it.',
        '- Return the COMPLETE updated workflow, not a delta.',
        '',
        `FIX TO APPLY: ${fixText}`,
      ].join('\n');
      const ac = new AbortController();
      abortRef.current = ac;
      // RC-97f — see same-file aiCanvasBuild call notes; identical pattern.
      const res = await aiCanvasBuild(fixPrompt, currentCanvas, connectorIds, ac.signal, linkedAgentSkills);
      if (res?.ok && res.data?.nodes && res.data.nodes.length > 0) {
        /* RC-117c — chip-apply is the AI-driven quick-fix path; tag the
         * next save's narrative so Versions history reads "AI fix"
         * instead of "Manual save". */
        canvas.loadCanvasData(res.data, { autoLayoutOnLoad: false, zoomToFitOnLoad: false, changeKind: 'ai_fix' });
        setMessages((prev) => [...prev, {
          role: 'assistant', isFix: true,
          text: `✓ Applied: ${fixText}\n\nReview the canvas and execute when ready.`,
        }]);
        addToast('AI fix applied — review and execute', 'success');
        setAppliedChipFixes((prev) => {
          const next = new Set(prev);
          next.add(fixText);
          return next;
        });
      } else {
        setMessages((prev) => [...prev, {
          role: 'assistant', isError: true,
          text: 'Fix attempt generated no nodes — try Debug again for updated recommendations.',
        }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant', isError: true,
        text: `Fix failed: ${err.message || String(err)}`,
      }]);
    } finally {
      setFixing(false);
    }
  }, [canvas, expanded, installedConnectors, addToast]);

  const handleDismissChipFix = useCallback((fixText) => {
    setDismissedChipFixes((prev) => {
      const next = new Set(prev);
      next.add(fixText);
      return next;
    });
  }, []);

  // ─── Color tokens (agentlet brand: warm red-orange + clean white) ───
  // ── Brand-aligned palette (matches AI Template Builder) ──
  const ai = {
    color: 'var(--v2-action)',            // brand primary — accent color for this builder
    bg: 'var(--v2-bg-surface)',
    stone50: 'var(--v2-bg-elevated)',
    stone100: 'var(--v2-bg-muted)',
    stone200: 'var(--v2-border-default)',
    stone400: 'var(--v2-text-disabled)',
    stone500: 'var(--v2-text-muted)',
    stone700: 'var(--v2-text-secondary)',
    stone900: 'var(--v2-text-primary)',
  };

  // Collapsed: sleek bar with gradient
  if (!expanded) {
    return (
      <div
        onClick={() => setExpanded(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          background: `linear-gradient(135deg, ${ai.color}08, ${ai.color}03)`,
          borderBottom: `1px solid ${ai.stone200}`,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ width: 28, height: 28, borderRadius: 8, background: ai.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Sparkles size={14} color={ai.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ai.stone900 }}>AI Workflow Builder</div>
          <div style={{ fontSize: 10, color: ai.stone500 }}>Build, modify, or debug workflows with AI</div>
        </div>
        <ChevronDown size={14} color={ai.stone400} />
      </div>
    );
  }

  // Expanded: full chat interface matching Template Builder pattern
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: ai.bg,
      borderBottom: `1px solid ${ai.stone200}`,
      maxHeight: 340, minHeight: 140,
    }}>
      {/* Header — gradient background with icon container */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          background: `linear-gradient(135deg, ${ai.color}08, ${ai.color}03)`,
          borderBottom: `1px solid ${ai.stone200}`, cursor: 'pointer', userSelect: 'none',
        }}
        onClick={() => setExpanded(false)}
      >
        <div style={{ width: 28, height: 28, borderRadius: 8, background: ai.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Sparkles size={14} color={ai.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ai.stone900, display: 'flex', alignItems: 'center', gap: 6 }}>
            AI Workflow Builder
            {loading && <Loader size={11} color={ai.color} style={{ animation: 'spin 1s linear infinite' }} />}
          </div>
          <div style={{ fontSize: 10, color: ai.stone500 }}>Build, modify, or debug workflows with AI</div>
        </div>
        <ChevronUp size={14} color={ai.stone400} />
      </div>

      {/* Messages — with avatars, matching Template Builder bubble style */}
      <div style={{ flex: 1, overflow: 'auto', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Suggestions — shown only when no messages */}
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: ai.stone400, textTransform: 'uppercase', marginBottom: 2 }}>Suggestions</div>
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => { setPrompt(s); inputRef.current?.focus(); }}
                style={{
                  padding: '7px 12px', fontSize: 11, background: ai.stone50, color: ai.stone700,
                  border: `1px solid ${ai.stone200}`, borderRadius: 8, cursor: 'pointer',
                  textAlign: 'left', lineHeight: 1.4, width: '100%',
                  display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                }}
              >
                <ArrowRight size={11} color={ai.color} style={{ flexShrink: 0 }} />
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((msg, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', gap: 8, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
              {/* Avatar */}
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                background: msg.role === 'user' ? ai.color : ai.color + '15',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {msg.role === 'user' ? <User size={11} color="#fff" /> : <Bot size={11} color={ai.color} />}
              </div>
              {/* Bubble */}
              <div style={{
                maxWidth: (msg.isDebug || msg.isFix) ? '92%' : '85%',
                padding: (msg.isDebug || msg.isFix) ? '8px 12px' : '8px 12px',
                borderRadius: 12,
                borderTopRightRadius: msg.role === 'user' ? 4 : 12,
                borderTopLeftRadius: msg.role === 'user' ? 12 : 4,
                background: msg.role === 'user' ? ai.color
                  : msg.isFix ? 'var(--v2-success-soft)'
                  : msg.isDebug ? 'var(--v2-action-soft)'
                  : msg.isError ? 'var(--v2-danger-soft)'
                  : ai.stone50,
                color: msg.role === 'user' ? 'var(--v2-bg-surface)'
                  : msg.isFix ? 'var(--v2-success)'
                  : msg.isDebug ? 'var(--v2-warning)'
                  : msg.isError ? 'var(--v2-danger)'
                  : ai.stone900,
                fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                ...(msg.isDebug ? { border: '1px solid var(--v2-danger-soft)', fontFamily: 'monospace, Inter, system-ui' } : {}),
                ...(msg.isFix ? { border: '1px solid var(--v2-success-soft)' } : {}),
              }}>
                {msg.text}
              </div>
            </div>
            {/* Inline Fix button after debug messages with fixable issues */}
            {msg.isDebug && msg.hasFixableIssues && lastDebugData && (
              <div style={{ alignSelf: 'flex-start', marginLeft: 32, marginTop: -4 }}>
                <button
                  onClick={handleFix}
                  disabled={fixing || loading}
                  style={{
                    padding: '5px 14px', fontSize: 11, fontWeight: 600,
                    background: (fixing || loading) ? 'var(--v2-border-default)' : 'var(--v2-success)',
                    color: (fixing || loading) ? 'var(--v2-text-disabled)' : 'var(--v2-bg-surface)',
                    border: 'none', borderRadius: 8,
                    cursor: (fixing || loading) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}
                  title="Apply AI-recommended fixes to the workflow"
                >
                  {fixing ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Wrench size={12} />}
                  {fixing ? 'Applying Fixes...' : 'Apply Fixes'}
                </button>
              </div>
            )}
          </React.Fragment>
        ))}
        {/* Loading state — with bot avatar and animated dots */}
        {(loading || debugging || fixing) && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: ai.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={11} color={ai.color} />
            </div>
            <div style={{
              padding: '8px 12px', borderRadius: 12, borderTopLeftRadius: 4,
              background: fixing ? 'var(--v2-success-soft)' : debugging ? 'var(--v2-action-soft)' : ai.stone50,
              fontSize: 12, color: fixing ? 'var(--v2-success)' : debugging ? 'var(--v2-warning)' : ai.stone500,
              display: 'flex', alignItems: 'center', gap: 6,
              ...(fixing ? { border: '1px solid var(--v2-success-soft)' } : {}),
            }}>
              <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />
              <span className="ai-typing">
                {fixing ? 'Applying AI fixes' : debugging ? 'Analyzing workflow' : 'Generating workflow'}
              </span>
              <style>{`.ai-typing::after { content: '...'; animation: dots 1.5s infinite; } @keyframes dots { 0%,20% { content: '.'; } 40% { content: '..'; } 60%,100% { content: '...'; } }`}</style>
            </div>
          </div>
        )}
        {/* Save/Discard confirmation prompt */}
        {pendingPrompt && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: ai.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={11} color={ai.color} />
            </div>
            <div style={{
              maxWidth: '90%', padding: '10px 14px', borderRadius: 12, borderTopLeftRadius: 4,
              background: ai.stone50, border: `1px solid ${ai.stone200}`,
              fontSize: 12, lineHeight: 1.6, color: ai.stone900,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Would you like to save the current workflow before building a new one?
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={handleConfirmSave} style={{ padding: '5px 14px', fontSize: 11, fontWeight: 600, borderRadius: 8, background: ai.color, color: 'var(--v2-bg-surface)', border: 'none', cursor: 'pointer' }}>
                  Save & Continue
                </button>
                <button onClick={handleConfirmDiscard} style={{ padding: '5px 14px', fontSize: 11, fontWeight: 600, borderRadius: 8, background: 'var(--v2-danger)', color: 'var(--v2-bg-surface)', border: 'none', cursor: 'pointer' }}>
                  Discard
                </button>
                <button onClick={handleConfirmCancel} style={{ padding: '5px 14px', fontSize: 11, fontWeight: 500, borderRadius: 8, background: 'transparent', color: ai.stone500, border: `1px solid ${ai.stone200}`, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* AI Recommendations rail — Tier-S #2 from CANVAS_V3_DIFFERENTIATORS.md.
          When Debug returns with quickFixes, surface the top 3 still-actionable
          ones as one-click chips above the input. Each chip → click → focused
          aiCanvasBuild with just that fix → canvas reloads. Chips that have
          been applied or dismissed disappear; the next Debug run resets both
          sets so the rail always reflects the freshest analysis.
          Gated by `features.ai.recommendationChips` (default ON). */}
      {isAIFeatureEnabled('recommendationChips') && (() => {
        const all = (lastDebugData && Array.isArray(lastDebugData.quickFixes))
          ? lastDebugData.quickFixes : [];
        const visible = all
          .filter((f) => typeof f === 'string' && f.length > 0
            && !appliedChipFixes.has(f) && !dismissedChipFixes.has(f))
          .slice(0, 3);
        if (visible.length === 0) return null;
        const fixingNow = !!fixing;
        const cantClick = loading || debugging || fixingNow;
        return (
          <div style={{
            padding: '8px 14px',
            background: 'var(--v2-warning-soft)',
            borderTop: `1px solid ${ai.stone200}`,
            display: 'flex', flexDirection: 'column', gap: 5,
          }}>
            <div style={{
              fontSize: 9, fontWeight: 700, color: 'var(--v2-warning)',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Sparkles size={10} style={{ color: 'var(--v2-warning)' }} />
              AI recommendations · click to apply
              {fixingNow && (
                <Loader size={10} style={{ color: 'var(--v2-warning)', animation: 'spin 1s linear infinite', marginLeft: 4 }} />
              )}
            </div>
            {visible.map((fix, i) => (
              <div key={`${i}-${fix.slice(0, 20)}`} style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                <button
                  type="button"
                  onClick={() => handleApplyChipFix(fix)}
                  disabled={cantClick}
                  title="Apply this fix to the workflow"
                  style={{
                    flex: 1, textAlign: 'left',
                    padding: '6px 10px', fontSize: 11,
                    background: cantClick ? ai.stone100 : 'var(--v2-bg-surface)',
                    color: 'var(--v2-warning)',
                    border: '1px solid var(--v2-warning-soft)', borderRadius: 6,
                    cursor: cantClick ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontFamily: 'inherit', lineHeight: 1.4,
                  }}
                >
                  <Wrench size={11} style={{ color: 'var(--v2-warning)', flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{fix}</span>
                  <ArrowRight size={11} style={{ color: 'var(--v2-warning)', flexShrink: 0 }} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDismissChipFix(fix)}
                  disabled={cantClick}
                  title="Dismiss this recommendation"
                  style={{
                    width: 24, padding: 0,
                    background: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: 6,
                    cursor: cantClick ? 'not-allowed' : 'pointer',
                    color: ai.stone400,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            ))}
            {all.length > visible.length && (
              <div style={{
                fontSize: 10, color: ai.stone500, marginTop: 2,
                fontStyle: 'italic',
              }}>
                +{all.length - visible.length} more in the chat — click <b>Fix</b> to apply everything at once.
              </div>
            )}
          </div>
        );
      })()}

      {/* Input — stone background footer matching Template Builder */}
      <form onSubmit={handleSubmit} style={{
        display: 'flex', gap: 8, padding: '10px 16px',
        borderTop: `1px solid ${ai.stone200}`, background: ai.stone50,
      }}>
        <input
          ref={inputRef}
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe a workflow to build or modification to make..."
          disabled={loading || fixing || !!pendingPrompt}
          style={{
            flex: 1, padding: '8px 12px', fontSize: 12, background: 'var(--v2-bg-surface)',
            color: ai.stone900, border: `1px solid ${ai.stone200}`, borderRadius: 8,
            outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
          onFocus={(e) => { e.target.style.borderColor = ai.color; }}
          onBlur={(e) => { e.target.style.borderColor = ai.stone200; }}
        />
        {/* Build button — gated by `features.ai.canvasBuilder`. */}
        {isAIFeatureEnabled('canvasBuilder') && (
          <button
            type="submit"
            disabled={loading || debugging || fixing || !prompt.trim() || !!pendingPrompt}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              background: (loading || debugging || fixing || !prompt.trim() || pendingPrompt) ? ai.stone200 : ai.color,
              color: 'var(--v2-bg-surface)', border: 'none', borderRadius: 8,
              cursor: (loading || debugging || fixing || !prompt.trim() || pendingPrompt) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {loading ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />}
            {loading ? 'Building...' : 'Build'}
          </button>
        )}
        {/* Debug button — gated by `features.ai.canvasDebugger`. */}
        {isAIFeatureEnabled('canvasDebugger') && (
          <button
            type="button"
            onClick={handleDebug}
            disabled={loading || debugging || (canvas.nodes || []).length === 0}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              background: (loading || debugging || (canvas.nodes || []).length === 0) ? ai.stone200 : ai.stone900,
              color: 'var(--v2-bg-surface)', border: 'none', borderRadius: 8,
              cursor: (loading || debugging || (canvas.nodes || []).length === 0) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
            title="Analyze workflow node-by-node and get fix recommendations"
          >
            {debugging ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Bug size={12} />}
            Debug
          </button>
        )}
        {lastDebugData && (
          <button
            type="button"
            onClick={handleFix}
            disabled={loading || debugging || fixing}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              background: (loading || debugging || fixing) ? ai.stone200 : 'var(--v2-success)',
              color: 'var(--v2-bg-surface)', border: 'none', borderRadius: 8,
              cursor: (loading || debugging || fixing) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
            title="Apply AI-recommended fixes from the last debug analysis"
          >
            {fixing ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Wrench size={12} />}
            Fix
          </button>
        )}
        {(isRunning || messages.length > 0) && (
          <button
            type="button"
            onClick={handleStopOrClear}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              background: isRunning ? 'var(--v2-danger)' : ai.stone500,
              color: 'var(--v2-bg-surface)', border: 'none', borderRadius: 8,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
            }}
            title={isRunning ? 'Stop the current AI operation' : 'Clear chat history'}
          >
            {isRunning ? <StopCircle size={12} /> : <X size={12} />}
            {isRunning ? 'Stop' : 'Clear'}
          </button>
        )}
      </form>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

function CanvasPageHeader({ viewMode, setViewMode, onNew }) {
  const modeBtn = (mode, icon, label) => (
    <button
      key={mode}
      onClick={() => setViewMode(mode)}
      style={{
        padding: '6px 12px', borderRadius: 6,
        border: `1px solid ${viewMode === mode ? 'var(--v2-text-primary)' : 'var(--v2-border-default)'}`,
        background: viewMode === mode ? 'var(--v2-text-primary)' : 'var(--v2-bg-surface)',
        color: viewMode === mode ? 'var(--v2-bg-surface)' : 'var(--v2-text-secondary)',
        fontWeight: viewMode === mode ? 600 : 400,
        fontSize: 12, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'Lexend, Inter, system-ui, sans-serif',
      }}
      title={`Switch to ${label}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 20, gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {modeBtn('canvas', <Eye size={14} />, 'Design Canvas')}
        {modeBtn('list', <Layers size={14} />, 'Workflow List')}
        {modeBtn('orchestration', <Activity size={14} />, 'Orchestration')}
      </div>
      <button
        onClick={onNew}
        style={{
          padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
          background: 'var(--v2-action)', color: 'var(--v2-bg-surface)', border: 'none', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'Lexend, Inter, system-ui, sans-serif',
        }}
      >
        <Plus size={14} />
        New Workflow
      </button>
    </div>
  );
}

// RC-CanvasV3-PaletteCanvasSync — single non-connector palette row. Owns
// its own ref so the scroll-into-view + highlight ring only re-runs when
// THIS row becomes the highlighted one, not on every PaletteCategory
// re-render. Splitting this out keeps the parent's render path clean.
function PaletteRow({ nodeDef, NodeIcon, disabled, isHighlighted }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!isHighlighted || !ref.current) return;
    // 'nearest' avoids dragging the entire palette to the top of the
    // panel on every selection — only scrolls if the row is actually
    // out of view. Smooth behavior makes the motion noticeable but not
    // jarring.
    try {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      // older browsers without smooth-scroll fallback to a plain jump.
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isHighlighted]);
  return (
    <button
      ref={ref}
      // RC-20f — Click-to-add removed; palette is read-only.
      disabled={disabled}
      style={{
        padding: '6px 8px',
        background: disabled
          ? 'var(--v2-bg-muted)'
          : isHighlighted
            ? 'var(--v2-action-soft)'
            : 'var(--v2-bg-surface)',
        border: isHighlighted
          ? '1px solid var(--v2-action-strong)'
          : '1px solid var(--v2-border-default)',
        outline: isHighlighted ? '2px solid var(--v2-action-soft)' : 'none',
        outlineOffset: isHighlighted ? 1 : 0,
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: disabled ? 'var(--v2-text-disabled)' : 'var(--v2-text-primary)',
        transition: 'background-color 180ms ease, border-color 180ms ease, outline-color 180ms ease',
      }}
      title={nodeDef.description}
    >
      <NodeIcon size={13} />
      <span style={{ flex: 1, textAlign: 'left' }}>{nodeDef.label}</span>
    </button>
  );
}

function PaletteCategory({ category, onAddNode, disabled, expanded, onToggle, installedConnectors, installedConnectorsMkIndex, highlightTypeId }) {
  const nodeDefs = getCategoryNodes(category.id);
  const Icon = getIcon(category.icon);
  const isConnector = category.id === 'connector';

  /* RC-122 — installed-only filter for the connector palette.
   *
   * Pre-RC-122 the palette listed every marketplace-catalogued
   * connector (49+ entries) regardless of whether the tenant had
   * configured any of them. Operators couldn't tell at a glance which
   * tiles were USABLE (have an active connector instance) vs which
   * were just marketplace-listings they'd need to install first; they
   * clicked Slack, dragged it in, and the workflow blew up at runtime
   * with "connector not configured."
   *
   * The filter defaults to ON when the tenant has at least one
   * installed connector (the common case — operator wants to see
   * what they can use NOW). It defaults to OFF when the tenant has
   * zero installed connectors so they still see the catalog to browse
   * for what to install first.
   *
   * Zero regression: when toggle is OFF, the rendering is identical
   * to the pre-RC-122 behavior. The toggle is per-PaletteCategory
   * instance state (not persisted) so opening the canvas always
   * starts in the high-signal "installed-only" view; operators who
   * want catalog-browsing flip the toggle every session — a small
   * cost vs the every-render noise of the pre-fix default.
   */
  const installedCount = React.useMemo(() => {
    if (!isConnector || !installedConnectors) return 0;
    return Object.values(installedConnectors).filter(Boolean).length;
  }, [isConnector, installedConnectors]);
  const [showOnlyInstalled, setShowOnlyInstalled] = React.useState(false);
  // Default-true the first time we have evidence the tenant has installed
  // connectors. We don't `setState` inside render for free; instead the
  // useEffect below flips the flag once on installedCount transition
  // 0 → >0. After that, the user owns the toggle.
  const didInitInstalledFilterRef = React.useRef(false);
  React.useEffect(() => {
    if (!isConnector) return;
    if (didInitInstalledFilterRef.current) return;
    if (installedCount > 0) {
      setShowOnlyInstalled(true);
      didInitInstalledFilterRef.current = true;
    }
  }, [isConnector, installedCount]);

  // Check installation status for a connector node
  const getConnectorStatus = React.useCallback((nodeDef) => {
    if (!nodeDef.connectorMeta) return null;
    const mkId = nodeDef.connectorMeta.marketplaceId;
    // First check marketplace-id index, then fall back to searching by name
    if (installedConnectorsMkIndex?.[mkId]) return installedConnectorsMkIndex[mkId];
    // Fallback: search installed connectors by name match
    if (installedConnectors) {
      const match = Object.values(installedConnectors).find((c) =>
        c.name && nodeDef.connectorMeta.name && c.name.toLowerCase() === nodeDef.connectorMeta.name.toLowerCase()
      );
      if (match) return match;
    }
    return null;
  }, [installedConnectors, installedConnectorsMkIndex]);

  // For connectors, group by marketplace sub-category for better organization.
  // RC-122 — when showOnlyInstalled is on, drop nodes that aren't installed
  // for this tenant; drop empty groups so the catalog UI stays clean.
  const connectorGroups = React.useMemo(() => {
    if (!isConnector) return null;
    const groups = {};
    nodeDefs.forEach((nd) => {
      if (showOnlyInstalled && !getConnectorStatus(nd)) return;
      const mCat = nd.connectorMeta?.marketplaceCategory || 'other';
      const catLabel = nd.connectorMeta?.category || mCat;
      if (!groups[mCat]) groups[mCat] = { label: catLabel, nodes: [] };
      groups[mCat].nodes.push(nd);
    });
    return groups;
  }, [isConnector, nodeDefs, showOnlyInstalled, getConnectorStatus]);

  // Count of connector tiles hidden by the installed-only filter — drives
  // the "Browse N more connectors" footer that unhides them.
  const hiddenConnectorCount = React.useMemo(() => {
    if (!isConnector || !showOnlyInstalled) return 0;
    return nodeDefs.filter((nd) => !getConnectorStatus(nd)).length;
  }, [isConnector, showOnlyInstalled, nodeDefs, getConnectorStatus]);

  const renderConnectorItem = (nodeDef) => {
    const NodeIcon = getIcon(nodeDef.icon);
    const installed = getConnectorStatus(nodeDef);
    const isActive = installed?.status === 'active';
    const emoji = nodeDef.connectorMeta?.icon || '';

    return (
      <button
        key={nodeDef.type}
        // RC-20f (task #1) — Click-to-add removed from the left palette.
        // Insertion ONLY happens through the TaskPicker (per-node "+"
        // button or ⌘E global trigger). The left palette is now a
        // read-only browsing surface for discovering node types;
        // clicking a tile no longer slaps it onto the canvas. Drag-and-
        // drop was removed in RC-14 for the same reason — both produced
        // accidental insertions in the wrong DAG slot.
        disabled={disabled}
        style={{
          padding: '5px 8px',
          background: disabled ? 'var(--v2-bg-muted)' : isActive ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)',
          border: isActive ? '1px solid var(--v2-success-soft)' : '1px solid var(--v2-border-default)',
          borderRadius: 4,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          color: disabled ? 'var(--v2-text-disabled)' : 'var(--v2-text-primary)',
          position: 'relative',
        }}
        title={`${nodeDef.description}${installed ? ` [${installed.status}]` : ' [not installed]'}`}
      >
        {emoji ? <span style={{ fontSize: 12 }}>{emoji}</span> : <NodeIcon size={12} />}
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nodeDef.label}
        </span>
        {isActive && (
          <CheckCircle size={10} style={{ color: 'var(--v2-success)', flexShrink: 0 }} />
        )}
        {installed && !isActive && (
          <Circle size={10} style={{ color: 'var(--v2-warning)', flexShrink: 0 }} />
        )}
        {nodeDef.connectorMeta?.skills?.length > 0 && (
          <span style={{ fontSize: 9, color: 'var(--v2-text-disabled)', flexShrink: 0 }}>
            {nodeDef.connectorMeta.skills.length}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '8px 8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--v2-text-primary)',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Icon size={14} style={{ color: category.color }} />
        <span>{category.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--v2-text-disabled)' }}>
          {nodeDefs.length}
        </span>
      </button>

      {expanded && !isConnector && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 8px 4px 32px' }}>
          {nodeDefs.map((nodeDef) => {
            const NodeIcon = getIcon(nodeDef.icon);
            // RC-CanvasV3-PaletteCanvasSync — highlight + scroll-into-view
            // for the canvas-selected type. The ref + scroll runs once per
            // type change via the effect just below the map.
            const isHighlighted = highlightTypeId && nodeDef.type === highlightTypeId;
            return (
              <PaletteRow
                key={nodeDef.type}
                nodeDef={nodeDef}
                NodeIcon={NodeIcon}
                disabled={disabled}
                isHighlighted={isHighlighted}
              />
            );
          })}
        </div>
      )}

      {/* Connector palette: grouped by marketplace sub-category */}
      {expanded && isConnector && connectorGroups && (
        <div style={{ padding: '4px 8px 4px 20px' }}>
          {/* RC-122 — Installed-only toggle. The check-box-style affordance
              uses a plain checkbox to mirror the rest of the V2 palette
              chrome (no V2 Toggle primitive ships with this color tone).
              `installedCount` is the source-of-truth label so operators
              know what flipping the toggle does without trial-and-error. */}
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 4px 6px', fontSize: 10,
              color: 'var(--v2-text-secondary)',
              cursor: 'pointer', userSelect: 'none',
            }}
            title={showOnlyInstalled
              ? 'Showing connectors configured for this tenant'
              : 'Showing all marketplace connectors (most aren\'t configured yet)'}
          >
            <input
              type="checkbox"
              checked={showOnlyInstalled}
              onChange={(e) => setShowOnlyInstalled(e.target.checked)}
              style={{ margin: 0, width: 12, height: 12 }}
            />
            <span>Installed only ({installedCount})</span>
          </label>

          {/* Empty-state: filter is on but no connectors are installed.
              Surfaces a clear next step instead of an unexplained blank
              area below the category header. */}
          {showOnlyInstalled && Object.keys(connectorGroups).length === 0 && (
            <div style={{
              padding: '8px 4px 12px', fontSize: 11,
              color: 'var(--v2-text-muted)', lineHeight: 1.45,
            }}>
              No connectors configured for this tenant yet.{' '}
              <button
                type="button"
                onClick={() => setShowOnlyInstalled(false)}
                style={{
                  padding: 0, background: 'none', border: 'none',
                  color: 'var(--v2-action)', cursor: 'pointer',
                  fontSize: 11, textDecoration: 'underline',
                }}
              >
                Browse the catalog
              </button>
              {' '}to install one.
            </div>
          )}

          {Object.entries(connectorGroups).map(([catId, group]) => (
            <div key={catId} style={{ marginBottom: 8 }}>
              <div style={{
                fontSize: 9, fontWeight: 700, color: 'var(--v2-text-disabled)', textTransform: 'uppercase',
                padding: '4px 4px 2px', letterSpacing: 0.5,
              }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 4 }}>
                {group.nodes.map(renderConnectorItem)}
              </div>
            </div>
          ))}

          {/* RC-122 — "Browse N more" footer. Only rendered when there
              ARE hidden tiles AND the filter is currently on; clicking
              it unhides everything (operator can still re-check the
              filter to re-collapse). Friendlier than a permanent "Show
              all" button because it shows the cost of the click. */}
          {hiddenConnectorCount > 0 && (
            <button
              type="button"
              onClick={() => setShowOnlyInstalled(false)}
              style={{
                width: '100%', padding: '6px 4px', marginTop: 4,
                background: 'transparent', border: '1px dashed var(--v2-border-default)',
                borderRadius: 4, cursor: 'pointer',
                fontSize: 10, color: 'var(--v2-text-muted)',
                textAlign: 'center',
              }}
            >
              Browse {hiddenConnectorCount} more connectors…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SearchNodeResults({ query, onAddNode }) {
  const results = searchNodes(query);

  if (results.length === 0) {
    return (
      <div style={{ padding: 12, color: 'var(--v2-text-disabled)', fontSize: 12, textAlign: 'center' }}>
        No nodes found matching "{query}"
      </div>
    );
  }

  return (
    <div style={{ padding: '0 8px' }}>
      {results.map((nodeDef) => {
        const Icon = getIcon(nodeDef.icon);
        return (
          <button
            key={nodeDef.type}
            // RC-14 (task #194) — Click-to-add via onAddNode (= handleAddNode).
            // RC-20f — Click-to-add removed from search results too;
            // palette + search are now read-only browsing surfaces.
            style={{
              width: '100%',
              padding: '8px',
              marginBottom: 4,
              background: 'var(--v2-bg-surface)',
              border: '1px solid var(--v2-border-default)',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
            title={nodeDef.description}
          >
            <Icon size={14} style={{ color: nodeDef.color }} />
            <div style={{ textAlign: 'left', flex: 1 }}>
              <div style={{ fontWeight: 600, color: 'var(--v2-text-primary)' }}>{nodeDef.label}</div>
              <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)' }}>{nodeDef.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function CanvasNode({
  node,
  selected,
  onSelect,
  onMultiSelect,
  onStartDrag,
  onEndDrag,
  onStartConnect,
  onEndConnect,
  isConnecting,
  pan,
  zoom,
  getPortPosition,
  nodeDef,
  connections,
  executionState,
}) {
  // Compute which ports have active connections (to hide empty port dots)
  const connectedInPorts = React.useMemo(() => {
    const set = new Set();
    (connections || []).forEach(c => {
      if (c.to === node.id) set.add(c.toPort || 'default');
    });
    return set;
  }, [connections, node.id]);

  const connectedOutPorts = React.useMemo(() => {
    const set = new Set();
    (connections || []).forEach(c => {
      if (c.from === node.id) set.add(c.fromPort || 'default');
    });
    return set;
  }, [connections, node.id]);
  // Correct transform: screen = canvas * zoom + pan
  // Phase R1: the IR pipeline stamps per-type heights onto each node via
  // `layoutDAGviaIR` (rule S-G — heights bucketed to {100…150} by task type).
  // Legacy Sugiyama sets no node.h, so the `|| NODE_H` fallback keeps its
  // flat 100px behaviour intact. Width stays flat across every scalar task.
  const effectiveH = (typeof node.h === 'number' && node.h > 0) ? node.h : NODE_H;
  const screenX = node.x * zoom + pan.x;
  const screenY = node.y * zoom + pan.y;
  const screenW = NODE_W * zoom;
  const screenH = effectiveH * zoom;

  const execStatus = executionState?.status;

  const statusBadge = (() => {
    if (!execStatus) return null;
    if (execStatus === 'running') return <Loader size={12} style={{ animation: 'spin 1s linear infinite', color: 'var(--v2-bg-surface)' }} />;
    if (execStatus === 'completed') return <CheckCircle size={12} style={{ color: 'var(--v2-bg-surface)' }} />;
    if (execStatus === 'failed') return <XCircle size={12} style={{ color: 'var(--v2-bg-surface)' }} />;
    if (execStatus === 'skipped') return <SkipForward size={12} style={{ color: 'var(--v2-bg-surface)' }} />;
    if (execStatus === 'awaiting_approval') return <Clock size={12} style={{ color: 'var(--v2-bg-surface)' }} />;
    return null;
  })();

  // Execution state drives the entire node appearance
  const borderColor = (() => {
    if (execStatus === 'running') return 'var(--v2-action)';    // blue
    if (execStatus === 'completed') return 'var(--v2-success)';  // green
    if (execStatus === 'failed') return 'var(--v2-danger)';     // red
    if (execStatus === 'skipped') return 'var(--v2-text-disabled)';    // grey
    return selected ? 'var(--v2-action)' : 'var(--v2-border-default)';
  })();

  const nodeFill = (() => {
    if (execStatus === 'running') return 'var(--v2-bg-muted)';    // light blue bg
    if (execStatus === 'completed') return 'var(--v2-bg-muted)';  // light green bg
    if (execStatus === 'failed') return 'var(--v2-danger-soft)';     // light red bg
    if (execStatus === 'skipped') return 'var(--v2-bg-elevated)';    // light grey bg
    return selected ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)';
  })();

  const statusBarColor = (() => {
    if (execStatus === 'running') return 'var(--v2-action)';    // blue
    if (execStatus === 'completed') return 'var(--v2-success)';  // green
    if (execStatus === 'failed') return 'var(--v2-danger)';     // red
    if (execStatus === 'skipped') return 'var(--v2-text-disabled)';    // grey
    return nodeDef?.color || 'var(--v2-action)';
  })();

  const Icon = getIcon(nodeDef?.icon);

  const isExecMode = !!execStatus; // true if this node has execution state

  // ── Shape classification (uses centralized SHAPE_GEOM) ──
  const nodeType = node.type || '';
  const shape = getNodeShapeType(nodeType);
  const isStartNode = nodeType === 'manual_start';
  const isTriggerNode = ['webhook', 'cron', 'schedule', 'event_trigger'].includes(nodeType);
  const isEndNode = nodeType === 'end';              // 'end' only — 'terminate' is interactive task
  const isTerminateNode = nodeType === 'terminate';   // Terminate keeps bookend pill rendering
  const isCircleNode = shape === 'circle';            // Start/End — non-interactive circle labels
  const isBookendNode = shape === 'bookend';          // Terminate only now
  const isDiamondNode = shape === 'diamond';
  const isInlineExternal = node.config?.gelPhase === 'INLINE_EXTERNAL_TASK';
  // Convergence-like nodes render as compact Conductor OSS-style pills (catch pill / Join Branches).
  // Match the fan-in set used by the edge renderer (FAN_IN_TARGET_TYPES below) so
  // Conductor JOIN tasks — which the converter emits as canvas type `parallel_join`
  // or `join` — use the same compact pill as SWITCH convergence markers instead of
  // falling through to the full task-box render (which produced a large
  // rectangular "Join Branches / Parallel Join" box instead of a tight pill).
  const isConvergenceNode = (
    nodeType === 'convergence' ||
    nodeType === 'join' ||
    nodeType === 'parallel_join' ||
    nodeType === 'switch_join'
  );
  // Conductor OSS parity: loop (DO_WHILE / FOR_EACH) and sub_workflow nodes need visual
  // badges so they're instantly recognizable in the DAG.
  const isLoopNode = nodeType === 'loop' || nodeType === 'do_while' || nodeType === 'for_each';
  const isSubworkflowNode = nodeType === 'sub_workflow' || nodeType === 'start_workflow' || nodeType === 'async_sub_workflow';
  // Conductor OSS parity: Start/End circles are PURE DISPLAY — not interactive, not selectable, no panel.
  // (Click them on Conductor OSS and nothing happens — no right task panel.) Convergence remains selectable
  // for deletion flows, but we skip selection for Start/End entirely.
  const isDisplayOnlyNode = isCircleNode;
  const isLabelOnly = isCircleNode || isConvergenceNode;

  // ── Shape geometry (derived from SHAPE_GEOM, converted to screen coords) ──
  // Circle — Conductor OSS-style small circle for Start/End labels
  const circleCX = screenX + screenW / 2;
  const circleCY = screenY + screenH / 2;
  const circleR = SHAPE_GEOM.circle.RADIUS * zoom;

  // Bookend pill (now only used for Terminate)
  const bookendH = SHAPE_GEOM.bookend.pillH * zoom;
  const bookendY = screenY + (screenH - bookendH) / 2;
  const bookendR = bookendH / 2;

  // Diamond — uses SHAPE_GEOM.diamond which extends beyond the bounding box
  const diamondCX = screenX + screenW / 2;
  const diamondCY = screenY + screenH / 2;
  const diamondRX = SHAPE_GEOM.diamond.HALF_W * zoom;
  const diamondRY = SHAPE_GEOM.diamond.HALF_H * zoom;

  return (
    <g
      data-node-id={node.id}
      onMouseDown={(e) => {
        // Conductor OSS parity: Start/End circles are pure display — clicking them does NOTHING.
        // No selection, no right panel, no drag. Let the event bubble to canvas background.
        if (isDisplayOnlyNode) return;
        e.stopPropagation();
        // Label-only nodes (Convergence) — selectable but NOT draggable
        if (e.shiftKey) onMultiSelect();
        else onSelect();
        // Only allow dragging for interactive nodes in design mode
        if (!isLabelOnly && !isExecMode) onStartDrag(e);
      }}
      onMouseUp={(e) => {
        if (isDisplayOnlyNode) return;
        e.stopPropagation();
        if (!isExecMode && onEndDrag) onEndDrag();
        if (!isExecMode && isConnecting && onEndConnect) {
          onEndConnect(node.id, 'default');
        }
      }}
      style={{ cursor: isDisplayOnlyNode ? 'default' : (isLabelOnly ? 'pointer' : (isExecMode ? 'pointer' : 'move')), pointerEvents: isDisplayOnlyNode ? 'none' : 'auto' }}
    >
      {/* ── Container chrome (#141): tinted rounded rectangle wrapping
          every container kind — SWITCH / FORK_JOIN / FORK_JOIN_DYNAMIC /
          DO_WHILE / SUB_WORKFLOW. dagLayout.applyContainerSizing publishes
          the bbox on `node._containerBounds` after reading the backend's
          typed `_containerChildren` wire hint. Rendered BEFORE the shape
          branches below so it sits behind the container header and behind
          (in sibling-iteration order) every body node. */}
      {node._containerBounds && (() => {
        const cb = node._containerBounds;
        const cx = cb.x * zoom + pan.x;
        const cy = cb.y * zoom + pan.y;
        const cw = cb.width * zoom;
        const ch = cb.height * zoom;
        // Per-kind tint — each container kind gets a distinct but muted
        // fill so viewers can tell a DO_WHILE from a FORK from a SWITCH
        // without reading the header. Borders stay on the same neutral
        // var(--v2-text-disabled) to keep the canvas cohesive.
        const kind = node._containerKind || 'doWhile';
        const fill = (
          kind === 'switch'       ? 'var(--v2-warning-soft)' :   // amber-50  (condition)
          kind === 'fork'         ? 'var(--v2-action-soft)' :   // blue-100  (parallel fork)
          kind === 'dynamicFork'  ? 'var(--v2-action-soft)' :   // indigo-100 (dynamic fork)
          kind === 'subWorkflow'  ? 'var(--v2-accent-trust-soft)' :   // purple-100 (sub-workflow)
                                    'var(--v2-border-subtle)'     // blue-grey (DO_WHILE — matches #122)
        );
        return (
          <>
            {/* Soft shadow behind container */}
            <rect
              x={cx + 2} y={cy + 4}
              width={cw} height={ch}
              rx={20 * zoom}
              fill="rgba(0,0,0,0.06)"
            />
            {/* Tinted container body. */}
            <rect
              x={cx} y={cy}
              width={cw} height={ch}
              rx={20 * zoom}
              fill={fill}
              stroke="#B1B1B7"
              strokeWidth={1}
              opacity={0.75}
            />
          </>
        );
      })()}
      {/* ── Node shape: Circle for Start/End, Bookend pill for Terminate, Diamond for conditions, Convergence dashed rect, Rect for tasks ── */}
      {isCircleNode ? (
        (() => {
          // Agentlets palette (NOT Conductor OSS teal). Circle stroke is driven by
          // execution state so the Start/End markers tell the workflow story:
          //   • Default (design-time, no run):  Agentlets grey var(--v2-text-disabled)
          //   • Start while running / completed: green var(--v2-success)
          //   • Start on failure / cancelled:   red var(--v2-danger)
          //   • End stays grey until workflow terminates; then adopts the
          //     final outcome color (green/red). While a run is still in
          //     flight the End marker remains neutral grey.
          const isFailState = execStatus === 'failed' || execStatus === 'cancelled';
          let circleStroke = 'var(--v2-text-disabled)';
          let circleFill = 'var(--v2-bg-surface)';
          if (isStartNode) {
            if (execStatus === 'running' || execStatus === 'completed') { circleStroke = 'var(--v2-success)'; circleFill = 'var(--v2-success-soft)'; }
            else if (isFailState) { circleStroke = 'var(--v2-danger)'; circleFill = 'var(--v2-danger-soft)'; }
          } else if (isEndNode) {
            if (execStatus === 'completed') { circleStroke = 'var(--v2-success)'; circleFill = 'var(--v2-success-soft)'; }
            else if (isFailState) { circleStroke = 'var(--v2-danger)'; circleFill = 'var(--v2-danger-soft)'; }
            // running → stay grey (workflow not yet finished)
          }
          return (
            <>
              {/* 80×80 circle, 5px stroke, centered 13px label. Pure display
                  marker — non-interactive (pointerEvents disabled on outer <g>). */}
              <circle
                cx={circleCX} cy={circleCY} r={circleR}
                fill={circleFill}
                stroke={circleStroke}
                strokeWidth={5 * zoom}
              />
              <text
                x={circleCX} y={circleCY}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={Math.max(10, 13 * zoom)}
                fontWeight="500"
                fill="#0f0f0f"
                style={{ fontFamily: 'Lexend, Inter, system-ui, sans-serif', pointerEvents: 'none' }}
              >
                {isStartNode ? 'Start' : (isEndNode ? 'End' : (node.label || ''))}
              </text>
            </>
          );
        })()
      ) : isBookendNode ? (
        <>
          {/* Terminate pill — Conductor OSS-style dark navy (var(--v2-action)) with subtle inner shadow */}
          <rect
            x={screenX + 1} y={bookendY + 2} width={screenW} height={bookendH}
            rx={bookendR}
            fill="rgba(0,0,0,0.15)"
          />
          <rect
            x={screenX} y={bookendY} width={screenW} height={bookendH}
            rx={bookendR}
            fill={'var(--v2-action)'}
            stroke={'var(--v2-text-primary)'}
            strokeWidth={isExecMode ? 2.5 : (selected ? 2 : 1.5)}
          />
          {/* Conductor OSS-style status chip — COMPLETED (green) vs TERMINATED (amber).
              Shows the terminationStatus so visually-similar Terminate pills
              are instantly distinguishable at a glance. Chip is inset far
              enough from the pill's right rounded cap so the text is fully
              legible at every zoom. */}
          {(() => {
            const ts = (node.config?.terminationStatus || node.config?.status || '').toString().toUpperCase();
            if (!ts) return null;
            const isCompleted = ts === 'COMPLETED';
            const chipW = 94 * zoom;
            const chipH = 18 * zoom;
            // Inset = pill radius + a bit more, so chip clears the rounded cap
            const chipX = screenX + screenW - chipW - (bookendR + 6 * zoom);
            const chipY = bookendY + (bookendH - chipH) / 2;
            return (
              <>
                <rect
                  x={chipX} y={chipY} width={chipW} height={chipH}
                  rx={chipH / 2}
                  fill={isCompleted ? 'var(--v2-success)' : 'var(--v2-warning)'}
                  opacity={0.95}
                />
                <foreignObject x={chipX} y={chipY} width={chipW} height={chipH}>
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Lexend, Inter, system-ui, sans-serif',
                    fontSize: Math.max(8, 9 * zoom), fontWeight: 700,
                    color: 'var(--v2-bg-surface)', letterSpacing: '0.04em',
                    pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'nowrap',
                  }}>
                    {ts}
                  </div>
                </foreignObject>
              </>
            );
          })()}
        </>
      ) : isDiamondNode ? (
        <>
          {/* Subtle shadow for diamond */}
          <polygon
            points={`${diamondCX},${diamondCY - diamondRY + 2} ${diamondCX + diamondRX + 1},${diamondCY + 2} ${diamondCX},${diamondCY + diamondRY + 2} ${diamondCX - diamondRX + 1},${diamondCY + 2}`}
            fill="rgba(0,0,0,0.04)"
            stroke="none"
            strokeLinejoin="round"
          />
          <polygon
            points={`${diamondCX},${diamondCY - diamondRY} ${diamondCX + diamondRX},${diamondCY} ${diamondCX},${diamondCY + diamondRY} ${diamondCX - diamondRX},${diamondCY}`}
            fill={nodeFill}
            stroke={borderColor}
            strokeWidth={isExecMode ? 2.5 : (selected ? 2 : 1.2)}
            strokeLinejoin="round"
          />
          {/* Diamond uses the regular Icon block below — no duplicate decorative icon */}
        </>
      ) : isConvergenceNode ? (
        <>
          {/* Convergence (switch_join) — Conductor OSS parity (remeasured live DOM
              2026-04-22 third pass, see DAG_DESIGN_GUIDELINES §7):
              three patterns:
              (a) `// Marks end of switch X` — a CATCH PILL. 263×34 visible
                  pill in a 350×55 foreignObject, with:
                    border: 1px dashed var(--v2-text-primary)   (reads grey at 1px)
                    background: var(--v2-bg-muted)       (light grey fill)
                    border-radius: 20px       (fully rounded stadium)
                    padding: 0 20px           (horizontal)
                  Dashed vertical edges from terminated branches LAND on the
                  pill's top edge and exit from the bottom. This is the key
                  Conductor OSS trick: the pill gives the dashed drops something to
                  terminate on so they don't pass through sibling columns.
              (b) Labeled pill — legacy / named JOIN (e.g. FORK_JOIN label).
              (c) Unlabeled dot — compact 14px circle (legacy convergence). */}
          {(node.label && node.label.startsWith('//')) ? (
            // (a) Conductor OSS "catch pill" — 75% of FO width, centered, 44px tall,
            // dashed border, var(--v2-bg-muted) fill. Text is rendered separately below.
            (() => {
              const pillW = screenW * 0.75;
              const pillH = 44 * zoom;
              const pillX = screenX + (screenW - pillW) / 2;
              const pillY = screenY + (screenH - pillH) / 2;
              return (
                <rect
                  x={pillX} y={pillY} width={pillW} height={pillH}
                  rx={22 * zoom}
                  fill="#EFEFEF"
                  stroke="#6b7280"
                  strokeWidth={1}
                  strokeDasharray="3,2"
                />
              );
            })()
          ) : node.label ? (
            // (b) Legacy labeled pill
            <>
              <rect
                x={screenX + 1} y={screenY + (screenH - 44 * zoom) / 2 + 2} width={screenW} height={44 * zoom}
                rx={20 * zoom} fill="rgba(0,0,0,0.03)"
              />
              <rect
                x={screenX} y={screenY + (screenH - 44 * zoom) / 2} width={screenW} height={44 * zoom}
                rx={20 * zoom} fill="#FFFFFF"
                stroke="#D1D5DB"
                strokeWidth={selected ? 2 : 1}
              />
            </>
          ) : (
            // (c) Unlabeled compact dot (fallback for orphan convergences we
            // haven't yet migrated to comment-style labels).
            <circle
              cx={screenX + screenW / 2}
              cy={screenY + screenH / 2}
              r={14 * zoom}
              fill="#FFFFFF"
              stroke="#B1B1B7"
              strokeWidth={selected ? 2 : 1.5}
            />
          )}
          {/* Comment-style text for pattern (a) — rendered as a clipped
              foreignObject so long "// Marks end of switch <very_long_name>"
              labels can't spill past the pill border into neighbouring nodes.
              SVG <text> has no intrinsic clipping; the foreignObject path
              gives us CSS overflow:hidden + textOverflow:ellipsis. #132 */}
          {node.label && node.label.startsWith('//') && (() => {
            const pillW = screenW * 0.75;
            const pillH = 44 * zoom;
            const pillX = screenX + (screenW - pillW) / 2;
            const pillY = screenY + (screenH - pillH) / 2;
            return (
              <foreignObject
                x={pillX}
                y={pillY}
                width={pillW}
                height={pillH}
                style={{ pointerEvents: 'none', overflow: 'hidden' }}
              >
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Lexend, Inter, system-ui, sans-serif',
                  fontSize: 14 * zoom,  /* #132: no floor; scales with zoom */
                  color: 'var(--v2-text-disabled)',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  padding: '0 10px',
                  boxSizing: 'border-box',
                  userSelect: 'none',
                }}>
                  {node.label}
                </div>
              </foreignObject>
            );
          })()}
        </>
      ) : (
        <>
          {/* Loop fallback chrome: if dagLayout didn't publish
              `_containerBounds` for this loop (e.g. the hand-authored IR
              fixture omitted a body hint), draw the legacy thin dashed
              outline 4px outside the header so the loop is still
              visually identifiable. The primary tinted chrome is rendered
              once — for ALL container kinds — at the top of the <g> group
              (#141 above). */}
          {isLoopNode && !node._containerBounds && (
            <rect
              x={screenX - 4 * zoom} y={screenY - 4 * zoom}
              width={screenW + 8 * zoom} height={screenH + 8 * zoom}
              rx={24 * zoom}
              fill="none"
              stroke="#757575"
              strokeWidth={1}
              strokeDasharray="4,3"
              opacity={0.6}
            />
          )}
          {/* Standard task node — Conductor OSS-style 20px radius rounded rectangle with subtle shadow */}
          <rect
            x={screenX + 1} y={screenY + 2} width={screenW} height={screenH}
            rx={20 * zoom} fill="rgba(0,0,0,0.04)"
          />
          <rect
            x={screenX} y={screenY} width={screenW} height={screenH}
            rx={20 * zoom} fill={isInlineExternal ? 'var(--v2-bg-elevated)' : nodeFill}
            stroke={isInlineExternal ? 'var(--v2-action-soft)' : borderColor}
            strokeWidth={isExecMode ? 2.5 : (selected ? 2 : 1)}
            strokeDasharray={isInlineExternal ? '6,3' : 'none'}
            opacity={isInlineExternal ? 0.85 : 1}
          />
          {/* Subworkflow — Conductor OSS renders with a stacked-rectangles affordance in
              the top-right. We add a small offset "shadow" rect to suggest a
              nested deck, and a SUBWORKFLOW label badge. */}
          {isSubworkflowNode && (
            <>
              <rect
                x={screenX + screenW - 26 * zoom}
                y={screenY + 6 * zoom}
                width={18 * zoom}
                height={14 * zoom}
                rx={3 * zoom}
                fill="#FFFFFF"
                stroke="#485A74"
                strokeWidth={1}
              />
              <rect
                x={screenX + screenW - 22 * zoom}
                y={screenY + 10 * zoom}
                width={18 * zoom}
                height={14 * zoom}
                rx={3 * zoom}
                fill="#E1F3FF"
                stroke="#485A74"
                strokeWidth={1}
              />
            </>
          )}
          {/* Left accent bar — clipped to node shape for clean rounded corners */}
          <defs>
            <clipPath id={`clip-accent-${node.id}`}>
              <rect x={screenX} y={screenY} width={screenW} height={screenH} rx={20 * zoom} />
            </clipPath>
          </defs>
          <rect
            x={screenX} y={screenY} width={4 * zoom} height={screenH}
            fill={statusBarColor}
            clipPath={`url(#clip-accent-${node.id})`}
          />
        </>
      )}

      {/* Icon — centered for diamonds, left-aligned for bookend/rect, hidden for circle/convergence */}
      {Icon && !isCircleNode && !isConvergenceNode && (
        <foreignObject
          x={isBookendNode ? screenX + 12 * zoom : isDiamondNode ? diamondCX - 9 * zoom : screenX + 8 * zoom}
          y={isBookendNode ? bookendY + (bookendH - 18 * zoom) / 2 : isDiamondNode ? diamondCY - 22 * zoom : screenY + 8 * zoom}
          width={isDiamondNode ? 18 * zoom : 20 * zoom} height={isDiamondNode ? 18 * zoom : 20 * zoom}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={isBookendNode ? 15 : isDiamondNode ? 14 : 14} style={{ color: (isTerminateNode || isEndNode) ? 'var(--v2-text-disabled)' : (nodeDef?.color || 'var(--v2-action)') }} />
          </div>
        </foreignObject>
      )}

      {/* Label — main node name. Circle nodes render their label inside the shape block above.
           For rect nodes the label gets the full width minus the icon area.
           For convergence nodes, use full width with centered alignment.
           Conductor OSS "// Marks ..." comment-style convergences render their label
           inline with the shape block above, so we suppress the duplicate here.
           
           P3-A: Diamond nodes now expand to fit label text to prevent clipping
           of "Invalid" and "Uninsured" style labels. */}
      {!isCircleNode && !(isConvergenceNode && node.label && node.label.startsWith('//')) && (() => {
        // Reserve room on the right for the Terminate chip when one is rendered
        const hasTermChip = isBookendNode && (node.config?.terminationStatus || node.config?.status);
        // When a chip is present, the pill is shared: left pad (icon+margin ~48) +
        // label width + chip width (94) + right pad for rounded cap (~34). Reserve
        // enough room on the right so label text never bleeds under the chip.
        const bookendLabelW = hasTermChip ? (NODE_W - 48 - 128) * zoom : (NODE_W - 48) * zoom;
        return (
      <foreignObject
        x={isBookendNode ? screenX + 34 * zoom : isDiamondNode ? diamondCX - (NODE_W * 0.45) * zoom : isConvergenceNode ? screenX + 8 * zoom : screenX + 28 * zoom}
        y={isBookendNode ? bookendY + (bookendH - 18 * zoom) / 2 : isDiamondNode ? diamondCY - 2 * zoom : isConvergenceNode ? screenY + (screenH - 22 * zoom) / 2 : screenY + 6 * zoom}
        width={isBookendNode ? bookendLabelW : isDiamondNode ? (NODE_W * 0.90) * zoom : isConvergenceNode ? (NODE_W - 16) * zoom : (NODE_W - 36) * zoom}
        height={isBookendNode ? 20 * zoom : isDiamondNode ? 28 * zoom : isConvergenceNode ? 22 * zoom : 34 * zoom}
        style={{ pointerEvents: 'none', overflow: 'hidden' }}
      >
        {/* #132: Text-clipping fix.
            Old code used `Math.max(9, fontBase * zoom)` as a font-size floor,
            which at zoom < ~0.75 made the font taller than the `foreignObject`
            height (which scales with zoom, no floor) — so labels visually spilled
            or got truncated to a single ellipsised character. New policy: let
            fontSize scale proportionally with zoom (no floor). Labels are
            intentionally unreadable at very low zoom — that's correct; the user
            wants to see the *topology* at 24%, not read text. Zooming in
            restores legibility. Removing the floor also makes line-height
            consistent with container-height, so the 2-line box is preserved.
        */}
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          fontSize: (isBookendNode ? 13 : isDiamondNode ? 11 : isConvergenceNode ? 11 : 12) * zoom,
          fontWeight: isBookendNode ? 700 : isConvergenceNode ? 400 : 600,
          color: (isEndNode || isTerminateNode) ? 'var(--v2-bg-surface)' : isConvergenceNode ? 'var(--v2-text-muted)' : 'var(--v2-text-primary)',
          fontFamily: 'Lexend, Inter, system-ui, sans-serif', lineHeight: '1.25',
          overflow: 'hidden', textOverflow: 'ellipsis',
          textAlign: isDiamondNode || isConvergenceNode ? 'center' : 'left',
          display: '-webkit-box', WebkitLineClamp: isDiamondNode ? 2 : 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word', margin: 0, padding: 0,
          width: '100%', height: '100%', boxSizing: 'border-box',
          letterSpacing: isBookendNode ? '0.03em' : 'normal',
        }}>
          {node.label}
        </div>
      </foreignObject>
        );
      })()}

      {/* Type sublabel — shows execution status or node type (hidden for circle, bookend, diamond, and convergence nodes).
           This is the ONLY place node type is shown (the old top-right type badge was removed
           because it overlapped with the label and was redundant with this sublabel). */}
      {!isCircleNode && !isBookendNode && !isDiamondNode && !isConvergenceNode && (
      <foreignObject
        x={screenX + 28 * zoom}
        y={screenY + 42 * zoom}
        width={(NODE_W - 36) * zoom}
        height={22 * zoom}
        style={{ pointerEvents: 'none', overflow: 'hidden' }}
      >
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          fontSize: 10 * zoom,  /* #132: no floor — scales with zoom so it fits container */
          fontWeight: isExecMode ? 600 : 400,
          color: execStatus === 'running' ? 'var(--v2-action)' : execStatus === 'completed' ? 'var(--v2-success)' : execStatus === 'failed' ? 'var(--v2-danger)' : 'var(--v2-text-disabled)',
          fontFamily: 'Lexend, Inter, system-ui, sans-serif', lineHeight: '1.2',
          overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', margin: 0, padding: 0,
          width: '100%', boxSizing: 'border-box',
        }}>
          {execStatus === 'running' ? 'Running...'
            : execStatus === 'completed' ? 'Completed'
            : execStatus === 'failed' ? 'Failed'
            : execStatus === 'skipped' ? 'Skipped'
            : nodeDef?.label}
        </div>
      </foreignObject>
      )}

      {/* Phase R1 metadata line — third line under the sublabel for task
          types that carry recognisable config (HTTP method+URL, KAFKA topic,
          INLINE/LAMBDA code preview, WAIT duration, etc.). Rendered as a
          single truncated line; `kind` drives a subtle colour tint so each
          task family is instantly recognisable without reading the full
          text. Hidden in exec mode (status sublabel takes over) and for
          special-shape nodes that have no rectangular body to host it.
          Falls back silently when the node's type/config don't produce a
          metadata line — no layout ripple. */}
      {!isCircleNode && !isBookendNode && !isDiamondNode && !isConvergenceNode && !isExecMode && (() => {
        const meta = getTaskMetadata(node.type, node.config);
        if (!meta) return null;
        // Per-kind accent colour — muted so it reads as an annotation, not a
        // badge competing with the status bar.
        const kindColor = {
          http:     'var(--v2-accent-cyan)',
          event:    'var(--v2-accent-trust)',
          kafka:    'var(--v2-warning)',
          code:     'var(--v2-text-secondary)',
          jq:       'var(--v2-text-secondary)',
          wait:     'var(--v2-success)',
          var:      'var(--v2-accent-cyan)',
          human:    'var(--v2-accent-rose)',
          subflow:  'var(--v2-accent-trust)',
          dynamic:  'var(--v2-accent-trust)',
          auth:     'var(--v2-danger)',
          webhook:  'var(--v2-accent-cyan)',
          rule:     'var(--v2-warning)',
        }[meta.kind] || 'var(--v2-text-secondary)';
        return (
          <foreignObject
            x={screenX + 28 * zoom}
            y={screenY + 64 * zoom}
            width={(NODE_W - 36) * zoom}
            height={22 * zoom}
            style={{ pointerEvents: 'none', overflow: 'hidden' }}
          >
            <div xmlns="http://www.w3.org/1999/xhtml" style={{
              fontSize: 10 * zoom,  /* #132: no floor — scales with zoom so it fits container */
              fontWeight: 500,
              color: kindColor,
              fontFamily: meta.kind === 'code' || meta.kind === 'jq'
                ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
                : 'Lexend, Inter, system-ui, sans-serif',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              margin: 0,
              padding: 0,
              width: '100%',
              boxSizing: 'border-box',
              letterSpacing: meta.kind === 'code' || meta.kind === 'jq' ? '-0.01em' : 'normal',
            }}>
              {meta.line}
            </div>
          </foreignObject>
        );
      })()}

      {/* Type badge removed — it overlapped with the main label at top-right and was
           redundant with the sublabel above. The sublabel provides the same nodeDef.label
           info in a non-overlapping position below the main label. */}

      {/* Status badge icon (top-right) — positioned on pill for bookend nodes, hidden for circle/convergence */}
      {statusBadge && !isCircleNode && !isConvergenceNode && (
        <foreignObject x={screenX + screenW - 22 * zoom} y={isBookendNode ? bookendY + 2 * zoom : screenY + 4 * zoom} width={20 * zoom} height={20 * zoom}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', height: '100%', borderRadius: '50%',
            background: execStatus === 'running' ? 'var(--v2-action)'
              : execStatus === 'completed' ? 'var(--v2-success)'
              : execStatus === 'failed' ? 'var(--v2-danger)'
              : execStatus === 'skipped' ? 'var(--v2-text-disabled)'
              : 'var(--v2-text-muted)',
          }}>
            {statusBadge}
          </div>
        </foreignObject>
      )}

      {/* ── Conductor OSS-style single anchor ports ──
          All in/out ports collapse to a single top/bottom-center anchor per node
          (see getNodePorts in useCanvas.js). We render ONE dot per direction
          instead of one-per-port to eliminate orphan port visuals, and the edge
          layer handles fan-out/fan-in via Bézier offsets.
          The hit-area still listens for each named port so drag-connect can
          target the right logical port when the user starts from a specific
          case (default, true, false, named cases, etc.).
      */}
      {!isLabelOnly && nodeDef?.ports?.in && nodeDef.ports.in.length > 0 && (() => {
        // Use the first port's position — all inputs share the same anchor.
        const firstPortName = nodeDef.ports.in[0].name || nodeDef.ports.in[0];
        const anchorPos = getPortPosition(node.id, firstPortName, 'top');
        const anchorX = anchorPos.x * zoom + pan.x;
        const anchorY = anchorPos.y * zoom + pan.y;
        const anyConnected = nodeDef.ports.in.some((p) => connectedInPorts.has(p.name || p));
        const showAnchor = anyConnected || isConnecting || selected;

        return (
          <g key="in-anchor">
            {/* Invisible larger hit area for connection drop targeting */}
            <circle
              cx={anchorX}
              cy={anchorY}
              r={14 * zoom}
              fill="transparent"
              stroke="none"
              style={{ cursor: isConnecting ? 'crosshair' : 'default' }}
              onMouseUp={(e) => {
                e.stopPropagation();
                if (onEndDrag) onEndDrag();
                if (onEndConnect) onEndConnect(node.id, firstPortName);
              }}
            />
            {/* Single visible anchor dot — shows only when connected, selected, or during drag */}
            {showAnchor && (
              <circle
                cx={anchorX}
                cy={anchorY}
                r={(isConnecting || selected ? 4 : 3) * zoom}
                fill={anyConnected ? 'var(--v2-text-muted)' : 'var(--v2-border-default)'}
                stroke="none"
                style={{ cursor: 'crosshair', transition: 'r 0.15s, fill 0.15s' }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onStartConnect(firstPortName, 'top');
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  if (onEndDrag) onEndDrag();
                  if (onEndConnect) onEndConnect(node.id, firstPortName);
                }}
              />
            )}
          </g>
        );
      })()}

      {!isLabelOnly && nodeDef?.ports?.out && nodeDef.ports.out.length > 0 && (() => {
        // Use the first port's position — all outputs share the same anchor.
        const firstPortName = nodeDef.ports.out[0].name || nodeDef.ports.out[0];
        const anchorPos = getPortPosition(node.id, firstPortName, 'bottom');
        const anchorX = anchorPos.x * zoom + pan.x;
        const anchorY = anchorPos.y * zoom + pan.y;
        const anyConnected = nodeDef.ports.out.some((p) => connectedOutPorts.has(p.name || p));
        const showAnchor = anyConnected || isConnecting || selected;
        const anchorColor = nodeDef?.color || 'var(--v2-text-muted)';

        return (
          <g key="out-anchor">
            {/* Invisible larger hit area for connection initiation */}
            <circle
              cx={anchorX}
              cy={anchorY}
              r={14 * zoom}
              fill="transparent"
              stroke="none"
              style={{ cursor: isConnecting || selected ? 'crosshair' : 'default' }}
              onMouseUp={(e) => {
                e.stopPropagation();
                if (onEndDrag) onEndDrag();
                if (onEndConnect) onEndConnect(node.id, firstPortName);
              }}
            />
            {/* Single visible anchor dot */}
            {showAnchor && (
              <circle
                cx={anchorX}
                cy={anchorY}
                r={(isConnecting || selected ? 4 : 3) * zoom}
                fill={anyConnected ? anchorColor : 'var(--v2-border-default)'}
                stroke="none"
                style={{ cursor: 'crosshair', transition: 'r 0.15s, fill 0.15s' }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onStartConnect(firstPortName, 'bottom');
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  if (onEndDrag) onEndDrag();
                  if (onEndConnect) onEndConnect(node.id, firstPortName);
                }}
              />
            )}
          </g>
        );
      })()}
    </g>
  );
}

function CanvasConnection({
  connection,
  nodes,
  connections = [],
  getPortPosition,
  pan,
  zoom,
  selected,
  onSelect,
  onDelete,
  onInsertNode,
  executionState,
  isExecutionMode,
}) {
  const fromNode = nodes.find((n) => n.id === connection.from);
  const toNode = nodes.find((n) => n.id === connection.to);

  if (!fromNode || !toNode) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[CanvasConnection] Orphaned connection:', connection.id, 'from:', connection.from, 'to:', connection.to);
    }
    return null;
  }

  const fromPort = getPortPosition(fromNode.id, connection.fromPort || 'default', 'bottom');
  const toPort = getPortPosition(toNode.id, connection.toPort || 'default', 'top');

  // Anchor positions on the node surface (all in/out ports share one anchor each).
  const anchorX1 = fromPort.x * zoom + pan.x;
  const anchorY1 = fromPort.y * zoom + pan.y;
  const anchorX2 = toPort.x * zoom + pan.x;
  const anchorY2 = toPort.y * zoom + pan.y;

  // ── Conductor OSS-style fan-out / fan-in ──
  // When a switch/condition has multiple outgoing edges, they all share the
  // same bottom-center anchor in our port model. Visually we spread each edge's
  // starting x toward its target so they don't overlap at the anchor. Conductor OSS
  // spreads by ~±75px from the switch center regardless of target position.
  //
  // Same trick for convergence/join nodes: multiple incoming edges spread
  // their endpoint x toward the source.
  const FAN_OUT_X = 70 * zoom;   // horizontal offset when fanning out from switch
  const FAN_IN_X = 70 * zoom;    // horizontal offset when fanning into join

  const FAN_OUT_SOURCE_TYPES = new Set(['condition', 'switch', 'fork']);
  const FAN_IN_TARGET_TYPES = new Set(['convergence', 'join', 'switch_join']);

  const outgoingFromSource = connections.filter((c) => c.from === fromNode.id);
  const incomingToTarget = connections.filter((c) => c.to === toNode.id);

  const sourceIsFanOut = FAN_OUT_SOURCE_TYPES.has(fromNode.type) && outgoingFromSource.length > 1;
  const targetIsFanIn = FAN_IN_TARGET_TYPES.has(toNode.type) && incomingToTarget.length > 1;

  let x1 = anchorX1;
  let y1 = anchorY1;
  let x2 = anchorX2;
  let y2 = anchorY2;

  if (sourceIsFanOut) {
    const sign = Math.sign(anchorX2 - anchorX1);
    if (sign !== 0) x1 = anchorX1 + sign * FAN_OUT_X;
  }
  if (targetIsFanIn) {
    const sign = Math.sign(anchorX1 - anchorX2);
    if (sign !== 0) x2 = anchorX2 + sign * FAN_IN_X;
  }

  // ── Smooth bezier routing (Conductor OSS-style curves) ──
  // Conductor OSS uses: short vertical stem from the anchor → cubic Bézier to the target
  // stem → short vertical stem into target. Produces a clean vertical entry/exit
  // and graceful curves between fanned-out/fanned-in points.
  const fromShape = getNodeShapeType(fromNode.type);
  const toShape = getNodeShapeType(toNode.type);
  const dy = y2 - y1;
  const dx = Math.abs(x2 - x1);
  const absDy = Math.abs(dy);
  const midY = (y1 + y2) / 2;

  let cpy1, cpy2;
  if (dx < 2) {
    // Pure vertical edge — collapse control points to the midpoint so it renders
    // as a clean straight line (matches Conductor OSS's `M x,y1 C x,midY x,midY x,y2`).
    cpy1 = midY;
    cpy2 = midY;
  } else {
    // Fork/merge — gentle S-curve. Control offset proportional to vertical distance
    // but clamped so short edges don't bulge.
    const baseOffset = Math.max(20, Math.min(absDy * 0.5, 60));
    const shapeScale = (s) => (s === 'diamond' || s === 'circle') ? 0.7 : 1;
    const fromOffset = baseOffset * shapeScale(fromShape);
    const toOffset = baseOffset * shapeScale(toShape);
    cpy1 = dy >= 0 ? y1 + fromOffset : y1 - fromOffset;
    cpy2 = dy >= 0 ? y2 - toOffset : y2 + toOffset;
  }

  // Conductor OSS-style vertical stems at anchor entry/exit for clean vertical join to node.
  const STEM = 8 * zoom;
  const stem1 = sourceIsFanOut ? STEM : 0;
  const stem2 = targetIsFanIn ? STEM : 0;
  const stemStartY = anchorY1 + (dy >= 0 ? stem1 : -stem1);
  const stemEndY = anchorY2 - (dy >= 0 ? stem2 : -stem2);

  // ── Pre-compute edge classification so we can choose routing strategy ──
  // (Duplicated from the stroke-color block below so the path generator can
  //  use it. Cheap booleans; DRY-safe.)
  //
  // Dashed is a STRUCTURAL SCAFFOLD, not a "maybe-taken" indicator. An edge
  // is dashed ONLY when it lands on a TERMINATE node — a true dead-end. End
  // nodes are NOT dashed targets (a real path reaching End is a real path),
  // and a condition's `default` port can carry a real execution branch so
  // must not be dashed by default either.
  const _isToTerminal = toNode?.type === 'terminate';
  const _isToCatchPillEarly = !!(
    toNode && (
      toNode.type === 'comment' ||
      toNode.type === 'marker' ||
      (typeof toNode.label === 'string' && toNode.label.trim().startsWith('//'))
    )
  );
  const _isDashedRoute = _isToTerminal || _isToCatchPillEarly;

  // ── Orthogonal path from ELK bend points (preferred when available) ──
  // When ELK has routed this edge, connection.bendPoints is an array of
  // {x, y} waypoints in canvas coordinates. We anchor the endpoints to our
  // actual port positions (so the line terminates exactly at the port, not
  // at ELK's node-border attach point) and connect through the interior
  // bend points with straight orthogonal segments. This avoids drawing
  // edges over other node shapes — the key fix visible against Conductor OSS.
  let pathData;
  const hasElkRouting = Array.isArray(connection.bendPoints) && connection.bendPoints.length >= 2;
  if (hasElkRouting) {
    const inner = connection.bendPoints.slice(1, -1); // drop ELK's own endpoint attach points
    const segs = [`M ${anchorX1} ${anchorY1}`];
    for (const bp of inner) segs.push(`L ${bp.x} ${bp.y}`);
    segs.push(`L ${anchorX2} ${anchorY2}`);
    pathData = segs.join(' ');
  } else {
    // ── Unified bezier/diagonal routing — L-shape removed per user request ──
    // Previously this branch emitted a pure-orthogonal L-shape (vertical drop
    // + 90° corner + horizontal slide) for cross-column edges and for any
    // dashed TERMINATE route. The L-shape produced "drops in between" — hard
    // right-angle kinks that visually detached empty-case branches from the
    // main spine and left unaesthetic 90° corners mid-flow. Every edge —
    // including long cross-column drops and dashed terminate routes — now
    // uses a smooth cubic bezier. Fanning edges still get the S-curve with
    // stubs (clean exit/entry at the source/target port); non-fanning edges
    // get a simple two-control-point bezier.
    pathData = (sourceIsFanOut || targetIsFanIn)
      ? `M ${anchorX1} ${anchorY1} L ${anchorX1} ${stemStartY} C ${x1} ${cpy1} ${x2} ${cpy2} ${anchorX2} ${stemEndY} L ${anchorX2} ${anchorY2}`
      : `M ${x1} ${y1} C ${x1} ${cpy1} ${x2} ${cpy2} ${x2} ${y2}`;
  }

  // Midpoint for delete/insert buttons — when ELK-routed, use the middle bend
  // point so the button sits on the actual path rather than the straight-line
  // midpoint (which could land over a node rectangle on L-shaped routes).
  const midBend = hasElkRouting
    ? connection.bendPoints[Math.floor(connection.bendPoints.length / 2)]
    : null;
  const midX = midBend ? midBend.x : (anchorX1 + anchorX2) / 2;
  const midYElk = midBend ? midBend.y : null;

  // Conductor OSS-style edge stroke: var(--v2-text-muted) (warm gray) for normal paths,
  // var(--v2-text-disabled) (dashed) for structural scaffold edges:
  //   - edges that land on a TERMINATE node (true dead-ends)
  //   - edges that land on a catch-pill / comment-marker convergence
  //     (e.g. "// Marks end of switch_X") — these are structural scaffolds
  //     that mark leg reconvergence, not executable paths
  // Edges landing on End, or leaving a switch's `default` port toward a
  // real task, stay SOLID — per user: "the dashed lines are not meant for
  // actual paths the workflow could take". Status colors override during
  // execution.
  const isToTerminalNode = toNode?.type === 'terminate';
  const isToCatchPill = !!(
    toNode && (
      toNode.type === 'comment' ||
      toNode.type === 'marker' ||
      (typeof toNode.label === 'string' && toNode.label.trim().startsWith('//'))
    )
  );
  const useDashedEdge = isToTerminalNode || isToCatchPill;

  let stroke = useDashedEdge ? 'var(--v2-text-disabled)' : 'var(--v2-text-muted)';
  let arrowheadFill = 'var(--v2-text-secondary)'; // Conductor OSS navy arrowhead — stays navy regardless of stroke color

  if (executionState?.status === 'running') {
    stroke = 'var(--v2-action)';
    arrowheadFill = 'var(--v2-action)';
  } else if (executionState?.status === 'completed') {
    stroke = 'var(--v2-text-secondary)';
    arrowheadFill = 'var(--v2-text-secondary)';
  } else if (executionState?.status === 'failed') {
    stroke = 'var(--v2-danger)';
    arrowheadFill = 'var(--v2-danger)';
  }

  return (
    <g>
      {/* Invisible wider hit area for easier clicking */}
      <path
        d={pathData}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        strokeLinecap="round"
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      />
      {/* Arrowhead marker — Conductor OSS-style slim triangle (M0,-4 L8,0 L0,4), navy fill,
          sized so it doesn't dominate the hairline stroke. */}
      <defs>
        <marker
          id={`arrow-${connection.id}`}
          markerWidth="6"
          markerHeight="6"
          refX="5.5"
          refY="3"
          orient="auto"
        >
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={selected ? 'var(--v2-action)' : arrowheadFill} stroke="none" />
        </marker>
      </defs>
      {/* Visible connection line — dashed is intentionally subtle scaffold,
          not a competing rendered path. Per user feedback: dashed edges are
          structural (drop-to-marker-to-end for terminated branches), so they
          should read hair-thin — same 1px weight as solid, only the pattern
          distinguishes them. Tightened dasharray (4,3) so the dashes don't
          dominate at design-time zoom. Selected bumps slightly. */}
      <path
        d={pathData}
        fill="none"
        stroke={selected ? 'var(--v2-action)' : stroke}
        strokeWidth={selected ? 1.5 : 1}
        strokeDasharray={useDashedEdge && !selected ? '4,3' : 'none'}
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd={`url(#arrow-${connection.id})`}
        style={{ cursor: 'pointer', transition: 'stroke 0.15s, stroke-width 0.15s' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      />
      {/* Branch label pill — shown on connections from condition/switch ports (Conductor OSS-style).
           Named branches ("true"/"false"/"policy-valid"/etc.) always get a pill.
           The "default" port only gets a pill when leaving a condition node (Conductor OSS parity);
           regular sequential edges also use fromPort='default' and must NOT be labeled. */}
      {connection.fromPort && connection.fromPort !== 'out' &&
       (connection.fromPort !== 'default' || (fromNode && (fromNode.type === 'condition' || fromNode.type === 'switch'))) && (
        (() => {
          // Label placement: prefer ELK's computed label position (inline on the
          // routed orthogonal path, guaranteed not to overlap a node). Fallback
          // to bezier-curve 25% parameterization for the pre-ELK paint.
          let labelX, labelY;
          if (connection.labelPosition) {
            labelX = connection.labelPosition.x;
            labelY = connection.labelPosition.y;
          } else if (hasElkRouting) {
            // §121d Gap B: place pill at t=0.15 along the orthogonal polyline
            // (near the source node) — Conductor OSS parity. Cumulative segment length
            // parameterization keeps the pill on a straight run, not on a bend.
            const pts = [{ x: anchorX1, y: anchorY1 }, ...connection.bendPoints.slice(1, -1), { x: anchorX2, y: anchorY2 }];
            let total = 0;
            for (let i = 0; i < pts.length - 1; i++) total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
            let target = total * 0.15;
            labelX = pts[0].x; labelY = pts[0].y;
            for (let i = 0; i < pts.length - 1; i++) {
              const segLen = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
              if (target <= segLen) {
                const t2 = segLen > 0 ? target / segLen : 0;
                labelX = pts[i].x + (pts[i + 1].x - pts[i].x) * t2;
                labelY = pts[i].y + (pts[i + 1].y - pts[i].y) * t2;
                break;
              }
              target -= segLen;
            }
          } else {
            // §121d Gap B: Conductor OSS renders branch-label pills at t=0.15 along
            // the edge bezier (near the source), not midpoint — matches the
            // gold-standard DOM spec.
            const t = 0.15;
            labelX = (1 - t) * (1 - t) * (1 - t) * x1 + 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t * x2;
            labelY = (1 - t) * (1 - t) * (1 - t) * y1 + 3 * (1 - t) * (1 - t) * t * cpy1 + 3 * (1 - t) * t * t * cpy2 + t * t * t * y2;
          }
          const rawLabel = connection.fromPort === 'true' ? 'true' : connection.fromPort === 'false' ? 'false' : connection.fromPort;
          const isDefault = connection.fromPort === 'default';
          // Truncate long branch labels (e.g. "policy-not-valid" → "policy-not-v…")
          const MAX_LABEL = 14;
          const label = rawLabel.length > MAX_LABEL ? rawLabel.slice(0, MAX_LABEL - 1) + '…' : rawLabel;
          // Conductor OSS case-label pill styling: pale-blue var(--v2-action-soft), no border, near-black text,
          // 8px radius, 13px fw=300 Lexend. We keep subtle type-colored variants for true/false/success/error
          // since those communicate branch semantics at a glance.
          const pillColor = isDefault ? 'var(--v2-action-soft)' :
                            connection.fromPort === 'true' || connection.fromPort === 'success' ? 'var(--v2-action-soft)' :
                            connection.fromPort === 'false' || connection.fromPort === 'error' ? 'var(--v2-danger-soft)' : 'var(--v2-action-soft)';
          const strokeColor = 'none';
          const textColor = connection.fromPort === 'false' || connection.fromPort === 'error' ? 'var(--v2-danger)' : 'var(--v2-text-primary)';
          // §121d Gap B: Conductor OSS branch-label pill is 170×30 at zoom=1.
          // Width still scales with label length (so short labels like "true"
          // don't render as huge empty pills), but the ceiling is 170 and
          // the height is pinned to 30 to match the gold-standard DOM spec.
          const pillW = Math.min(Math.max(label.length * 8 + 24, 60), 170) * zoom;
          const pillH = 30 * zoom;

          // #130: Branch-label pill anti-collision.
          // The old alternating ±40px offset could push pills *past* the target
          // node top on short layer pitches (70px). Replace with a hard clamp
          // into the safe vertical band between source-bottom and target-top,
          // plus a horizontal clamp into the span between source.x and target.x
          // so a pill can't drift onto a sibling branch.
          let finalLabelY = labelY;

          // Vertical safe band: pill fully inside source.bottom → target.top,
          // with 8px gaps at each end so it never touches a node border.
          const fromH = (typeof fromNode.h === 'number' && fromNode.h > 0) ? fromNode.h : NODE_H;
          const sourceBottomY = (fromNode.y + fromH) * zoom + pan.y;
          const targetTopY = toNode.y * zoom + pan.y;
          const vGap = 8 * zoom;
          const yLo = sourceBottomY + pillH / 2 + vGap;
          const yHi = targetTopY - pillH / 2 - vGap;
          if (yHi > yLo) {
            // Normal case: gap is tall enough to hold the pill. Clamp.
            finalLabelY = Math.max(yLo, Math.min(yHi, finalLabelY));
          } else {
            // Degenerate (very short pitch): centre it in whatever gap exists.
            finalLabelY = (sourceBottomY + targetTopY) / 2;
          }

          // Horizontal safe band: keep the pill centre between source-centre
          // and target-centre (± half a pill width). Prevents the pill from
          // drifting over a sibling branch's vertical stub on wide switches.
          const fromCx = (fromNode.x + NODE_W / 2) * zoom + pan.x;
          const toCx = (toNode.x + NODE_W / 2) * zoom + pan.x;
          const [xLo, xHi] = fromCx <= toCx ? [fromCx, toCx] : [toCx, fromCx];
          labelX = Math.max(xLo - pillW / 2, Math.min(xHi + pillW / 2, labelX));

          return (
            <g style={{ pointerEvents: 'none' }}>
              <title>{rawLabel}</title>
              <rect
                x={labelX - pillW / 2} y={finalLabelY - pillH / 2}
                width={pillW} height={pillH}
                rx={8 * zoom} fill={pillColor} stroke={strokeColor}
                strokeWidth={0} opacity={1}
              />
              <text
                x={labelX} y={finalLabelY + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={Math.max(9, 13 * zoom)} fontWeight={300}
                fill={textColor}
                style={{ fontFamily: 'Lexend, Inter, system-ui, sans-serif', pointerEvents: 'none' }}
              >
                {label}
              </text>
            </g>
          );
        })()
      )}
      {/* Delete button — shown when selected and not in execution mode.
          Uses ELK-routed midpoint when available so the button sits on the
          orthogonal path, not over an unrelated node. */}
      {selected && !isExecutionMode && onDelete && (() => {
        const btnY = midYElk != null ? midYElk : midY;
        return (
        <g
          onClick={(e) => {
            e.stopPropagation();
            onDelete(connection.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          <circle cx={midX} cy={btnY} r={10 * zoom} fill="#fff" stroke="#dc2626" strokeWidth={1.5} />
          <line x1={midX - 4 * zoom} y1={btnY - 4 * zoom} x2={midX + 4 * zoom} y2={btnY + 4 * zoom}
            stroke="#dc2626" strokeWidth={2} strokeLinecap="round" />
          <line x1={midX + 4 * zoom} y1={btnY - 4 * zoom} x2={midX - 4 * zoom} y2={btnY + 4 * zoom}
            stroke="#dc2626" strokeWidth={2} strokeLinecap="round" />
        </g>
        );
      })()}
      {/* "+" insertion button — Conductor OSS-style sequential task addition (design mode only).
          Uses ELK-routed midpoint when available so the button sits on the routed
          polyline instead of floating over an unrelated node. */}
      {!isExecutionMode && !selected && onInsertNode && (() => {
        const btnY = midYElk != null ? midYElk : midY;
        return (
        <g
          className="insert-plus-btn"
          onClick={(e) => {
            e.stopPropagation();
            onInsertNode(connection.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          {/* Background circle — appears on hover via CSS */}
          <circle
            cx={midX} cy={btnY} r={11 * zoom}
            fill="#fff" stroke="#94a3b8" strokeWidth={1.2}
            opacity={0}
            className="insert-plus-bg"
          />
          {/* Plus icon */}
          <line
            x1={midX - 5 * zoom} y1={btnY} x2={midX + 5 * zoom} y2={btnY}
            stroke="#64748b" strokeWidth={1.8} strokeLinecap="round"
            opacity={0}
            className="insert-plus-icon"
          />
          <line
            x1={midX} y1={btnY - 5 * zoom} x2={midX} y2={btnY + 5 * zoom}
            stroke="#64748b" strokeWidth={1.8} strokeLinecap="round"
            opacity={0}
            className="insert-plus-icon"
          />
          {/* Invisible hit area — always present for hover detection */}
          <circle
            cx={midX} cy={btnY} r={16 * zoom}
            fill="transparent" stroke="none"
          />
        </g>
        );
      })()}
    </g>
  );
}

/**
 * RC-120 Phase 1 — Failure deep-dive banner for the Node Inspector.
 *
 * Renders a sticky red banner at the top of the Inspector when the
 * selected node has executionState.status === 'failed'. Shows the
 * error message (collapsed to one line by default; click to expand
 * for the full text) and two jump-affordances:
 *
 *   • "Fix in Config" → switches the Inspector to the config tab so
 *     the operator can edit the field that caused the failure.
 *   • "Open execution details" → switches to the Execution tab so
 *     the operator can see input/output/timing, run the AI Debug
 *     analysis, or apply suggested configFixes.
 *
 * No engineering jargon (feedback_no_engineering_jargon_in_ui) — the
 * BE's raw error text is shown verbatim because it's the most accurate
 * signal we have; if the error contains internal codes, the next pass
 * is to add structured error envelopes (out of scope for Phase 1).
 *
 * Zero regression by construction: purely additive; renders only on
 * the failed branch; the existing ExecutionTab + Config tabs are
 * untouched.
 */
/* RC-120b — Heuristic extraction of the offending config field name
 * from an arbitrary BE error string. Returns null when no field can
 * be confidently identified.
 *
 * The "right" solution is a structured error envelope (BE returns
 * `{message, fieldKey}` instead of just `message`) — that's tracked
 * as a separate phase. Until then, we recognise the common patterns
 * the platform's task handlers actually emit:
 *
 *   • "X is required"           → field = X
 *   • "X must be ..."           → field = X
 *   • "Missing X"               → field = X
 *   • "Invalid X"               → field = X
 *   • "X cannot be empty"       → field = X
 *   • "field 'X' ..."           → field = X
 *   • "config.X ..."            → field = X
 *
 * Lower-camel and snake-case names are both common; we preserve the
 * original casing so the FE can do an exact key match against the
 * node's config schema.
 *
 * Never throws — returns null on any failure so the calling banner
 * silently degrades to the pre-RC-120b behavior (jump to config tab,
 * no field highlight).
 */
function extractFieldKeyFromError(errorText) {
  if (!errorText || typeof errorText !== 'string') return null;
  try {
    const text = errorText.trim();
    // Pattern matchers in priority order — first match wins.
    const patterns = [
      /^field\s+['"]([a-zA-Z_][\w.]*)['"]\s/i,
      /^config\.([a-zA-Z_][\w]*)\s/,
      /^([a-zA-Z_][\w]*)\s+is\s+required\b/i,
      /^([a-zA-Z_][\w]*)\s+must\s+be\b/i,
      /^missing\s+(?:required\s+)?(?:field\s+)?['"]?([a-zA-Z_][\w]*)['"]?/i,
      /^invalid\s+(?:value\s+for\s+)?['"]?([a-zA-Z_][\w]*)['"]?/i,
      /^([a-zA-Z_][\w]*)\s+cannot\s+be\s+empty\b/i,
      /^([a-zA-Z_][\w]*)\s+(?:value\s+)?is\s+(?:not\s+valid|invalid)\b/i,
    ];
    for (const re of patterns) {
      const m = text.match(re);
      if (m && m[1]) return m[1];
    }
  } catch { /* noop — heuristic, never fatal */ }
  return null;
}

function FailureDeepDiveBanner({ executionState, onJumpToConfig, onJumpToExecution, onReplayRun, replayBusy }) {
  const [expanded, setExpanded] = useState(false);

  // Both `error` (older runs) and `errorMessage` (newer envelope) are
  // seen in the wild; prefer the richer one when both are present.
  const rawError = executionState?.errorMessage || executionState?.error || '';
  const errorText = typeof rawError === 'string' ? rawError : String(rawError);
  if (!errorText.trim()) return null;

  const oneLine = errorText.replace(/\s+/g, ' ').trim();
  const truncated = oneLine.length > 140 ? oneLine.slice(0, 140) + '…' : oneLine;

  /* RC-120b — Best-effort field-key extraction. When the regexes
   * recognise the offending field, "Fix in Config" passes that key to
   * the parent so NodeConfigPanel can scroll/highlight the input.
   * When extraction fails (most cases — BE error strings are wildly
   * inconsistent), the jump still happens but without field-level
   * targeting — pre-RC-120b behavior preserved. */
  const extractedFieldKey = extractFieldKeyFromError(errorText);

  return (
    <div style={{
      margin: '8px 10px 6px',
      padding: '8px 10px',
      background: 'var(--v2-danger-soft)',
      border: '1px solid var(--v2-accent-rose-soft)',
      borderLeft: '3px solid var(--v2-danger)',
      borderRadius: 6,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        fontSize: 11, color: 'var(--v2-danger)',
      }}>
        <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 3 }}>
            This step failed on the last run
          </div>
          <div
            onClick={() => setExpanded((v) => !v)}
            style={{
              color: 'var(--v2-text-secondary)',
              cursor: 'pointer',
              fontSize: 11,
              lineHeight: 1.4,
              wordBreak: 'break-word',
              whiteSpace: expanded ? 'pre-wrap' : 'normal',
            }}
            title={expanded ? 'Click to collapse' : 'Click to see the full error'}
          >
            {expanded ? errorText : truncated}
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onJumpToConfig && onJumpToConfig(extractedFieldKey)}
              style={{
                padding: '3px 9px', fontSize: 10, fontWeight: 600,
                color: 'var(--v2-bg-surface)', background: 'var(--v2-danger)',
                border: 'none', borderRadius: 4, cursor: 'pointer',
              }}
              title={extractedFieldKey
                ? `Jump to the '${extractedFieldKey}' field`
                : 'Open the configuration tab for this step'}
            >
              {extractedFieldKey ? `Fix '${extractedFieldKey}'` : 'Fix in Config'}
            </button>
            <button
              type="button"
              onClick={onJumpToExecution}
              style={{
                padding: '3px 9px', fontSize: 10, fontWeight: 600,
                color: 'var(--v2-danger)', background: 'var(--v2-bg-surface)',
                border: '1px solid var(--v2-accent-rose-soft)', borderRadius: 4, cursor: 'pointer',
              }}
            >
              Open execution details
            </button>
            {/* RC-120b — Replay-with-stored-inputs affordance. Only shown
                when the parent wired a handler (= we have the originating
                runId on hand). The handler fires the new /runs/{runId}/replay
                endpoint with the prior input envelope. Disabled while
                in-flight to prevent double-firing. */}
            {onReplayRun && (
              <button
                type="button"
                onClick={onReplayRun}
                disabled={!!replayBusy}
                style={{
                  padding: '3px 9px', fontSize: 10, fontWeight: 600,
                  color: 'var(--v2-text-secondary)', background: 'var(--v2-bg-surface)',
                  border: '1px solid var(--v2-border-default)', borderRadius: 4,
                  cursor: replayBusy ? 'not-allowed' : 'pointer',
                  opacity: replayBusy ? 0.6 : 1,
                }}
                title="Re-run the workflow from the start with the same inputs as this run"
              >
                {replayBusy ? 'Replaying…' : 'Replay with same inputs'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NodePropertiesPanel({
  node,
  nodeDef,
  onUpdate,
  onDelete,
  executionState,
  installedConnectors,
  installedConnectorsMkIndex,
  agents,
  nodes,
  connections,
  onDebugNode,
  isExecutionMode,
  /* RC-120b — passed through from CanvasPageV3 when the inspector is
   * looking at a failed execution. Used by the deep-dive banner's
   * "Replay with same inputs" button to fire the new BE endpoint. */
  selectedRunId,
  onReplayRun,
  replayBusy,
  /* RC-152c-1c — Currently-loaded workflow id, threaded down to
   * V3Inspector → per-type inspectors that need to deep-link to
   * other surfaces (HumanTaskInspector's "Design form ↗" button →
   * /forms/design?wid=…&nid=…). Source-of-truth is
   * CanvasPage.selectedWorkflowId because not every navigation
   * path that lands on a workflow updates the URL with
   * ?workflowId= (e.g. "Recent" / agent-bound links). Optional —
   * inspectors fall back to URL searchParams when absent so
   * standalone embeddings (tests, slideouts) keep working. */
  selectedWorkflowId,
}) {
  // ── Hooks MUST run on every render (Rules of Hooks — fixes React error #310).
  // Any early return based on `node` has to come AFTER these hooks.
  const isWorkerTask = node && (node.type === 'worker_task' || node.type === 'task');
  const [activeTab, setActiveTab] = useState(
    isExecutionMode && executionState
      ? 'execution'
      : (isWorkerTask ? 'worker' : 'general')
  );

  /* RC-120b — Field-key hint set by FailureDeepDiveBanner's "Fix in
   * Config" handler. NodeConfigPanel reads this prop and (a) scrolls
   * the matching input into view, (b) shows a transient highlight
   * ring so the operator's eye lands on the right field. Cleared by
   * the consumer (auto-clears after the highlight effect fires). */
  const [highlightFieldKey, setHighlightFieldKey] = useState(null);
  // Auto-clear the highlight after 2.5s so it doesn't persist forever.
  useEffect(() => {
    if (!highlightFieldKey) return;
    const t = setTimeout(() => setHighlightFieldKey(null), 2500);
    return () => clearTimeout(t);
  }, [highlightFieldKey]);

  // Auto-switch to execution tab when entering execution mode and node has execution data
  useEffect(() => {
    if (isExecutionMode && executionState && activeTab !== 'execution') {
      setActiveTab('execution');
    }
  }, [isExecutionMode, executionState, node?.id, activeTab]);

  if (!node) return <EmptyPropertiesPanel />;

  const isConnector = !!nodeDef?.connectorMeta;
  // The Worker tab leads when present so users see the
  // worker-shape (taskDefName / generated code / poll URL) the
  // moment they click an imported SIMPLE node — that's the gap that
  // motivated task #18.
  //
  // Tab semantics:
  //   - 'config'  : handler-specific fields (URL, method, body, prompt, etc.)
  //                 — what was historically called "input". Renamed because
  //                 the key/value Conductor OSS-style mapping (below) is what
  //                 builders mean when they say "inputs".
  //   - 'inputs'  : Conductor OSS-canonical inputParameters key/value editor.
  //                 First-class on every node, edits node.config.inputMapping.
  //   - 'output'  : output schema + output mapping aliases.
  // The 'coverage' tab only appears for policy/governance node types whose
  // configuration determines what L1 rules will fire at runtime. Surfacing
  // it on every node would be noise — operators looking at an HTTP node
  // don't care which policy rules WOULD fire if a policy gate were here.
  const POLICY_NODE_TYPES = new Set([
    'policy_enforcement_gate', 'policy_enforcement',
    'policy_rule_evaluation', 'policy_audit',
    'agent_autonomy_gate', 'dpg_scan', 'dpg_scan_inbound',
    'dpg_scan_outbound', 'dpg_internode_scan', 'data_classification',
  ]);
  const isPolicyNode = node && POLICY_NODE_TYPES.has(node.type);
  /* G290 — Materialised policy gate nodes (policy_gate_org / _tenant /
   * _agent) come from buildAgentSkillsDag's policy layer with the
   * resolved rule context already in config (ruleId / ruleName /
   * ruleDescription / ruleSeverity / ruleEnforcementPoint /
   * bindingScope / inheritedFrom). The 'rule' tab makes that context
   * readable so an operator clicking each gate can tell exactly what
   * it enforces instead of guessing from 7 identical labels. */
  const MATERIALISED_POLICY_GATE_TYPES = new Set([
    'policy_gate_org', 'policy_gate_tenant', 'policy_gate_agent',
  ]);
  const isMaterialisedPolicyGate = !!(node && MATERIALISED_POLICY_GATE_TYPES.has(node.type));
  /* G293 — Materialised skill nodes also lack readable task-level context.
   * The canvas stamps skill identity (skillName / skillKind / autonomy /
   * riskLevel / inferenceMode / category / description) plus the
   * AGENT_EXECUTION_MODEL §2.2 execution-logic fields (callability,
   * preconditions, parallelGroup, retryPolicy, timeoutMs,
   * idempotencyKeyTemplate, rateLimits) into config when materialising
   * an agent's skills via buildAgentSkillsDag. Without a dedicated tab,
   * an operator inspecting 8 generic `standard`-typed nodes sees only
   * "Node ID / Type / Description" and can't tell what each skill
   * actually does or how the runtime treats it. The 'skill' tab makes
   * that context readable.
   *
   * Gated by node.config.agentSkillId — present on every node
   * buildAgentSkillsDag emits for an agent skill, absent on operator-
   * added generic nodes. */
  const isMaterialisedSkill = !!(node && node.config && node.config.agentSkillId);
  /* G269 / LET_CANVAS_GOLDEN_RULES §4.4 — Per-skill execution stats tab.
   *
   * Surfaces only for nodes the canvas-materialiser stamped with
   * exec stats (G243 → G263 aggregator). That's currently materialised
   * skill nodes + the cognitive_loop node. The presence of
   * executionStats in config.executionStats IS the tab gate, so as new
   * node types start collecting per-invocation stats they pick up the
   * tab for free. */
  const hasExecStats = !!(node && node.config && node.config.executionStats);
  const tabs = [
    ...(isWorkerTask ? ['worker'] : []),
    'general',
    ...(isConnector ? ['connector'] : []),
    'config',
    'inputs',
    'output',
    'attributes',
    ...(isPolicyNode ? ['coverage'] : []),
    /* G290 — Materialised gate nodes get a dedicated 'rule' tab that
     * reads the resolved PolicyRule context (name, description,
     * category, severity, enforcement point, scope, inheritedFrom)
     * straight out of node.config. The 'coverage' tab above is for
     * operator-added policy_enforcement_gate nodes (which fire a SET of
     * rules); this 'rule' tab is for the single enforced rule a
     * materialised gate represents. */
    ...(isMaterialisedPolicyGate ? ['rule'] : []),
    /* G293 — Materialised skill nodes get a dedicated 'skill' tab that
     * reads the skill identity + AGENT_EXECUTION_MODEL §2.2 execution-
     * logic fields stamped into config by buildAgentSkillsDag. Without
     * this tab an operator can't tell `Enrich IOC (VirusTotal)` from
     * `Notify SOC Channel` beyond the label; with it they see callability
     * mode, preconditions, parallel group, idempotency template, retry
     * policy, timeout, rate limits — every dial the cognitive runtime
     * reads when invoking the skill. */
    ...(isMaterialisedSkill ? ['skill'] : []),
    ...(hasExecStats ? ['stats'] : []),
    ...(executionState ? ['execution'] : []),
  ];

  return (
    /* G298 — minWidth:0 propagates the flex parent's width constraint
       down to children so a long UUID or code-block inside RuleTab /
       SkillTab / StatsTab can't push the inspector horizontally past
       its allocated width. Without this, flexbox treats child intrinsic
       width as the floor and the whole pane drifts right (visible as
       truncated "0.0%" / "RROR RATE" / "ast invoked" in the user's
       screenshot). */
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: 12, borderBottom: '1px solid var(--v2-border-default)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {nodeDef?.connectorMeta?.icon && (
            <span style={{ fontSize: 16 }}>{nodeDef.connectorMeta.icon}</span>
          )}
          {nodeDef && <Badge text={nodeDef.label} variant="info" />}
          {isConnector && (() => {
            const mkId = nodeDef.connectorMeta.marketplaceId;
            const inst = installedConnectorsMkIndex?.[mkId]
              || Object.values(installedConnectors || {}).find((c) => c.name && nodeDef.connectorMeta.name && c.name.toLowerCase() === nodeDef.connectorMeta.name.toLowerCase());
            if (inst?.status === 'active') return <Badge text="Connected" variant="success" />;
            if (inst) return <Badge text={inst.status} variant="warning" />;
            return <Badge text="Not Installed" variant="default" />;
          })()}
        </div>
        <input
          type="text"
          value={node.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          style={{
            ...inputStyle,
            fontSize: 13,
            fontWeight: 600,
          }}
          placeholder="Node label"
        />
      </div>

      {/* RC-120 Phase 1 — failed-node deep-dive banner.
       *
       * When the selected node has a failed execution state, surface
       * the error at the very top of the Inspector — above the tab bar
       * — so the operator can see what broke without first switching
       * to the Execution tab. Pre-RC-120 the flow required: click
       * failed node → switch panel to Execution tab → read error →
       * switch to Config tab → find the offending field → edit. The
       * banner collapses that to a single visible affordance, with
       * "Fix in Config" jumping straight to the config editor and
       * "Open execution details" jumping to the Execution tab when
       * the operator wants the full trace.
       *
       * Visibility rules:
       *   - Renders ONLY for status='failed' (other states are not
       *     errors and don't need the deep-dive affordance).
       *   - Renders regardless of which tab is currently active so
       *     the operator's first glance always sees the error.
       *   - "Fix in Config" targets the 'config' tab if the tab list
       *     includes it (handler-specific fields), else 'worker'
       *     (worker_task), else 'general' (fallback for nodes with
       *     no dedicated config surface).
       *
       * Field-level scroll-to-failing-config is deferred to RC-120b —
       * extracting the offending field-name from an arbitrary BE
       * error string is brittle without a structured error envelope
       * and falls outside Phase 1. */}
      {executionState && executionState.status === 'failed' && (executionState.error || executionState.errorMessage) && (
        <FailureDeepDiveBanner
          executionState={executionState}
          onJumpToConfig={(fieldKey) => {
            const target = tabs.includes('config')
              ? 'config'
              : (tabs.includes('worker') ? 'worker' : 'general');
            setActiveTab(target);
            /* RC-120b — When the banner extracted an offending field
             * name from the error, stash it for the config panel to
             * scroll-into-view + highlight on mount. Null = no
             * targeting (pre-RC-120b behavior, jump only). */
            if (fieldKey && typeof fieldKey === 'string') {
              setHighlightFieldKey(fieldKey);
            }
          }}
          onJumpToExecution={() => setActiveTab('execution')}
          /* RC-120b — Replay button only wires when the parent gave us
           * a runId AND an onReplayRun handler. Avoids rendering a
           * dead button on workflows opened without an execution
           * context. */
          onReplayRun={(selectedRunId && onReplayRun) ? () => onReplayRun(selectedRunId) : undefined}
          replayBusy={replayBusy}
        />
      )}

      {/* Tabs — flex-wrap allows two-row tab bar when ≥6 tabs are
          conditionally surfaced (general/config/inputs/output/attributes
          + rule/skill/stats/execution). Single-row flex:1 squeezes
          longer labels ("execution", "attributes") to ≤45px and the
          inspector pane is only ~320px wide, so the tabs were truncating
          and the content panel below was rendering past the right
          edge. flex-basis ensures a sane minimum width per tab; wrap
          turns overflow into a second row instead. */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--v2-border-default)',
        background: 'var(--v2-bg-elevated)',
        gap: 0,
      }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: '1 1 60px',
              minWidth: 60,
              padding: '10px 6px',
              background: activeTab === tab ? 'var(--v2-bg-surface)' : 'transparent',
              border: activeTab === tab ? '1px solid var(--v2-border-default)' : 'none',
              borderBottom: activeTab === tab ? 'none' : '1px solid var(--v2-border-default)',
              cursor: 'pointer',
              fontSize: 10,
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
              textTransform: 'capitalize',
              borderRadius: '4px 4px 0 0',
              whiteSpace: 'nowrap',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content — `minWidth: 0` is essential under `flex: 1` so a
          long code block / UUID inside doesn't blow out the inspector
          horizontally. `overflowX: hidden` clamps any remaining stray. */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, padding: 12 }}>
        {activeTab === 'worker' && isWorkerTask && (
          <WorkerTab node={node} onUpdate={onUpdate} />
        )}
        {activeTab === 'general' && (
          <GeneralTab node={node} nodeDef={nodeDef} onUpdate={onUpdate} onDelete={onDelete} />
        )}
        {activeTab === 'connector' && isConnector && (
          <ConnectorTab nodeDef={nodeDef} installedConnectors={installedConnectors} installedConnectorsMkIndex={installedConnectorsMkIndex} />
        )}
        {activeTab === 'config' && (() => {
          /* RC-18d (task #202) — V3Inspector router. For node types in
           * V3Inspector's NODE_INSPECTOR_REGISTRY, render the per-type
           * inspector (LlmTask, ConnectorTask, DataTransform, HumanTask,
           * etc.). For everything else, fall through to the generic InputTab.
           * Zero regression: any type not in the registry renders exactly
           * the same UI it did pre-RC-18d. Mirror the registry key set from
           * ./v3/V3Inspector.jsx NODE_INSPECTOR_REGISTRY — if V3Inspector
           * adds a new inspector, add the type to this set to route through
           * it as well. */
          const INSPECTOR_SUPPORTED_TYPES = new Set([
            'http_task', 'llm_task', 'condition', 'terminate', 'end',
            'switch_case', 'script_task', 'notification_task', 'variable_set',
            'connector_task', 'data_transform', 'agent_chat', 'human_task',
            'approval_gate', 'webhook', 'schedule', 'parallel_fork', 'fork',
            'loop', 'sub_workflow',
            /* RC-152c-1c — form_task routes through V3Inspector so the
             * RC-152c-1 HumanTaskInspector mapping (form_task gets the
             * field-builder + "Design form ↗" button) actually reaches
             * form_task instances. Previously form_task fell through to
             * the schema-driven generic InputTab (raw JSON textarea on
             * config.formSchema) because this allowlist gated it out.
             * RC-103 confirmed form_task and human_task share the same
             * formSchema shape — the runtime + Approval Center already
             * treat them identically, so the inspector should too. */
            'form_task',
          ]);
          if (node?.type && INSPECTOR_SUPPORTED_TYPES.has(node.type)) {
            return (
              <V3Inspector
                selectedNode={node}
                onUpdate={(id, patch) => {
                  // V3Inspector signature: (nodeId, patch). This panel's
                  // onUpdate signature is (patch) where the selected-node id is
                  // implicit. id should always match node.id since
                  // selectedNode came from us; ignore the id and forward.
                  if (typeof onUpdate === 'function') onUpdate(patch);
                }}
                /* bodyOnly — V3's panel already supplies the node header,
                 * sub-tabs, and the padded scroll container. Render only the
                 * per-type editor body so it flows like the sibling tabs
                 * (fixes the fixed-width / nested-scroll / duplicated-header
                 * mis-size the full <aside> caused here). */
                bodyOnly
                readOnly={false}
                /* RC-152c-1c — Plumb the currently-loaded workflow id
                 * through to per-type inspectors that need it (the
                 * HumanTaskInspector's "Design form ↗" button deep-links
                 * to /forms/design?wid=…&nid=…). Source-of-truth is the
                 * canvas's selectedWorkflowId state because not every
                 * navigation path that lands on a workflow updates the
                 * URL with ?workflowId= (e.g. opening from "Recent" or
                 * from a deep agent-bound link). Inspectors still fall
                 * back to URL searchParams when this prop is absent so
                 * standalone embeddings (tests, slideouts) keep working. */
                workflowId={selectedWorkflowId || null}
              />
            );
          }
          return (
            /* RC-120b — highlightFieldKey is set by the failure deep-dive
             * banner's "Fix in Config" handler when the BE error string
             * contains a recognisable field name. InputTab (and any future
             * per-type inspectors that consume the prop) can scroll the
             * matching input into view and add a transient highlight ring.
             * Today InputTab IGNORES the prop — Phase 2 of RC-120b will
             * wire the actual scroll/highlight behavior across the input
             * renderers. Threading the prop now keeps the wiring small
             * for the Phase 2 follow-up. */
            <InputTab node={node} nodeDef={nodeDef} onUpdate={onUpdate} installedConnectors={installedConnectors} agents={agents} nodes={nodes} connections={connections} highlightFieldKey={highlightFieldKey} />
          );
        })()}
        {activeTab === 'inputs' && (
          <InputsTab node={node} nodeDef={nodeDef} onUpdate={onUpdate} nodes={nodes} connections={connections} />
        )}
        {activeTab === 'output' && (
          <OutputTab nodeDef={nodeDef} executionState={executionState} node={node} onUpdate={onUpdate} />
        )}
        {activeTab === 'attributes' && (
          <AttributesTab node={node} onUpdate={onUpdate} />
        )}
        {activeTab === 'coverage' && isPolicyNode && (
          <PolicyCoverageTab node={node} />
        )}
        {/* G290 — Materialised-gate rule inspector. The gate carries the
            single PolicyRule context the binding resolved to; surface it
            so operators can tell ORG/TENANT/AGENT gates apart at a glance
            instead of decoding 7 identical "ORG · platform_default" labels. */}
        {activeTab === 'rule' && isMaterialisedPolicyGate && (
          <RuleTab node={node} />
        )}
        {/* G293 — Materialised skill inspector. Surfaces the same skill
            identity + execution-logic context the runtime reads when
            invoking the skill, so operators can tell skills apart at a
            glance and audit how each will behave under cognitive /
            deterministic / hybrid execution. */}
        {activeTab === 'skill' && isMaterialisedSkill && (
          <SkillTab node={node} />
        )}
        {activeTab === 'stats' && hasExecStats && (
          <StatsTab node={node} />
        )}
        {activeTab === 'execution' && executionState && (
          <ExecutionTab executionState={executionState} nodeId={node.id} nodeLabel={node.label} onDebugNode={onDebugNode} node={node} onUpdate={onUpdate} />
        )}
      </div>
    </div>
  );
}

/** Connector-specific tab — shows auth, skills, endpoint details */
function ConnectorTab({ nodeDef, installedConnectors, installedConnectorsMkIndex }) {
  const meta = nodeDef?.connectorMeta;
  if (!meta) return null;

  const inst = installedConnectorsMkIndex?.[meta.marketplaceId]
    || Object.values(installedConnectors || {}).find((c) => c.name && meta.name && c.name.toLowerCase() === meta.name.toLowerCase());
  const statusColor = inst?.status === 'active' ? 'var(--v2-success)' : inst ? 'var(--v2-warning)' : 'var(--v2-text-disabled)';
  const statusLabel = inst?.status === 'active' ? 'Connected & Active' : inst ? `Status: ${inst.status}` : 'Not Installed';

  return (
    <div>
      {/* Connection Status */}
      <div style={{
        padding: 10, borderRadius: 6, marginBottom: 12,
        background: inst?.status === 'active' ? 'var(--v2-bg-muted)' : 'var(--v2-warning-soft)',
        border: `1px solid ${statusColor}33`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>{statusLabel}</span>
        </div>
        {!inst && (
          <div style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
            Install this connector from the Connectors page to use it in workflows.
          </div>
        )}
      </div>

      {/* Connector Details */}
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Vendor</label>
        <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-muted)', fontSize: 11 }}>
          {meta.vendor} v{meta.version}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Auth Type</label>
        <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-muted)', fontSize: 11 }}>
          {meta.authType?.toUpperCase() || 'N/A'}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Base URL</label>
        <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-muted)', fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {meta.baseUrl || 'N/A'}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Category</label>
        <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-muted)', fontSize: 11 }}>
          {meta.category}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Tier</label>
        <Badge text={meta.tier || 'standard'} variant={meta.tier === 'enterprise' ? 'warning' : 'info'} />
      </div>

      {/* Available Skills */}
      {meta.skills?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Available Skills ({meta.skills.length})</label>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {meta.skills.map((skill) => (
              <div key={skill.id} style={{
                padding: '6px 8px', marginBottom: 4, background: 'var(--v2-bg-elevated)',
                border: '1px solid var(--v2-border-default)', borderRadius: 4, fontSize: 11,
              }}>
                <div style={{ fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 2 }}>
                  <span style={{
                    display: 'inline-block', padding: '1px 4px', borderRadius: 3,
                    background: skill.method === 'GET' ? 'var(--v2-action-soft)' : skill.method === 'POST' ? 'var(--v2-bg-muted)' : 'var(--v2-action-soft)',
                    color: skill.method === 'GET' ? 'var(--v2-action)' : skill.method === 'POST' ? 'var(--v2-success)' : 'var(--v2-warning)',
                    fontSize: 9, fontWeight: 700, marginRight: 4,
                  }}>{skill.method}</span>
                  {skill.name}
                </div>
                <div style={{ color: 'var(--v2-text-muted)', fontSize: 10 }}>{skill.endpoint}</div>
                {skill.description && (
                  <div style={{ color: 'var(--v2-text-disabled)', fontSize: 10, marginTop: 2 }}>{skill.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Docs link */}
      {meta.docs && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Documentation</label>
          <a href={meta.docs} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'var(--v2-action)', textDecoration: 'underline', wordBreak: 'break-all' }}>
            {meta.docs}
          </a>
        </div>
      )}

      {/* Tags */}
      {meta.tags?.length > 0 && (
        <div>
          <label style={labelStyle}>Tags</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {meta.tags.map((tag) => (
              <span key={tag} style={{
                padding: '2px 6px', fontSize: 10, background: 'var(--v2-bg-muted)',
                border: '1px solid var(--v2-border-default)', borderRadius: 10, color: 'var(--v2-text-muted)',
              }}>{tag}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GeneralTab({ node, nodeDef, onUpdate, onDelete }) {
  // Detect a SCRIPT-shaped node — these are the ones the C-06 policy
  // disables, and the ones the JS→JQ converter exists to migrate.
  const isScriptLike =
    node.type === 'script_task' || node.type === 'script' ||
    node.type === 'inline'      || node.type === 'INLINE';
  const scriptBody = (node.config && (node.config.expression || node.config.script)) || '';

  const [showConvertModal, setShowConvertModal] = useState(false);

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Node ID</label>
        <div style={{
          ...inputStyle,
          background: 'var(--v2-bg-muted)',
          color: 'var(--v2-text-muted)',
          fontFamily: 'monospace',
          fontSize: 11,
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
        }}>
          {node.id}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Type</label>
        <div style={{
          ...inputStyle,
          background: 'var(--v2-bg-muted)',
          color: 'var(--v2-text-muted)',
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
        }}>
          {nodeDef?.label || node.type}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Description</label>
        <textarea
          value={node.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          placeholder="Node description"
        />
      </div>

      {/* SCRIPT → data_transform (JQ) migration — visible on any
          script-shaped node so users can pre-emptively convert before
          hitting the C-06 disabled-script wall. The actual conversion
          and confidence assessment lives in utils/jsToJq.js. */}
      {isScriptLike && (
        <div style={{
          marginBottom: 14,
          padding: 12,
          background: 'var(--v2-warning-soft)',
          border: '1px solid var(--v2-warning-soft)',
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-warning)', marginBottom: 4 }}>
            Convert to a safe JQ Data Transform
          </div>
          <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', lineHeight: 1.5, marginBottom: 8 }}>
            Inline JavaScript execution is disabled platform-wide
            (policy C-06; the same RCE class as Conductor OSS's
            CVE-2025-26074). Most {' '}
            <code style={{ fontSize: 10 }}>$.foo.map(...)</code> /{' '}
            <code style={{ fontSize: 10 }}>$.foo.filter(...)</code> scripts
            are pure data projections that have a direct, safe JQ
            equivalent. Click below to see the suggested rewrite and
            apply it.
          </div>
          <button
            onClick={() => setShowConvertModal(true)}
            style={{
              ...btnBase, background: 'var(--v2-warning)', color: 'var(--v2-bg-surface)',
              width: '100%', fontWeight: 600,
            }}
          >
            Convert to JQ Data Transform
          </button>
          {showConvertModal && (
            <ScriptToJqConvertModal
              scriptBody={scriptBody}
              onCancel={() => setShowConvertModal(false)}
              onConfirm={(jqExpression) => {
                onUpdate(buildJqConversionPatch(jqExpression, node.config));
                setShowConvertModal(false);
              }}
            />
          )}
        </div>
      )}

      {/* G385 — Hide Delete Node when the canvas is locked. Parent passes
          null for onDelete in that case so we can render a lock indicator
          instead — making it visually obvious that the action is unavailable
          rather than a broken button. */}
      {onDelete ? (
        <button
          onClick={onDelete}
          style={{ ...btnBase, background: 'var(--v2-danger-soft)', color: 'var(--v2-danger)', width: '100%' }}
        >
          <Trash2 size={14} style={{ marginRight: 4 }} />
          Delete Node
        </button>
      ) : (
        <div style={{
          padding: '8px 10px', borderRadius: 6, border: '1px dashed var(--v2-warning-soft)',
          background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)', fontSize: 11, lineHeight: 1.4,
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <span style={{ flexShrink: 0, marginTop: 1 }}>🔒</span>
          <span>
            {/* RC-48 — Removed internal rule reference ("per GOLDEN_CEG_RULES §1.3")
                from operator-facing copy. The rule still governs the behavior on
                the backend; the operator just doesn't need to see the citation. */}
            <strong>Read-only.</strong> Canvas mutations are disabled for this
            workflow kind — AutoLet CEGs are immutable. Upload a revised runbook
            to create v2.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Modal that runs `jsToJq` over the original script body, shows the
 * suggested JQ + confidence + caveats, lets the user edit before
 * confirming, and emits the final JQ via onConfirm. Pure presentational
 * — the conversion patch is built by the caller from the returned
 * string so this component doesn't need to know about node.config
 * shape.
 */
function ScriptToJqConvertModal({ scriptBody, onCancel, onConfirm }) {
  const initial = useMemo(() => jsToJq(scriptBody), [scriptBody]);
  const [jq, setJq] = useState(initial.jq);

  const confidenceColor =
    initial.confidence === 'high'   ? 'var(--v2-success)'
    : initial.confidence === 'medium' ? 'var(--v2-warning)'
    : 'var(--v2-danger)';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0, 0, 0, 0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}
    onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--v2-bg-surface)',
          borderRadius: 12,
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
          padding: 24,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
          Convert Inline Script → JQ Data Transform
        </div>
        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
          The agentlets backend evaluates the JQ on the right server-side via
          a pure-functional library (no eval, no I/O, sandboxed by construction).
          Review the suggestion, edit if needed, and confirm to rewrite this
          node's type from <code>script_task</code> to <code>data_transform</code>.
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
            Original JavaScript
          </div>
          <pre style={{
            margin: 0, padding: 10,
            background: 'var(--v2-text-primary)', color: 'var(--v2-success-soft)',
            borderRadius: 6, fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11, lineHeight: 1.5,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 160, overflowY: 'auto',
          }}>
            {scriptBody || '(empty script)'}
          </pre>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 4,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-muted)', textTransform: 'uppercase' }}>
              Suggested JQ (editable)
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700,
              padding: '2px 8px', borderRadius: 999,
              background: confidenceColor + '22', color: confidenceColor,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {initial.confidence} confidence
            </span>
          </div>
          <textarea
            value={jq}
            onChange={(e) => setJq(e.target.value)}
            style={{
              width: '100%', minHeight: 100,
              padding: 10, fontSize: 12,
              fontFamily: '"JetBrains Mono", monospace',
              border: '1px solid var(--v2-border-default)', borderRadius: 6,
              boxSizing: 'border-box', resize: 'vertical',
            }}
            placeholder='.policies | map({"const": .policy_number, "title": .policy_type})'
          />
        </div>

        {initial.notes.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
              Notes from the converter
            </div>
            <ul style={{
              margin: 0, paddingLeft: 18,
              fontSize: 11, color: 'var(--v2-text-secondary)', lineHeight: 1.6,
            }}>
              {initial.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              background: 'transparent', color: 'var(--v2-text-primary)',
              border: '1px solid var(--v2-border-default)', borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(jq)}
            disabled={!jq.trim()}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              background: jq.trim() ? 'var(--v2-success)' : 'var(--v2-text-disabled)',
              color: 'var(--v2-bg-surface)', border: 'none', borderRadius: 6,
              cursor: jq.trim() ? 'pointer' : 'default',
            }}
          >
            Apply Conversion
          </button>
        </div>
      </div>
    </div>
  );
}

/** Collapsible section with clickable header — used in properties panel */
function CollapsibleSection({ title, subtitle, defaultOpen = true, badge, color = 'var(--v2-text-primary)', bgColor, borderColor, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      marginBottom: 12,
      border: borderColor ? `1px solid ${borderColor}` : '1px solid var(--v2-border-default)',
      borderRadius: 8,
      overflow: 'hidden',
      background: bgColor || 'var(--v2-bg-surface)',
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px', cursor: 'pointer', userSelect: 'none',
          background: bgColor || 'var(--v2-bg-elevated)',
          borderBottom: open ? `1px solid ${borderColor || 'var(--v2-border-default)'}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 10, color: 'var(--v2-text-muted)', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
          <span style={{ fontSize: 11, fontWeight: 700, color }}>{title}</span>
          {badge != null && <span style={{ fontSize: 9, background: 'var(--v2-border-default)', color: 'var(--v2-text-secondary)', padding: '1px 5px', borderRadius: 8, fontWeight: 600 }}>{badge}</span>}
        </div>
        {subtitle && <span style={{ fontSize: 9, color: 'var(--v2-text-disabled)', flexShrink: 0 }}>{subtitle}</span>}
      </div>
      {open && <div style={{ padding: '10px' }}>{children}</div>}
    </div>
  );
}

/**
 * WorkerTab — surfaces the Conductor worker-shape on the right Task
 * panel for any `worker_task` node (or a legacy `task` node carrying
 * the same shape). Three sections, in priority order:
 *   1. Definition — the editable task-def fields (taskDefName, retries,
 *      timeouts, polling). Lives here so users see *what* the workflow
 *      expects of their worker before writing any code.
 *   2. Generated Code — language picker + per-language stub. Stubs are
 *      grounded in this node's metadata (taskDefName / inputKeys /
 *      outputKeys) so paste-into-IDE is one step.
 *   3. Wiring — the env vars + poll URL the worker process needs, plus
 *      the Hosting Mode toggle behind `workerHosting` feature flag.
 *
 * The Conductor docs link panel is intentionally lightweight; this tab
 * is the agentlets equivalent of Conductor OSS "Sample worker code" sidebar
 * but with field values that match THIS node, not generic templates.
 */
function WorkerTab({ node, onUpdate }) {
  const [language, setLanguage] = useState('java');
  const [copied, setCopied] = useState(false);
  const cfg = (node && node.config) || {};

  const stub = useMemo(() => generateWorkerStub(node, language) || '', [node, language]);

  const updateConfig = (patch) => onUpdate({ config: { ...cfg, ...patch } });

  const copyStub = async () => {
    try {
      await navigator.clipboard.writeText(stub);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked — no-op */ }
  };

  const downloadStub = () => {
    const blob = new Blob([stub], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const base = (cfg.taskDefName || node.id || 'worker').replace(/[^a-zA-Z0-9]+/g, '_');
    a.href = url;
    a.download = `${base}.${fileExtensionFor(language)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // The backend exposes the worker task API on two surfaces:
  //   • SDK-compat path (Conductor OSS/Netflix Conductor SDK convention):
  //         GET  ${CONDUCTOR_SERVER_URL}/tasks/poll/{name}
  //         POST ${CONDUCTOR_SERVER_URL}/tasks            (TaskResult)
  //   • Agentlets-native path (raw HTTP convenience):
  //         POST ${CONDUCTOR_SERVER_URL}/v1/tasks/poll/{name}
  //         POST ${CONDUCTOR_SERVER_URL}/v1/tasks/{taskId}/{complete|fail}
  // Both validate via GatewayAuthenticationFilter against worker keys.
  // We surface BOTH paths but lead with the SDK-compat one since that's
  // what conductor-python / conductor-java / etc. talk to. The
  // examples use ${CONDUCTOR_SERVER_URL} as a placeholder rather than
  // the frontend's origin — the worker process typically runs against
  // a different host (often plain HTTP) than the browser session
  // (typically self-signed HTTPS in dev), and a literal URL pulled
  // from window.location.origin would mislead the user.
  const sdkPollPath  = cfg.taskDefName ? `\${CONDUCTOR_SERVER_URL}/tasks/poll/${cfg.taskDefName}`    : '';
  const sdkUpdatePath = cfg.taskDefName ? `POST \${CONDUCTOR_SERVER_URL}/tasks  (TaskResult body)`   : '';
  const v1PollPath   = cfg.taskDefName ? `\${CONDUCTOR_SERVER_URL}/v1/tasks/poll/${cfg.taskDefName}` : '';

  const hostingManaged = cfg.hostingMode === 'agentlets_managed';
  const showHostingToggle = isWorkerHostingEnabled();

  const sectionLabel = {
    fontSize: 10, fontWeight: 700, color: 'var(--v2-text-muted)',
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginTop: 14, marginBottom: 6,
  };

  return (
    <div>
      {/* ── 1. DEFINITION ── */}
      <div style={{ ...sectionLabel, marginTop: 0 }}>Worker Definition</div>

      <label style={labelStyle}>Task Definition Name</label>
      <input
        type="text"
        value={cfg.taskDefName || ''}
        onChange={(e) => updateConfig({ taskDefName: e.target.value })}
        placeholder="findPolicyForCustomer"
        style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}
      />
      <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2, marginBottom: 10 }}>
        Must match the <code>@WorkerTask("…")</code> name in your worker code.
      </div>

      {/* Generic retry policy + execution timeout live on the Attributes
          tab — that's the single source of truth for ALL task types so
          a worker_task and an http_task and a script_task share the
          same behaviour. We deliberately don't repeat those fields
          here; previous iterations did, and three places to set the
          same value caused real confusion at runtime. The two fields
          below ARE worker-specific (no equivalent on Attributes) so
          they stay on the Worker tab. */}
      <div style={{
        marginTop: 4, marginBottom: 10, padding: '8px 10px',
        background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-border-default)', borderRadius: 6,
        fontSize: 11, color: 'var(--v2-text-secondary)', lineHeight: 1.5,
      }}>
        Retry policy + execution timeout live on the <strong>Attributes</strong> tab —
        applied consistently to every task type. Worker-specific knobs (response
        timeout, poll timeout, concurrency, domain, ownerEmail) stay below.
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Response Timeout (s)</label>
          <input
            type="number" min="1"
            value={cfg.responseTimeoutSeconds ?? 600}
            onChange={(e) => updateConfig({ responseTimeoutSeconds: parseInt(e.target.value, 10) || 0 })}
            style={inputStyle}
          />
          <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2 }}>
            How long after CLAIMED before the reaper marks the row TIMED_OUT.
            Worker-specific — distinct from the execution timeout on Attributes.
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Poll Timeout (s)</label>
          <input
            type="number" min="1"
            value={cfg.pollTimeoutSeconds ?? 3600}
            onChange={(e) => updateConfig({ pollTimeoutSeconds: parseInt(e.target.value, 10) || 0 })}
            style={inputStyle}
          />
          <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2 }}>
            How long a queued task waits for a worker before being abandoned.
          </div>
        </div>
      </div>

      <label style={{ ...labelStyle, marginTop: 8 }}>Input Keys</label>
      <input
        type="text"
        value={Array.isArray(cfg.inputKeys) ? JSON.stringify(cfg.inputKeys) : (cfg.inputKeys || '')}
        onChange={(e) => updateConfig({ inputKeys: e.target.value })}
        placeholder='["customerId", "policyType"]'
        style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}
      />

      <label style={{ ...labelStyle, marginTop: 8 }}>Output Keys</label>
      <input
        type="text"
        value={Array.isArray(cfg.outputKeys) ? JSON.stringify(cfg.outputKeys) : (cfg.outputKeys || '')}
        onChange={(e) => updateConfig({ outputKeys: e.target.value })}
        placeholder='["policyNumber", "policyType", "issuedAt"]'
        style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}
      />

      {/* ── 2. GENERATED CODE ── */}
      <div style={sectionLabel}>Generated Worker Code</div>

      <div style={{
        display: 'flex', gap: 0, marginBottom: 6,
        borderBottom: '1px solid var(--v2-border-default)',
      }}>
        {WORKER_STUB_LANGUAGES.map((l) => (
          <button
            key={l.value}
            onClick={() => setLanguage(l.value)}
            title={l.comment}
            style={{
              padding: '6px 8px', fontSize: 11,
              fontWeight: language === l.value ? 700 : 500,
              color: language === l.value ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
              background: 'transparent',
              border: 'none',
              borderBottom: language === l.value ? '2px solid var(--v2-action)' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: -1,
            }}
          >
            {l.label}
          </button>
        ))}
      </div>

      <pre style={{
        margin: 0, padding: '10px 12px',
        background: 'var(--v2-text-primary)', color: 'var(--v2-bg-muted)',
        borderRadius: 6, fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontSize: 10, lineHeight: 1.5,
        maxHeight: 360, overflow: 'auto',
        whiteSpace: 'pre',
      }}>
        {stub || '// Set a Task Definition Name above to generate a worker stub.'}
      </pre>

      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          onClick={copyStub}
          disabled={!stub}
          style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: copied ? 'var(--v2-success)' : 'var(--v2-text-primary)', color: 'var(--v2-bg-surface)',
            border: 'none', borderRadius: 4,
            cursor: stub ? 'pointer' : 'default',
          }}
        >
          {copied ? '✓ Copied' : 'Copy stub'}
        </button>
        <button
          onClick={downloadStub}
          disabled={!stub}
          style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: 'transparent', color: 'var(--v2-text-primary)',
            border: '1px solid var(--v2-border-default)', borderRadius: 4,
            cursor: stub ? 'pointer' : 'default',
          }}
        >
          Download as file
        </button>
      </div>

      {/* ── 3. WIRING ── */}
      <div style={sectionLabel}>Wiring</div>

      <div style={{ fontSize: 11, color: 'var(--v2-text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
        The worker process needs three environment variables. Auth keys
        come from <strong>Protection Hub → API Keys</strong> (Key Type =
        Worker). Set <code>CONDUCTOR_SERVER_URL</code> to whatever host
        your worker runs against — typically{' '}
        <code>http://localhost:3000/api</code> in dev (the same backend
        the canvas talks to via HTTPS) or your production URL.
      </div>

      <div style={{
        background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)', borderRadius: 6,
        padding: '8px 10px', fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
        color: 'var(--v2-text-primary)', lineHeight: 1.7,
      }}>
        <div>CONDUCTOR_SERVER_URL=<span style={{ color: 'var(--v2-text-disabled)' }}>&lt;your-backend-url&gt;/api</span></div>
        <div>CONDUCTOR_AUTH_KEY=<span style={{ color: 'var(--v2-text-disabled)' }}>&lt;keyId — UUID from Worker key creation&gt;</span></div>
        <div>CONDUCTOR_AUTH_SECRET=<span style={{ color: 'var(--v2-text-disabled)' }}>&lt;agt_wk_… — shown once&gt;</span></div>
      </div>

      {sdkPollPath && (
        <>
          <label style={{ ...labelStyle, marginTop: 12 }}>
            Endpoints — SDK-compat (conductor-python, conductor-java, &amp;c.)
          </label>
          <div style={{
            ...inputStyle,
            background: 'var(--v2-bg-muted)', color: 'var(--v2-text-secondary)',
            fontFamily: '"JetBrains Mono", monospace', fontSize: 10,
            wordBreak: 'break-all', overflowWrap: 'anywhere',
            lineHeight: 1.6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span>GET {sdkPollPath}</span>
              <button
                onClick={() => navigator.clipboard.writeText(sdkPollPath)}
                title="Copy poll URL template"
                style={{
                  padding: '2px 6px', fontSize: 10,
                  background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)',
                  border: '1px solid var(--v2-border-default)', borderRadius: 3,
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                Copy
              </button>
            </div>
            <div>{sdkUpdatePath}</div>
          </div>

          <label style={{ ...labelStyle, marginTop: 8 }}>
            Endpoints — agentlets-native (raw HTTP convenience)
          </label>
          <div style={{
            ...inputStyle,
            background: 'var(--v2-bg-muted)', color: 'var(--v2-text-secondary)',
            fontFamily: '"JetBrains Mono", monospace', fontSize: 10,
            wordBreak: 'break-all', overflowWrap: 'anywhere',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 6,
          }}>
            <span>POST {v1PollPath}</span>
            <button
              onClick={() => navigator.clipboard.writeText(v1PollPath)}
              title="Copy poll URL template"
              style={{
                padding: '2px 6px', fontSize: 10,
                background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)',
                border: '1px solid var(--v2-border-default)', borderRadius: 3,
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              Copy
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 4 }}>
            Both surfaces hit the same queue and validate with the same
            worker key. SDKs use the top one; curl / hand-rolled HTTP
            workers can use either.
          </div>
        </>
      )}

      {showHostingToggle && (
        <div style={{
          marginTop: 12,
          padding: '10px 12px',
          background: hostingManaged ? 'var(--v2-danger-soft)' : 'var(--v2-success-soft)',
          border: `1px solid ${hostingManaged ? 'var(--v2-danger-soft)' : 'var(--v2-success-soft)'}`,
          borderRadius: 6,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={hostingManaged}
              onChange={(e) => updateConfig({ hostingMode: e.target.checked ? 'agentlets_managed' : 'conductor_poll' })}
              style={{ width: 14, height: 14, cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: hostingManaged ? 'var(--v2-danger)' : 'var(--v2-success)' }}>
                Run on Agentlets Workers (managed)
              </div>
              <div style={{ fontSize: 10, color: 'var(--v2-text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
                {hostingManaged
                  ? 'This worker is hosted on our Temporal OSS-backed runtime — no worker process for you to operate. Activity body comes from the generated code above.'
                  : 'Currently in Conductor-style polling mode — you run the worker process yourself. Toggle on to hand off operation to Agentlets.'}
              </div>
            </div>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Top-level template-picker component. Previously this was defined inline
 * inside InputTab as `() => { useState(); useEffect(); … }` and re-created
 * on every parent render — meaning React kept seeing a fresh component
 * identity and unmounting/remounting it, which (a) wiped local state
 * every keystroke in the parent and (b) made hook accounting brittle.
 * Hoisting it to module scope fixes both. Used by InputTab for the
 * `template_select` field type on regular workflow nodes (writes to
 * `node.config[field.field]`).
 */
function TemplateSelectField({ field, node, onUpdate }) {
  const [templates, setTemplates] = React.useState([]);
  const [loadingTpl, setLoadingTpl] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoadingTpl(true);
    getTemplates(field.templateType || 'email').then((res) => {
      if (!cancelled && res?.ok) {
        setTemplates((res.data || []).filter((t) => t.status === 'published' || t.status === 'draft'));
      }
      setLoadingTpl(false);
    }).catch(() => setLoadingTpl(false));
    return () => { cancelled = true; };
  }, [field.templateType]);

  const selectedId = node.config?.[field.field] || '';
  const selectedTpl = templates.find((t) => t.id === selectedId);

  const handleSelect = (e) => {
    const tplId = e.target.value;
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) {
      onUpdate({
        config: {
          ...node.config,
          [field.field]: tplId,
          templateName: tpl.name,
          subject: tpl.subject || node.config?.subject || '',
          body: tpl.body || node.config?.body || '',
        },
      });
    } else {
      onUpdate({ config: { ...node.config, [field.field]: '', templateName: '' } });
    }
  };

  return (
    <div>
      <select value={selectedId} onChange={handleSelect} style={inputStyle}>
        <option value="">{loadingTpl ? 'Loading templates...' : '(No template — use custom below)'}</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}{t.category ? ` [${t.category}]` : ''}{t.status === 'draft' ? ' (Draft)' : ''}
          </option>
        ))}
      </select>
      {selectedTpl && (
        <div style={{
          marginTop: 6, padding: '8px 10px', background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)',
          borderRadius: 6, fontSize: 11, color: 'var(--v2-success)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>✓ {selectedTpl.name}</div>
          <div style={{ color: 'var(--v2-success)', fontSize: 10 }}>{selectedTpl.description || 'No description'}</div>
          {selectedTpl.variables && selectedTpl.variables !== '[]' && (
            <div style={{ marginTop: 4, fontSize: 10, color: 'var(--v2-success)' }}>
              Variables: <code style={{ background: 'var(--v2-success-soft)', padding: '1px 4px', borderRadius: 3 }}>
                {selectedTpl.variables}
              </code>
            </div>
          )}
          <button
            onClick={() => setPreviewOpen(!previewOpen)}
            style={{
              marginTop: 4, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, color: 'var(--v2-action)', fontWeight: 600, padding: 0,
            }}
          >
            {previewOpen ? '▾ Hide Preview' : '▸ Preview Template'}
          </button>
          {previewOpen && selectedTpl.body && (
            <div style={{
              marginTop: 6, padding: 8, background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)',
              borderRadius: 6, maxHeight: 200, overflowY: 'auto', fontSize: 11,
            }}>
              {field.templateType === 'email' ? (
                <iframe
                  srcDoc={selectedTpl.body}
                  title="Template Preview"
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: 180, border: 'none' }}
                />
              ) : (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 10 }}>
                  {selectedTpl.body.substring(0, 500)}{selectedTpl.body.length > 500 ? '...' : ''}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Top-level template-picker for connector_task skills — same shape as
 * TemplateSelectField but writes to `node.config.skillParams.{...}`
 * because connector_task fields nest one level deeper. Hoisted out of
 * InputTab for the same Rules-of-Hooks reasons.
 */
function ConnectorTemplateSelect({ param, node, onUpdate }) {
  const [templates, setTemplates] = React.useState([]);
  const [loadingTpl, setLoadingTpl] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoadingTpl(true);
    getTemplates(param.templateType || 'email').then((res) => {
      if (!cancelled && res?.ok) {
        setTemplates((res.data || []).filter((t) => t.status === 'published' || t.status === 'draft'));
      }
      setLoadingTpl(false);
    }).catch(() => setLoadingTpl(false));
    return () => { cancelled = true; };
  }, [param.templateType]);

  const currentParams = node.config?.skillParams || {};
  const selectedId = currentParams.templateId || '';
  const selectedTpl = templates.find((t) => t.id === selectedId);

  const handleSelect = (e) => {
    const tplId = e.target.value;
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) {
      onUpdate({
        config: {
          ...node.config,
          skillParams: {
            ...currentParams,
            templateId: tplId,
            templateName: tpl.name,
            subject: tpl.subject || currentParams.subject || '',
            body: tpl.body || currentParams.body || '',
          },
        },
      });
    } else {
      onUpdate({
        config: {
          ...node.config,
          skillParams: { ...currentParams, templateId: '', templateName: '' },
        },
      });
    }
  };

  return (
    <div>
      <select value={selectedId} onChange={handleSelect} style={inputStyle}>
        <option value="">{loadingTpl ? 'Loading templates...' : '(No template — use custom below)'}</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}{t.category ? ` [${t.category}]` : ''}{t.status === 'draft' ? ' (Draft)' : ''}
          </option>
        ))}
      </select>
      {selectedTpl && (
        <div style={{
          marginTop: 6, padding: '8px 10px', background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)',
          borderRadius: 6, fontSize: 11, color: 'var(--v2-success)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>✓ {selectedTpl.name}</div>
          <div style={{ color: 'var(--v2-success)', fontSize: 10 }}>{selectedTpl.description || 'No description'}</div>
          <button
            onClick={() => setPreviewOpen(!previewOpen)}
            style={{
              marginTop: 4, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, color: 'var(--v2-action)', fontWeight: 600, padding: 0,
            }}
          >
            {previewOpen ? '▾ Hide Preview' : '▸ Preview Template'}
          </button>
          {previewOpen && selectedTpl.body && (
            <div style={{
              marginTop: 6, padding: 8, background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)',
              borderRadius: 6, maxHeight: 200, overflowY: 'auto', fontSize: 11,
            }}>
              {param.templateType === 'email' ? (
                <iframe
                  srcDoc={selectedTpl.body}
                  title="Template Preview"
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: 180, border: 'none' }}
                />
              ) : (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 10 }}>
                  {selectedTpl.body.substring(0, 500)}{selectedTpl.body.length > 500 ? '...' : ''}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Schedule-node helper — cron quick-presets, human-readable description,
 * next-execution preview, and output-variable guidance. Previously this
 * was an inline component inside InputTab's IIFE which meant React
 * remounted it on every parent render, dropping `scheduleInfo` state and
 * re-firing every effect. Hoisted to module scope.
 */
const SCHEDULE_CRON_PRESETS = [
  { label: 'Every minute', cron: '* * * * *' },
  { label: 'Every 5 minutes', cron: '*/5 * * * *' },
  { label: 'Every 15 minutes', cron: '*/15 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Every 3 hours', cron: '0 */3 * * *' },
  { label: 'Daily at 9 AM', cron: '0 9 * * *' },
  { label: 'Weekdays at 9 AM', cron: '0 9 * * 1-5' },
  { label: 'Monday at 8:30 AM', cron: '30 8 * * 1' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Twice daily (9 AM & 5 PM)', cron: '0 9,17 * * *' },
  { label: 'Monthly (1st at midnight)', cron: '0 0 1 * *' },
];

function describeCronExpression(cron) {
  if (!cron) return null;
  const p = cron.trim().split(/\s+/);
  if (p.length !== 5) return 'Invalid (expected 5 fields: min hour day month dow)';
  const [min, hr, dom, mon, dow] = p;
  if (cron === '* * * * *') return 'Every minute';
  if (min.startsWith('*/') && hr === '*' && dom === '*' && mon === '*' && dow === '*')
    return `Every ${min.slice(2)} minutes`;
  if (min === '0' && hr.startsWith('*/') && dom === '*' && mon === '*' && dow === '*')
    return `Every ${hr.slice(2)} hours`;
  if (min === '0' && hr === '*' && dom === '*' && mon === '*' && dow === '*')
    return 'Every hour at :00';
  if (dom === '*' && mon === '*' && dow === '*')
    return `Daily at ${hr}:${min.padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow === '1-5')
    return `Weekdays at ${hr}:${min.padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow !== '*')
    return `Days ${dow} at ${hr}:${min.padStart(2, '0')}`;
  if (dom === '1' && mon === '*' && dow === '*')
    return `Monthly (1st) at ${hr}:${min.padStart(2, '0')}`;
  return `${min} ${hr} ${dom} ${mon} ${dow}`;
}

function ScheduleHelper({ node, onUpdate }) {
  const [scheduleInfo, setScheduleInfo] = React.useState(null);
  const [loadingStatus, setLoadingStatus] = React.useState(false);
  const cronVal = node.config?.cron || '';
  const tz = node.config?.timezone || 'UTC';

  // Pull workflowId from URL — supports both legacy /canvas/<id> and
  // /workflows/<id> mounts so a Schedule node renders identically across
  // routes.
  const workflowId = window.location.pathname.split('/canvas/')[1]?.split('/')[0]
    || window.location.pathname.split('/workflows/')[1]?.split('/')[0];

  const fetchStatus = React.useCallback(async () => {
    if (!workflowId) return;
    setLoadingStatus(true);
    try {
      const data = await getScheduleStatus(workflowId);
      setScheduleInfo(data);
    } catch { /* not saved yet */ }
    setLoadingStatus(false);
  }, [workflowId]);

  return (
    <div>
      <CollapsibleSection title="Quick Presets" badge={SCHEDULE_CRON_PRESETS.length} defaultOpen={false} color="var(--v2-warning)" bgColor="var(--v2-danger-soft)" borderColor="var(--v2-danger-soft)">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {SCHEDULE_CRON_PRESETS.map((preset) => (
            <button
              key={preset.cron}
              onClick={() => onUpdate({ config: { ...node.config, cron: preset.cron } })}
              style={{
                padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
                border: cronVal === preset.cron ? '1px solid var(--v2-warning)' : '1px solid var(--v2-border-default)',
                background: cronVal === preset.cron ? 'var(--v2-danger-soft)' : 'var(--v2-bg-surface)',
                color: cronVal === preset.cron ? 'var(--v2-warning)' : 'var(--v2-text-secondary)',
                fontWeight: cronVal === preset.cron ? 600 : 400,
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {cronVal && (
        <div style={{
          margin: '8px 0', padding: '8px 10px', background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)',
          borderRadius: 6, fontSize: 11, color: 'var(--v2-success)',
        }}>
          <span style={{ fontWeight: 600 }}>Schedule:</span>{' '}
          {describeCronExpression(cronVal)} ({tz})
        </div>
      )}

      <CollapsibleSection title="Cron Reference" defaultOpen={false} color="var(--v2-text-muted)" bgColor="var(--v2-bg-elevated)" borderColor="var(--v2-border-default)">
        <div style={{ fontSize: 10, color: 'var(--v2-text-secondary)', lineHeight: 1.6, fontFamily: 'monospace' }}>
          <div style={{ marginBottom: 6, fontFamily: 'inherit', fontSize: 10, color: 'var(--v2-text-muted)' }}>
            Format: <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>minute hour day-of-month month day-of-week</code>
          </div>
          <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['*', 'Any value'],
                ['*/N', 'Every N units'],
                ['1,5,10', 'Specific values'],
                ['1-5', 'Range (inclusive)'],
                ['0-59', 'Minutes (field 1)'],
                ['0-23', 'Hours (field 2)'],
                ['1-31', 'Day of month (field 3)'],
                ['1-12', 'Month (field 4)'],
                ['0-6', 'Day of week (field 5, 0=Sun)'],
              ].map(([val, desc]) => (
                <tr key={val}>
                  <td style={{ padding: '2px 8px 2px 0', color: 'var(--v2-warning)', fontWeight: 600 }}>{val}</td>
                  <td style={{ padding: '2px 0', color: 'var(--v2-text-secondary)', fontFamily: 'inherit' }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      {workflowId && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={fetchStatus}
            disabled={loadingStatus}
            style={{
              padding: '4px 10px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
              border: '1px solid var(--v2-warning)', background: 'var(--v2-bg-surface)', color: 'var(--v2-warning)', fontWeight: 600,
            }}
          >
            {loadingStatus ? 'Loading...' : 'Preview Next Executions'}
          </button>
          {scheduleInfo && scheduleInfo.nextExecutions && (
            <div style={{
              marginTop: 6, padding: '8px 10px', background: 'var(--v2-action-soft)', border: '1px solid var(--v2-action-soft)',
              borderRadius: 6, fontSize: 10,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--v2-action)', marginBottom: 4 }}>
                Next {scheduleInfo.nextExecutions.length} executions:
              </div>
              {scheduleInfo.nextExecutions.map((t, i) => (
                <div key={i} style={{ color: 'var(--v2-action)', padding: '1px 0', fontFamily: 'monospace' }}>
                  {i + 1}. {new Date(t).toLocaleString()}
                </div>
              ))}
              {scheduleInfo.totalRuns != null && (
                <div style={{ marginTop: 4, color: 'var(--v2-text-muted)', fontSize: 10 }}>
                  Total runs so far: <strong>{scheduleInfo.totalRuns}</strong>
                </div>
              )}
              {!scheduleInfo.cronValid && scheduleInfo.cronError && (
                <div style={{ marginTop: 4, color: 'var(--v2-danger)', fontSize: 10 }}>
                  Cron error: {scheduleInfo.cronError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <CollapsibleSection title="Output Variables" badge={3} defaultOpen={true} color="var(--v2-accent-cyan)" bgColor="var(--v2-accent-cyan-soft)" borderColor="var(--v2-accent-cyan-soft)">
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 6 }}>
          When this schedule triggers, downstream nodes receive these variables:
        </div>
        {[
          { name: 'timestamp', type: 'ISO8601', desc: 'When the schedule fired' },
          { name: 'executionNumber', type: 'integer', desc: 'Sequential run count for this workflow' },
          { name: 'nextExecutionTime', type: 'ISO8601', desc: 'When the next execution will occur' },
        ].map((v) => (
          <div key={v.name} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
            borderBottom: '1px solid var(--v2-accent-cyan-soft)',
          }}>
            <button
              onClick={() => navigator.clipboard.writeText(`\${${v.name}}`)}
              style={{
                padding: '2px 6px', fontSize: 9, fontFamily: 'monospace',
                background: 'var(--v2-accent-cyan-soft)', border: '1px solid var(--v2-accent-cyan-soft)', borderRadius: 3,
                cursor: 'pointer', color: 'var(--v2-accent-cyan)', whiteSpace: 'nowrap',
              }}
              title={`Click to copy \${${v.name}}`}
            >
              ${'{' + v.name + '}'}
            </button>
            <span style={{ fontSize: 9, color: 'var(--v2-text-disabled)' }}>({v.type})</span>
            <span style={{ fontSize: 10, color: 'var(--v2-text-muted)' }}>{v.desc}</span>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Schedule Trigger Tips" defaultOpen={false} color="var(--v2-text-muted)" bgColor="var(--v2-bg-elevated)" borderColor="var(--v2-border-default)">
        <div style={{ fontSize: 10, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 6px' }}>
            <strong>How scheduling works:</strong> When you save a workflow with a Schedule trigger,
            the backend automatically detects the cron expression and runs the workflow at each scheduled time.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Workflow status:</strong> The workflow must be <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>active</code> for
            scheduled runs to fire. Set status via the workflow settings.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Deduplication:</strong> The scheduler prevents double-firing within the same minute window.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Input context:</strong> Each scheduled execution receives <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>triggerType</code>,{' '}
            <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>scheduledAt</code>,{' '}
            <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>executionNumber</code>, and{' '}
            <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>cronExpression</code> as input metadata.
          </p>
        </div>
      </CollapsibleSection>
    </div>
  );
}

function InputTab({ node, nodeDef: rawNodeDef, onUpdate, installedConnectors, agents, nodes, connections }) {
  // Live marketplace catalogue from the backend registry — used by the
  // connector_task config schema below to resolve installed connectors to
  // their marketplace templates and to list marketplace-only connectors.
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();

  // Resolve alias nodes: if this node type is an alias (e.g. dpg_scan_inbound → dpg_scan),
  // use the parent node's configSchema so the user gets the full configuration form.
  let nodeDef = rawNodeDef;
  if (nodeDef?.aliasOf && (!nodeDef.configSchema || nodeDef.configSchema.length === 0)) {
    const parentDef = getNodeTypeDef(nodeDef.aliasOf);
    if (parentDef?.configSchema && parentDef.configSchema.length > 0) {
      nodeDef = { ...nodeDef, configSchema: parentDef.configSchema };
    }
  }

  // ── Rules of Hooks: NO early return before hooks. Flag missing schema and
  // render the placeholder AFTER all top-level hooks have run (fixes React #310).
  const hasConfigSchema = !!(nodeDef?.configSchema && nodeDef.configSchema.length > 0);

  // For connector nodes, find the selected skill to show its parameter fields
  const isConnector = !!nodeDef?.connectorMeta;
  const selectedSkillId = node.config?.skill;
  const selectedSkill = isConnector && selectedSkillId
    ? nodeDef.connectorMeta.skills?.find((s) => s.id === selectedSkillId)
    : null;

  // ── Dynamic options for connector_task node type ──────────────────────
  // Match three shapes:
  //   1. Base 'connector_task' nodes — the canonical schema with
  //      connectorId / action / parameters fields
  //   2. Marketplace-templated nodes (connector_<vendor>_<v>) — these
  //      carry a connectorMeta block from buildConnectorNode and use the
  //      same field names as the base now (post-#94 unification)
  //   3. Synthesized fallback connector nodes from getNodeTypeDef when
  //      the marketplace catalogue hasn't loaded — they re-use the base
  //      connector_task schema, so anything starting with 'connector_'
  //      should also walk this code path.
  // Without this widening the dropdown-population logic at lines 8809-
  // 8830 and the rich per-action panel at line 9246 don't fire for
  // marketplace nodes, which is exactly the regression we're fixing.
  const isConnectorTask = node?.type === 'connector_task'
    || !!nodeDef?.connectorMeta
    || (typeof node?.type === 'string' && node.type.startsWith('connector_'));

  // RC-8 (task #188) — LLM_TASK is a connector-driven task at the
  // config level. It exposes a `connectorId` field whose dropdown is
  // populated from the tenant's installed connectors of LLM type
  // (anthropic, openai, azure_openai, bedrock, gemini, etc.). Backend
  // RC-8 auto-resolves an LLM connector via llmProvider as a legacy
  // grace path; this picker makes the choice explicit going forward.
  //
  // RC-11 (task #191) — match against the marketplace manifest's
  // `category: "llm-provider"` rather than a hardcoded type allowlist.
  // The installedConnectors prop is a {uuid → entry} map, so we have
  // to walk Object.values to enumerate. Matching the manifest by id /
  // name / vendor catches every shape the backend emits (lowercased
  // manifest id, vendor display name, etc.) so the dropdown reliably
  // surfaces any installed LLM connector instead of silently dropping
  // it when the type string doesn't exactly match an allowlist entry.
  const isLlmTask = node?.type === 'llm_task';
  /** Build (and remember) the set of marketplace manifest ids that are
   *  LLM providers. Source of truth is the manifest's `category` field
   *  ("llm-provider" in the registry JSON). */
  const llmProviderManifestIds = React.useMemo(() => {
    const ids = new Set();
    (marketplaceConnectors || []).forEach((mc) => {
      const cat = (mc?.category || '').toLowerCase().replace(/[_\s]/g, '-');
      if (cat === 'llm-provider' || cat === 'llm') {
        if (mc.id) ids.add(String(mc.id).toLowerCase());
      }
    });
    return ids;
  }, [marketplaceConnectors]);

  const llmConnectorOptions = React.useMemo(() => {
    if (!isLlmTask) return [];
    // installedConnectors is { uuid → entry } (set by CanvasPageV3 effect).
    const list = Array.isArray(installedConnectors)
      ? installedConnectors
      : Object.values(installedConnectors || {});
    return list
      .filter((c) => {
        if (!c) return false;
        // Match 1: connector.type matches a marketplace LLM-provider manifest id.
        const t = String(c.type || '').toLowerCase().trim();
        if (t && llmProviderManifestIds.has(t)) return true;
        // Match 2: connector's marketplace entry's category is llm-provider.
        const mc = (marketplaceConnectors || []).find((m) =>
          (m.id && m.id === c.type)
          || (m.name && c.name && m.name.toLowerCase() === c.name.toLowerCase())
        );
        if (mc) {
          const cat = String(mc.category || '').toLowerCase().replace(/[_\s]/g, '-');
          if (cat === 'llm-provider' || cat === 'llm') return true;
        }
        return false;
      })
      .map((c) => ({
        value: c.id,
        label: (c.name || c.type || c.id) + (c.status ? ` · ${c.status}` : ''),
      }));
  }, [isLlmTask, installedConnectors, llmProviderManifestIds, marketplaceConnectors]);

  // RC-11 — Dynamic Model dropdown driven by the picked connector's
  // manifest. When `config.connectorId` resolves to a marketplace entry
  // whose manifest declares `models: [{id, displayName, ...}]`, we
  // render those exact models. Otherwise the soarNodes.js hardcoded
  // fallback list keeps the field usable until the connector loads.
  const llmModelOptionsForPicked = React.useMemo(() => {
    if (!isLlmTask) return null;
    const cid = node?.config?.connectorId;
    if (!cid) return null;
    const installed = Array.isArray(installedConnectors)
      ? installedConnectors
      : Object.values(installedConnectors || {});
    const picked = installed.find((c) => c?.id === cid);
    if (!picked) return null;
    const mc = (marketplaceConnectors || []).find((m) =>
      (m.id && m.id === picked.type)
      || (m.name && picked.name && m.name.toLowerCase() === picked.name.toLowerCase())
    );
    if (!mc || !Array.isArray(mc.models) || mc.models.length === 0) return null;
    return mc.models.map((m) => ({
      value: m.id,
      label: m.displayName ? `${m.displayName}${m.tier ? ` · ${m.tier}` : ''}` : m.id,
    }));
  }, [isLlmTask, node?.config?.connectorId, installedConnectors, marketplaceConnectors]);

  // Build connector options from installed connectors + marketplace metadata
  // Uses the same matching logic as ConnectorsPage (name or URL prefix)
  const connectorOptions = React.useMemo(() => {
    if (!isConnectorTask) return [];

    // Helper: match an installed connector to a marketplace definition
    // IMPORTANT: uses a priority cascade — name matches first (definitive),
    // then URL matches as fallback. This prevents cross-matching when multiple
    // marketplace connectors share the same baseUrl (e.g., Microsoft Graph API).
    const findMarketplaceRef = (conn) => {
      const cName = (conn.name || '').toLowerCase().trim();
      const cType = (conn.type || '').toLowerCase().trim();
      // Pass 1: high-confidence matches (ID or name)
      const nameMatch = marketplaceConnectors.find((mc) => {
        if (mc.id === conn.type || mc.id === conn.connectorType || mc.id === conn.marketplaceId) return true;
        if (mc.name === conn.name) return true;
        if (cName && mc.name.toLowerCase().trim() === cName) return true;
        return false;
      });
      if (nameMatch) return nameMatch;
      // Pass 2: fuzzy matches (type-in-name, URL prefix, description)
      return marketplaceConnectors.find((mc) => {
        if (cType && mc.name.toLowerCase().includes(cType)) return true;
        if (cType && mc.id.toLowerCase().includes(cType)) return true;
        if (conn.url && mc.baseUrl && mc.baseUrl !== '') {
          const cleanBase = mc.baseUrl.replace(/\{[^}]+\}/g, '');
          if (cleanBase.length > 5 && conn.url.startsWith(cleanBase)) return true;
        }
        if (conn.description && mc.description && conn.description === mc.description) return true;
        return false;
      }) || null;
    };

    // Build installed list from backend connectors
    const installed = [];
    const matchedMkIds = new Set();
    if (installedConnectors && Object.keys(installedConnectors).length > 0) {
      Object.values(installedConnectors).forEach((c) => {
        const mktDef = findMarketplaceRef(c);
        const mkId = mktDef?.id || c.type || c.id;
        matchedMkIds.add(mkId);
        installed.push({
          value: c.id,       // backend connector UUID
          label: `${mktDef?.icon || '🔌'} ${c.name || mktDef?.name || c.type}`,
          mkId,
          skills: mktDef?.skills || [],
          isInstalled: true,
          status: c.status,
        });
      });
    }

    // Remaining marketplace connectors that have skills but are NOT installed
    const marketplace = marketplaceConnectors
      .filter((m) => m.skills && m.skills.length > 0 && !matchedMkIds.has(m.id))
      .map((m) => ({
        value: m.id,
        label: `${m.icon || '🔌'} ${m.name}`,
        mkId: m.id,
        skills: m.skills || [],
        isInstalled: false,
        status: 'not installed',
      }));

    // Installed first, then marketplace (grayed out)
    return [
      ...installed.sort((a, b) => a.label.localeCompare(b.label)),
      ...marketplace.sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [isConnectorTask, installedConnectors, marketplaceConnectors]);

  // Build action options based on selected connector's skills
  const selectedConnectorId = node.config?.connectorId;
  const actionOptions = React.useMemo(() => {
    if (!isConnectorTask || !selectedConnectorId) return [];
    const chosen = connectorOptions.find((c) => c.value === selectedConnectorId);
    if (!chosen || !chosen.skills.length) return [];
    return chosen.skills.map((s) => ({
      value: s.id,
      label: `${s.method ? `[${s.method}] ` : ''}${s.name}`,
      skill: s,
    }));
  }, [isConnectorTask, selectedConnectorId, connectorOptions]);

  // Find selected action's skill definition for parameter rendering.
  // Backwards-compat: marketplace-templated nodes used to write the
  // chosen operation to `node.config.skill` (pre-#94 unification). Read
  // either field so workflows persisted before this fix still resolve.
  const selectedActionId = node.config?.action || node.config?.skill;
  const selectedAction = actionOptions.find((a) => a.value === selectedActionId)?.skill || null;

  // ── Dynamic options for all agent-related node types ──────────────────
  // These node types all have an 'agentId' field and need the agent dropdown populated:
  //   agent_task  — invoke a deployed agent (tasks category)
  //   skill_task  — execute a specific skill from an agent (tasks category)
  //   agent_chat  — interactive multi-turn conversation with agent (agents category)
  //   skill_eval  — run evaluation suite on a skill (agents category)
  const AGENT_NODE_TYPES = new Set(['agent_task', 'skill_task', 'agent_chat', 'skill_eval']);
  // Types that have a skill selection field (skillName or selectedSkill)
  const SKILL_NODE_TYPES = new Set(['skill_task', 'skill_eval', 'agent_chat']);

  const isAgentNode = AGENT_NODE_TYPES.has(node?.type);
  const isSkillNode = SKILL_NODE_TYPES.has(node?.type);
  const isAgentChat = node?.type === 'agent_chat';
  const isSkillEval = node?.type === 'skill_eval';
  // Keep legacy aliases for backward compatibility in the rendering logic
  const isAgentTask = isAgentNode;
  const isSkillTask = isSkillNode;

  // Map non-emoji icon names (Lucide / custom) to emoji equivalents for <option> text
  const iconToEmoji = (icon) => {
    if (!icon) return '🤖';
    // Already an emoji — starts with a high Unicode codepoint (emoji range)
    const cp = icon.codePointAt(0);
    if (cp > 0x1F00) return icon;  // Likely emoji / symbol block
    // Known Lucide / custom icon name → emoji mapping
    const map = {
      'shield': '🛡️', 'shield-check': '🛡️', 'shield-alert': '🛡️',
      'lock': '🔒', 'lock-keyhole': '🔒',
      'user-check': '👤', 'user': '👤', 'user-cog': '⚙️',
      'brain': '🧠', 'brain-circuit': '🧠',
      'bot': '🤖', 'bot-message-square': '🤖',
      'scale': '⚖️', 'scales': '⚖️',
      'search': '🔍', 'scan': '🔍', 'scan-eye': '👁️',
      'database': '🗄️', 'server': '🖥️',
      'alert-triangle': '⚠️', 'alert-circle': '🔴',
      'check-circle': '✅', 'check': '✅',
      'file-text': '📄', 'file': '📄', 'folder': '📁',
      'globe': '🌐', 'network': '🌐',
      'zap': '⚡', 'activity': '📊',
      'key': '🔑', 'fingerprint': '🔐',
      'eye': '👁️', 'eye-off': '🚫',
      'message-square': '💬', 'mail': '📧',
      'clock': '⏰', 'timer': '⏱️',
      'sophie-icon': '🧠', 'sophie': '🧠',
    };
    return map[icon.toLowerCase()] || '🤖';
  };

  // Build agent options from loaded agents — grouped by Agentlets (native) vs External
  const agentOptions = React.useMemo(() => {
    if (!isAgentNode) return [];
    if (!agents || !Array.isArray(agents) || agents.length === 0) return [];
    return agents
      .map((a) => ({
        value: a.id,
        label: `${iconToEmoji(a.icon)} ${a.name}`,
        status: a.status,
        published: a.published,
        isExternal: !!a.isExternal || !!a.externalRegistrationId,
        skills: a.skills || [],
        evals: a.evals || [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isAgentNode, agents]);

  // Build skill options based on selected agent's skills — with published/draft grouping
  const selectedAgentId = node.config?.agentId;
  const selectedAgentData = React.useMemo(() => {
    return agentOptions.find((a) => a.value === selectedAgentId) || null;
  }, [agentOptions, selectedAgentId]);

  const skillOptions = React.useMemo(() => {
    if (!isSkillNode || !selectedAgentId || !selectedAgentData) return [];
    if (!selectedAgentData.skills.length) return [];
    return selectedAgentData.skills.map((s) => ({
      value: s.id || s.name,
      label: s.name || s.id,
      skill: s,
      published: s.published !== false, // default to published if not specified
      endpoint: s.endpoint || '',
      method: s.method || '',
    }));
  }, [isSkillNode, selectedAgentId, selectedAgentData]);

  // Build eval suite options based on selected agent's eval suites
  const evalSuiteOptions = React.useMemo(() => {
    if (!isSkillEval || !selectedAgentId || !selectedAgentData) return [];
    // Eval suites may come from agent.evals or from the skills themselves
    const suites = selectedAgentData.evals || [];
    if (suites.length > 0) {
      return suites.map((e) => ({
        value: e.id || e.name,
        label: e.name || e.id,
        skillName: e.skillName || e.skillId || '',
      }));
    }
    // Fallback: build eval options from skills that have evals
    const skillEvals = [];
    (selectedAgentData.skills || []).forEach((s) => {
      if (s.evals && Array.isArray(s.evals)) {
        s.evals.forEach((e) => {
          skillEvals.push({
            value: e.id || e.name,
            label: `${s.name} — ${e.name || e.id}`,
            skillName: s.name,
          });
        });
      }
    });
    return skillEvals;
  }, [isSkillEval, selectedAgentId, selectedAgentData]);

  // ── Upstream variables for agent_prompt_builder ────────────────────────
  // Collect output variables from all upstream nodes for the variable inserter
  const upstreamVariables = React.useMemo(() => {
    if (!isAgentChat) return [];
    const vars = [];
    // Find upstream nodes via connections
    const upstreamNodeIds = connections
      .filter((c) => c.to === node.id)
      .map((c) => c.from);

    upstreamNodeIds.forEach((upId) => {
      const upNode = nodes.find((n) => n.id === upId);
      if (!upNode) return;
      const upDef = getNodeTypeDef(upNode.type);
      const prefix = upNode.id;
      const label = upNode.label || upNode.type;

      // Add standard output variables
      vars.push({ variable: `${prefix}_result`, label: `${label} → result`, nodeLabel: label });
      vars.push({ variable: `${prefix}_success`, label: `${label} → success`, nodeLabel: label });

      // Add output schema fields if available
      if (upDef?.outputSchema) {
        Object.keys(upDef.outputSchema).forEach((key) => {
          if (key !== 'result' && key !== 'success') {
            vars.push({ variable: `${prefix}_${key}`, label: `${label} → ${key}`, nodeLabel: label });
          }
        });
      }

      // For connector tasks, add common auto-flattened fields
      if (upNode.type === 'connector_task') {
        ['number', 'severity', 'priority', 'short_description', 'sys_id', 'state', 'category']
          .forEach((f) => vars.push({ variable: f, label: `${label} → ${f} (auto-flattened)`, nodeLabel: label }));
      }
    });
    return vars;
  }, [isAgentChat, node.id, connections, nodes]);

  // Safe to early-return now — every hook above has been called on this render.
  if (!hasConfigSchema) {
    return (
      <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
        No configuration available
      </div>
    );
  }

  return (
    <div style={{ overflow: 'hidden' }}>
      <CollapsibleSection title="Configuration" badge={nodeDef.configSchema.length} defaultOpen={true}>
      {nodeDef.configSchema.map((field) => {
        // Skip the read-only connectorId field for cleaner UI
        if (field.readOnly && field.field === 'connectorId') {
          return (
            <div key={field.field} style={{ marginBottom: 8 }}>
              <label style={labelStyle}>{field.label}</label>
              <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-disabled)', fontSize: 10, fontFamily: 'monospace' }}>
                {field.defaultValue || node.config?.[field.field] || ''}
              </div>
            </div>
          );
        }

        return (
          <div key={field.field} style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              {field.label}
              {field.required && <span style={{ color: 'var(--v2-danger)' }}>*</span>}
            </label>

            {field.type === 'text' && (
              <input
                type="text"
                value={node.config?.[field.field] || ''}
                onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value } })}
                style={inputStyle}
                placeholder={field.placeholder}
              />
            )}

            {field.type === 'textarea' && (
              <textarea
                value={node.config?.[field.field] || ''}
                onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value } })}
                style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }}
                placeholder={field.placeholder}
              />
            )}

            {/* ── Expression: monospace textarea for condition/switch expressions ── */}
            {field.type === 'expression' && (
              <div>
                <textarea
                  value={node.config?.[field.field] || ''}
                  onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value } })}
                  style={{
                    ...inputStyle,
                    minHeight: 60,
                    fontFamily: 'ui-monospace, "SF Mono", "Cascadia Mono", Menlo, monospace',
                    fontSize: 12,
                    resize: 'vertical',
                    background: 'var(--v2-bg-elevated)',
                    lineHeight: 1.5,
                  }}
                  placeholder={field.placeholder || 'e.g. ${success} == true'}
                />
                <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)', marginTop: 4 }}>
                  Use <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>${'{variable}'}</code> to
                  reference upstream outputs. Available: <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>success</code>,{' '}
                  <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>sys_id</code>,{' '}
                  <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>number</code>,{' '}
                  <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>nodeId_success</code>
                </div>
              </div>
            )}

            {/* ── Agent Prompt Builder: structured prompt with variable insertion ── */}
            {field.type === 'agent_prompt_builder' && (() => {
              const promptValue = node.config?.[field.field] || '';
              const textareaRef = React.createRef();

              const insertVariable = (varName) => {
                const el = textareaRef.current;
                if (!el) return;
                const start = el.selectionStart || promptValue.length;
                const end = el.selectionEnd || promptValue.length;
                const before = promptValue.slice(0, start);
                const after = promptValue.slice(end);
                const newVal = `${before}\${${varName}}${after}`;
                onUpdate({ config: { ...node.config, [field.field]: newVal } });
                // Move cursor after the inserted variable
                requestAnimationFrame(() => {
                  if (el) {
                    const pos = start + varName.length + 3; // ${ + varName + }
                    el.selectionStart = el.selectionEnd = pos;
                    el.focus();
                  }
                });
              };

              // Group upstream variables by source node
              const varsByNode = {};
              upstreamVariables.forEach((v) => {
                const key = v.nodeLabel || 'Other';
                if (!varsByNode[key]) varsByNode[key] = [];
                varsByNode[key].push(v);
              });

              // Selected skill info for context
              const selectedSkillId = node.config?.selectedSkill;
              const selectedSkillDef = selectedSkillId
                ? skillOptions.find((s) => s.value === selectedSkillId)
                : null;

              return (
                <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 8, overflow: 'hidden' }}>
                  {/* Skill context hint */}
                  {selectedSkillDef && (
                    <div style={{
                      padding: '6px 10px', background: 'var(--v2-action-soft)', borderBottom: '1px solid var(--v2-warning)',
                      fontSize: 11, color: 'var(--v2-success)', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{ fontWeight: 600 }}>Skill:</span> {selectedSkillDef.label}
                      {selectedSkillDef.method && <span style={{ background: 'var(--v2-warning)', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>{selectedSkillDef.method}</span>}
                      {selectedSkillDef.endpoint && <span style={{ color: 'var(--v2-success)' }}>{selectedSkillDef.endpoint}</span>}
                    </div>
                  )}

                  {/* Variable insertion toolbar */}
                  {upstreamVariables.length > 0 && (
                    <div style={{
                      padding: '6px 10px', background: 'var(--v2-bg-elevated)', borderBottom: '1px solid var(--v2-border-default)',
                      fontSize: 11,
                    }}>
                      <div style={{ fontWeight: 600, color: 'var(--v2-text-secondary)', marginBottom: 4 }}>
                        Insert upstream data:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Object.entries(varsByNode).map(([nodeLabel, vars]) => (
                          <React.Fragment key={nodeLabel}>
                            {vars.slice(0, 5).map((v) => (
                              <button
                                key={v.variable}
                                onClick={() => insertVariable(v.variable)}
                                title={v.label}
                                style={{
                                  fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                  border: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-surface)', cursor: 'pointer',
                                  color: 'var(--v2-text-secondary)', fontFamily: 'monospace',
                                }}
                              >
                                {'${' + v.variable + '}'}
                              </button>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prompt textarea */}
                  <textarea
                    ref={textareaRef}
                    value={promptValue}
                    onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value } })}
                    style={{
                      width: '100%', minHeight: 80, padding: '8px 10px', border: 'none',
                      fontFamily: 'inherit', fontSize: 12, resize: 'vertical',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                    placeholder={field.placeholder || 'Enter prompt for the agent...'}
                  />

                  {/* Output forwarding hint */}
                  <div style={{
                    padding: '6px 10px', background: 'var(--v2-action-soft)', borderTop: '1px solid var(--v2-action-soft)',
                    fontSize: 10, color: 'var(--v2-warning)',
                  }}>
                    Output available downstream as: <code style={{ background: 'var(--v2-action-soft)', padding: '1px 4px', borderRadius: 2 }}>
                      {'${' + node.id + '_result}'}</code>,{' '}
                    <code style={{ background: 'var(--v2-action-soft)', padding: '1px 4px', borderRadius: 2 }}>
                      {'${' + node.id + '_finalResponse}'}</code>
                    {node.config?.outputVariable && node.config.outputVariable !== 'agent_response' && (
                      <>, <code style={{ background: 'var(--v2-action-soft)', padding: '1px 4px', borderRadius: 2 }}>
                        {'${' + node.config.outputVariable + '}'}</code></>
                    )}
                  </div>
                </div>
              );
            })()}

            {field.type === 'number' && (
              <input
                type="number"
                value={node.config?.[field.field] !== undefined ? node.config[field.field] : ''}
                onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value ? parseFloat(e.target.value) : null } })}
                style={inputStyle}
                placeholder={field.placeholder}
              />
            )}

            {field.type === 'boolean' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={node.config?.[field.field] || false}
                  onChange={(e) => onUpdate({ config: { ...node.config, [field.field]: e.target.checked } })}
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
                <label style={{ ...labelStyle, margin: 0, marginBottom: 0 }}>Enable</label>
              </div>
            )}

            {/* ── Template Select: fetches templates from Template Center.
                  Component is now top-level (not inline) so React doesn't
                  remount it every InputTab render — fixes #300 and the
                  state-loss-on-keystroke bug. */}
            {field.type === 'template_select' && (
              <TemplateSelectField field={field} node={node} onUpdate={onUpdate} />
            )}

            {field.type === 'select' && (() => {
              // Dynamic options for connector_task fields
              let effectiveOptions = field.options || [];
              let onChangeHandler = (e) => onUpdate({ config: { ...node.config, [field.field]: e.target.value } });

              if (isConnectorTask && field.field === 'connectorId') {
                effectiveOptions = connectorOptions;
                // When connector changes, reset action + parameters
                onChangeHandler = (e) => onUpdate({
                  config: { ...node.config, connectorId: e.target.value, action: '', parameters: null, skillParams: {} },
                });
              } else if (isLlmTask && field.field === 'connectorId') {
                // RC-8 (task #188) — LLM_TASK connector picker, scoped to
                // tenant's installed LLM connectors (anthropic / openai /
                // azure_openai / bedrock / gemini / etc.). Picking one
                // mirrors the chosen connector's provider into
                // config.llmProvider for downstream observability + the
                // backend auto-resolve grace path. Also resets the Model
                // field so the operator picks a model that the new
                // connector actually supports (avoids carrying e.g. a
                // gpt-4o pick across an Anthropic→Anthropic re-bind that
                // happens after swapping installed connectors).
                effectiveOptions = llmConnectorOptions;
                onChangeHandler = (e) => {
                  const installed = Array.isArray(installedConnectors)
                    ? installedConnectors
                    : Object.values(installedConnectors || {});
                  const picked = installed.find((c) => c?.id === e.target.value);
                  onUpdate({
                    config: {
                      ...node.config,
                      connectorId: e.target.value,
                      llmProvider: picked?.type || node.config?.llmProvider || '',
                      // Clear model — operator picks a fresh one from the
                      // picked connector's manifest-declared model list.
                      model: '',
                    },
                  });
                };
              } else if (isLlmTask && field.field === 'model' && llmModelOptionsForPicked) {
                // RC-11 (task #191) — Model dropdown driven by the picked
                // connector's manifest. Falls through to the hardcoded
                // field.options when no connector is picked yet (keeps
                // the UI usable until the operator selects one).
                effectiveOptions = llmModelOptionsForPicked;
              } else if (isConnectorTask && field.field === 'action') {
                effectiveOptions = actionOptions;
                // When action changes, reset parameters AND store the skill's endpoint + method in config
                onChangeHandler = (e) => {
                  const selectedOpt = actionOptions.find((a) => a.value === e.target.value);
                  const skill = selectedOpt?.skill;
                  onUpdate({
                    config: {
                      ...node.config,
                      action: e.target.value,
                      endpoint: skill?.endpoint || '',
                      method: skill?.method || '',
                      skillParams: {},
                    },
                  });
                };
              }

              // Dynamic options for all agent node types' agentId field
              if (isAgentNode && field.field === 'agentId') {
                effectiveOptions = agentOptions;
                if (isSkillNode || isAgentChat) {
                  // When agent changes, reset skill/eval selections
                  onChangeHandler = (e) => onUpdate({
                    config: { ...node.config, agentId: e.target.value, skillName: '', selectedSkill: '', evalSuite: '' },
                  });
                }
              }

              // Dynamic options for skill/eval nodes' skillName field
              if (isSkillNode && (field.field === 'skillName' || field.field === 'selectedSkill')) {
                effectiveOptions = skillOptions;
              }

              // Dynamic options for skill_eval evalSuite field
              if (isSkillEval && field.field === 'evalSuite') {
                effectiveOptions = evalSuiteOptions;
              }

              // ── Grouped skill dropdown (Published skills at top, then Draft) ──
              if (isSkillNode && (field.field === 'skillName' || field.field === 'selectedSkill') && skillOptions.length > 0) {
                const publishedSkills = skillOptions.filter((s) => s.published);
                const draftSkills = skillOptions.filter((s) => !s.published);
                const isOptional = field.field === 'selectedSkill'; // agent_chat skill is optional
                return (
                  <select
                    value={node.config?.[field.field] || ''}
                    onChange={onChangeHandler}
                    style={inputStyle}
                  >
                    <option value="">{isOptional ? '(Auto-select — agent chooses best skill)' : 'Select a skill...'}</option>
                    {publishedSkills.length > 0 && (
                      <optgroup label="── Published Skills ──">
                        {publishedSkills.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}{opt.method ? ` [${opt.method}]` : ''}{opt.endpoint ? ` ${opt.endpoint}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {draftSkills.length > 0 && (
                      <optgroup label="── Draft / Unpublished Skills ──">
                        {draftSkills.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}{opt.method ? ` [${opt.method}]` : ''}{opt.endpoint ? ` ${opt.endpoint}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              }

              // ── Grouped eval suite dropdown ──
              if (isSkillEval && field.field === 'evalSuite' && evalSuiteOptions.length > 0) {
                return (
                  <select
                    value={node.config?.[field.field] || ''}
                    onChange={onChangeHandler}
                    style={inputStyle}
                  >
                    <option value="">Select evaluation suite...</option>
                    {evalSuiteOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}{opt.skillName ? ` (${opt.skillName})` : ''}
                      </option>
                    ))}
                  </select>
                );
              }

              // For connector_task connectorId: use grouped select with optgroups
              if (isConnectorTask && field.field === 'connectorId') {
                const installedOpts = effectiveOptions.filter((o) => o.isInstalled);
                const notInstalledOpts = effectiveOptions.filter((o) => !o.isInstalled);
                return (
                  <select
                    value={node.config?.[field.field] || ''}
                    onChange={onChangeHandler}
                    style={inputStyle}
                  >
                    <option value="">Select a connector...</option>
                    {installedOpts.length > 0 && (
                      <optgroup label="── Installed Connectors ──">
                        {installedOpts.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {notInstalledOpts.length > 0 && (
                      <optgroup label="── Not Installed (configure in Connectors page) ──">
                        {notInstalledOpts.map((opt) => (
                          <option key={opt.value} value={opt.value} disabled style={{ color: 'var(--v2-text-disabled)' }}>
                            {opt.label}  ⛔
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              }

              // For agent_task / skill_task agentId: grouped select — Agentlets vs External published agents
              if ((isAgentTask || isSkillTask) && field.field === 'agentId' && agentOptions.length > 0) {
                const agentletsAgents = agentOptions.filter((a) => !a.isExternal && a.published);
                const externalAgents = agentOptions.filter((a) => a.isExternal);
                return (
                  <select
                    value={node.config?.[field.field] || ''}
                    onChange={onChangeHandler}
                    style={inputStyle}
                  >
                    <option value="">Select an agent...</option>
                    {agentletsAgents.length > 0 && (
                      <optgroup label="── Agentlets Agents ──">
                        {agentletsAgents.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} ({opt.skills.length} skill{opt.skills.length !== 1 ? 's' : ''})
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {externalAgents.length > 0 && (
                      <optgroup label="── External Agents ──">
                        {externalAgents.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} ({opt.skills.length} skill{opt.skills.length !== 1 ? 's' : ''}){!opt.published ? ' [draft]' : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              }

              return (
                <select
                  value={node.config?.[field.field] || ''}
                  onChange={onChangeHandler}
                  style={inputStyle}
                >
                  <option value="">Select...</option>
                  {effectiveOptions && Array.isArray(effectiveOptions) && effectiveOptions.map((opt) => {
                    const optValue = typeof opt === 'string' ? opt : (opt.value || opt);
                    const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value || opt);
                    return (
                      <option key={optValue} value={optValue}>
                        {optLabel}
                      </option>
                    );
                  })}
                </select>
              );
            })()}

            {field.type === 'json' && (
              <JsonFieldEditor
                value={node.config?.[field.field]}
                onChange={(val) => onUpdate({ config: { ...node.config, [field.field]: val } })}
                placeholder={field.placeholder}
              />
            )}

            {field.type === 'assignments' && (() => {
              const assignments = Array.isArray(node.config?.assignments) ? node.config.assignments : [];
              const updateAssignments = (newAssignments) => {
                onUpdate({ config: { ...node.config, assignments: newAssignments } });
              };
              return (
                <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 6, padding: 8, background: 'var(--v2-bg-elevated)' }}>
                  {assignments.length === 0 && (
                    <div style={{ color: 'var(--v2-text-disabled)', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>
                      No assignments configured. Add one to extract data from upstream nodes.
                    </div>
                  )}
                  {assignments.map((assignment, idx) => (
                    <div key={idx} style={{ marginBottom: 8, padding: 8, background: 'var(--v2-bg-surface)', borderRadius: 4, border: '1px solid var(--v2-border-default)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v2-text-muted)' }}>Assignment {idx + 1}</span>
                        <button
                          onClick={() => updateAssignments(assignments.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', color: 'var(--v2-danger)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                          title="Remove"
                        >&times;</button>
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontSize: 10, color: 'var(--v2-text-muted)', display: 'block', marginBottom: 2 }}>Variable Name</label>
                        <input
                          type="text"
                          value={assignment.name || ''}
                          onChange={(e) => {
                            const updated = [...assignments];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            updateAssignments(updated);
                          }}
                          style={{ ...inputStyle, fontSize: 11, fontFamily: 'monospace' }}
                          placeholder="ticket_number"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: 'var(--v2-text-muted)', display: 'block', marginBottom: 2 }}>Expression</label>
                        <input
                          type="text"
                          value={assignment.expression || ''}
                          onChange={(e) => {
                            const updated = [...assignments];
                            updated[idx] = { ...updated[idx], expression: e.target.value };
                            updateAssignments(updated);
                          }}
                          style={{ ...inputStyle, fontSize: 11, fontFamily: 'monospace' }}
                          placeholder="${nodeId_response.result.number}"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => updateAssignments([...assignments, { name: '', expression: '' }])}
                    style={{
                      width: '100%', padding: '6px 0', background: 'var(--v2-accent-cyan-soft)', border: '1px dashed var(--v2-action-soft)',
                      borderRadius: 4, color: 'var(--v2-action)', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    + Add Assignment
                  </button>
                </div>
              );
            })()}

            {/* Connector status badge for connector_task */}
            {isConnectorTask && field.field === 'connectorId' && selectedConnectorId && (() => {
              const chosen = connectorOptions.find((c) => c.value === selectedConnectorId);
              if (!chosen) return null;
              const statusColor = chosen.isInstalled
                ? (chosen.status === 'active' ? 'var(--v2-success)' : 'var(--v2-warning)')
                : 'var(--v2-text-disabled)';
              const statusLabel = chosen.isInstalled
                ? (chosen.status === 'active' ? 'Connected' : chosen.status)
                : 'Not Installed — install from Connectors page';
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
                  <span style={{ fontSize: 10, color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
                  <span style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>— {chosen.skills.length} action{chosen.skills.length !== 1 ? 's' : ''} available</span>
                </div>
              );
            })()}
            {field.helpText && (
              <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 4 }}>
                {field.helpText}
              </div>
            )}
          </div>
        );
      })}
      </CollapsibleSection>

      {/* Dynamic skill parameters — shown when a connector skill is selected */}
      {isConnector && selectedSkill && (
        <CollapsibleSection
          title={`${selectedSkill.method} ${selectedSkill.name}`}
          badge={selectedSkill.params?.length || 0}
          defaultOpen={true}
          color="var(--v2-accent-cyan)"
          bgColor="var(--v2-accent-cyan-soft)"
          borderColor="var(--v2-accent-cyan-soft)"
        >
          {selectedSkill.description && (
            <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 8 }}>{selectedSkill.description}</div>
          )}
          <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--v2-text-muted)', marginBottom: 10, background: 'var(--v2-accent-cyan-soft)', padding: '3px 6px', borderRadius: 3, wordBreak: 'break-all' }}>
            {selectedSkill.endpoint}
          </div>

          {/* Render each skill param as a form field */}
          {selectedSkill.params?.map((param) => (
            <div key={param.name} style={{ marginBottom: 10 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>
                {param.name}
                {param.required && <span style={{ color: 'var(--v2-danger)' }}> *</span>}
                <span style={{ color: 'var(--v2-text-disabled)', fontWeight: 400, marginLeft: 4 }}>({param.type === 'select' ? 'choice' : param.type})</span>
              </label>
              {param.type === 'select' && param.options ? (
                <select
                  value={node.config?.skillParams?.[param.name] || ''}
                  onChange={(e) => onUpdate({
                    config: {
                      ...node.config,
                      skillParams: { ...(node.config?.skillParams || {}), [param.name]: e.target.value },
                    },
                  })}
                  style={inputStyle}
                >
                  <option value="">{param.required ? 'Select...' : '— None (auto) —'}</option>
                  {param.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={param.type === 'number' ? 'number' : 'text'}
                  value={node.config?.skillParams?.[param.name] || ''}
                  onChange={(e) => onUpdate({
                    config: {
                      ...node.config,
                      skillParams: { ...(node.config?.skillParams || {}), [param.name]: e.target.value },
                    },
                  })}
                  style={inputStyle}
                  placeholder={param.helpText || param.example || `Enter ${param.name}`}
                />
              )}
              {param.helpText && (
                <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2 }}>{param.helpText}</div>
              )}
            </div>
          ))}

          {/* Guardrails */}
          {selectedSkill.guardrails?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <label style={{ ...labelStyle, fontSize: 10, color: 'var(--v2-warning)' }}>Guardrails</label>
              {selectedSkill.guardrails.map((g, i) => (
                <div key={i} style={{ fontSize: 10, color: 'var(--v2-warning)', padding: '2px 0', display: 'flex', gap: 4 }}>
                  <Shield size={10} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{g}</span>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Available Upstream Variables — shown when node has upstream connections */}
      {(() => {
        // Compute upstream variables for this node
        const upIds = (connections || []).filter((c) => c.to === node.id).map((c) => c.from);
        // Walk further upstream (grandparent nodes) so we get the full chain
        const allUpIds = new Set(upIds);
        const queue = [...upIds];
        while (queue.length > 0) {
          const cur = queue.shift();
          (connections || []).filter((c) => c.to === cur).forEach((c) => {
            if (!allUpIds.has(c.from)) { allUpIds.add(c.from); queue.push(c.from); }
          });
        }
        const upNodes = (nodes || []).filter((n) => allUpIds.has(n.id));
        if (upNodes.length === 0) return null;
        return (
          <CollapsibleSection
            title="Available Variables"
            badge={upNodes.length}
            defaultOpen={true}
            color="var(--v2-text-secondary)"
            bgColor="var(--v2-bg-elevated)"
            borderColor="var(--v2-border-default)"
          >
            <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 6 }}>
              Click a variable to copy it. Paste into any parameter field below.
            </div>
            {upNodes.map((un) => {
              const outVar = un.config?.outputVariable || un.id;
              const isConn = un.type === 'connector_task' || un.type === 'connector';
              const isCond = un.type === 'condition';
              const vars = [
                { label: outVar, ref: `\${${outVar}}` },
                { label: `${un.id}_result`, ref: `\${${un.id}_result}` },
              ];
              if (isConn) {
                vars.push(
                  { label: `${un.id}_response`, ref: `\${${un.id}_response}` },
                  { label: `${un.id}_response.result`, ref: `\${${un.id}_response.result}` },
                  { label: `${un.id}_success`, ref: `\${${un.id}_success}` },
                );
              }
              if (isCond) {
                vars.push({ label: `${un.id}_result`, ref: `\${${un.id}_result}` });
              }
              // Deduplicate
              const seen = new Set();
              const uniqueVars = vars.filter((v) => { if (seen.has(v.ref)) return false; seen.add(v.ref); return true; });
              return (
                <div key={un.id} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid var(--v2-bg-muted)' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--v2-text-secondary)', marginBottom: 3 }}>
                    {un.label || un.id} <span style={{ fontWeight: 400, color: 'var(--v2-text-disabled)' }}>({un.type})</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {uniqueVars.map((v) => (
                      <button
                        key={v.label}
                        onClick={() => { navigator.clipboard.writeText(v.ref); }}
                        style={{
                          padding: '2px 6px', fontSize: 9, fontFamily: 'monospace',
                          background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-border-default)', borderRadius: 3,
                          cursor: 'pointer', color: 'var(--v2-text-secondary)', whiteSpace: 'nowrap',
                        }}
                        title={`Click to copy ${v.ref}`}
                      >
                        {v.ref}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </CollapsibleSection>
        );
      })()}

      {/* Dynamic skill parameters — shown when a connector_task action is selected */}
      {isConnectorTask && selectedAction && (
        <CollapsibleSection
          title={`${selectedAction.method} ${selectedAction.name}`}
          badge={selectedAction.params?.length || 0}
          defaultOpen={true}
          color="var(--v2-accent-cyan)"
          bgColor="var(--v2-accent-cyan-soft)"
          borderColor="var(--v2-accent-cyan-soft)"
        >
          {selectedAction.description && (
            <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 8 }}>{selectedAction.description}</div>
          )}
          {selectedAction.endpoint && (
            <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--v2-text-muted)', marginBottom: 10, background: 'var(--v2-accent-cyan-soft)', padding: '3px 6px', borderRadius: 3, wordBreak: 'break-all' }}>
              {selectedAction.endpoint}
            </div>
          )}

          {/* Render each action param as a form field */}
          {selectedAction.params?.map((param) => {
            const isLongText = param.type === 'text' || param.name === 'body' || param.name === 'subject'
              || param.name === 'description' || param.name === 'template';

            // Template select for connector_task email nodes — uses
            // top-level ConnectorTemplateSelect (was inline; see comment
            // on the component declaration above for why).
            if (param.type === 'template_select') {
              return (
                <ConnectorTemplateSelect
                  key={param.name}
                  param={param}
                  node={node}
                  onUpdate={onUpdate}
                />
              );
            }

            return (
            <div key={param.name} style={{ marginBottom: 10 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>
                {param.name}
                {param.required && <span style={{ color: 'var(--v2-danger)' }}> *</span>}
                <span style={{ color: 'var(--v2-text-disabled)', fontWeight: 400, marginLeft: 4 }}>({param.type === 'select' ? 'choice' : param.type})</span>
              </label>
              {param.type === 'select' && param.options ? (
                <select
                  value={node.config?.skillParams?.[param.name] || ''}
                  onChange={(e) => onUpdate({
                    config: {
                      ...node.config,
                      skillParams: { ...(node.config?.skillParams || {}), [param.name]: e.target.value },
                    },
                  })}
                  style={inputStyle}
                >
                  <option value="">{param.required ? 'Select...' : '— None (auto) —'}</option>
                  {param.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : isLongText ? (
                <textarea
                  value={node.config?.skillParams?.[param.name] || ''}
                  onChange={(e) => onUpdate({
                    config: {
                      ...node.config,
                      skillParams: { ...(node.config?.skillParams || {}), [param.name]: e.target.value },
                    },
                  })}
                  style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }}
                  placeholder={param.helpText || param.example || `Enter ${param.name}\nUse \${variable} to reference upstream outputs`}
                />
              ) : (
                <input
                  type={param.type === 'number' ? 'number' : 'text'}
                  value={node.config?.skillParams?.[param.name] || ''}
                  onChange={(e) => onUpdate({
                    config: {
                      ...node.config,
                      skillParams: { ...(node.config?.skillParams || {}), [param.name]: e.target.value },
                    },
                  })}
                  style={inputStyle}
                  placeholder={param.helpText || param.example || `Enter ${param.name}`}
                />
              )}
              {param.helpText && (
                <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 2 }}>{param.helpText}</div>
              )}
            </div>
            );
          })}

          {/* Guardrails */}
          {selectedAction.guardrails?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <label style={{ ...labelStyle, fontSize: 10, color: 'var(--v2-warning)' }}>Guardrails</label>
              {selectedAction.guardrails.map((g, i) => (
                <div key={i} style={{ fontSize: 10, color: 'var(--v2-warning)', padding: '2px 0', display: 'flex', gap: 4 }}>
                  <Shield size={10} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{g}</span>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* ═══ SCHEDULE NODE — Cron helper, preview, and output guidance ═══
          Uses the top-level ScheduleHelper component (was inline). The
          inline version remounted on every InputTab render so any in-
          flight `Preview Next Executions` fetch was orphaned. */}
      {node.type === 'schedule' && (
        <ScheduleHelper node={node} onUpdate={onUpdate} />
      )}
      {/* The legacy inline ScheduleHelper IIFE used to live here; it
          remounted on every render so its `Preview Next Executions`
          fetch state never survived a parent update. Replaced by the
          hoisted <ScheduleHelper/> component above. The IIFE form is
          retained behind `false` purely so the diff history is
          reviewable in one commit and so any unmerged branches that
          touched it still compile against this file. Will be deleted
          on the next janitorial pass. */}
      {false && (() => {
        const CRON_PRESETS = [
          { label: 'Every minute', cron: '* * * * *' },
          { label: 'Every 5 minutes', cron: '*/5 * * * *' },
          { label: 'Every 15 minutes', cron: '*/15 * * * *' },
          { label: 'Every hour', cron: '0 * * * *' },
          { label: 'Every 3 hours', cron: '0 */3 * * *' },
          { label: 'Daily at 9 AM', cron: '0 9 * * *' },
          { label: 'Weekdays at 9 AM', cron: '0 9 * * 1-5' },
          { label: 'Monday at 8:30 AM', cron: '30 8 * * 1' },
          { label: 'Every 6 hours', cron: '0 */6 * * *' },
          { label: 'Twice daily (9 AM & 5 PM)', cron: '0 9,17 * * *' },
          { label: 'Monthly (1st at midnight)', cron: '0 0 1 * *' },
        ];

        const describeCron = (cron) => {
          if (!cron) return null;
          const p = cron.trim().split(/\s+/);
          if (p.length !== 5) return 'Invalid (expected 5 fields: min hour day month dow)';
          const [min, hr, dom, mon, dow] = p;
          // Simple human-readable descriptions for common patterns
          if (cron === '* * * * *') return 'Every minute';
          if (min.startsWith('*/') && hr === '*' && dom === '*' && mon === '*' && dow === '*')
            return `Every ${min.slice(2)} minutes`;
          if (min === '0' && hr.startsWith('*/') && dom === '*' && mon === '*' && dow === '*')
            return `Every ${hr.slice(2)} hours`;
          if (min === '0' && hr === '*' && dom === '*' && mon === '*' && dow === '*')
            return 'Every hour at :00';
          if (dom === '*' && mon === '*' && dow === '*')
            return `Daily at ${hr}:${min.padStart(2, '0')}`;
          if (dom === '*' && mon === '*' && dow === '1-5')
            return `Weekdays at ${hr}:${min.padStart(2, '0')}`;
          if (dom === '*' && mon === '*' && dow !== '*')
            return `Days ${dow} at ${hr}:${min.padStart(2, '0')}`;
          if (dom === '1' && mon === '*' && dow === '*')
            return `Monthly (1st) at ${hr}:${min.padStart(2, '0')}`;
          return `${min} ${hr} ${dom} ${mon} ${dow}`;
        };

        const ScheduleHelper = () => {
          const [scheduleInfo, setScheduleInfo] = React.useState(null);
          const [loadingStatus, setLoadingStatus] = React.useState(false);
          const cronVal = node.config?.cron || '';
          const tz = node.config?.timezone || 'UTC';

          // Get schedule status from backend when workflow is saved
          const workflowId = window.location.pathname.split('/canvas/')[1]?.split('/')[0]
            || window.location.pathname.split('/workflows/')[1]?.split('/')[0];

          const fetchStatus = React.useCallback(async () => {
            if (!workflowId) return;
            setLoadingStatus(true);
            try {
              const data = await getScheduleStatus(workflowId);
              setScheduleInfo(data);
            } catch { /* not saved yet */ }
            setLoadingStatus(false);
          }, [workflowId]);

          return (
            <div>
              {/* Cron Quick Presets */}
              <CollapsibleSection title="Quick Presets" badge={CRON_PRESETS.length} defaultOpen={false} color="var(--v2-warning)" bgColor="var(--v2-danger-soft)" borderColor="var(--v2-danger-soft)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {CRON_PRESETS.map((preset) => (
                    <button
                      key={preset.cron}
                      onClick={() => onUpdate({ config: { ...node.config, cron: preset.cron } })}
                      style={{
                        padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
                        border: cronVal === preset.cron ? '1px solid var(--v2-warning)' : '1px solid var(--v2-border-default)',
                        background: cronVal === preset.cron ? 'var(--v2-danger-soft)' : 'var(--v2-bg-surface)',
                        color: cronVal === preset.cron ? 'var(--v2-warning)' : 'var(--v2-text-secondary)',
                        fontWeight: cronVal === preset.cron ? 600 : 400,
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Human-readable description */}
              {cronVal && (
                <div style={{
                  margin: '8px 0', padding: '8px 10px', background: 'var(--v2-success-soft)', border: '1px solid var(--v2-success-soft)',
                  borderRadius: 6, fontSize: 11, color: 'var(--v2-success)',
                }}>
                  <span style={{ fontWeight: 600 }}>Schedule:</span>{' '}
                  {describeCron(cronVal)} ({tz})
                </div>
              )}

              {/* Cron field reference */}
              <CollapsibleSection title="Cron Reference" defaultOpen={false} color="var(--v2-text-muted)" bgColor="var(--v2-bg-elevated)" borderColor="var(--v2-border-default)">
                <div style={{ fontSize: 10, color: 'var(--v2-text-secondary)', lineHeight: 1.6, fontFamily: 'monospace' }}>
                  <div style={{ marginBottom: 6, fontFamily: 'inherit', fontSize: 10, color: 'var(--v2-text-muted)' }}>
                    Format: <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>minute hour day-of-month month day-of-week</code>
                  </div>
                  <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        ['*', 'Any value'],
                        ['*/N', 'Every N units'],
                        ['1,5,10', 'Specific values'],
                        ['1-5', 'Range (inclusive)'],
                        ['0-59', 'Minutes (field 1)'],
                        ['0-23', 'Hours (field 2)'],
                        ['1-31', 'Day of month (field 3)'],
                        ['1-12', 'Month (field 4)'],
                        ['0-6', 'Day of week (field 5, 0=Sun)'],
                      ].map(([val, desc]) => (
                        <tr key={val}>
                          <td style={{ padding: '2px 8px 2px 0', color: 'var(--v2-warning)', fontWeight: 600 }}>{val}</td>
                          <td style={{ padding: '2px 0', color: 'var(--v2-text-secondary)', fontFamily: 'inherit' }}>{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>

              {/* Next Execution Preview */}
              {workflowId && (
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={fetchStatus}
                    disabled={loadingStatus}
                    style={{
                      padding: '4px 10px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
                      border: '1px solid var(--v2-warning)', background: 'var(--v2-bg-surface)', color: 'var(--v2-warning)', fontWeight: 600,
                    }}
                  >
                    {loadingStatus ? 'Loading...' : 'Preview Next Executions'}
                  </button>
                  {scheduleInfo && scheduleInfo.nextExecutions && (
                    <div style={{
                      marginTop: 6, padding: '8px 10px', background: 'var(--v2-action-soft)', border: '1px solid var(--v2-action-soft)',
                      borderRadius: 6, fontSize: 10,
                    }}>
                      <div style={{ fontWeight: 600, color: 'var(--v2-action)', marginBottom: 4 }}>
                        Next {scheduleInfo.nextExecutions.length} executions:
                      </div>
                      {scheduleInfo.nextExecutions.map((t, i) => (
                        <div key={i} style={{ color: 'var(--v2-action)', padding: '1px 0', fontFamily: 'monospace' }}>
                          {i + 1}. {new Date(t).toLocaleString()}
                        </div>
                      ))}
                      {scheduleInfo.totalRuns != null && (
                        <div style={{ marginTop: 4, color: 'var(--v2-text-muted)', fontSize: 10 }}>
                          Total runs so far: <strong>{scheduleInfo.totalRuns}</strong>
                        </div>
                      )}
                      {!scheduleInfo.cronValid && scheduleInfo.cronError && (
                        <div style={{ marginTop: 4, color: 'var(--v2-danger)', fontSize: 10 }}>
                          Cron error: {scheduleInfo.cronError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Output Template Guidance */}
              <CollapsibleSection title="Output Variables" badge={3} defaultOpen={true} color="var(--v2-accent-cyan)" bgColor="var(--v2-accent-cyan-soft)" borderColor="var(--v2-accent-cyan-soft)">
                <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 6 }}>
                  When this schedule triggers, downstream nodes receive these variables:
                </div>
                {[
                  { name: 'timestamp', type: 'ISO8601', desc: 'When the schedule fired' },
                  { name: 'executionNumber', type: 'integer', desc: 'Sequential run count for this workflow' },
                  { name: 'nextExecutionTime', type: 'ISO8601', desc: 'When the next execution will occur' },
                ].map((v) => (
                  <div key={v.name} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
                    borderBottom: '1px solid var(--v2-accent-cyan-soft)',
                  }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(`\${${v.name}}`)}
                      style={{
                        padding: '2px 6px', fontSize: 9, fontFamily: 'monospace',
                        background: 'var(--v2-accent-cyan-soft)', border: '1px solid var(--v2-accent-cyan-soft)', borderRadius: 3,
                        cursor: 'pointer', color: 'var(--v2-accent-cyan)', whiteSpace: 'nowrap',
                      }}
                      title={`Click to copy \${${v.name}}`}
                    >
                      ${'{' + v.name + '}'}
                    </button>
                    <span style={{ fontSize: 9, color: 'var(--v2-text-disabled)' }}>({v.type})</span>
                    <span style={{ fontSize: 10, color: 'var(--v2-text-muted)' }}>{v.desc}</span>
                  </div>
                ))}
              </CollapsibleSection>

              {/* Input Template Guidance */}
              <CollapsibleSection title="Schedule Trigger Tips" defaultOpen={false} color="var(--v2-text-muted)" bgColor="var(--v2-bg-elevated)" borderColor="var(--v2-border-default)">
                <div style={{ fontSize: 10, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
                  <p style={{ margin: '0 0 6px' }}>
                    <strong>How scheduling works:</strong> When you save a workflow with a Schedule trigger,
                    the backend automatically detects the cron expression and runs the workflow at each scheduled time.
                  </p>
                  <p style={{ margin: '0 0 6px' }}>
                    <strong>Workflow status:</strong> The workflow must be <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>active</code> for
                    scheduled runs to fire. Set status via the workflow settings.
                  </p>
                  <p style={{ margin: '0 0 6px' }}>
                    <strong>Deduplication:</strong> The scheduler prevents double-firing within the same minute window.
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Input context:</strong> Each scheduled execution receives <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>triggerType</code>,{' '}
                    <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>scheduledAt</code>,{' '}
                    <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>executionNumber</code>, and{' '}
                    <code style={{ background: 'var(--v2-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>cronExpression</code> as input metadata.
                  </p>
                </div>
              </CollapsibleSection>
            </div>
          );
        };
        return <ScheduleHelper />;
      })()}

      {/*
        INPUT PARAMETERS (Conductor OSS-canonical) now lives on its own
        top-level "Inputs" tab (see <InputsTab/> below). Removing the
        duplicate render here keeps a single source of truth — both
        old and new editors mutated the same node.config.inputMapping,
        which led to confusing double-renders during testing.
      */}
    </div>
  );
}

/** InputMappingEditor — lets users map upstream node output variables to this node's input parameters. */
function InputMappingEditor({ node, onUpdate, nodes, connections }) {
  const inputMapping = node?.config?.inputMapping || {};
  const [customModes, setCustomModes] = useState({}); // tracks which rows are in "custom" text mode

  // Find upstream nodes (nodes that have an edge pointing TO this node)
  const upstreamNodeIds = React.useMemo(() => {
    if (!connections || !node) return [];
    return connections.filter((c) => c.to === node.id).map((c) => c.from);
  }, [connections, node]);

  const upstreamNodes = React.useMemo(() => {
    if (!nodes) return [];
    return nodes.filter((n) => upstreamNodeIds.includes(n.id));
  }, [nodes, upstreamNodeIds]);

  // Build list of available output variables from upstream nodes
  const availableVariables = React.useMemo(() => {
    const vars = [];
    upstreamNodes.forEach((n) => {
      const outVar = n.config?.outputVariable || n.id;
      vars.push({ nodeId: n.id, nodeLabel: n.label || n.id, variable: outVar });
      if (n.type === 'connector_task' || n.type === 'connector') {
        ['_result', '_response', '_httpStatus', '_success'].forEach((suffix) => {
          vars.push({ nodeId: n.id, nodeLabel: n.label || n.id, variable: `${n.id}${suffix}` });
        });
      }
    });
    return vars;
  }, [upstreamNodes]);

  const mappingEntries = Object.entries(inputMapping);

  const handleAddMapping = () => {
    // Use a unique temp key to avoid overwriting existing empty-key entries
    const tempKey = `param_${Date.now().toString(36)}`;
    onUpdate({ config: { ...node.config, inputMapping: { ...inputMapping, [tempKey]: '' } } });
  };

  const handleUpdateMappingKey = (oldKey, newKey) => {
    const newMapping = {};
    Object.entries(inputMapping).forEach(([k, v]) => { newMapping[k === oldKey ? newKey : k] = v; });
    onUpdate({ config: { ...node.config, inputMapping: newMapping } });
  };

  const handleUpdateMappingValue = (key, newValue) => {
    onUpdate({ config: { ...node.config, inputMapping: { ...inputMapping, [key]: newValue } } });
  };

  const handleRemoveMapping = (key) => {
    const newMapping = { ...inputMapping };
    delete newMapping[key];
    setCustomModes((prev) => { const next = { ...prev }; delete next[key]; return next; });
    onUpdate({ config: { ...node.config, inputMapping: newMapping } });
  };

  const toggleCustomMode = (key) => {
    setCustomModes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <CollapsibleSection
      title="Input Mapping"
      badge={mappingEntries.length || null}
      defaultOpen={mappingEntries.length > 0}
      color="var(--v2-warning)"
      bgColor="var(--v2-warning-soft)"
      borderColor="var(--v2-warning)"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--v2-warning)' }}>Wire upstream outputs as inputs</div>
        <button onClick={handleAddMapping} style={{
          padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer',
          background: 'var(--v2-warning)', color: 'var(--v2-warning)', border: 'none', borderRadius: 4,
        }}>+ Add</button>
      </div>

      {mappingEntries.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--v2-warning)', textAlign: 'center', padding: '8px 0' }}>
          No input mappings. Click "+ Add" to wire an upstream output.
        </div>
      )}

      {mappingEntries.map(([key, value], idx) => {
        const isCustom = customModes[key] || !availableVariables.some((v) => v.variable === value);
        const hasVars = availableVariables.length > 0;

        return (
          <div key={idx} style={{ marginBottom: 10, padding: 8, background: 'var(--v2-action-soft)', borderRadius: 6, border: '1px solid var(--v2-action-soft)' }}>
            {/* Row 1: param name */}
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 9, fontWeight: 600, color: 'var(--v2-warning)', display: 'block', marginBottom: 2 }}>Input Parameter</label>
              <input
                type="text"
                value={key}
                onChange={(e) => handleUpdateMappingKey(key, e.target.value)}
                style={{ width: '100%', padding: '5px 8px', fontSize: 11, border: '1px solid var(--v2-border-default)', borderRadius: 4, fontFamily: 'monospace', background: 'var(--v2-bg-surface)', boxSizing: 'border-box' }}
                placeholder="e.g. short_description"
              />
            </div>
            {/* Row 2: source variable */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <label style={{ fontSize: 9, fontWeight: 600, color: 'var(--v2-warning)' }}>Source Variable</label>
                {hasVars && (
                  <button onClick={() => toggleCustomMode(key)} style={{
                    fontSize: 9, color: 'var(--v2-warning)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                  }}>{isCustom ? 'Pick from list' : 'Type custom'}</button>
                )}
              </div>
              {!isCustom && hasVars ? (
                <select
                  value={value}
                  onChange={(e) => handleUpdateMappingValue(key, e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', fontSize: 11, border: '1px solid var(--v2-border-default)', borderRadius: 4, fontFamily: 'monospace', background: 'var(--v2-bg-surface)', boxSizing: 'border-box' }}
                >
                  <option value="">Select source...</option>
                  {availableVariables.map((v, i) => (
                    <option key={i} value={v.variable}>{v.variable} ({v.nodeLabel})</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleUpdateMappingValue(key, e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', fontSize: 11, border: '1px solid var(--v2-border-default)', borderRadius: 4, fontFamily: 'monospace', background: 'var(--v2-bg-surface)', boxSizing: 'border-box' }}
                  placeholder="e.g. n_abc123_response.result.sys_id"
                />
              )}
            </div>
            {/* Remove button */}
            <button onClick={() => handleRemoveMapping(key)} style={{
              fontSize: 9, color: 'var(--v2-danger)', background: 'var(--v2-danger-soft)', border: 'none', borderRadius: 3, cursor: 'pointer', padding: '2px 8px', marginTop: 2,
            }}>Remove</button>
          </div>
        );
      })}

      <div style={{ fontSize: 10, color: 'var(--v2-warning)', lineHeight: 1.4, marginTop: 4 }}>
        Use dot notation for nested fields: <code style={{ background: 'var(--v2-action-soft)', padding: '1px 3px', borderRadius: 2, fontSize: 9 }}>nodeId_response.result.sys_id</code>
      </div>
    </CollapsibleSection>
  );
}

/**
 * JsonFieldEditor — editable JSON textarea that stores raw text locally
 * and only parses on blur. This allows free-form typing without the
 * controlled value snapping back to the last valid parse on every keystroke.
 */
function JsonFieldEditor({ value, onChange, placeholder }) {
  // Serialize the stored value to a display string (pretty-printed or raw)
  const serialize = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  };

  const [raw, setRaw] = useState(serialize(value));
  const [jsonError, setJsonError] = useState(null);

  // Sync from parent when the node/value changes externally
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setRaw(serialize(value));
      setJsonError(null);
    }
  }, [value]);

  const handleChange = (e) => {
    const text = e.target.value;
    setRaw(text);
    setJsonError(null);

    // Also try to parse live so valid JSON is saved immediately
    if (!text.trim()) {
      onChange(null);
      prevValueRef.current = null;
      return;
    }
    try {
      const parsed = JSON.parse(text);
      onChange(parsed);
      prevValueRef.current = parsed;
      setJsonError(null);
    } catch {
      // Not valid JSON yet — that's fine, store as raw string so user can keep typing
      // Save as raw string so the value isn't lost
      onChange(text);
      prevValueRef.current = text;
    }
  };

  const handleBlur = () => {
    if (!raw.trim()) {
      onChange(null);
      setJsonError(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      onChange(parsed);
      prevValueRef.current = parsed;
      setJsonError(null);
      // Pretty-print on blur for readability
      setRaw(JSON.stringify(parsed, null, 2));
    } catch {
      // Keep the raw string — show a hint but don't block
      setJsonError('Invalid JSON — value saved as plain text');
    }
  };

  return (
    <>
      <textarea
        value={raw}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{
          ...inputStyle,
          minHeight: 80,
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          fontSize: 11,
          resize: 'vertical',
          borderColor: jsonError ? 'var(--v2-warning)' : undefined,
        }}
        placeholder={placeholder}
      />
      {jsonError && (
        <div style={{ fontSize: 10, color: 'var(--v2-warning)', marginTop: 2 }}>
          {jsonError}
        </div>
      )}
    </>
  );
}

/**
 * InputsTab — Conductor OSS-canonical inputParameters editor.
 *
 * Mirrors Conductor OSS pattern: a flat key/value table where each row is
 * `paramName: <expression>`. Expressions are resolved at runtime by
 * CanvasDAGWorkflowImpl#resolveNodeInput, supporting:
 *   - ${workflow.input.X}              — workflow-level inputs
 *   - ${TASK_REF.output.field.path}    — upstream node output
 *   - ${TASK_REF.input.field}          — upstream node's resolved inputs
 *   - ${TASK_REF.field}                — Conductor short form (= .output.field)
 *   - "literal string with ${X} mixed" — full templated substitution
 *   - bare static values (number / string / object)              — passed verbatim
 *
 * The "Insert reference" dropdown writes the canonical
 * ${TASK.output.field} form. Underscore-prefixed legacy refs still
 * work at runtime so existing workflows don't break.
 *
 * Persists to node.config.inputMapping (same field shape as the
 * legacy InputMappingEditor — they edit the same data).
 */
function InputsTab({ node, nodeDef, onUpdate, nodes, connections }) {
  const inputMapping = node?.config?.inputMapping || {};
  const entries = Object.entries(inputMapping);

  // Build the canonical reference catalogue from the upstream graph.
  // Each entry is { label, ref } where ref is the literal string we'll
  // splice into the expression field.
  const upstreamIds = React.useMemo(() => {
    if (!connections || !node) return [];
    return connections.filter((c) => c.to === node.id).map((c) => c.from);
  }, [connections, node]);

  const references = React.useMemo(() => {
    const out = [];
    out.push({ group: 'Workflow', label: '${workflow.input.X}', ref: '${workflow.input.}' });
    (nodes || []).forEach((n) => {
      if (!upstreamIds.includes(n.id)) return;
      const def = typeof getNodeTypeDef === 'function' ? getNodeTypeDef(n.type) : null;
      const schema = def?.outputSchema;
      if (schema && Object.keys(schema).length > 0) {
        Object.keys(schema).forEach((field) => {
          out.push({
            group: n.label || n.id,
            label: `\${${n.id}.output.${field}}`,
            ref: `\${${n.id}.output.${field}}`,
          });
        });
      } else {
        // Fallback when the node type doesn't declare a schema —
        // surface the bare envelope so the builder can pick a path.
        out.push({
          group: n.label || n.id,
          label: `\${${n.id}.output}`,
          ref: `\${${n.id}.output}`,
        });
      }
    });
    return out;
  }, [nodes, upstreamIds]);

  const writeMapping = (next) => {
    if (!onUpdate) return;
    onUpdate({ config: { ...node.config, inputMapping: next } });
  };

  const addRow = () => {
    // Generate a unique placeholder key so React keys stay stable
    const tempKey = `param_${Date.now().toString(36)}`;
    writeMapping({ ...inputMapping, [tempKey]: '' });
  };

  const renameKey = (oldKey, newKey) => {
    if (oldKey === newKey) return;
    const next = {};
    Object.entries(inputMapping).forEach(([k, v]) => {
      next[k === oldKey ? newKey : k] = v;
    });
    writeMapping(next);
  };

  const updateValue = (key, value) => {
    writeMapping({ ...inputMapping, [key]: value });
  };

  const removeKey = (key) => {
    const next = { ...inputMapping };
    delete next[key];
    writeMapping(next);
  };

  // Identifier validation — mirrors Conductor OSS / our own resolver: param
  // names must be safe identifiers so dot-walking ${TASK.input.<name>}
  // works without escaping. Trailing/leading whitespace is trimmed at
  // save-time, but during typing we allow it so the input isn't jumpy.
  const isValidName = (n) => /^[A-Za-z_][A-Za-z0-9_]*$/.test((n || '').trim());

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 6 }}>
          Input Parameters
        </h4>
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', lineHeight: 1.5 }}>
          Map inputs to this node from upstream outputs or workflow inputs.
          Use the canonical form
          <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
            {'${TASK.output.field}'}
          </code>
          or
          <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
            {'${workflow.input.X}'}
          </code>
          . Templated strings like
          <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
            {'"Hello ${user.name}"'}
          </code>
          are supported.
        </div>
      </div>

      {entries.length === 0 ? (
        <div style={{
          padding: '10px 12px', fontSize: 11, color: 'var(--v2-text-muted)',
          background: 'var(--v2-bg-elevated)', border: '1px dashed var(--v2-border-default)', borderRadius: 4,
          marginBottom: 8,
        }}>
          No input parameters yet. Click <strong>+ Add parameter</strong> below.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          {entries.map(([key, value], idx) => {
            const nameError = key && !isValidName(key);
            return (
              <div key={idx} style={{
                padding: 8, background: 'var(--v2-bg-elevated)',
                border: '1px solid var(--v2-border-default)', borderRadius: 6,
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1.6fr auto',
                  gap: 6, alignItems: 'start',
                }}>
                  {/* Param name */}
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 600, color: 'var(--v2-text-secondary)', display: 'block', marginBottom: 2 }}>
                      Parameter
                    </label>
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => renameKey(key, e.target.value)}
                      placeholder="policyId"
                      style={{
                        width: '100%', padding: '6px 8px', fontSize: 11,
                        border: `1px solid ${nameError ? 'var(--v2-warning)' : 'var(--v2-border-default)'}`,
                        borderRadius: 4,
                        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                        boxSizing: 'border-box', background: 'var(--v2-bg-surface)',
                      }}
                    />
                    {nameError && (
                      <div style={{ fontSize: 9, color: 'var(--v2-warning)', marginTop: 2 }}>
                        Invalid identifier
                      </div>
                    )}
                  </div>

                  {/* Expression */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <label style={{ fontSize: 9, fontWeight: 600, color: 'var(--v2-text-secondary)' }}>
                        Expression
                      </label>
                      {references.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            // Append the picked reference to whatever the
                            // user has typed so far so they can build
                            // composite templates without losing context.
                            const next = (value || '') + (value ? ' ' : '') + v;
                            updateValue(key, next);
                            // Reset select to placeholder so the same item
                            // can be picked again
                            e.target.value = '';
                          }}
                          style={{
                            fontSize: 9, color: 'var(--v2-text-secondary)',
                            background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)',
                            borderRadius: 3, padding: '1px 4px', cursor: 'pointer',
                          }}
                        >
                          <option value="">Insert reference…</option>
                          {(() => {
                            // Group references by their source for the dropdown
                            const groups = {};
                            references.forEach((r) => {
                              if (!groups[r.group]) groups[r.group] = [];
                              groups[r.group].push(r);
                            });
                            return Object.entries(groups).map(([group, refs]) => (
                              <optgroup key={group} label={group}>
                                {refs.map((r) => (
                                  <option key={r.label} value={r.ref}>{r.label}</option>
                                ))}
                              </optgroup>
                            ));
                          })()}
                        </select>
                      )}
                    </div>
                    <input
                      type="text"
                      value={value || ''}
                      onChange={(e) => updateValue(key, e.target.value)}
                      placeholder="${nodeId.output.field}"
                      style={{
                        width: '100%', padding: '6px 8px', fontSize: 11,
                        border: '1px solid var(--v2-border-default)', borderRadius: 4,
                        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                        boxSizing: 'border-box', background: 'var(--v2-bg-surface)',
                      }}
                    />
                  </div>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => removeKey(key)}
                    title="Remove"
                    style={{
                      marginTop: 14, padding: '4px 8px', fontSize: 11,
                      background: 'transparent',
                      border: '1px solid var(--v2-border-default)', borderRadius: 4,
                      cursor: 'pointer', color: 'var(--v2-text-muted)',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        style={{
          padding: '6px 10px', fontSize: 11, fontWeight: 500,
          background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)', borderRadius: 4,
          cursor: 'pointer', color: 'var(--v2-text-primary)',
        }}
      >
        + Add parameter
      </button>

      {/* Available references hint when no upstream nodes are connected */}
      {upstreamIds.length === 0 && (
        <div style={{
          marginTop: 14, padding: 8, background: 'var(--v2-warning-soft)',
          border: '1px solid var(--v2-warning-soft)', borderRadius: 4, fontSize: 10, color: 'var(--v2-warning)',
        }}>
          No upstream nodes connected yet. Connect this node's input to
          another node first, then references like
          <code style={{ background: 'var(--v2-warning-soft)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
            {'${upstream.output.field}'}
          </code>
          will autocomplete here.
        </div>
      )}
    </div>
  );
}

function OutputTab({ nodeDef, executionState, node, onUpdate }) {
  const outputSchema = nodeDef?.outputSchema;
  const nodeId = node?.id || 'node';
  const outputMapping = Array.isArray(node?.config?.outputMapping)
    ? node.config.outputMapping
    : [];

  // Helper: persist a new outputMapping array on the node config.
  const writeMapping = (next) => {
    if (!onUpdate) return;
    onUpdate({ config: { ...node?.config, outputMapping: next } });
  };

  const addMappingRow = () => {
    // Pre-fill the expression with the most common idiom for this node type
    // when we can. For nodes that document an outputSchema, default the
    // first row's expression to ${<nodeId>_<firstSchemaField>} so the
    // builder gets a working alias without typing anything.
    const firstField = outputSchema ? Object.keys(outputSchema)[0] : null;
    const newRow = {
      name: '',
      expression: firstField ? `\${${nodeId}_${firstField}}` : '',
    };
    writeMapping([...outputMapping, newRow]);
  };

  const updateMappingRow = (idx, patch) => {
    const next = outputMapping.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    writeMapping(next);
  };

  const removeMappingRow = (idx) => {
    writeMapping(outputMapping.filter((_, i) => i !== idx));
  };

  // Validate identifier — backend rejects names that don't match
  // [A-Za-z_][A-Za-z0-9_]* and silently skips them at runtime, so we surface
  // the same rule at edit time to prevent silent drops in production.
  const isValidName = (name) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(name || '');

  return (
    <div>
      {/* Output Variable Name — always shown so users can name this node's output */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>Output Variable Name</h4>
        <input
          type="text"
          value={node?.config?.outputVariable || ''}
          onChange={(e) => onUpdate && onUpdate({
            config: { ...node?.config, outputVariable: e.target.value },
          })}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 12, border: '1px solid var(--v2-border-default)',
            borderRadius: 4, fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            boxSizing: 'border-box',
          }}
          placeholder={node?.id || 'myNodeOutput'}
        />
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 4 }}>
          Name for this node's output in the execution context. Downstream nodes can reference
          this in their Input Mapping. Defaults to the node ID if left empty.
        </div>
      </div>

      {/*
        Output Variables (editable aliases) — closes the gap between the
        Output Schema (what the node publishes) and what downstream switches
        / mappings actually want to reference. e.g. an HTTP node publishes
        ${<nodeId>_statusCode}, but a builder may want to call it
        "httpStatus" — they add a row { name: "httpStatus",
        expression: "${<nodeId>_statusCode}" } and downstream nodes can
        reference ${<nodeId>_httpStatus}.

        Persists as node.config.outputMapping; the runtime
        (CanvasDAGWorkflowImpl#applyOutputMapping) evaluates each row after
        the node completes and publishes it into executionContext.
      */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
          Output Variables
        </h4>
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 8 }}>
          Define custom aliases over this node's output. Each becomes
          <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
            {`\${${nodeId}_<name>}`}
          </code>
          for downstream switches and mappings.
        </div>

        {outputMapping.length === 0 ? (
          <div style={{
            padding: '8px 10px', fontSize: 11, color: 'var(--v2-text-muted)',
            background: 'var(--v2-bg-elevated)', border: '1px dashed var(--v2-border-default)', borderRadius: 4,
            marginBottom: 8,
          }}>
            No custom output variables yet. Click <strong>+ Add</strong> below.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            {outputMapping.map((row, idx) => {
              const nameError = row.name && !isValidName(row.name);
              return (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1.6fr auto',
                  gap: 6,
                  alignItems: 'start',
                }}>
                  <div>
                    <input
                      type="text"
                      value={row.name || ''}
                      onChange={(e) => updateMappingRow(idx, { name: e.target.value })}
                      placeholder="httpStatus"
                      style={{
                        width: '100%', padding: '6px 8px', fontSize: 11,
                        border: `1px solid ${nameError ? 'var(--v2-warning)' : 'var(--v2-border-default)'}`,
                        borderRadius: 4,
                        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                        boxSizing: 'border-box',
                      }}
                    />
                    {nameError && (
                      <div style={{ fontSize: 9, color: 'var(--v2-warning)', marginTop: 2 }}>
                        Use letters, digits, underscore (start with letter or _).
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={row.expression || ''}
                    onChange={(e) => updateMappingRow(idx, { expression: e.target.value })}
                    placeholder={`\${${nodeId}_statusCode}`}
                    style={{
                      width: '100%', padding: '6px 8px', fontSize: 11,
                      border: '1px solid var(--v2-border-default)', borderRadius: 4,
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeMappingRow(idx)}
                    title="Remove"
                    style={{
                      padding: '4px 8px', fontSize: 11, background: 'transparent',
                      border: '1px solid var(--v2-border-default)', borderRadius: 4, cursor: 'pointer',
                      color: 'var(--v2-text-muted)',
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={addMappingRow}
          style={{
            padding: '6px 10px', fontSize: 11, fontWeight: 500,
            background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)', borderRadius: 4,
            cursor: 'pointer', color: 'var(--v2-text-primary)',
          }}
        >
          + Add output variable
        </button>
      </div>

      {outputSchema && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>Output Schema</h4>
          <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 6 }}>
            Reference these from downstream nodes as
            <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
              {`\${${nodeId}.output.<field>}`}
            </code>
            (canonical) or
            <code style={{ background: 'var(--v2-bg-muted)', padding: '0 4px', margin: '0 2px', borderRadius: 2 }}>
              {`\${${nodeId}_<field>}`}
            </code>
            (legacy alias). Click any row to copy the canonical reference.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(outputSchema).map(([key, type]) => {
              const canonicalRef = `\${${nodeId}.output.${key}}`;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (typeof navigator !== 'undefined' && navigator.clipboard) {
                      navigator.clipboard.writeText(canonicalRef).catch(() => { /* clipboard blocked — silent */ });
                    }
                  }}
                  title={`Copy ${canonicalRef}`}
                  style={{
                    padding: '8px 10px',
                    background: 'var(--v2-bg-muted)',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--v2-border-default)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--v2-text-primary)', fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>
                    {canonicalRef}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--v2-text-muted)', fontFamily: 'monospace' }}>{type}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {executionState?.output && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>Execution Output</h4>
          <pre style={{
            background: 'var(--v2-bg-muted)',
            padding: '8px',
            borderRadius: 4,
            fontSize: 10,
            fontFamily: 'monospace',
            color: 'var(--v2-text-primary)',
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: 200,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {JSON.stringify(executionState.output, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function AttributesTab({ node, onUpdate }) {
  // Retry policy helper — compute preview of delay sequence
  const retryPolicy = node.retryPolicy || {};
  const strategy = retryPolicy.backoffStrategy || 'none';
  const maxRetries = retryPolicy.maxRetries ?? (node.retryCount || 0);
  const baseDelay = retryPolicy.baseDelayMs ?? (node.retryDelay || 1000);
  const maxDelay = retryPolicy.maxDelayMs ?? 60000;
  const multiplier = retryPolicy.multiplier ?? 2;
  const jitter = retryPolicy.jitter ?? false;

  const updateRetryPolicy = (patch) => {
    onUpdate({ retryPolicy: { ...retryPolicy, ...patch } });
  };

  // Compute preview delays
  const previewDelays = [];
  for (let i = 1; i <= Math.min(maxRetries, 6); i++) {
    let delay;
    if (strategy === 'exponential') {
      delay = Math.min(baseDelay * Math.pow(multiplier, i - 1), maxDelay);
    } else if (strategy === 'linear') {
      delay = Math.min(baseDelay * i, maxDelay);
    } else {
      delay = baseDelay;
    }
    previewDelays.push(delay);
  }

  return (
    <div>
      {/* ── Retry & Backoff Policy ── */}
      <div style={{
        marginBottom: 16, padding: '10px 12px', background: 'var(--v2-bg-elevated)',
        border: '1px solid var(--v2-border-default)', borderRadius: 8,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--v2-text-disabled)', textTransform: 'uppercase',
          marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <RotateCw size={11} /> Retry & Backoff Policy
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Max Retries</label>
          <input
            type="number"
            min="0"
            max="20"
            value={maxRetries}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10) || 0;
              updateRetryPolicy({ maxRetries: v });
              onUpdate({ retryCount: v });
            }}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Backoff Strategy</label>
          <select
            value={strategy}
            onChange={(e) => updateRetryPolicy({ backoffStrategy: e.target.value })}
            style={inputStyle}
          >
            <option value="none">Fixed Delay (no backoff)</option>
            <option value="linear">Linear Backoff</option>
            <option value="exponential">Exponential Backoff</option>
          </select>
          <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)', marginTop: 3 }}>
            {strategy === 'none' && 'Same delay between every retry attempt'}
            {strategy === 'linear' && 'Delay increases by baseDelay each attempt: base×1, base×2, base×3 …'}
            {strategy === 'exponential' && `Delay doubles each attempt: base×${multiplier}⁰, base×${multiplier}¹, base×${multiplier}² …`}
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Base Delay (ms)</label>
          <input
            type="number"
            min="100"
            step="100"
            value={baseDelay}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10) || 1000;
              updateRetryPolicy({ baseDelayMs: v });
              onUpdate({ retryDelay: v });
            }}
            style={inputStyle}
          />
        </div>

        {strategy !== 'none' && (
          <>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>Max Delay Cap (ms)</label>
              <input
                type="number"
                min="1000"
                step="1000"
                value={maxDelay}
                onChange={(e) => updateRetryPolicy({ maxDelayMs: parseInt(e.target.value, 10) || 60000 })}
                style={inputStyle}
              />
              <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)', marginTop: 3 }}>
                Delay will never exceed this value regardless of backoff calculation
              </div>
            </div>

            {strategy === 'exponential' && (
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Multiplier</label>
                <input
                  type="number"
                  min="1.1"
                  max="10"
                  step="0.1"
                  value={multiplier}
                  onChange={(e) => updateRetryPolicy({ multiplier: parseFloat(e.target.value) || 2 })}
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id={`jitter-${node.id}`}
                checked={jitter}
                onChange={(e) => updateRetryPolicy({ jitter: e.target.checked })}
              />
              <label htmlFor={`jitter-${node.id}`} style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                Add Jitter
              </label>
              <span style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>
                Randomize ±25% to prevent thundering herd
              </span>
            </div>
          </>
        )}

        {/* Delay Preview */}
        {maxRetries > 0 && (
          <div style={{
            marginTop: 8, padding: '8px 10px', background: 'var(--v2-bg-muted)',
            borderRadius: 6, fontSize: 11, fontFamily: 'monospace',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--v2-text-muted)', marginBottom: 4 }}>
              Retry Schedule Preview
            </div>
            {previewDelays.map((d, i) => (
              <div key={i} style={{ color: 'var(--v2-text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                <span>Attempt {i + 1}</span>
                <span>
                  {d >= 1000 ? `${(d / 1000).toFixed(1)}s` : `${d}ms`}
                  {jitter ? ' ±25%' : ''}
                </span>
              </div>
            ))}
            {maxRetries > 6 && (
              <div style={{ color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>… +{maxRetries - 6} more</div>
            )}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>Retryable Error Codes (optional)</label>
          <input
            type="text"
            value={(retryPolicy.retryOnCodes || []).join(', ')}
            onChange={(e) => updateRetryPolicy({
              retryOnCodes: e.target.value.split(',').map(c => c.trim()).filter(Boolean),
            })}
            style={inputStyle}
            placeholder="e.g. 429, 502, 503, 529"
          />
          <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)', marginTop: 3 }}>
            Only retry on these HTTP status codes. Leave empty to retry on all errors.
          </div>
        </div>
      </div>

      {/* ── Timeout & Rate Limit ── */}
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Timeout (seconds)</label>
        <input
          type="number"
          min="1"
          value={node.timeout || 30}
          onChange={(e) => onUpdate({ timeout: parseInt(e.target.value, 10) || 30 })}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Rate Limit (req/min)</label>
        <input
          type="number"
          min="0"
          value={node.rateLimit || 0}
          onChange={(e) => onUpdate({ rateLimit: parseInt(e.target.value, 10) || 0 })}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>On Failure</label>
        <select
          value={node.errorHandling || 'fail'}
          onChange={(e) => onUpdate({ errorHandling: e.target.value })}
          style={inputStyle}
        >
          <option value="fail">Stop — halt all downstream nodes</option>
          <option value="retry">Retry — use retry policy above, then fail</option>
          <option value="skip">Continue — skip this node and continue downstream</option>
          <option value="fallback">Fallback — use fallback value and continue</option>
        </select>
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 4 }}>
          {node.errorHandling === 'skip' || node.errorHandling === 'fallback'
            ? '⚠ Downstream nodes will run even if this node fails. Use with caution.'
            : 'Downstream nodes will be skipped if this node fails. Parallel branches are not affected.'}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Tags</label>
        <input
          type="text"
          value={(node.tags || []).join(', ')}
          onChange={(e) => onUpdate({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
          style={inputStyle}
          placeholder="Comma-separated tags"
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Notes</label>
        <textarea
          value={node.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }}
          placeholder="Internal notes"
        />
      </div>
    </div>
  );
}

/**
 * G269 / LET_CANVAS_GOLDEN_RULES §4.4 — Right-pane execution stats panel.
 *
 * Reads from `node.config.executionStats` which the canvas materialiser
 * (CanvasPageV3.buildAgentSkillsDag) populates from the G263 aggregator
 * endpoint `GET /api/v1/agents/{id}/canvas-materialisation`. Server-side
 * stats are computed by `PolicyAuditService.computeAgentSkillExecutionStats`
 * over a configurable window (default 30 days) so the panel is
 * authoritative — not a client-side guess over a sampled slice.
 *
 * Surface area (per AGENT_EXECUTION_MODEL §6):
 *   count           — invocations in the window
 *   p50             — median latency (ms)
 *   p95             — 95th-percentile latency (ms)
 *   errorRate       — fraction of invocations that returned a runtime error
 *   blocked         — count of pre-policy / post-policy denials
 *   errors          — count of returned errors
 *   lastInvokedAt   — most recent invocation timestamp
 *   skillName       — server-resolved name (for the label)
 *   windowDays      — the window over which all of the above is computed
 *
 * The panel renders these as a 6-tile grid + a "last invoked" timestamp
 * row. The "last 5 invocations" timeline described in the original
 * delivery plan ships in a follow-up once we add a per-skill audit
 * pagination endpoint; for now we surface what the existing aggregator
 * already returns.
 */
/* G290 — RuleTab
 *
 * Materialised policy gate nodes (policy_gate_org / _tenant / _agent) come
 * from buildAgentSkillsDag's `policyLayer()` with the resolved PolicyRule
 * stamped into node.config (ruleName / ruleDescription / ruleCategory /
 * ruleSeverity / ruleEnforcementPoint / bindingScope / inheritedFrom /
 * sourceTag / ruleId / bindingId / enforcement / activationStatus).
 *
 * Without this panel an operator inspecting a canvas with 7 ORG gates +
 * 3 TENANT gates + 4 AGENT gates sees identical labels ("ORG · platform
 * default") and has no signal as to what each gate actually enforces.
 * This tab makes that resolved rule context human-readable.
 *
 * Source of truth (backend): PolicyAssignmentService.getInheritedBindings
 *   → bulk-loads PolicyRule rows by ruleId
 *   → stamps ruleName/description/category/severity/enforcementPoint
 *     into each binding row before returning to the canvas materialiser.
 */
function RuleTab({ node }) {
  const cfg = (node && node.config) || {};
  const ruleName = cfg.ruleName || null;
  const ruleDescription = cfg.ruleDescription || null;
  const ruleCategory = cfg.ruleCategory || null;
  const ruleSeverity = cfg.ruleSeverity || null;
  const ruleEnforcementPoint = cfg.ruleEnforcementPoint || cfg.enforcement || null;
  const bindingScope = cfg.bindingScope || null;
  const inheritedFrom = cfg.inheritedFrom || null;
  const sourceTag = cfg.sourceTag || null;
  const ruleId = cfg.ruleId || null;
  const bindingId = cfg.bindingId || null;
  const activationStatus = cfg.activationStatus || null;
  const frameworkBindingId = cfg.frameworkBindingId || null;

  // Severity colour palette mirrors PolicyConfigPageV2 / Compliance Center
  // so the same rule reads the same colour wherever it surfaces.
  const sevKey = (ruleSeverity || '').toString().toLowerCase();
  const sevStyle = (
    sevKey === 'critical' ? { fg: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' } :
    sevKey === 'high'     ? { fg: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' } :
    sevKey === 'medium'   ? { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' } :
    sevKey === 'low'      ? { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' } :
                            { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' }
  );

  const scopeStyle = (
    bindingScope === 'org'    ? { fg: 'var(--v2-accent-trust)', bg: 'var(--v2-accent-trust-soft)', border: 'var(--v2-accent-trust-soft)' } :
    bindingScope === 'tenant' ? { fg: 'var(--v2-action)', bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)' } :
    bindingScope === 'agent'  ? { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' } :
                                { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' }
  );

  // Inline row — label on top, value below. Stacking instead of
  // side-by-side because the inspector pane is only ~300px wide and a
  // 130px fixed label column left the value cell narrow enough to
  // truncate UUIDs and long rule descriptions. Stacked layout gives
  // the value the full width of the row.
  const row = (label, value, opts = {}) => {
    const empty = value == null || value === '' || value === false;
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '8px 0',
        borderBottom: '1px solid var(--v2-bg-muted)',
        minWidth: 0,
      }}>
        <div style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: 'var(--v2-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 12.5,
          color: empty ? 'var(--v2-text-disabled)' : 'var(--v2-text-primary)',
          fontStyle: empty ? 'italic' : 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          minWidth: 0,
        }}>
          {empty ? '—' : (opts.render ? opts.render(value) : String(value))}
        </div>
      </div>
    );
  };

  const pill = (text, style) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      color: style.fg,
      background: style.bg,
      border: `1px solid ${style.border}`,
    }}>
      {text}
    </span>
  );

  return (
    <div style={{ padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, color: 'var(--v2-text-muted)', textTransform: 'uppercase',
          letterSpacing: 0.5, fontWeight: 600,
        }}>
          Enforced policy rule
        </div>
        <div style={{ fontSize: 14, color: 'var(--v2-text-primary)', fontWeight: 700, marginTop: 4 }}>
          {ruleName || (sourceTag ? sourceTag.replace(/_/g, ' ').toLowerCase() : 'unnamed rule')}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {bindingScope && pill(
            `${bindingScope.toUpperCase()} scope`,
            scopeStyle,
          )}
          {ruleSeverity && pill(
            ruleSeverity.toString().toUpperCase(),
            sevStyle,
          )}
          {ruleEnforcementPoint && pill(
            `${ruleEnforcementPoint.toString().toUpperCase()} gate`,
            { fg: 'var(--v2-accent-cyan)', bg: 'var(--v2-accent-cyan-soft)', border: 'var(--v2-accent-cyan-soft)' },
          )}
          {activationStatus && activationStatus !== 'active' && pill(
            activationStatus.toString().toUpperCase(),
            { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' },
          )}
        </div>
      </div>

      {ruleDescription && (
        <div style={{
          padding: '10px 12px',
          borderRadius: 6,
          background: 'var(--v2-bg-elevated)',
          border: '1px solid var(--v2-border-default)',
          fontSize: 12.5,
          color: 'var(--v2-text-primary)',
          lineHeight: 1.5,
          marginBottom: 12,
          /* G298 round 2 — long rule descriptions / no-space copy needs
             explicit wrap rules; the default `normal` whiteSpace + no
             overflow-wrap let long words push past the inspector edge. */
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          minWidth: 0,
        }}>
          {ruleDescription}
        </div>
      )}

      {/* Fact sheet — every binding/rule attribute the materialiser stamped. */}
      <div style={{
        border: '1px solid var(--v2-border-default)',
        borderRadius: 6,
        padding: '0 10px',
        background: 'var(--v2-bg-surface)',
      }}>
        {row('Category', ruleCategory)}
        {row('Severity', ruleSeverity)}
        {row('Enforcement', ruleEnforcementPoint, {
          render: (v) => v === 'pre'  ? 'Pre-execution (blocks invocation before it runs)' :
                         v === 'post' ? 'Post-execution (inspects result before commit)' :
                                        String(v),
        })}
        {row('Scope', bindingScope, {
          render: (v) => v === 'org'    ? 'Organisation-wide (applies to every agent in the org)' :
                         v === 'tenant' ? 'Tenant-wide (applies to every agent in this tenant)' :
                         v === 'agent'  ? 'Agent-specific (applies only to this agent)' :
                                          String(v),
        })}
        {row('Inherited from', inheritedFrom)}
        {row('Source', sourceTag, {
          render: (v) => String(v).replace(/_/g, ' ').toLowerCase(),
        })}
        {row('Activation', activationStatus)}
        {row('Framework binding', frameworkBindingId)}
        {row('Rule ID', ruleId, {
          render: (v) => (
            <code style={{
              fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
              fontSize: 10.5,
              color: 'var(--v2-text-secondary)',
            }}>{v}</code>
          ),
        })}
        {row('Binding ID', bindingId, {
          render: (v) => (
            <code style={{
              fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
              fontSize: 10.5,
              color: 'var(--v2-text-secondary)',
            }}>{v}</code>
          ),
        })}
      </div>

      <div style={{
        marginTop: 12,
        padding: '8px 10px',
        borderRadius: 6,
        background: 'var(--v2-accent-trust-soft)',
        border: '1px solid var(--v2-accent-trust-soft)',
        fontSize: 11, color: 'var(--v2-accent-trust)',
        lineHeight: 1.45,
        /* G298 round 2 — long fully-qualified class names like
           "PolicyAssignmentService.getInheritedBindings" do NOT wrap by
           default (CSS treats them as single words). Without these
           three properties the <code> element pushes the source-block
           past the inspector's right edge — visible in the screenshot
           as the right pill of the panel clipped. */
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0,
      }}>
        <span style={{ fontWeight: 700, marginRight: 4 }}>Source:</span>
        <code style={{
          fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 10.5,
          wordBreak: 'break-all', overflowWrap: 'anywhere',
        }}>
          PolicyAssignmentService.getInheritedBindings
        </code>{' '}
        → buildAgentSkillsDag.policyLayer. This is a materialised (read-only)
        gate — its position and identity reflect the org/tenant/agent
        bindings resolved at canvas render time, not an operator-added node.
        To change which rules fire here, edit the binding in Policy Config,
        not this canvas.
      </div>
    </div>
  );
}

/* G293 — SkillTab
 *
 * Materialised skill nodes (config.agentSkillId present) come from
 * buildAgentSkillsDag with skill identity + AGENT_EXECUTION_MODEL §2.2
 * execution-logic fields stamped into node.config:
 *   - identity: skillName, skillKind, autonomy, riskLevel, inferenceMode,
 *               category, description, hybridSegment, cognitiveAvailability
 *   - execution-logic: callability, preconditions, parallelGroup,
 *                      retryPolicy, timeoutMs, idempotencyKeyTemplate,
 *                      rateLimits
 *
 * Without this panel an operator inspecting a canvas with 8 generic
 * `standard`-typed skill nodes sees only "Node ID / Type=standard /
 * Description" on the General tab — zero signal about what each skill
 * does or how the runtime treats it.
 *
 * Mirrors the RuleTab layout (G290) for visual consistency: title +
 * identity pills + description block + fact-sheet rows + source note.
 */
function SkillTab({ node }) {
  const cfg = (node && node.config) || {};
  const skillName = cfg.skillName || node?.label || null;
  const agentSkillId = cfg.agentSkillId || null;
  const skillKind = cfg.skillKind || null;
  const description = cfg.description || null;
  const category = cfg.category || null;
  const autonomy = cfg.autonomy || null;
  const riskLevel = cfg.riskLevel || null;
  const inferenceMode = cfg.inferenceMode || null;
  const callability = cfg.callability || null;
  const preconditions = Array.isArray(cfg.preconditions) ? cfg.preconditions
                     : (cfg.preconditions ? [cfg.preconditions] : []);
  const parallelGroup = cfg.parallelGroup || null;
  const retryPolicy = cfg.retryPolicy || null;
  const timeoutMs = cfg.timeoutMs || null;
  const idempotencyKeyTemplate = cfg.idempotencyKeyTemplate || null;
  const rateLimits = cfg.rateLimits || null;
  const hybridSegment = cfg.hybridSegment || null;
  const cognitiveAvailability = cfg.cognitiveAvailability;

  /* Severity-tone palette — matches RuleTab/policy gates so the same
   * risk colour reads the same everywhere on the canvas inspector. */
  const riskKey = (riskLevel || '').toString().toLowerCase();
  const riskStyle = (
    riskKey === 'critical' ? { fg: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' } :
    riskKey === 'high'     ? { fg: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' } :
    riskKey === 'medium'   ? { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' } :
    riskKey === 'low'      ? { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' } :
                             { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' }
  );

  /* Callability-mode palette — encodes runtime semantics:
   *   always   = LLM may pick whenever it wants (green)
   *   guarded  = preconditions must hold (amber)
   *   startup  = runs once at conversation start (blue)
   *   manual   = human-triggered only (slate)
   */
  const callKey = (callability || '').toLowerCase();
  const callStyle = (
    callKey === 'always'  ? { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' } :
    callKey === 'guarded' ? { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' } :
    callKey === 'startup' ? { fg: 'var(--v2-action)', bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)' } :
    callKey === 'manual'  ? { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' } :
                            { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' }
  );

  /* Autonomy palette — green for fully autonomous, amber for HITL,
   * slate for fully human-controlled. */
  const autKey = (autonomy || '').toUpperCase();
  const autStyle = (
    autKey === 'FULL' || autKey === 'AUTONOMOUS' ? { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' } :
    autKey === 'SUPERVISED' || autKey === 'HUMAN_IN_LOOP' ? { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' } :
    autKey === 'HUMAN_CONTROLLED' ? { fg: 'var(--v2-action)', bg: 'var(--v2-action-soft)', border: 'var(--v2-action-soft)' } :
                                    { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' }
  );

  // G298 — Stacked label-above-value layout. Side-by-side was overflowing
  // the ~300px inspector pane on long UUIDs / connector_skill metadata.
  // Stacked gives the value the full row width and lets overflowWrap
  // anywhere break long IDs at any char.
  const row = (label, value, opts = {}) => {
    const empty = value == null || value === '' || value === false ||
                  (Array.isArray(value) && value.length === 0);
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0',
        borderBottom: '1px solid var(--v2-bg-muted)', minWidth: 0,
      }}>
        <div style={{
          fontSize: 10.5, fontWeight: 700, color: 'var(--v2-text-muted)',
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 12.5,
          color: empty ? 'var(--v2-text-disabled)' : 'var(--v2-text-primary)',
          fontStyle: empty ? 'italic' : 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          minWidth: 0,
        }}>
          {empty ? '—' : (opts.render ? opts.render(value) : String(value))}
        </div>
      </div>
    );
  };

  const pill = (text, style) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600,
      color: style.fg, background: style.bg, border: `1px solid ${style.border}`,
    }}>
      {text}
    </span>
  );

  const codeBlock = (value) => (
    <pre style={{
      margin: 0, padding: '6px 8px', borderRadius: 4,
      background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)',
      fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
      fontSize: 10.5, color: 'var(--v2-text-primary)', lineHeight: 1.4,
      /* G298 round 2 — pre-wrap respects newlines AND wraps long
         lines; overflowWrap:anywhere lets JSON values like long
         SpEL expressions or rate-limit blobs wrap inside the
         300px inspector. */
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      maxWidth: '100%',
      minWidth: 0,
    }}>
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );

  return (
    <div style={{ padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, color: 'var(--v2-text-muted)', textTransform: 'uppercase',
          letterSpacing: 0.5, fontWeight: 600,
        }}>
          Agent skill
        </div>
        <div style={{ fontSize: 14, color: 'var(--v2-text-primary)', fontWeight: 700, marginTop: 4 }}>
          {skillName || 'Unnamed skill'}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {skillKind && pill(skillKind.toString().toUpperCase(), { fg: 'var(--v2-accent-trust)', bg: 'var(--v2-accent-trust-soft)', border: 'var(--v2-accent-trust-soft)' })}
          {riskLevel && pill(`${riskLevel.toString().toUpperCase()} risk`, riskStyle)}
          {autonomy && pill(autonomy.toString().replace(/_/g, ' '), autStyle)}
          {callability && pill(`callable: ${callability}`, callStyle)}
          {hybridSegment && pill(`hybrid ${hybridSegment}`, { fg: 'var(--v2-accent-cyan)', bg: 'var(--v2-accent-cyan-soft)', border: 'var(--v2-accent-cyan-soft)' })}
          {cognitiveAvailability && pill('cognitive tool', { fg: 'var(--v2-accent-trust)', bg: 'var(--v2-accent-trust-soft)', border: 'var(--v2-accent-trust-soft)' })}
        </div>
      </div>

      {description && (
        <div style={{
          padding: '10px 12px', borderRadius: 6,
          background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)',
          fontSize: 12.5, color: 'var(--v2-text-primary)', lineHeight: 1.5, marginBottom: 12,
          /* G298 round 2 — wrap long skill descriptions so they don't
             push the inspector past its allocated width. */
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          minWidth: 0,
        }}>
          {description}
        </div>
      )}

      {/* Identity — what the skill IS. */}
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: 'var(--v2-text-disabled)',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
      }}>
        Identity
      </div>
      <div style={{
        border: '1px solid var(--v2-border-default)', borderRadius: 6,
        padding: '0 10px', background: 'var(--v2-bg-surface)', marginBottom: 12,
      }}>
        {row('Category', category)}
        {row('Risk level', riskLevel)}
        {row('Autonomy', autonomy, {
          render: (v) => {
            const up = v.toString().toUpperCase();
            return up === 'FULL' || up === 'AUTONOMOUS'
              ? 'Fully autonomous — runs without human approval'
              : up === 'SUPERVISED' || up === 'HUMAN_IN_LOOP'
              ? 'Human-in-loop — approval required before invocation'
              : up === 'HUMAN_CONTROLLED'
              ? 'Human-controlled — operator triggers explicitly'
              : v.toString();
          },
        })}
        {row('Inference mode', inferenceMode, {
          render: (v) => v.toString().replace(/_/g, ' ').toLowerCase(),
        })}
        {row('Skill kind', skillKind)}
        {row('Agent skill ID', agentSkillId, {
          render: (v) => (
            <code style={{
              fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
              fontSize: 10.5, color: 'var(--v2-text-secondary)',
              /* G298 round 2 — UUIDs (36 chars, no spaces) need
                 break-all so they wrap inside the narrow inspector. */
              wordBreak: 'break-all', overflowWrap: 'anywhere',
              display: 'inline-block', maxWidth: '100%',
            }}>{v}</code>
          ),
        })}
      </div>

      {/* Execution logic — how the runtime invokes it (AGENT_EXECUTION_MODEL §2.2). */}
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: 'var(--v2-text-disabled)',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
      }}>
        Execution logic (§2.2)
      </div>
      <div style={{
        border: '1px solid var(--v2-border-default)', borderRadius: 6,
        padding: '0 10px', background: 'var(--v2-bg-surface)', marginBottom: 12,
      }}>
        {row('Callability', callability, {
          render: (v) => v === 'always'  ? 'always — LLM may pick this skill whenever'
                       : v === 'guarded' ? 'guarded — preconditions must evaluate true first'
                       : v === 'startup' ? 'startup — runs once at conversation start'
                       : v === 'manual'  ? 'manual — never auto-invoked, human triggers it'
                       : String(v),
        })}
        {row('Preconditions', preconditions.length > 0 ? preconditions : null, {
          render: (arr) => codeBlock(arr.join('\n')),
        })}
        {row('Parallel group', parallelGroup, {
          render: (v) => (
            <code style={{
              fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
              fontSize: 10.5, color: 'var(--v2-text-secondary)',
              /* G298 round 2 — UUIDs (36 chars, no spaces) need
                 break-all so they wrap inside the narrow inspector. */
              wordBreak: 'break-all', overflowWrap: 'anywhere',
              display: 'inline-block', maxWidth: '100%',
            }}>{v}</code>
          ),
        })}
        {row('Timeout', timeoutMs, {
          render: (v) => `${Number(v).toLocaleString()} ms`,
        })}
        {row('Retry policy', retryPolicy, { render: (v) => codeBlock(v) })}
        {row('Idempotency key', idempotencyKeyTemplate, {
          render: (v) => (
            <code style={{
              fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
              fontSize: 10.5, color: 'var(--v2-text-secondary)',
              /* G298 round 2 — UUIDs (36 chars, no spaces) need
                 break-all so they wrap inside the narrow inspector. */
              wordBreak: 'break-all', overflowWrap: 'anywhere',
              display: 'inline-block', maxWidth: '100%',
            }}>{v}</code>
          ),
        })}
        {row('Rate limits', rateLimits, { render: (v) => codeBlock(v) })}
      </div>

      <div style={{
        padding: '8px 10px', borderRadius: 6,
        background: 'var(--v2-accent-trust-soft)', border: '1px solid var(--v2-accent-trust-soft)',
        fontSize: 11, color: 'var(--v2-accent-trust)', lineHeight: 1.45,
        /* G298 round 2 — wrap long words inside the source note. */
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0,
      }}>
        <span style={{ fontWeight: 700, marginRight: 4 }}>Source:</span>
        Stamped into config by{' '}
        <code style={{
          fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 10.5,
          wordBreak: 'break-all', overflowWrap: 'anywhere',
        }}>
          buildAgentSkillsDag
        </code>{' '}
        from the agent's <code style={{
          fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 10.5,
          wordBreak: 'break-all', overflowWrap: 'anywhere',
        }}>skills</code>{' '}
        array. To edit the skill itself, open the agent's Build tab and the
        change will reflect here after re-materialise. Execution-logic
        fields (§2.2) are the dials the cognitive runtime reads on every
        invocation.
      </div>
    </div>
  );
}

function StatsTab({ node }) {
  const stats = (node && node.config && node.config.executionStats) || {};
  const fmtMs = (v) => (v == null || Number.isNaN(Number(v)))
    ? '—'
    : `${Math.round(Number(v))} ms`;
  const fmtPct = (v) => (v == null || Number.isNaN(Number(v)))
    ? '—'
    : `${(Number(v) * 100).toFixed(1)}%`;
  const fmtInt = (v) => (v == null || Number.isNaN(Number(v)))
    ? '—'
    : Number(v).toLocaleString();
  const fmtTs = (v) => {
    if (!v) return 'never';
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const now = Date.now();
      const diff = now - d.getTime();
      const m = Math.round(diff / 60000);
      if (m < 1) return 'just now';
      if (m < 60) return `${m}m ago`;
      const h = Math.round(m / 60);
      if (h < 24) return `${h}h ago`;
      const days = Math.round(h / 24);
      if (days < 30) return `${days}d ago`;
      return d.toISOString().slice(0, 10);
    } catch (_) {
      return String(v);
    }
  };

  const count = Number(stats.count) || 0;
  const errorRate = stats.errorRate != null ? Number(stats.errorRate) : null;
  // Threshold-driven tone: ≥5% errors reads red, ≥1% reads amber, else green.
  // Same buckets the ExecStatsBadge uses in V2Leaf so the canvas and the
  // panel agree on "is this skill healthy".
  const errorTone = errorRate == null
    ? 'muted'
    : errorRate >= 0.05 ? 'danger'
    : errorRate >= 0.01 ? 'warn'
    : 'success';
  const errorToneStyle = {
    danger:  { fg: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' },
    warn:    { fg: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' },
    success: { fg: 'var(--v2-success)', bg: 'var(--v2-success-soft)', border: 'var(--v2-success-soft)' },
    muted:   { fg: 'var(--v2-text-secondary)', bg: 'var(--v2-bg-muted)', border: 'var(--v2-border-default)' },
  }[errorTone];

  const tile = (label, value, hint) => (
    <div style={{
      flex: '1 1 80px', minWidth: 80,
      padding: '8px 10px',
      borderRadius: 6,
      border: '1px solid var(--v2-border-default)',
      background: 'var(--v2-bg-elevated)',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-muted)',
                    textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--v2-text-primary)', marginTop: 4 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 10.5, color: 'var(--v2-text-disabled)', marginTop: 2 }}>{hint}</div>
      )}
    </div>
  );

  const skillName = stats.skillName || node?.config?.skillName || node?.label || 'Skill';
  const windowDays = Number(stats.windowDays) || 30;

  return (
    <div style={{ padding: 12 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', textTransform: 'uppercase',
                      letterSpacing: 0.5, fontWeight: 600 }}>
          Execution stats
        </div>
        <div style={{ fontSize: 13, color: 'var(--v2-text-primary)', fontWeight: 600, marginTop: 2 }}>
          {skillName}
        </div>
        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 2 }}>
          Last {windowDays} day{windowDays === 1 ? '' : 's'} · server-computed
        </div>
      </div>

      {count === 0 ? (
        <div style={{
          padding: '14px 12px',
          borderRadius: 6,
          background: 'var(--v2-bg-elevated)',
          border: '1px dashed var(--v2-border-default)',
          fontSize: 12, color: 'var(--v2-text-muted)',
          lineHeight: 1.5,
        }}>
          No invocations recorded in this window. Run the skill via Test Run
          or have a published agent invoke it; stats refresh on the next
          canvas materialisation.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {tile('Invocations', fmtInt(stats.count), `≈ ${(count / windowDays).toFixed(1)}/day`)}
            {tile('p50 latency', fmtMs(stats.p50), 'median')}
            {tile('p95 latency', fmtMs(stats.p95), '95th pctile')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {tile('Errors', fmtInt(stats.errors), null)}
            {tile('Blocked', fmtInt(stats.blocked), 'pre / post policy')}
            <div style={{
              flex: '1 1 80px', minWidth: 80,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid ' + errorToneStyle.border,
              background: errorToneStyle.bg,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: errorToneStyle.fg,
                            textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Error rate
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: errorToneStyle.fg, marginTop: 4 }}>
                {fmtPct(stats.errorRate)}
              </div>
              <div style={{ fontSize: 10.5, color: errorToneStyle.fg, marginTop: 2, opacity: 0.8 }}>
                {errorTone === 'danger' ? '⚠ above 5% threshold' :
                 errorTone === 'warn'   ? 'above 1% threshold'  :
                 errorTone === 'success'? 'within target'        : ''}
              </div>
            </div>
          </div>
          <div style={{
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--v2-bg-elevated)',
            border: '1px solid var(--v2-border-default)',
            fontSize: 12, color: 'var(--v2-text-primary)',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--v2-text-muted)', marginRight: 6 }}>
              Last invoked:
            </span>
            {fmtTs(stats.lastInvokedAt)}
          </div>
        </>
      )}

      <div style={{
        marginTop: 12,
        padding: '8px 10px',
        borderRadius: 6,
        background: 'var(--v2-accent-trust-soft)',
        border: '1px solid var(--v2-accent-trust-soft)',
        fontSize: 11, color: 'var(--v2-accent-trust)',
        lineHeight: 1.45,
        /* G298 round 2 — wrap the long FQCN code identifier
           "PolicyAuditService.computeAgentSkillExecutionStats" which
           otherwise pushes the source note past the inspector edge
           (visible in user's screenshot as "...computeAgentSkillE"
           clipped). */
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0,
      }}>
        <span style={{ fontWeight: 700, marginRight: 4 }}>Source:</span>
        <code style={{
          fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 10.5,
          wordBreak: 'break-all', overflowWrap: 'anywhere',
        }}>
          PolicyAuditService.computeAgentSkillExecutionStats
        </code>{' '}
        via the G263 canvas-materialisation aggregator. Audit events follow
        the cognitive taxonomy in AGENT_EXECUTION_MODEL §6 — every invocation
        contributes to count / latency percentiles; pre- and post-policy
        denials contribute to blocked.
      </div>
    </div>
  );
}

function ExecutionTab({ executionState, nodeId, nodeLabel, onDebugNode, node, onUpdate }) {
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [appliedFixes, setAppliedFixes] = useState(new Set()); // track which fixes have been applied
  const [showSavePrompt, setShowSavePrompt] = useState(false); // show save & re-execute prompt after fix

  const status = executionState?.status || 'unknown';
  const statusColor = {
    pending: 'var(--v2-warning)',
    running: 'var(--v2-action)',
    completed: 'var(--v2-text-secondary)',
    failed: 'var(--v2-danger)',
    skipped: 'var(--v2-text-disabled)',
  }[status] || 'var(--v2-text-muted)';

  const handleDebugClick = async () => {
    if (!onDebugNode || !nodeId) return;
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const result = await onDebugNode(nodeId);
      setDebugResult(result);
    } finally {
      setDebugLoading(false);
    }
  };

  // Apply a set of config fixes to the current node
  const applyConfigFixes = (fixes, fixKey) => {
    if (!fixes || !onUpdate || !node) return;
    const updated = { ...node };
    // Known config-level keys that the AI might return without the "config." prefix
    const configKeys = new Set([
      'expression', 'conditionExpression', 'action', 'connectorId', 'method',
      'endpoint', 'table', 'body', 'headers', 'params', 'skill', 'agentId',
      'outputVariable', 'timeout', 'retryPolicy', 'selectedSkill', 'prompt',
    ]);
    Object.entries(fixes).forEach(([key, value]) => {
      // Support dotted paths like "config.action", "inputMapping.table"
      const parts = key.split('.');
      if (parts.length === 1) {
        // If it's a known config key, put it under config; otherwise top-level
        if (configKeys.has(key)) {
          updated.config = { ...(updated.config || {}), [key]: value };
        } else {
          updated[key] = value;
        }
      } else if (parts[0] === 'config') {
        // Nested under config
        updated.config = { ...(updated.config || {}) };
        if (parts.length === 2) {
          updated.config[parts[1]] = value;
        } else {
          // Deep path like config.params.table
          let target = updated.config;
          for (let i = 1; i < parts.length - 1; i++) {
            target[parts[i]] = { ...(target[parts[i]] || {}) };
            target = target[parts[i]];
          }
          target[parts[parts.length - 1]] = value;
        }
      } else if (parts[0] === 'inputMapping') {
        updated.inputMapping = { ...(updated.inputMapping || {}) };
        if (parts.length === 2) {
          updated.inputMapping[parts[1]] = value;
        } else {
          let target = updated.inputMapping;
          for (let i = 1; i < parts.length - 1; i++) {
            target[parts[i]] = { ...(target[parts[i]] || {}) };
            target = target[parts[i]];
          }
          target[parts[parts.length - 1]] = value;
        }
      } else {
        // Arbitrary top-level object path
        updated[parts[0]] = { ...(updated[parts[0]] || {}) };
        let target = updated[parts[0]];
        for (let i = 1; i < parts.length - 1; i++) {
          target[parts[i]] = { ...(target[parts[i]] || {}) };
          target = target[parts[i]];
        }
        target[parts[parts.length - 1]] = value;
      }
    });
    // Apply the merged update
    const { id, ...updates } = updated;
    onUpdate(updates);
    setAppliedFixes((prev) => new Set(prev).add(fixKey));
    setShowSavePrompt(true);
  };

  // Apply all fixes from all nodes in the debug result
  const applyAllFixes = () => {
    if (!debugResult?.nodeAnalysis || !onUpdate || !node) return;
    // Collect all configFixes from nodes matching this nodeId
    const allFixes = {};
    debugResult.nodeAnalysis.forEach((na) => {
      if (na.nodeId === nodeId && na.configFixes) {
        Object.assign(allFixes, na.configFixes);
      }
    });
    // Also apply top-level configFixes if present
    if (debugResult.configFixes) {
      Object.assign(allFixes, debugResult.configFixes);
    }
    if (Object.keys(allFixes).length > 0) {
      applyConfigFixes(allFixes, '_all_');
    }
  };

  // Format the debug result into readable sections
  const renderDebugResult = () => {
    if (!debugResult) return null;

    if (debugResult.error) {
      return (
        <div style={{ padding: 10, background: 'var(--v2-danger-soft)', border: '1px solid var(--v2-danger-soft)', borderRadius: 6, fontSize: 11, color: 'var(--v2-danger)' }}>
          {debugResult.error}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Summary */}
        {debugResult.summary && (
          <div style={{ padding: 10, background: debugResult.overallHealth === 'critical' ? 'var(--v2-danger-soft)' : debugResult.overallHealth === 'warning' ? 'var(--v2-action-soft)' : 'var(--v2-bg-muted)', border: '1px solid ' + (debugResult.overallHealth === 'critical' ? 'var(--v2-danger-soft)' : debugResult.overallHealth === 'warning' ? 'var(--v2-warning)' : 'var(--v2-text-disabled)'), borderRadius: 6, fontSize: 11, fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {debugResult.overallHealth === 'critical' ? '🔴' : debugResult.overallHealth === 'warning' ? '🟡' : '🟢'} {debugResult.summary}
          </div>
        )}

        {/* Apply All Fixes Button — shown when any configFixes exist */}
        {(() => {
          const hasAnyFixes = (debugResult.configFixes && Object.keys(debugResult.configFixes).length > 0) ||
            (debugResult.nodeAnalysis || []).some((na) => na.nodeId === nodeId && na.configFixes && Object.keys(na.configFixes).length > 0);
          if (!hasAnyFixes) return null;
          const allApplied = appliedFixes.has('_all_');
          return (
            <button
              onClick={applyAllFixes}
              disabled={allApplied}
              style={{
                ...btnBase,
                width: '100%',
                padding: '8px 12px',
                background: allApplied ? 'var(--v2-bg-muted)' : 'var(--v2-success)',
                color: allApplied ? 'var(--v2-text-secondary)' : 'var(--v2-bg-surface)',
                fontWeight: 700,
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderRadius: 6,
                border: allApplied ? '1px solid var(--v2-text-disabled)' : '1px solid var(--v2-success)',
                cursor: allApplied ? 'default' : 'pointer',
              }}
            >
              {allApplied ? <><CheckCircle size={13} /> All Fixes Applied</> : <><Wrench size={13} /> Apply All Fixes to This Node</>}
            </button>
          );
        })()}

        {/* Quick Fixes */}
        {debugResult.quickFixes && debugResult.quickFixes.length > 0 && (
          <div style={{ padding: 10, background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-text-disabled)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-secondary)', marginBottom: 6 }}>⚡ Quick Fixes</div>
            {debugResult.quickFixes.map((fix, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--v2-text-secondary)', marginBottom: 3, paddingLeft: 8, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                {i + 1}. {fix}
              </div>
            ))}
          </div>
        )}

        {/* Per-Node Analysis */}
        {debugResult.nodeAnalysis && debugResult.nodeAnalysis.length > 0 && (
          <div style={{ padding: 10, background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 6 }}>📋 Data Flow Trace</div>
            {debugResult.nodeAnalysis.map((na, i) => {
              const fixKey = `node_${na.nodeId || i}`;
              const hasConfigFixes = na.configFixes && Object.keys(na.configFixes).length > 0;
              const isThisNode = na.nodeId === nodeId;
              const fixApplied = appliedFixes.has(fixKey) || appliedFixes.has('_all_');
              return (
                <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: i < debugResult.nodeAnalysis.length - 1 ? '1px solid var(--v2-border-default)' : 'none' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: na.status === 'error' ? 'var(--v2-danger)' : na.status === 'warning' ? 'var(--v2-warning)' : 'var(--v2-action-strong)', marginBottom: 3 }}>
                    {na.status === 'error' ? '❌' : na.status === 'warning' ? '⚠️' : '✅'} {na.nodeLabel || na.nodeId}
                    {isThisNode && <span style={{ fontSize: 9, color: 'var(--v2-text-muted)', fontWeight: 400, marginLeft: 4 }}>(this node)</span>}
                  </div>
                  {na.issues && na.issues.length > 0 && na.issues.map((issue, j) => (
                    <div key={`i${j}`} style={{ fontSize: 10, color: 'var(--v2-text-muted)', paddingLeft: 16, marginBottom: 2, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>• {issue}</div>
                  ))}
                  {na.recommendations && na.recommendations.length > 0 && na.recommendations.map((rec, j) => (
                    <div key={`r${j}`} style={{ fontSize: 10, color: 'var(--v2-text-secondary)', paddingLeft: 16, marginBottom: 2, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>→ {rec}</div>
                  ))}
                  {/* Per-node config fixes with Apply button */}
                  {hasConfigFixes && (
                    <div style={{ marginTop: 6, marginLeft: 16 }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--v2-text-secondary)', marginBottom: 4 }}>Config Changes:</div>
                      <pre style={{
                        fontSize: 9, fontFamily: 'monospace', color: 'var(--v2-text-primary)',
                        background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-border-default)', borderRadius: 4,
                        padding: '6px 8px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {JSON.stringify(na.configFixes, null, 2)}
                      </pre>
                      {isThisNode && (
                        <button
                          onClick={() => applyConfigFixes(na.configFixes, fixKey)}
                          disabled={fixApplied}
                          style={{
                            ...btnBase,
                            marginTop: 4,
                            padding: '4px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            background: fixApplied ? 'var(--v2-bg-muted)' : 'var(--v2-action)',
                            color: fixApplied ? 'var(--v2-text-muted)' : 'var(--v2-bg-surface)',
                            border: fixApplied ? '1px solid var(--v2-text-disabled)' : '1px solid var(--v2-action-strong)',
                            borderRadius: 4,
                            cursor: fixApplied ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          {fixApplied ? <><CheckCircle size={10} /> Applied</> : <><Wrench size={10} /> Apply Fix</>}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Config Fixes */}
        {debugResult.configFixes && Object.keys(debugResult.configFixes).length > 0 && (
          <div style={{ padding: 10, background: 'var(--v2-bg-muted)', border: '1px solid var(--v2-text-disabled)', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-text-secondary)' }}>🔧 Config Fixes</div>
              {(() => {
                const fixApplied = appliedFixes.has('_toplevel_') || appliedFixes.has('_all_');
                return (
                  <button
                    onClick={() => applyConfigFixes(debugResult.configFixes, '_toplevel_')}
                    disabled={fixApplied}
                    style={{
                      ...btnBase,
                      padding: '3px 8px',
                      fontSize: 9,
                      fontWeight: 600,
                      background: fixApplied ? 'var(--v2-bg-muted)' : 'var(--v2-action)',
                      color: fixApplied ? 'var(--v2-text-muted)' : 'var(--v2-bg-surface)',
                      border: fixApplied ? '1px solid var(--v2-text-disabled)' : '1px solid var(--v2-action-strong)',
                      borderRadius: 4,
                      cursor: fixApplied ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    {fixApplied ? <><CheckCircle size={9} /> Applied</> : <><Wrench size={9} /> Apply</>}
                  </button>
                );
              })()}
            </div>
            <pre style={{ fontSize: 10, color: 'var(--v2-text-primary)', fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(debugResult.configFixes, null, 2)}
            </pre>
          </div>
        )}

        {/* Flow Issues */}
        {debugResult.flowIssues && debugResult.flowIssues.length > 0 && (
          <div style={{ padding: 10, background: 'var(--v2-action-soft)', border: '1px solid var(--v2-warning)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--v2-warning)', marginBottom: 6 }}>🔗 Flow Issues</div>
            {debugResult.flowIssues.map((issue, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--v2-warning)', marginBottom: 3, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>• {issue}</div>
            ))}
          </div>
        )}

        {/* Fallback: plain text analysis */}
        {debugResult.analysis && !debugResult.summary && !debugResult.nodeAnalysis && (
          <div style={{ padding: 10, background: 'var(--v2-bg-elevated)', border: '1px solid var(--v2-border-default)', borderRadius: 6 }}>
            <pre style={{ fontSize: 10, color: 'var(--v2-text-primary)', fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>
              {debugResult.analysis}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Status Banner */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>Status</h4>
        <div style={{
          padding: '10px 12px',
          background: statusColor + '20',
          border: `1px solid ${statusColor}`,
          borderRadius: 4,
          color: statusColor,
          fontWeight: 600,
          fontSize: 12,
          textAlign: 'center',
        }}>
          {status.toUpperCase()}
        </div>
      </div>

      {/* Timing */}
      {executionState?.startedAt && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Started</label>
          <div style={{ fontSize: 12, color: 'var(--v2-text-primary)' }}>
            {new Date(executionState.startedAt).toLocaleString()}
          </div>
        </div>
      )}

      {executionState?.completedAt && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Completed</label>
          <div style={{ fontSize: 12, color: 'var(--v2-text-primary)' }}>
            {new Date(executionState.completedAt).toLocaleString()}
          </div>
        </div>
      )}

      {executionState?.durationMs && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Duration</label>
          <div style={{ fontSize: 12, color: 'var(--v2-text-primary)' }}>
            {(executionState.durationMs / 1000).toFixed(2)}s
          </div>
        </div>
      )}

      {/* Error */}
      {executionState?.error && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Error</label>
          <div style={{
            fontSize: 11,
            color: 'var(--v2-danger)',
            padding: '8px',
            background: 'var(--v2-danger-soft)',
            borderRadius: 4,
            fontFamily: 'monospace',
            wordBreak: 'break-word',
          }}>
            {executionState.error}
          </div>
        </div>
      )}

      {/* Input Data (what this node received) */}
      {executionState?.input && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Input Received</label>
          <pre style={{
            fontSize: 10,
            color: 'var(--v2-text-primary)',
            padding: '8px',
            background: 'var(--v2-bg-muted)',
            border: '1px solid var(--v2-text-disabled)',
            borderRadius: 4,
            fontFamily: 'monospace',
            overflow: 'auto',
            maxHeight: 150,
            margin: 0,
          }}>
            {JSON.stringify(executionState.input, null, 2)}
          </pre>
        </div>
      )}

      {/* Output Data */}
      {executionState?.output && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Output Produced</label>
          <pre style={{
            fontSize: 10,
            color: 'var(--v2-text-primary)',
            padding: '8px',
            background: 'var(--v2-bg-muted)',
            border: '1px solid var(--v2-text-disabled)',
            borderRadius: 4,
            fontFamily: 'monospace',
            overflow: 'auto',
            maxHeight: 200,
            margin: 0,
          }}>
            {JSON.stringify(executionState.output, null, 2)}
          </pre>
        </div>
      )}

      {/* Debug This Node Button */}
      <div style={{ marginTop: 16, marginBottom: 12 }}>
        <button
          onClick={handleDebugClick}
          disabled={debugLoading}
          style={{
            ...btnBase,
            background: debugLoading ? 'var(--v2-action-soft)' : 'var(--v2-warning)',
            color: 'var(--v2-warning)',
            width: '100%',
            padding: '10px 12px',
            fontWeight: 700,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            opacity: debugLoading ? 0.7 : 1,
            cursor: debugLoading ? 'wait' : 'pointer',
          }}
        >
          {debugLoading ? (
            <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing data flow...</>
          ) : (
            <><Bug size={14} /> Debug This Node</>
          )}
        </button>
        <div style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginTop: 4, textAlign: 'center' }}>
          Traces input → config → execution → output for this node
        </div>
      </div>

      {/* Debug Results */}
      {(debugResult || debugLoading) && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>
            🔍 Debug Analysis {nodeLabel ? `— ${nodeLabel}` : ''}
          </label>
          {debugLoading ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--v2-warning)', fontSize: 11 }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginBottom: 4 }} />
              <div>AI is tracing the data flow through upstream nodes...</div>
            </div>
          ) : (
            renderDebugResult()
          )}
        </div>
      )}

      {/* Save & Re-Execute Prompt */}
      {showSavePrompt && appliedFixes.size > 0 && (
        <div style={{
          marginTop: 12,
          padding: '10px 12px',
          background: 'var(--v2-warning-soft)',
          border: '1px solid var(--v2-warning-soft)',
          borderRadius: 6,
          fontSize: 11,
          color: 'var(--v2-warning)',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertTriangle size={12} /> Fixes applied to node config
          </div>
          <div style={{ lineHeight: 1.5 }}>
            Switch to <strong>Design</strong> tab to review the changes, then <strong>Save</strong> the workflow and <strong>Execute</strong> again to verify the fix.
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectionPropertiesPanel({
  connection,
  onDelete,
  onReroute,
  nodes,
  connections,
  getNodePorts,
}) {
  // P1-C ported: edit-in-place reroute mode (cycle / self-loop / duplicate
  // pre-validated; canConnect runs again at apply time as the safety net).
  const [editing, setEditing] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(connection?.from || '');
  const [draftFromPort, setDraftFromPort] = React.useState(connection?.fromPort || 'default');
  const [draftTo, setDraftTo] = React.useState(connection?.to || '');
  const [draftToPort, setDraftToPort] = React.useState(connection?.toPort || 'default');

  // Re-keying via a ref means a parent re-render that produces a fresh
  // `connection` object reference (find() returns a new ref each render)
  // doesn't clobber the user's in-progress edit.
  const lastInitConnIdRef = React.useRef(null);
  React.useEffect(() => {
    const id = connection?.id || null;
    if (id === lastInitConnIdRef.current) return;
    lastInitConnIdRef.current = id;
    if (!connection) return;
    setEditing(false);
    setDraftFrom(connection.from);
    setDraftFromPort(connection.fromPort || 'default');
    setDraftTo(connection.to);
    setDraftToPort(connection.toPort || 'default');
  }, [connection]);

  if (!connection) return <EmptyPropertiesPanel />;

  const fromNode = nodes.find((n) => n.id === connection.from);
  const toNode = nodes.find((n) => n.id === connection.to);

  const portsFor = (nodeId, side) => {
    const n = nodes.find((x) => x.id === nodeId);
    if (!n || typeof getNodePorts !== 'function') return ['default'];
    const cfg = getNodePorts(n.id);
    if (!cfg) return ['default'];
    const list = side === 'output' ? (cfg.outputs || []) : (cfg.inputs || []);
    if (!Array.isArray(list) || list.length === 0) return ['default'];
    return list.map((p) => (typeof p === 'string' ? p : (p && p.name) || 'default'));
  };
  const fromPorts = editing ? portsFor(draftFrom, 'output') : [];
  const toPorts = editing ? portsFor(draftTo, 'input') : [];

  const draftInvalidReason = (() => {
    if (!editing) return null;
    if (!draftFrom || !draftTo) return 'Pick both endpoints.';
    if (draftFrom === draftTo) return 'A connection cannot loop a node to itself.';
    const isDuplicate = (connections || []).some((c) =>
      c.id !== connection.id
      && c.from === draftFrom && c.to === draftTo
      && (c.fromPort || 'default') === (draftFromPort || 'default')
      && (c.toPort || 'default') === (draftToPort || 'default')
    );
    if (isDuplicate) return 'An identical connection already exists.';
    return null;
  })();

  const applyReroute = () => {
    if (draftInvalidReason || !onReroute) return;
    const ok = onReroute(draftFrom, draftFromPort, draftTo, draftToPort);
    if (ok) setEditing(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: 12, borderBottom: '1px solid var(--v2-border-default)', flex: 1, overflowY: 'auto' }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--v2-text-primary)' }}>Connection</h3>

        {!editing ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>From</label>
              <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-primary)', display: 'flex', alignItems: 'center' }}>
                {fromNode?.label || <em style={{ color: 'var(--v2-text-disabled)' }}>missing node</em>}
                <span style={{ color: 'var(--v2-text-disabled)', marginLeft: 'auto', fontSize: 11 }}>→ {connection.fromPort || 'default'}</span>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>To</label>
              <div style={{ ...inputStyle, background: 'var(--v2-bg-muted)', color: 'var(--v2-text-primary)', display: 'flex', alignItems: 'center' }}>
                {toNode?.label || <em style={{ color: 'var(--v2-text-disabled)' }}>missing node</em>}
                <span style={{ color: 'var(--v2-text-disabled)', marginLeft: 'auto', fontSize: 11 }}>{connection.toPort || 'default'} ←</span>
              </div>
            </div>
            {onReroute && (
              <button onClick={() => setEditing(true)} style={{ ...btnBase, width: '100%', marginBottom: 8 }}>
                ↺ Reroute connection
              </button>
            )}
            <button onClick={onDelete} style={{ ...btnBase, background: 'var(--v2-danger-soft)', color: 'var(--v2-danger)', width: '100%' }}>
              <Trash2 size={14} style={{ marginRight: 4 }} />
              Delete Connection
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>From node</label>
              <select
                value={draftFrom}
                onChange={(e) => { setDraftFrom(e.target.value); setDraftFromPort('default'); }}
                style={inputStyle}
              >
                {nodes.map((n) => (<option key={n.id} value={n.id}>{n.label || n.type}</option>))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>From port</label>
              <select value={draftFromPort} onChange={(e) => setDraftFromPort(e.target.value)} style={inputStyle}>
                {fromPorts.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>To node</label>
              <select
                value={draftTo}
                onChange={(e) => { setDraftTo(e.target.value); setDraftToPort('default'); }}
                style={inputStyle}
              >
                {nodes.map((n) => (<option key={n.id} value={n.id}>{n.label || n.type}</option>))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>To port</label>
              <select value={draftToPort} onChange={(e) => setDraftToPort(e.target.value)} style={inputStyle}>
                {toPorts.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {draftInvalidReason && (
              <div style={{
                fontSize: 12, color: 'var(--v2-danger)', marginBottom: 8,
                display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.45,
              }}>
                <span aria-hidden="true">⚠</span>
                <span>{draftInvalidReason}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setEditing(false)} style={{ ...btnBase, flex: 1 }}>Cancel</button>
              <button
                onClick={applyReroute}
                disabled={!!draftInvalidReason}
                style={{ ...btnPrimary, flex: 1, ...(draftInvalidReason ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
              >Apply</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyPropertiesPanel() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: 'var(--v2-text-disabled)',
    }}>
      <Circle size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
      <div style={{ fontSize: 12, textAlign: 'center' }}>
        Select a node or connection to view properties
      </div>
    </div>
  );
}

function Minimap({ nodes, connections, pan, zoom }) {
  if (nodes.length === 0) return null;

  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs) + NODE_W;
  const maxY = Math.max(...ys) + NODE_H;

  const width = 140;
  const height = 100;
  const padding = 8;
  const rangeX = maxX - minX;
  const rangeY = maxY - minY;

  const scaleX = (width - padding * 2) / rangeX;
  const scaleY = (height - padding * 2) / rangeY;
  const scale = Math.min(scaleX, scaleY);

  return (
    <div style={{
      position: 'absolute',
      bottom: 60,
      left: 16,
      background: 'var(--v2-bg-surface)',
      border: '1px solid var(--v2-border-default)',
      borderRadius: 6,
      width,
      height,
      overflow: 'hidden',
    }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Nodes — shape-aware minimap rendering */}
        {nodes.map((node) => {
          const s = getNodeShapeType(node.type || '');
          const nx = padding + (node.x - minX) * scale;
          const ny = padding + (node.y - minY) * scale;
          const nw = Math.max(2, NODE_W * scale * 0.3);
          const nh = Math.max(2, NODE_H * scale * 0.3);
          const fill = s === 'bookend' ? 'var(--v2-text-muted)' : s === 'diamond' ? 'var(--v2-accent-trust)' : 'var(--v2-action)';
          if (s === 'diamond') {
            const cx = nx + nw / 2, cy = ny + nh / 2;
            return <polygon key={node.id} points={`${cx},${ny} ${nx+nw},${cy} ${cx},${ny+nh} ${nx},${cy}`} fill={fill} opacity={0.7} />;
          }
          return <rect key={node.id} x={nx} y={ny} width={nw} height={nh} rx={s === 'bookend' ? nh/2 : 1} fill={fill} opacity={0.6} />;
        })}
      </svg>
    </div>
  );
}

function WorkflowListView({ workflows, selectedId, onSelect, onDelete, onArchive, onUnarchive, showArchived, onToggleArchived }) {
  const visibleWorkflows = showArchived
    ? workflows.filter((wf) => wf.status === 'archived')           // V132 — Archived view: archived only
    : workflows.filter((wf) => wf.status !== 'archived');         // Default: hide archived
  const archivedCount = workflows.filter((wf) => wf.status === 'archived').length;
  const activeCount = workflows.length - archivedCount;

  return (
    <div style={cardStyle}>
      {/* V132 — Header strip. Always render the toggle so archived workflows
          are discoverable even when the count is zero. Active label is primary
          when showing active list; Archived label is primary when in archive
          view. This replaces the old "show only when archivedCount > 0" rule
          which hid the entire feature from first-time users. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px 0', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
          {showArchived
            ? <><strong style={{ color: 'var(--v2-accent-trust)' }}>Archived</strong> workflows ({archivedCount}) — restore to return to active list</>
            : <><strong style={{ color: 'var(--v2-text-primary)' }}>Active</strong> workflows ({activeCount})</>}
        </div>
        <button
          onClick={onToggleArchived}
          style={{
            ...miniBtn,
            fontSize: 11,
            color: showArchived ? 'var(--v2-text-primary)' : 'var(--v2-accent-trust)',
            borderColor: showArchived ? 'var(--v2-border-default)' : 'var(--v2-accent-trust-soft)',
          }}
          title={showArchived ? 'Return to active workflows' : 'View archived workflows'}
        >
          <Archive size={11} style={{ marginRight: 4 }} />
          {showArchived ? `Back to Active (${activeCount})` : `View Archived (${archivedCount})`}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--v2-border-default)' }}>
            <th style={{ ...labelStyle, display: 'table-cell', padding: 12, textAlign: 'left' }}>Name</th>
            <th style={{ ...labelStyle, display: 'table-cell', padding: 12, textAlign: 'left' }}>Version</th>
            <th style={{ ...labelStyle, display: 'table-cell', padding: 12, textAlign: 'left' }}>Status</th>
            <th style={{ ...labelStyle, display: 'table-cell', padding: 12, textAlign: 'left' }}>Updated</th>
            <th style={{ ...labelStyle, display: 'table-cell', padding: 12, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleWorkflows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--v2-text-disabled)', fontSize: 13 }}>
                {showArchived
                  ? 'No archived workflows. Archived workflows will appear here and can be restored.'
                  : 'No active workflows yet. Create one from the Design tab.'}
              </td>
            </tr>
          )}
          {visibleWorkflows.map((wf) => {
            const isArchived = wf.status === 'archived';
            return (
              <tr key={wf.id} style={{ borderBottom: '1px solid var(--v2-border-default)', opacity: isArchived ? 0.55 : 1 }}>
                <td style={{ padding: 12, cursor: 'pointer', color: 'var(--v2-text-primary)', fontWeight: 500 }} onClick={() => onSelect(wf.id)}>
                  {wf.name}
                  {isArchived && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--v2-text-disabled)', fontStyle: 'italic' }}>archived</span>
                  )}
                </td>
                <td style={{ padding: 12, color: 'var(--v2-text-muted)', fontSize: 12 }}>
                  v{wf.version || 1}
                </td>
                <td style={{ padding: 12 }}>
                  <Badge
                    text={wf.status}
                    variant={wf.status === 'published' ? 'success' : wf.status === 'archived' ? 'neutral' : 'info'}
                  />
                </td>
                <td style={{ padding: 12, color: 'var(--v2-text-muted)', fontSize: 12 }}>
                  {wf.updatedAt && new Date(wf.updatedAt).toLocaleDateString()}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                    <button onClick={() => onSelect(wf.id)} style={miniBtn}>
                      Edit
                    </button>
                    {isArchived ? (
                      <button
                        onClick={() => onUnarchive(wf.id)}
                        style={{ ...miniBtn, color: 'var(--v2-accent-trust)', borderColor: 'var(--v2-accent-trust-soft)' }}
                        title="Restore from archive"
                      >
                        <ArchiveRestore size={12} />
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => onArchive(wf.id)}
                          style={{ ...miniBtn, color: 'var(--v2-text-muted)', borderColor: 'var(--v2-border-default)' }}
                          title="Archive workflow"
                        >
                          <Archive size={12} />
                        </button>
                        <button
                          onClick={() => onDelete(wf.id)}
                          style={{ ...miniBtn, color: 'var(--v2-danger)', borderColor: 'var(--v2-danger-soft)' }}
                          title="Delete workflow"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OrchestrationDashboard({ stats, workflows, workflowRuns, onRefresh, onCancelRun }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Workflows', value: workflows.length, color: 'var(--v2-action)' },
          { label: 'Active Runs', value: stats?.activeRuns || 0, color: 'var(--v2-text-secondary)' },
          { label: 'Failed Today', value: stats?.failedToday || 0, color: 'var(--v2-danger)' },
          { label: 'Success Rate', value: `${stats?.successRate || 0}%`, color: 'var(--v2-action)' },
        ].map((stat, idx) => (
          <div key={idx} style={cardStyle}>
            <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 8 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: stat.color }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--v2-text-primary)' }}>
          Recent Runs
        </h3>
        {workflowRuns.length === 0 ? (
          <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, padding: 12, textAlign: 'center' }}>
            No runs yet
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--v2-border-default)' }}>
                <th style={{ padding: 8, textAlign: 'left', fontSize: 11, fontWeight: 600 }}>Workflow</th>
                <th style={{ padding: 8, textAlign: 'left', fontSize: 11, fontWeight: 600 }}>Source</th>
                <th style={{ padding: 8, textAlign: 'left', fontSize: 11, fontWeight: 600 }}>Status</th>
                <th style={{ padding: 8, textAlign: 'left', fontSize: 11, fontWeight: 600 }}>Started</th>
              </tr>
            </thead>
            <tbody>
              {workflowRuns.slice(0, 10).map((run) => {
                const isAgentlet = run.triggerType === 'agentlet';
                return (
                <tr key={run.id} style={{
                  borderBottom: '1px solid var(--v2-border-default)',
                  borderLeft: isAgentlet ? '3px solid var(--v2-action)' : '3px solid transparent',
                  background: isAgentlet ? 'var(--v2-accent-trust-soft)' : 'transparent',
                }}>
                  <td style={{ padding: 8, fontSize: 12, color: 'var(--v2-text-primary)' }}>
                    {workflows.find((w) => w.id === run.workflowId)?.name || 'Unknown'}
                  </td>
                  <td style={{ padding: 8 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
                      padding: '1px 5px', borderRadius: 4,
                      background: isAgentlet ? 'var(--v2-action-soft)' : 'var(--v2-bg-muted)',
                      color: isAgentlet ? 'var(--v2-action)' : 'var(--v2-text-muted)',
                      border: `1px solid ${isAgentlet ? 'var(--v2-accent-trust-soft)' : 'var(--v2-border-default)'}`,
                    }}>
                      {isAgentlet ? 'AGENT' : 'MANUAL'}
                    </span>
                    {isAgentlet && run.triggerAgentName && (
                      <div style={{ fontSize: 9, color: 'var(--v2-action-strong)', marginTop: 2 }}>
                        {run.triggerAgentName}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 8 }}>
                    <RunStatusIcon status={run.status} />
                  </td>
                  <td style={{ padding: 8, fontSize: 11, color: 'var(--v2-text-muted)' }}>
                    {run.createdAt && new Date(run.createdAt).toLocaleString()}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RunStatusIcon({ status }) {
  const color = (() => {
    if (status === 'success' || status === 'completed') return 'var(--v2-text-secondary)';
    if (status === 'failed' || status === 'error' || status === 'rejected') return 'var(--v2-danger)';
    if (status === 'running') return 'var(--v2-action)';
    if (status === 'pending_approval') return 'var(--v2-warning)';
    return 'var(--v2-warning)';
  })();

  const Icon = (() => {
    if (status === 'success' || status === 'completed') return CheckCircle;
    if (status === 'failed' || status === 'error') return XCircle;
    if (status === 'rejected') return XCircle;
    if (status === 'running') return Loader;
    if (status === 'pending_approval') return Shield;
    return Clock;
  })();

  return <Icon size={14} style={{ color, display: 'inline' }} />;
}

// ============================================================================
// EXECUTION TIMELINE — Graphical pipeline view for top half of split canvas
// ============================================================================

/**
 * ExecutionStatusOverlay — Floating header bar shown on the DAG canvas during execution.
 * Displays progress stats, node counts, and run ID as a compact overlay.
 * The actual DAG canvas with color-coded nodes renders behind it.
 */
function ExecutionStatusOverlay({ nodeExecutions, selectedRun }) {
  const total = nodeExecutions.length;
  const completed = nodeExecutions.filter((n) => n.status === 'completed').length;
  const failed = nodeExecutions.filter((n) => n.status === 'failed').length;
  const running = nodeExecutions.filter((n) => n.status === 'running').length;
  // RC-21 (task #1) — Skipped is a TERMINAL node status: the runtime
  // decided not to execute the node (e.g. a SWITCH took the other branch,
  // a condition evaluated false, a parallel leg short-circuited). Skipped
  // nodes are NOT pending — they're done. Counting them toward the
  // progress denominator's "completed work" is the correct semantic:
  // the workflow has fully accounted for them and the run is one step
  // closer to terminal.
  const skipped = nodeExecutions.filter(
    (n) => n.status === 'skipped' || n.status === 'SKIPPED'
  ).length;
  const pending = total - completed - failed - running - skipped;

  // Progress snaps to 100% when the OVERALL run is in a terminal state.
  // A terminal run by definition has resolved every node into one of
  // {completed, failed, skipped, terminated} — the per-node aggregation
  // above usually matches, but on a slow socket the run-status update
  // can land before the last node-status update, so we honour the
  // authoritative run-level status to avoid the "completed run shows
  // 67%" cosmetic bug the operator surfaced.
  const runStatus = String(selectedRun?.status || '').toUpperCase();
  const RUN_TERMINAL = new Set([
    'COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED',
    'TERMINATED', 'TIMED_OUT', 'CANCELLED',
  ]);
  const isRunTerminal = RUN_TERMINAL.has(runStatus);

  const progressPct = isRunTerminal
    ? 100
    : (total > 0 ? Math.round(((completed + failed + skipped) / total) * 100) : 0);

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      padding: '10px 20px',
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(6px)',
      borderBottom: '1px solid var(--v2-border-default)',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Activity size={15} style={{ color: 'var(--v2-action)' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--v2-text-primary)' }}>Execution Pipeline</span>
      </div>

      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--v2-text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--v2-text-secondary)' }} />
          {completed} passed
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--v2-danger)' }} />
          {failed} failed
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--v2-action)' }} />
          {running} running
        </span>
        {/* RC-21 — Skipped is its own category. Lumping it into "pending"
            (as before) misled the operator into reading the run as
            "67% done" on a completed SWITCH-branched workflow. The
            visual treatment matches a node's SKIPPED chip (slate dot). */}
        {skipped > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Nodes the runtime intentionally bypassed (e.g. the dead leg of a SWITCH or a false branch)">
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--v2-text-disabled)' }} />
            {skipped} skipped
          </span>
        )}
        {pending > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--v2-border-default)' }} />
            {pending} pending
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ flex: 1, maxWidth: 180, marginLeft: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--v2-text-muted)', marginBottom: 2 }}>
          <span>Progress</span>
          <span>{progressPct}%</span>
        </div>
        <div style={{ height: 5, background: 'var(--v2-border-default)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            borderRadius: 3,
            width: `${progressPct}%`,
            background: failed > 0 ? 'linear-gradient(90deg, var(--v2-text-secondary), var(--v2-danger))' : 'var(--v2-text-secondary)',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {selectedRun && (
        <div style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>
          Run #{selectedRun.executionNumber || selectedRun.id?.substring(0, 8)}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EXECUTION DETAIL PANEL — Bottom half with tabbed Input/Output/Logs/Audit
// ============================================================================

// ============================================================================
// buildWorkflowJSON — Convert internal canvas format to Conductor-style JSON
// ============================================================================
function buildWorkflowJSON(nodes, connections, meta) {
  if (!nodes || nodes.length === 0) return null;

  // Build adjacency: nodeId → [downstream nodeIds]
  const downstream = {};
  (connections || []).forEach((c) => {
    const from = c.from || c.source;
    const to = c.to || c.target;
    const fromPort = c.fromPort || c.sourceHandle || 'default';
    if (!downstream[from]) downstream[from] = [];
    downstream[from].push({ to, fromPort });
  });

  // Map each canvas node → Conductor-style task
  const tasks = nodes.map((node) => {
    const cfg = node.config || node.data?.config || {};
    const nodeType = node.type || node.data?.nodeType || 'unknown';
    const label = node.label || node.data?.label || node.id;

    // Determine Conductor-equivalent task type
    let taskType = 'SIMPLE';
    if (nodeType === 'condition') taskType = 'SWITCH';
    else if (nodeType === 'manual_start' || nodeType === 'trigger') taskType = 'START';
    else if (nodeType === 'parallel_fork' || nodeType === 'fork') taskType = 'FORK';
    else if (nodeType === 'parallel_join' || nodeType === 'join') taskType = 'JOIN';
    else if (nodeType === 'delay' || nodeType === 'wait') taskType = 'WAIT';
    else if (nodeType === 'connector_task' || nodeType === 'connector') taskType = 'CONNECTOR';
    else if (nodeType === 'error_handler') taskType = 'ERROR_HANDLER';
    else if (nodeType === 'http_task' || nodeType === 'http') taskType = 'HTTP';
    else if (nodeType === 'script_task' || nodeType === 'script') taskType = 'SCRIPT';
    else if (nodeType === 'notification_task') taskType = 'NOTIFICATION';
    else if (nodeType === 'data_transform' || nodeType === 'variable_set') taskType = 'DATA_TRANSFORM';
    else if (nodeType === 'skill_task' || nodeType === 'skill') taskType = 'SKILL';
    else if (nodeType === 'agent_task' || nodeType === 'agent') taskType = 'AGENT';

    const task = {
      name: label,
      taskReferenceName: node.id,
      type: taskType,
      inputParameters: {},
    };

    // Populate inputParameters from config
    if (cfg.connectorType) task.inputParameters.connectorType = cfg.connectorType;
    if (cfg.connectorId) task.inputParameters.connectorId = cfg.connectorId;
    if (cfg.action) task.inputParameters.action = cfg.action;
    if (cfg.skillParams && Object.keys(cfg.skillParams).length > 0) {
      task.inputParameters.skillParams = cfg.skillParams;
    }
    if (cfg.expression || cfg.conditionExpression) {
      task.inputParameters.expression = cfg.expression || cfg.conditionExpression;
    }
    if (cfg.inputMapping && Object.keys(cfg.inputMapping).length > 0) {
      task.inputParameters.inputMapping = cfg.inputMapping;
    }
    if (cfg.errorTypes) task.inputParameters.errorTypes = cfg.errorTypes;
    if (cfg.retryCount != null) task.inputParameters.retryCount = cfg.retryCount;
    if (cfg.fallbackAction) task.inputParameters.fallbackAction = cfg.fallbackAction;
    if (cfg.script) task.inputParameters.script = cfg.script;
    if (cfg.language) task.inputParameters.language = cfg.language;
    if (cfg.outputVariable) task.outputVariable = cfg.outputVariable;

    // Condition/switch: add decision cases from port edges
    if (taskType === 'SWITCH') {
      const trueTargets = (downstream[node.id] || []).filter(d => d.fromPort === 'true').map(d => d.to);
      const falseTargets = (downstream[node.id] || []).filter(d => d.fromPort === 'false').map(d => d.to);
      task.decisionCases = {};
      if (trueTargets.length > 0) task.decisionCases['true'] = trueTargets;
      if (falseTargets.length > 0) task.decisionCases['false'] = falseTargets;
      if (cfg.trueLabel) task.inputParameters.trueLabel = cfg.trueLabel;
      if (cfg.falseLabel) task.inputParameters.falseLabel = cfg.falseLabel;
    }

    // Add downstream connections
    const nexts = (downstream[node.id] || []).filter(d => d.fromPort === 'default');
    if (nexts.length === 1) task.next = nexts[0].to;
    else if (nexts.length > 1) task.next = nexts.map(d => d.to);

    return task;
  });

  return {
    name: meta?.name || 'Untitled Workflow',
    description: meta?.description || '',
    version: meta?.version || 1,
    schemaVersion: 1,
    ownerEmail: meta?.createdByName || meta?.createdBy || '',
    status: meta?.status || 'draft',
    inputParameters: [],
    tasks,
    connections: (connections || []).map((c) => ({
      from: c.from || c.source,
      to: c.to || c.target,
      fromPort: c.fromPort || c.sourceHandle || 'default',
      toPort: c.toPort || c.targetHandle || 'default',
    })),
  };
}

// ============================================================================
// parseWorkflowJSON — Convert Conductor-style JSON back to canvas nodes/connections
// ============================================================================
function parseWorkflowJSON(json) {
  if (!json || !json.tasks || !Array.isArray(json.tasks)) return null;

  // Map Conductor task types back to internal node types
  const typeMap = {
    START: 'manual_start', SWITCH: 'condition', CONNECTOR: 'connector_task',
    HTTP: 'http_task', SCRIPT: 'script_task', NOTIFICATION: 'notification_task',
    DATA_TRANSFORM: 'data_transform', WAIT: 'delay', FORK: 'parallel_fork',
    JOIN: 'parallel_join', ERROR_HANDLER: 'error_handler', SKILL: 'skill_task',
    AGENT: 'agent_task', SIMPLE: 'connector_task',
  };

  const nodes = json.tasks.map((task, idx) => {
    const nodeType = typeMap[task.type] || 'connector_task';
    const ip = task.inputParameters || {};
    const config = {};

    // Restore config fields from inputParameters
    if (ip.connectorType) config.connectorType = ip.connectorType;
    if (ip.connectorId) config.connectorId = ip.connectorId;
    if (ip.action) config.action = ip.action;
    if (ip.skillParams) config.skillParams = ip.skillParams;
    if (ip.expression) { config.expression = ip.expression; config.conditionExpression = ip.expression; }
    if (ip.inputMapping) config.inputMapping = ip.inputMapping;
    if (ip.errorTypes) config.errorTypes = ip.errorTypes;
    if (ip.retryCount != null) config.retryCount = ip.retryCount;
    if (ip.fallbackAction) config.fallbackAction = ip.fallbackAction;
    if (ip.script) config.script = ip.script;
    if (ip.language) config.language = ip.language;
    if (ip.trueLabel) config.trueLabel = ip.trueLabel;
    if (ip.falseLabel) config.falseLabel = ip.falseLabel;
    if (task.outputVariable) config.outputVariable = task.outputVariable;

    return {
      id: task.taskReferenceName || `n_${Date.now()}_${String(idx).padStart(3, '0')}`,
      x: 400,
      y: 80 + idx * 160,
      type: nodeType,
      label: task.name || task.taskReferenceName || `Task ${idx + 1}`,
      config,
    };
  });

  // Rebuild connections from the JSON
  let connections = [];
  if (json.connections && Array.isArray(json.connections)) {
    connections = json.connections.map((c) => ({
      from: c.from, to: c.to,
      fromPort: c.fromPort || 'default',
      toPort: c.toPort || 'default',
    }));
  } else {
    // Fallback: infer connections from task.next and decisionCases
    json.tasks.forEach((task) => {
      const fromId = task.taskReferenceName;
      if (task.next) {
        const nexts = Array.isArray(task.next) ? task.next : [task.next];
        nexts.forEach((toId) => {
          connections.push({ from: fromId, to: toId, fromPort: 'default', toPort: 'default' });
        });
      }
      if (task.decisionCases) {
        Object.entries(task.decisionCases).forEach(([port, targets]) => {
          const targetList = Array.isArray(targets) ? targets : [targets];
          targetList.forEach((toId) => {
            connections.push({ from: fromId, to: toId, fromPort: port, toPort: 'default' });
          });
        });
      }
    });
  }

  // Ensure name is always a clean string (guard against nested objects)
  const safeName = typeof json.name === 'string'
    ? json.name
    : (json.name?.name || JSON.stringify(json.name || 'Imported Workflow')).replace(/^"|"$/g, '');

  return { nodes, connections, name: safeName, description: json.description };
}

// Detect if a user message contains a pasted workflow JSON definition
function extractWorkflowJSON(message) {
  if (!message) return null;
  // Try to find a JSON object in the message (may be surrounded by text)
  const jsonMatch = message.match(/\{[\s\S]*"tasks"\s*:\s*\[[\s\S]*\][\s\S]*\}/);
  if (!jsonMatch) return null;
  try {
    const parsed = JSON.parse(jsonMatch[0]);
    if (parsed.tasks && Array.isArray(parsed.tasks) && parsed.tasks.length > 0) {
      return parsed;
    }
  } catch { /* not valid JSON */ }
  return null;
}

function ExecutionDetailPanel({
  nodeExecutions,
  selectedNodeId,
  onSelectNode,
  activeTab,
  onTabChange,
  auditLog,
  workflowAuditLog,
  selectedRun,
  workflowNodes,
  workflowConnections,
  workflowMeta,
}) {
  const selectedExec = nodeExecutions.find((n) => n.nodeId === selectedNodeId);
  const [jsonCopied, setJsonCopied] = React.useState(false);
  const [jsonCollapsed, setJsonCollapsed] = React.useState(false);
  const [jsonEditMode, setJsonEditMode] = React.useState(false);
  const [jsonEditValue, setJsonEditValue] = React.useState('');

  const workflowJSON = React.useMemo(
    () => buildWorkflowJSON(workflowNodes, workflowConnections, workflowMeta),
    [workflowNodes, workflowConnections, workflowMeta]
  );
  const jsonString = React.useMemo(
    () => workflowJSON ? JSON.stringify(workflowJSON, null, 2) : '{}',
    [workflowJSON]
  );

  const tabs = [
    { id: 'definition', label: 'Definition', icon: Hash },
    { id: 'input', label: 'Input', icon: ArrowRight },
    { id: 'output', label: 'Output', icon: Download },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'audit', label: 'Audit', icon: Clipboard },
    { id: 'steps', label: 'All Steps', icon: List },
  ];

  return (
    <div style={{
      flex: '0 0 50%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--v2-bg-surface)',
      overflow: 'hidden',
      minHeight: 0,
    }}>
      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--v2-border-default)',
        background: 'var(--v2-bg-elevated)',
        gap: 0,
        padding: '0 16px',
        minWidth: 0,           // allow flex children to shrink (long IDs)
        flexWrap: 'wrap',      // wrap to a new line on very narrow panels
      }}>
        {/* Selected node indicator. The nodeId can be very long (e.g.
            workflow-emitted refs like
              enf_dpg_internode_n_check_endpoint_ref_5955e1_…)
            so we cap it at 240px and let it ellipsis. Hover shows the
            full id via title. */}
        {selectedExec && (
          <div
            title={selectedExec.nodeId}
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--v2-text-primary)',
              padding: '10px 12px 10px 0',
              borderRight: '1px solid var(--v2-border-default)',
              marginRight: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
              maxWidth: 240,
              overflow: 'hidden',
            }}
          >
            <RunStatusIcon status={selectedExec.status} />
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}>
              {selectedExec.nodeId}
            </span>
          </div>
        )}

        {tabs.map(({ id, label, icon: TabIcon }) => (
          <button
            key={id}
            onClick={() => onTabChange(id)}
            style={{
              padding: '10px 14px',
              background: activeTab === id ? 'var(--v2-bg-surface)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === id ? '2px solid var(--v2-text-primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: activeTab === id ? 700 : 400,
              color: activeTab === id ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <TabIcon size={12} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
        {/* INPUT TAB */}
        {activeTab === 'input' && (
          selectedExec ? (
            <div>
              <h4 style={{
                fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12,
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                Input Data for: {selectedExec.nodeId}
              </h4>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', display: 'block', marginBottom: 4 }}>Node Type</label>
                <div style={{ fontSize: 12, color: 'var(--v2-text-primary)', padding: '8px 10px', background: 'var(--v2-bg-muted)', borderRadius: 4 }}>
                  {selectedExec.nodeType}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', display: 'block', marginBottom: 4 }}>Status</label>
                <div style={{
                  fontSize: 12, fontWeight: 600,
                  color: selectedExec.status === 'completed' ? 'var(--v2-text-secondary)' : selectedExec.status === 'failed' ? 'var(--v2-danger)' : 'var(--v2-action)',
                  padding: '8px 10px',
                  background: 'var(--v2-bg-muted)',
                  borderRadius: 4,
                  textTransform: 'uppercase',
                }}>
                  {selectedExec.status}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)', display: 'block', marginBottom: 4 }}>Input Parameters</label>
                <pre style={{
                  fontSize: 11,
                  background: 'var(--v2-text-primary)',
                  color: 'var(--v2-success-soft)',
                  padding: '12px 14px',
                  borderRadius: 6,
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: 200,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {selectedExec.input
                    ? JSON.stringify(selectedExec.input, null, 2)
                    : '// No input data recorded for this step'}
                </pre>
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
              <ArrowRight size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div>Click a step in the timeline above to view its input data</div>
            </div>
          )
        )}

        {/* OUTPUT TAB */}
        {activeTab === 'output' && (
          selectedExec ? (
            <div>
              <h4 style={{
                fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12,
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                Output Data for: {selectedExec.nodeId}
              </h4>
              {selectedExec.error && (
                <div style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  background: 'var(--v2-danger-soft)',
                  border: '1px solid var(--v2-danger-soft)',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}>
                  <XCircle size={16} style={{ color: 'var(--v2-danger)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--v2-danger)', marginBottom: 4 }}>Execution Failed</div>
                    <div style={{ fontSize: 11, color: 'var(--v2-danger)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {selectedExec.error}
                    </div>
                  </div>
                </div>
              )}
              {selectedExec.output ? (
                <pre style={{
                  fontSize: 11,
                  background: 'var(--v2-text-primary)',
                  color: 'var(--v2-success-soft)',
                  padding: '12px 14px',
                  borderRadius: 6,
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: 300,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(selectedExec.output, null, 2)}
                </pre>
              ) : (
                <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, padding: '16px 0' }}>
                  {selectedExec.status === 'pending' ? 'Step has not executed yet' :
                   selectedExec.status === 'running' ? 'Step is currently executing...' :
                   'No output data recorded'}
                </div>
              )}
              {/* Timing info */}
              <div style={{ display: 'flex', gap: 24, marginTop: 16, fontSize: 11, color: 'var(--v2-text-muted)' }}>
                {selectedExec.startedAt && (
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>Started:</span>{' '}
                    {new Date(selectedExec.startedAt).toLocaleString()}
                  </div>
                )}
                {selectedExec.completedAt && (
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>Completed:</span>{' '}
                    {new Date(selectedExec.completedAt).toLocaleString()}
                  </div>
                )}
                {selectedExec.durationMs != null && (
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>Duration:</span>{' '}
                    {(selectedExec.durationMs / 1000).toFixed(3)}s
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
              <Download size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div>Click a step in the timeline above to view its output data</div>
            </div>
          )
        )}

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12 }}>
              Execution Log
            </h4>
            <div style={{
              background: 'var(--v2-text-primary)',
              borderRadius: 6,
              padding: '12px 14px',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: 11,
              color: 'var(--v2-border-default)',
              maxHeight: 400,
              overflowY: 'auto',
              lineHeight: 1.7,
            }}>
              {nodeExecutions.length === 0 ? (
                <span style={{ color: 'var(--v2-text-muted)' }}>No execution data</span>
              ) : (
                nodeExecutions.map((ne, idx) => {
                  const ts = ne.startedAt ? new Date(ne.startedAt).toLocaleTimeString() : '--:--:--';
                  const statusColor = ne.status === 'completed' ? 'var(--v2-text-secondary)' : ne.status === 'failed' ? 'var(--v2-danger)' : ne.status === 'running' ? 'var(--v2-action)' : 'var(--v2-text-muted)';
                  return (
                    <div key={ne.id || idx} style={{
                      padding: '2px 0',
                      cursor: 'pointer',
                      background: selectedNodeId === ne.nodeId ? 'rgba(59,130,246,0.1)' : 'transparent',
                      borderRadius: 2,
                      wordBreak: 'break-all',
                      overflowWrap: 'anywhere',
                    }}
                    onClick={() => onSelectNode(ne.nodeId)}
                    >
                      <span style={{ color: 'var(--v2-text-muted)' }}>[{ts}]</span>{' '}
                      <span style={{ color: statusColor, fontWeight: 600 }}>{ne.status.toUpperCase().padEnd(10)}</span>{' '}
                      <span style={{ color: 'var(--v2-action-soft)' }}>{ne.nodeId}</span>{' '}
                      <span style={{ color: 'var(--v2-text-disabled)' }}>({ne.nodeType})</span>
                      {ne.durationMs != null && (
                        <span style={{ color: 'var(--v2-text-muted)' }}> [{(ne.durationMs / 1000).toFixed(2)}s]</span>
                      )}
                      {ne.error && (
                        <div style={{ color: 'var(--v2-danger-soft)', paddingLeft: 24, fontSize: 10, marginTop: 1 }}>
                          ↳ Error: {ne.error}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* AUDIT TAB */}
        {activeTab === 'audit' && (
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12 }}>
              Audit Trail
            </h4>
            {auditLog.length === 0 && workflowAuditLog.length === 0 ? (
              <div style={{ color: 'var(--v2-text-disabled)', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>
                No audit entries recorded
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {(auditLog.length > 0 ? auditLog : workflowAuditLog).map((entry, idx) => (
                  <div key={entry.id || idx} style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--v2-bg-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 12,
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'var(--v2-bg-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <FileText size={14} style={{ color: 'var(--v2-text-muted)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--v2-text-primary)' }}>
                        {entry.action}
                      </div>
                      {entry.userName && (
                        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                          by {entry.userName}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--v2-text-disabled)', whiteSpace: 'nowrap' }}>
                      {entry.createdAt && new Date(entry.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DEFINITION TAB — Conductor-style JSON */}
        {activeTab === 'definition' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 8, flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', margin: 0 }}>
                  Workflow Definition
                </h4>
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 4,
                  background: 'var(--v2-bg-muted)', color: 'var(--v2-success)', fontWeight: 600,
                }}>
                  JSON
                </span>
                {workflowJSON && (
                  <span style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>
                    {workflowJSON.tasks?.length || 0} tasks · v{workflowJSON.version}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {!jsonEditMode && (
                  <>
                    <button
                      onClick={() => { setJsonCollapsed(!jsonCollapsed); }}
                      style={{
                        fontSize: 10, padding: '4px 8px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 4, background: 'var(--v2-bg-surface)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4, color: 'var(--v2-text-secondary)',
                      }}
                      title={jsonCollapsed ? 'Expand' : 'Collapse'}
                    >
                      {jsonCollapsed ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                      {jsonCollapsed ? 'Expand' : 'Collapse'}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(jsonString);
                        setJsonCopied(true);
                        setTimeout(() => setJsonCopied(false), 2000);
                      }}
                      style={{
                        fontSize: 10, padding: '4px 8px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 4, background: jsonCopied ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                        color: jsonCopied ? 'var(--v2-success)' : 'var(--v2-text-secondary)',
                        transition: 'all 0.2s',
                      }}
                      title="Copy JSON to clipboard"
                    >
                      {jsonCopied ? <CheckCircle size={10} /> : <Copy size={10} />}
                      {jsonCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={() => { setJsonEditValue(jsonString); setJsonEditMode(true); }}
                      style={{
                        fontSize: 10, padding: '4px 8px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 4, background: 'var(--v2-bg-surface)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4, color: 'var(--v2-text-secondary)',
                      }}
                      title="Edit JSON"
                    >
                      <PenTool size={10} />
                      Edit
                    </button>
                  </>
                )}
                {jsonEditMode && (
                  <>
                    <button
                      onClick={() => setJsonEditMode(false)}
                      style={{
                        fontSize: 10, padding: '4px 8px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 4, background: 'var(--v2-bg-surface)', cursor: 'pointer',
                        color: 'var(--v2-text-muted)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(jsonEditValue);
                        setJsonCopied(true);
                        setTimeout(() => setJsonCopied(false), 2000);
                      }}
                      style={{
                        fontSize: 10, padding: '4px 8px', border: '1px solid var(--v2-border-default)',
                        borderRadius: 4, background: jsonCopied ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                        color: jsonCopied ? 'var(--v2-success)' : 'var(--v2-text-secondary)',
                      }}
                    >
                      {jsonCopied ? <CheckCircle size={10} /> : <Copy size={10} />}
                      {jsonCopied ? 'Copied!' : 'Copy Edits'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Hint banner */}
            <div style={{
              fontSize: 10, color: 'var(--v2-text-muted)', padding: '6px 10px', marginBottom: 8,
              background: 'var(--v2-bg-elevated)', borderRadius: 4, border: '1px solid var(--v2-bg-muted)',
              lineHeight: 1.6, flexShrink: 0,
            }}>
              <strong>Tip:</strong> Copy this JSON and paste it into the <strong>AI Workflow Builder</strong> chat
              with a prompt like <em>"Create this workflow"</em> to recreate or modify it.
              Use <strong>Edit</strong> to fix errors directly.
            </div>

            {/* JSON content */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {jsonEditMode ? (
                <textarea
                  value={jsonEditValue}
                  onChange={(e) => setJsonEditValue(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: '100%', height: '100%', minHeight: 300,
                    fontSize: 11, fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    background: 'var(--v2-text-primary)', color: 'var(--v2-success-soft)', border: '1px solid var(--v2-text-secondary)',
                    borderRadius: 6, padding: '12px 14px', resize: 'none',
                    lineHeight: 1.5, whiteSpace: 'pre', tabSize: 2,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              ) : (
                <pre style={{
                  fontSize: 11, fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  background: 'var(--v2-text-primary)', color: 'var(--v2-border-default)', padding: '12px 14px',
                  borderRadius: 6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  lineHeight: 1.5, overflowX: 'auto',
                  maxHeight: jsonCollapsed ? 120 : 'none',
                  overflow: jsonCollapsed ? 'hidden' : 'auto',
                  position: 'relative',
                }}>
                  {/* Syntax-highlighted JSON */}
                  {jsonString.split('\n').map((line, i) => {
                    // Simple syntax highlighting
                    const highlighted = line
                      .replace(/"([^"]+)":/g, '<span style="color:var(--v2-accent-cyan-soft)">"$1"</span>:')  // keys
                      .replace(/: "(.*?)"/g, ': <span style="color:var(--v2-success-soft)">"$1"</span>')   // string values
                      .replace(/: (\d+)/g, ': <span style="color:var(--v2-warning)">$1</span>')       // numbers
                      .replace(/: (true|false)/g, ': <span style="color:var(--v2-accent-trust-soft)">$1</span>') // booleans
                      .replace(/: (null)/g, ': <span style="color:var(--v2-text-muted)">$1</span>');      // null
                    return (
                      <div key={i} style={{ display: 'flex' }}>
                        <span style={{
                          display: 'inline-block', width: 36, flexShrink: 0,
                          color: 'var(--v2-text-secondary)', textAlign: 'right', paddingRight: 12,
                          userSelect: 'none', fontSize: 10,
                        }}>
                          {i + 1}
                        </span>
                        <span dangerouslySetInnerHTML={{ __html: highlighted }} />
                      </div>
                    );
                  })}
                  {jsonCollapsed && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: 40,
                      background: 'linear-gradient(transparent, var(--v2-text-primary))',
                      pointerEvents: 'none',
                    }} />
                  )}
                </pre>
              )}
            </div>
          </div>
        )}

        {/* ALL STEPS TAB */}
        {activeTab === 'steps' && (
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 12 }}>
              All Steps ({nodeExecutions.length})
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--v2-border-default)' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>#</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Node</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Type</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Status</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Duration</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Started</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--v2-text-muted)', fontSize: 11 }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {nodeExecutions.map((ne, idx) => {
                  const statusColor = ne.status === 'completed' ? 'var(--v2-text-secondary)' : ne.status === 'failed' ? 'var(--v2-danger)' : ne.status === 'running' ? 'var(--v2-action)' : 'var(--v2-text-muted)';
                  return (
                    <tr
                      key={ne.id || idx}
                      onClick={() => onSelectNode(ne.nodeId)}
                      style={{
                        borderBottom: '1px solid var(--v2-bg-muted)',
                        cursor: 'pointer',
                        background: selectedNodeId === ne.nodeId ? 'var(--v2-bg-muted)' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <td style={{ padding: '8px 10px', color: 'var(--v2-text-disabled)', fontWeight: 600 }}>{idx + 1}</td>
                      <td
                        title={ne.nodeId}
                        style={{
                          padding: '8px 10px', fontWeight: 500, color: 'var(--v2-text-primary)',
                          maxWidth: 280,
                          wordBreak: 'break-all', overflowWrap: 'anywhere',
                        }}
                      >
                        {ne.nodeId}
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--v2-text-muted)' }}>{ne.nodeType}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 600, color: statusColor, textTransform: 'uppercase',
                        }}>
                          <RunStatusIcon status={ne.status} />
                          {ne.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--v2-text-muted)', fontFamily: 'monospace', fontSize: 11 }}>
                        {ne.durationMs != null ? `${(ne.durationMs / 1000).toFixed(2)}s` : '-'}
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--v2-text-disabled)', fontSize: 11 }}>
                        {ne.startedAt ? new Date(ne.startedAt).toLocaleTimeString() : '-'}
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--v2-danger)', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={ne.error || ''}
                      >
                        {ne.error || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ConnectorsList removed — connectors are now shown only inside the Connectors node category
